/**
 * main.js — Redfur Trading Dashboard SPA (entry point)
 *
 * Hash-based routing. Fetches JSON from /api/* endpoints.
 * Admin routes injected after auth check.
 *
 * Sprint 11 Phase 4: This file is the bundler entry. Pure constants and
 * pure utility functions have been extracted into ./constants/* and
 * ./utils/* modules. The bulk of the SPA still lives here pending
 * incremental further extraction.
 */
'use strict';

import { ESO_ICON_SVG, DISCORD_SVG, DISCORD_BADGE_SVG } from './constants/icons.js';
import { FISSAL_TRANSITION_MSGS } from './constants/fissal.js';
import {
  TAMRIEL_GAMETIME_OFFSET,
  TAMRIEL_GAMETIME_CALIBRATION,
  TAMRIEL_EPOCH_SEC,
  TAMRIEL_SECS_PER_GAMEDAY,
  TAMRIEL_SECS_PER_GAMEYEAR,
  TAMRIEL_MONTHS,
  TAMRIEL_DAYS_IN_MONTH,
  LUNAR_CYCLE_DAYS,
  LUNAR_CYCLE_SEC,
  LUNAR_FULL_EPOCH_SEC,
  LORE_JODE_CYCLE_SEC,
  LORE_JONE_CYCLE_SEC,
  TOD_STATES,
  TOD_COMMENTARY,
} from './constants/tamriel.js';
import { getTamrielTime, getMoonLitStyle } from './utils/tamrielTime.js';

// ── State ────────────────────────────────────────────────────────────────
let currentUser = null;
let currentRoute = '/';
let weekLabels = []; // [{index,label,start,end},...]
let _fissalMsgs = []; // Shared Fissal chat session — synced across bubble + full page
let _fissalActiveSessionId = null; // Sprint 12 Phase 4: server-side session id this conversation belongs to
let _fissalAvatarUrl = null; // Bot's live Discord avatar URL, fetched once on login

// ── Fissal page-transition overlay ───────────────────────────────────────
let _fissalTransOverlay = null;
let _fissalTransTimer = null;

function _showFissalTransition() {
  if (_fissalTransOverlay) return;
  const msg = FISSAL_TRANSITION_MSGS[Math.floor(Math.random() * FISSAL_TRANSITION_MSGS.length)];
  const avatarSrc = _fissalAvatarUrl || '/assets/fissal-icon.png';
  const el = document.createElement('div');
  el.id = 'fissal-transition-overlay';
  el.innerHTML = `
    <div class="ftl-gear-wrap">
      <div class="ftl-gear"></div>
      <div class="ftl-inner-ring"></div>
      <img class="ftl-face" src="${escapeHtml(avatarSrc)}" alt="Fissal"
        onerror="this.style.display='none';this.nextElementSibling.style.display='block'">
      <span class="ftl-face-fallback" style="display:none">🐱</span>
    </div>
    <div class="ftl-message">${escapeHtml(msg)}</div>
    <div class="ftl-sub">— Fissal Relay</div>
  `;
  document.body.appendChild(el);
  _fissalTransOverlay = el;
}

function _hideFissalTransition() {
  clearTimeout(_fissalTransTimer);
  _fissalTransTimer = null;
  if (!_fissalTransOverlay) return;
  const el = _fissalTransOverlay;
  _fissalTransOverlay = null;
  el.classList.add('fading-out');
  el.addEventListener('animationend', () => el.remove(), { once: true });
  setTimeout(() => el.remove(), 400);
}

// ── DOM Refs ─────────────────────────────────────────────────────────────
const $content = document.getElementById('app-content');
const $sidebarNav = document.getElementById('sidebar-nav');
const $sidebarFooter = document.getElementById('sidebar-footer');
const $userArea = document.getElementById('user-area');
const $syncIndicator = document.getElementById('sync-indicator');
const $footerVersion = document.getElementById('footer-version');
let SITE_VERSION = 'v3.0.0'; // updated from /api/healthz on boot

// Backward-compat alias (some old code references $nav)
const $nav = $sidebarNav;

// ══════════════════════════════════════════════════════════════════════════
// TAMRIEL TIME ENGINE — constants and pure helpers extracted to
// ./constants/tamriel.js and ./utils/tamrielTime.js. The DOM-bound renderers
// (renderLunarClock, updateTimeOfDayTheme) remain below.
// ══════════════════════════════════════════════════════════════════════════

let _lunarClockInterval = null;

function renderLunarClock() {
  const $clock = document.getElementById('lunar-clock');
  if (!$clock) return;

  let _clockInit = false;

  function update() {
    const t = getTamrielTime();
    const hourStr = String(t.hour % 12 || 12);
    const minuteStr = String(t.minute).padStart(2, '0');
    const ampm = t.hour >= 12 ? 'PM' : 'AM';
    const ordinal = t.dayOfMonth === 1 ? 'st' : t.dayOfMonth === 2 ? 'nd' : t.dayOfMonth === 3 ? 'rd' : 'th';

    const commentary = TOD_COMMENTARY[t.todKey] || [];
    const commentaryText = commentary[Math.floor(Date.now() / 300000) % commentary.length] || '';

    const esoJodePct  = Math.round((1 - Math.abs(t.jodePhase * 2 - 1)) * 100);
    const esoJonePct  = Math.round((1 - Math.abs(t.jonePhase * 2 - 1)) * 100);
    const loreJodePct = Math.round((1 - Math.abs(t.loreJodePhase * 2 - 1)) * 100);
    const loreJonePct = Math.round((1 - Math.abs(t.loreJonePhase * 2 - 1)) * 100);
    const loreNote    = 'ESO syncs both moons (not lore-accurate). By the books, Jode (Masser) cycles every 30 days and Jone (Secunda) every 15.';

    const isNight   = (t.todKey === 'late_night' || t.todKey === 'night');
    const showSpawn = isNight && esoJodePct >= 70;
    const spawnMsg  = '🐺 Bloodmoon \u2014 contagious WW & 🧛 vampire NPCs active: Bangkorai \xb7 The Rift \xb7 Reaper\'s March';

    if (!_clockInit) {
      $clock.innerHTML = `
        <div class="lunar-moons">
          <div class="moon moon-jode" title="Jode (Masser)">
            <div class="moon-lit" data-lunar="jode" style="${getMoonLitStyle(t.jodePhase, 24)}"></div>
          </div>
          <div class="moon moon-jone" title="Jone (Secunda)">
            <div class="moon-lit" data-lunar="jone" style="${getMoonLitStyle(t.jonePhase, 18)}"></div>
          </div>
        </div>
        <span class="lunar-time-text" data-lunar="time">
          ${hourStr}:${minuteStr} ${ampm}
          <span class="lunar-time-date" data-lunar="date">${t.dayOfMonth}${ordinal} ${t.monthName}, ${t.era} ${t.year}</span>
        </span>
        <div class="lunar-tooltip">
          <div class="lunar-tooltip-date" data-lunar="ttdate">${t.dayOfMonth}${ordinal} of ${t.monthName}, ${t.era} ${t.year} — ${hourStr}:${minuteStr} ${ampm}</div>
          <div data-lunar="ttmoons">🌙 ESO: Jode ${esoJodePct}% lit · Jone: ${esoJonePct}% lit</div>
          <div data-lunar="ttlore" style="opacity:0.85;">📖 Lore Accurate: Jode ${loreJodePct}% · Jone ${loreJonePct}%</div>
          <div data-lunar="ttlorenote" style="opacity:0.6;font-size:0.8em;font-style:italic;margin-top:0.1rem;">${loreNote}</div>
          <div data-lunar="ttspawn" style="${showSpawn ? 'color:#c0933a;margin-top:0.2rem;font-size:0.8em;' : 'display:none;'}">${showSpawn ? spawnMsg : ''}</div>
          <div style="color:var(--gold);margin-top:0.15rem;" data-lunar="tttod">☀ ${t.todLabel}</div>
          <div class="lunar-tooltip-commentary" data-lunar="ttcomm">${commentaryText}</div>
        </div>
      `;
      _clockInit = true;
    } else {
      // Targeted updates — no DOM reconstruction
      const jode = $clock.querySelector('[data-lunar="jode"]');
      const jone = $clock.querySelector('[data-lunar="jone"]');
      const timeEl = $clock.querySelector('[data-lunar="time"]');
      const dateEl = $clock.querySelector('[data-lunar="date"]');
      const ttDate = $clock.querySelector('[data-lunar="ttdate"]');
      const ttMoons = $clock.querySelector('[data-lunar="ttmoons"]');
      const ttLore = $clock.querySelector('[data-lunar="ttlore"]');
      const ttTod = $clock.querySelector('[data-lunar="tttod"]');
      const ttComm = $clock.querySelector('[data-lunar="ttcomm"]');
      if (jode) jode.setAttribute('style', getMoonLitStyle(t.jodePhase, 24));
      if (jone) jone.setAttribute('style', getMoonLitStyle(t.jonePhase, 18));
      if (dateEl) dateEl.textContent = `${t.dayOfMonth}${ordinal} ${t.monthName}, ${t.era} ${t.year}`;
      if (timeEl) { timeEl.firstChild.textContent = `${hourStr}:${minuteStr} ${ampm} `; }
      if (ttDate) ttDate.textContent = `${t.dayOfMonth}${ordinal} of ${t.monthName}, ${t.era} ${t.year} — ${hourStr}:${minuteStr} ${ampm}`;
      if (ttMoons) ttMoons.textContent = `🌙 ESO: Jode ${esoJodePct}% lit · Jone: ${esoJonePct}% lit`;
      if (ttLore) ttLore.textContent = `📖 Lore Accurate: Jode ${loreJodePct}% · Jone ${loreJonePct}%`;
      const ttSpawn = $clock.querySelector('[data-lunar="ttspawn"]');
      if (ttSpawn) {
        if (showSpawn) {
          ttSpawn.style.cssText = 'color:#c0933a;margin-top:0.2rem;font-size:0.8em;';
          ttSpawn.textContent = spawnMsg;
        } else {
          ttSpawn.style.cssText = 'display:none;';
        }
      }
      if (ttTod) ttTod.textContent = `☀ ${t.todLabel}`;
      if (ttComm) ttComm.textContent = commentaryText;
    }

    // Update time-of-day CSS theming
    updateTimeOfDayTheme(t);
  }

  update();
  if (_lunarClockInterval) clearInterval(_lunarClockInterval);
  _lunarClockInterval = setInterval(update, 10_000); // Every 10 real seconds
}

function updateTimeOfDayTheme(t) {
  const state = TOD_STATES[t.todKey];
  if (!state) return;
  const root = document.documentElement;
  root.style.setProperty('--tod-hue', state.hue);
  root.style.setProperty('--tod-sat', state.sat);
  root.style.setProperty('--tod-bright', state.bright);
  root.style.setProperty('--tod-glow-r', state.glowR);
  root.style.setProperty('--tod-glow-g', state.glowG);
  root.style.setProperty('--tod-glow-b', state.glowB);
}

// ══════════════════════════════════════════════════════════════════════════
// SIDEBAR RENDERER
// ══════════════════════════════════════════════════════════════════════════

function renderSidebar() {
  const isLoggedIn = !!currentUser;
  const isAdmin = isLoggedIn && currentUser.isAdmin;

  let html = '';

  // ── 1. Navigation ──
  html += `<div class="sidebar-section sidebar-section-nav">`;
  html += `<div class="sidebar-section-header cat-nav"><span>🧭</span> Navigation</div>`;
  html += `<a href="#/" class="sidebar-link" data-route="/">🏠 Overview</a>`;
  html += `<a href="#/prices" class="sidebar-link" data-route="/prices">🏷️ Item Pricing & Analysis</a>`;
  html += `<a href="#/sales" class="sidebar-link" data-route="/sales">📊 My Sales</a>`;
  html += `<a href="#/leaderboard" class="sidebar-link" data-route="/leaderboard">👑 Leaderboard</a>`;
  html += `<a href="#/sync-stats" class="sidebar-link" data-route="/sync-stats">🔄 Sync Stats</a>`;
  html += `<a href="#/fissal" class="sidebar-link" data-route="/fissal">🐱 Fissal AI</a>`;
  html += `<a href="#/raffle" class="sidebar-link" data-route="/raffle">🎟 Raffle</a>`;
  html += `</div>`;

  // ── 2. Administration (admin only) ──
  if (isAdmin) {
    html += `<div class="sidebar-divider"></div>`;
    html += `<div class="sidebar-section sidebar-section-admin">`;
    html += `<div class="sidebar-section-header cat-admin"><span>🔒</span> Administration</div>`;
    html += `<a href="#/admin/sales" class="sidebar-link" data-route="/admin/sales">📋 Sales Reports</a>`;
    html += `<a href="#/admin/traders" class="sidebar-link" data-route="/admin/traders">📍 Trader Tracker</a>`;
    html += `<a href="#/admin/guild-audit" class="sidebar-link" data-route="/admin/guild-audit">🔍 Guild Audit</a>`;
    html += `<a href="#/admin/memories" class="sidebar-link" data-route="/admin/memories">🧠 AI User Memories</a>`;
    html += `<a href="#/admin/fissal" class="sidebar-link" data-route="/admin/fissal">🤖 Fissal AI Control</a>`;
    html += `<a href="#/admin/storage" class="sidebar-link" data-route="/admin/storage">💾 Storage</a>`;
    html += `<a href="#/admin/diagnostics" class="sidebar-link" data-route="/admin/diagnostics">⚙️ Diagnostics</a>`;
    html += `<a href="#/admin/perf" class="sidebar-link" data-route="/admin/perf">📊 Performance</a>`;
    if (currentUser.isOwner) {
      html += `<a href="#/admin/tools" class="sidebar-link" data-route="/admin/tools">🛠️ Tools</a>`;
    }
    // Sprint 10.0: renamed former "Spy Mode" (view-as-user) to "User View".
    // Spy Mode is now a real admin-only inspection toggle that exposes extra
    // controls around the UI (user cards, rows, etc).
    html += `<label class="sidebar-spy-toggle"><input type="checkbox" id="user-view-toggle" title="User View: preview the site as a regular (non-admin) user. Elements hidden from non-admins are indicated with a glowing icon while enabled."> 👁️ User View</label>`;
    html += `<label class="sidebar-spy-toggle"><input type="checkbox" id="spy-mode-toggle" title="Spy Mode: surface extra admin-only inspection controls across the interface — view-sales, raffle tickets, guild membership buttons on user cards."> 🕵️ Spy Mode</label>`;
    html += `</div>`;
  }

  // ── 3. Tools ──
  html += `<div class="sidebar-divider"></div>`;
  html += `<div class="sidebar-section sidebar-section-tools">`;
  html += `<div class="sidebar-section-header cat-tools"><span>🛠️</span> Tools</div>`;
  html += `<a href="#/api-docs" class="sidebar-link" data-route="/api-docs">🚧 API Docs</a>`;
  html += `</div>`;

  $sidebarNav.innerHTML = html;

  // ── Sidebar footer ──
  let footerHtml = '';
  if (!isLoggedIn) {
    const rememberFlag = localStorage.getItem('redfur-remember-me') === '1';
    const loginUrl = rememberFlag ? '/auth/login?remember=1' : '/auth/login';
    footerHtml += `
      <a href="${loginUrl}" class="sidebar-link sidebar-discord-login">
        <span class="discord-login-label">${DISCORD_SVG}<span>Login with Discord</span></span>
      </a>
      <label class="sidebar-remember-me"><input type="checkbox" id="qs-remember-me" ${rememberFlag ? 'checked' : ''}> Remember Me</label>
    `;
  } else {
    const avatarInner = currentUser.avatar
      ? `<img class="sup-avatar" src="${escapeHtml(currentUser.avatar)}" alt="">`
      : `<div class="sup-avatar-default">👤</div>`;
    const esoTag = currentUser.esoTag || localStorage.getItem('redfur-claimed-tag') || '';
    footerHtml += `
      <div class="sidebar-user-panel" data-ui-action="toggle-settings-panel" title="Settings">
        <div class="sup-avatar-wrap">
          ${avatarInner}
          <div class="sup-status-dot"></div>
        </div>
        <div class="sup-info">
          <div class="sup-name sup-name-discord">${escapeHtml(currentUser.displayName || 'User')}</div>
          ${esoTag ? `<div class="sup-tag sup-tag-eso">${ESO_ICON_SVG}<span>${escapeHtml(esoTag)}</span></div>` : ''}
        </div>
      </div>
    `;
  }
  footerHtml += `<div class="sidebar-divider"></div><div class="sidebar-version-strip"><span class="sidebar-site-version" title="Redfur Trading — ESO guild sales tracker &amp; price check portal">${SITE_VERSION}</span></div>`;
  $sidebarFooter.innerHTML = footerHtml;

  // Remember me handler
  const qsRememberMe = document.getElementById('qs-remember-me');
  if (qsRememberMe) {
    qsRememberMe.addEventListener('change', () => {
      localStorage.setItem('redfur-remember-me', qsRememberMe.checked ? '1' : '0');
    });
  }

  // Sprint 10.0: wire User View + Spy Mode toggles (admin-only checkboxes).
  // Both states persist via localStorage + toggle body classes that drive CSS.
  const userViewToggle = document.getElementById('user-view-toggle');
  if (userViewToggle) {
    const saved = localStorage.getItem('redfur-user-view') === '1';
    userViewToggle.checked = saved;
    document.body.classList.toggle('user-view-mode', saved);
    userViewToggle.addEventListener('change', () => {
      const on = userViewToggle.checked;
      localStorage.setItem('redfur-user-view', on ? '1' : '0');
      document.body.classList.toggle('user-view-mode', on);
    });
  }
  const spyToggle = document.getElementById('spy-mode-toggle');
  if (spyToggle) {
    const saved = localStorage.getItem('redfur-spy-mode') === '1';
    spyToggle.checked = saved;
    document.body.classList.toggle('spy-mode', saved);
    spyToggle.addEventListener('change', () => {
      const on = spyToggle.checked;
      localStorage.setItem('redfur-spy-mode', on ? '1' : '0');
      document.body.classList.toggle('spy-mode', on);
    });
  }

  // Update active state
  updateSidebarActive();
}

function updateSidebarActive() {
  const hash = window.location.hash.slice(1) || '/';
  const route = hash.split('?')[0];
  // Treat /item/* as a sub-route of /prices for sidebar highlighting
  const effectiveRoute = route.match(/^\/item\//) ? '/prices' : route;
  document.querySelectorAll('.sidebar-link').forEach(link => {
    link.classList.toggle('active', link.dataset.route === effectiveRoute);
  });
}

function toggleSidebar() {
  const sidebar = document.getElementById('app-sidebar');
  const backdrop = document.getElementById('sidebar-backdrop');
  sidebar.classList.toggle('open');
  backdrop.classList.toggle('open');
}

// ══════════════════════════════════════════════════════════════════════════
// DUAL IDENTITY — ESO Tag + Discord Avatar rendering
// ══════════════════════════════════════════════════════════════════════════

function renderDualIdentity() {
  // Sprint 10.7: top-right dual-identity panel was removed — the Discord-style
  // avatar at the bottom-left of the sidebar (`.sidebar-user-panel` in
  // renderSidebar) is now the sole identity widget. This function remains as
  // a no-op so every existing call site (auth refresh, 401 fallback, route
  // changes) still works without branching.
  if (!$userArea) return;
  $userArea.innerHTML = '';
}

// ══════════════════════════════════════════════════════════════════════════
// ADVANCED SETTINGS PANEL — slides in from left on Discord avatar click
// ══════════════════════════════════════════════════════════════════════════

function toggleSettingsPanel() {
  const feedPanel = document.getElementById('live-feed-panel');
  if (feedPanel?.classList.contains('open') && getFeedMode() !== 'dock') {
    closeLiveFeed();
  }
  const panel = document.getElementById('settings-panel');
  const backdrop = document.getElementById('settings-panel-backdrop');
  const isOpen = panel.classList.contains('open');

  if (!isOpen) {
    renderSettingsPanelContent();
  }
  panel.classList.toggle('open');
  backdrop.classList.toggle('open');
}

function renderSettingsPanelContent() {
  const panel = document.getElementById('settings-panel');
  const fxEnabled = localStorage.getItem('redfur-fx') !== '0';
  const fxVal = localStorage.getItem('redfur-fx-intensity') || '70';
  const relTs = localStorage.getItem('redfur-relative-ts') !== '0';
  const isAnonymous = currentUser?._settings?.anonymous || false;
  const savedTag = currentUser?.esoTag || localStorage.getItem('redfur-claimed-tag') || '';
  const fls = _feedLaneSettings; // shorthand for Live Feed Layout section

  let html = `
    <div class="settings-panel-header">
      <span class="settings-panel-title">⚙ Settings</span>
      <button type="button" class="settings-panel-close" data-ui-action="toggle-settings-panel">✕</button>
    </div>

    <!-- User info -->
    <div class="settings-section">
      <div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.3rem;">
        ${currentUser?.avatar ? `<img src="${escapeHtml(currentUser.avatar)}" style="width:32px;height:32px;border-radius:50%;" alt="">` : ''}
        <div>
          <div style="color:var(--t-white);font-weight:600;font-size:var(--fs-sm);">${escapeHtml(currentUser?.displayName || 'Guest')}</div>
          ${savedTag ? `<div style="color:var(--gold);font-size:var(--fs-xs);font-family:var(--font-mono);">${ESO_ICON_SVG} ${escapeHtml(savedTag)}</div>` : ''}
        </div>
      </div>
    </div>

    <!-- ESO Tag setting -->
    <div class="settings-section">
      <div class="settings-section-title">ESO @-Tag</div>
      <div class="settings-row">
        <input type="text" class="form-input${savedTag && currentUser?._settings?.tagInDatabase ? ' qs-tag-input-verified' : ''}" id="sp-tag" placeholder="@YourESOTag" value="${escapeHtml(savedTag)}" style="font-size:var(--fs-xs);flex:1;max-width:none;">
        <button class="btn btn-primary" id="sp-tag-save" style="padding:0.2rem 0.4rem;font-size:var(--fs-xs);">✓</button>
      </div>
      <div class="qs-tag-status${savedTag ? (currentUser?._settings?.tagInDatabase ? ' qs-tag-status-verified' : ' qs-tag-status-pending') : ''}" id="sp-tag-status">${savedTag ? (currentUser?._settings?.tagInDatabase ? '✅ Tag linked &amp; verified' : '⏳ Tag set — pending sync') : ''}</div>
    </div>

    <!-- Display settings -->
    <div class="settings-section">
      <div class="settings-section-title">Display</div>
      <div class="settings-row">
        <span class="settings-row-label">CRT Effects</span>
        <label class="term-switch qs-term-switch ${fxEnabled ? 'active' : ''}">
          <input type="checkbox" id="sp-fx-on" ${fxEnabled ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
      <div class="settings-row">
        <span class="settings-row-label">FX Intensity</span>
        <input type="range" min="0" max="100" value="${fxVal}" id="sp-fx-slider" class="fx-slider" style="width:80px;">
        <span style="font-family:var(--font-mono);font-size:var(--fs-xs);color:var(--t-dim);min-width:2rem;" id="sp-fx-val">${fxVal}%</span>
      </div>
      <div class="settings-row">
        <span class="settings-row-label">Relative Timestamps</span>
        <label class="term-switch qs-term-switch ${relTs ? 'active' : ''}">
          <input type="checkbox" id="sp-rel-ts" ${relTs ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
      <div class="settings-row">
        <span class="settings-row-label" title="Slide: overlay drawer. Dock: integrated column that shrinks page. Float: popout card.">Live Feed Mode</span>
        <select id="sp-feed-mode" class="form-input" style="width:auto;min-width:80px;padding:0.2rem 0.3rem;font-size:var(--fs-xs);">
          <option value="slide">Slide-in</option>
          <option value="dock">Docked</option>
          <option value="float">Floating</option>
        </select>
      </div>
    </div>

    <!-- Live Feed Layout (All→All lane preferences) -->
    <div class="settings-section">
      <div class="settings-section-title" title="Controls the All → All live feed lane layout">Live Feed Layout</div>
      <div class="settings-row-group-label">Sales Lane</div>
      <div class="settings-row">
        <span class="settings-row-label" title="Compact item-group header style in Sales lane">Compact Rows</span>
        <label class="term-switch qs-term-switch ${fls.sales.rowsCompact ? 'active' : ''}">
          <input type="checkbox" id="sp-fls-sales-compact" ${fls.sales.rowsCompact ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
      <div class="settings-row">
        <span class="settings-row-label" title="Item groups start with seller rows hidden (click group to expand)">Start Collapsed</span>
        <label class="term-switch qs-term-switch ${fls.sales.rowsCollapsed ? 'active' : ''}">
          <input type="checkbox" id="sp-fls-sales-collapsed" ${fls.sales.rowsCollapsed ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
      <div class="settings-row-group-label">Syncs Lane</div>
      <div class="settings-row">
        <span class="settings-row-label" title="Single-line compact style for sync entries">Compact Rows</span>
        <label class="term-switch qs-term-switch ${fls.syncs.rowsCompact ? 'active' : ''}">
          <input type="checkbox" id="sp-fls-syncs-compact" ${fls.syncs.rowsCompact ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
      <div class="settings-row-group-label">Activity Lane</div>
      <div class="settings-row">
        <span class="settings-row-label" title="Single-line compact style for activity entries">Compact Rows</span>
        <label class="term-switch qs-term-switch ${fls.activity.rowsCompact ? 'active' : ''}">
          <input type="checkbox" id="sp-fls-activity-compact" ${fls.activity.rowsCompact ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
    </div>

    <!-- Privacy -->
    <div class="settings-section">
      <div class="settings-section-title">Privacy</div>
      <div class="settings-row" title="Replace your @-tag with 'Anon. Redfur Trader #N' everywhere on the website (leaderboards, profiles, feeds). Admins see through with a 🔒 badge.">
        <span class="settings-row-label">Anonymize Username</span>
        <label class="term-switch qs-term-switch ${(currentUser?._settings?.privacy?.anonymous ?? currentUser?._settings?.anonymous) ? 'active' : ''}">
          <input type="checkbox" id="sp-anonymous" ${(currentUser?._settings?.privacy?.anonymous ?? currentUser?._settings?.anonymous) ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
      <div class="settings-row" title="Remove your name entirely from public leaderboards. Admins still see your placement with a 🔒 badge.">
        <span class="settings-row-label">Hide from Leaderboards</span>
        <label class="term-switch qs-term-switch ${currentUser?._settings?.privacy?.hideFromLeaderboard ? 'active' : ''}">
          <input type="checkbox" id="sp-hide-lb" ${currentUser?._settings?.privacy?.hideFromLeaderboard ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
      <div class="settings-row" title="Hide your weekly/all-time sale statistics from your public profile. Admins see through with a 🔒 badge.">
        <span class="settings-row-label">Hide Sale History</span>
        <label class="term-switch qs-term-switch ${currentUser?._settings?.privacy?.hideSaleHistory ? 'active' : ''}">
          <input type="checkbox" id="sp-hide-sales" ${currentUser?._settings?.privacy?.hideSaleHistory ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
      <div class="settings-row" title="Hide your sync points total from public leaderboards and profile. Admins see through with a 🔒 badge.">
        <span class="settings-row-label">Hide Points Total</span>
        <label class="term-switch qs-term-switch ${currentUser?._settings?.privacy?.hidePointsTotal ? 'active' : ''}">
          <input type="checkbox" id="sp-hide-points" ${currentUser?._settings?.privacy?.hidePointsTotal ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </label>
      </div>
    </div>

    <!-- Fissal Relay -->
    <div class="settings-section">
      <div class="settings-section-title">Fissal Relay</div>
      <div class="settings-row">
        <span class="settings-row-label">Status</span>
        <span class="relay-status ${currentUser?.relayLinked ? 'relay-status-linked' : 'relay-status-unlinked'}" id="fissal-relay-status"${currentUser?.relayLastSeen ? ` title="Last relay activity: ${escapeHtml(new Date(currentUser.relayLastSeen).toLocaleString())}"` : ''}>${currentUser?.relayLinked ? (currentUser?.relayDisplayName && currentUser.relayDisplayName !== 'Unknown' ? `Linked as ${escapeHtml(currentUser.relayDisplayName)}` : 'Linked') : 'Not Linked'}</span>
      </div>
    </div>

    <!-- Links -->
    <div class="settings-section" style="flex:1;">
      <a href="#/settings" class="settings-link" data-ui-action="close-settings-panel">✨ Advanced Settings</a>
      ${currentUser?.esoTag ? `<a href="#/sales" class="settings-link" data-ui-action="close-settings-panel">📊 My Analytics</a>` : ''}
      ${currentUser?.isAdmin && currentUser?.esoTag ? `<a href="#/admin/user/${encodeURIComponent(currentUser.esoTag)}" class="settings-link" data-ui-action="close-settings-panel">🔬 Deep Analytics</a>` : ''}
    </div>

    <!-- Logout -->
    <div class="settings-section">
      <a href="/auth/logout" class="settings-danger-btn">🚪 Logout</a>
    </div>
  `;

  panel.innerHTML = html;
  _attachSettingsPanelHandlers();
}

function _attachSettingsPanelHandlers() {
  const spFxOn = document.getElementById('sp-fx-on');
  const spFxSlider = document.getElementById('sp-fx-slider');
  const spFxVal = document.getElementById('sp-fx-val');
  const spRelTs = document.getElementById('sp-rel-ts');
  const spAnonymous = document.getElementById('sp-anonymous');
  const spTagSave = document.getElementById('sp-tag-save');
  const spTag = document.getElementById('sp-tag');

  if (spFxOn) spFxOn.addEventListener('change', () => {
    setFx(spFxOn.checked);
    spFxOn.closest('.term-switch')?.classList.toggle('active', spFxOn.checked);
  });
  if (spFxSlider) {
    spFxSlider.addEventListener('input', () => {
      setFxIntensity(spFxSlider.value);
      if (spFxVal) spFxVal.textContent = spFxSlider.value + '%';
    });
  }
  if (spRelTs) {
    spRelTs.addEventListener('change', () => {
      localStorage.setItem('redfur-relative-ts', spRelTs.checked ? '1' : '0');
      spRelTs.closest('.term-switch')?.classList.toggle('active', spRelTs.checked);
    });
  }
  const spFeedMode = document.getElementById('sp-feed-mode');
  if (spFeedMode && typeof getFeedMode === 'function') {
    spFeedMode.value = getFeedMode();
    spFeedMode.addEventListener('change', () => {
      setFeedMode(spFeedMode.value);
    });
  }
  if (spAnonymous) {
    spAnonymous.addEventListener('change', async () => {
      spAnonymous.closest('.term-switch')?.classList.toggle('active', spAnonymous.checked);
      try {
        await apiPost('/api/me/settings', { anonymous: spAnonymous.checked });
        if (currentUser._settings) {
          currentUser._settings.anonymous = spAnonymous.checked;
          if (currentUser._settings.privacy) currentUser._settings.privacy.anonymous = spAnonymous.checked;
        }
        toast(spAnonymous.checked ? 'Username anonymized site-wide.' : 'Username visible site-wide.', 'success');
      } catch (err) { toast(err.message, 'error'); }
    });
  }
  // Sprint 9.4: wire three new privacy toggles
  const _wirePrivacyToggle = (elId, key, onMsg, offMsg) => {
    const el = document.getElementById(elId);
    if (!el) return;
    el.addEventListener('change', async () => {
      el.closest('.term-switch')?.classList.toggle('active', el.checked);
      try {
        await apiPost('/api/me/settings', { [key]: el.checked });
        if (currentUser._settings?.privacy) currentUser._settings.privacy[key] = el.checked;
        toast(el.checked ? onMsg : offMsg, 'success');
      } catch (err) { toast(err.message, 'error'); }
    });
  };
  _wirePrivacyToggle('sp-hide-lb', 'hideFromLeaderboard', 'Hidden from leaderboards.', 'Visible on leaderboards.');
  _wirePrivacyToggle('sp-hide-sales', 'hideSaleHistory', 'Sale history hidden.', 'Sale history visible.');
  _wirePrivacyToggle('sp-hide-points', 'hidePointsTotal', 'Points total hidden.', 'Points total visible.');

  // Live Feed Layout toggles — all purely local (localStorage), no API call needed
  const _wireLaneSetting = (elId, lane, key, label) => {
    const el = document.getElementById(elId);
    if (!el) return;
    el.addEventListener('change', () => {
      el.closest('.term-switch')?.classList.toggle('active', el.checked);
      _feedLaneSettings[lane][key] = el.checked;
      // Changing rowsCollapsed needs the toggled-items Set cleared so stale
      // "expanded exceptions" don't bleed through after a polarity flip.
      if (key === 'rowsCollapsed') _feedCollapsedItems.clear();
      _saveFeedLaneSettings();
      if (typeof renderLiveFeed === 'function') renderLiveFeed();
      toast(`${label}: ${el.checked ? 'on' : 'off'}`, 'info');
    });
  };
  _wireLaneSetting('sp-fls-sales-compact',   'sales',    'rowsCompact',   'Sales compact rows');
  _wireLaneSetting('sp-fls-sales-collapsed', 'sales',    'rowsCollapsed', 'Sales start collapsed');
  _wireLaneSetting('sp-fls-syncs-compact',   'syncs',    'rowsCompact',   'Syncs compact rows');
  _wireLaneSetting('sp-fls-activity-compact','activity', 'rowsCompact',   'Activity compact rows');
  if (spTagSave && spTag) {
    spTagSave.addEventListener('click', async () => {
      let tag = spTag.value.trim();
      if (tag && tag.startsWith('#')) tag = '@' + tag.slice(1);
      else if (tag && !tag.startsWith('@')) tag = '@' + tag;
      if (!tag || tag === '@') {
        toast('Enter a tag like @YourESOName first.', 'error');
        return;
      }
      if (currentUser) {
        try {
          // Sprint 10.0: backend now returns structured `code` so we can
          // toast a targeted diagnostic. Error responses are thrown by
          // apiPost; success path may include OK_PENDING_SYNC for
          // tags not yet seen by the sync pipeline.
          const resp = await apiPost('/api/me/settings', { esoTag: tag });
          currentUser.esoTag = tag;
          const isPending = resp && resp.tagResult && resp.tagResult.code === 'OK_PENDING_SYNC';
          if (isPending) {
            toast(resp.tagResult.message || 'Tag linked — awaiting next sync.', 'info');
          } else {
            toast('Tag updated.', 'success');
          }
          // Update input border + status line to reflect verification state
          const spTagEl = document.getElementById('sp-tag');
          const spStatusEl = document.getElementById('sp-tag-status');
          if (spTagEl) {
            spTagEl.classList.remove('qs-tag-input-verified');
            if (!isPending) spTagEl.classList.add('qs-tag-input-verified');
          }
          if (spStatusEl) {
            spStatusEl.className = 'qs-tag-status ' + (isPending ? 'qs-tag-status-pending' : 'qs-tag-status-verified');
            spStatusEl.textContent = isPending ? '⏳ Tag set — pending sync' : '✅ Tag linked & verified';
          }
          if (currentUser._settings) currentUser._settings.tagInDatabase = !isPending;
          renderDualIdentity();
          renderSidebar();
        } catch (err) {
          // err.code surfaces the structured backend code when available.
          const msg = err && err.message ? err.message : 'Failed to update tag.';
          toast(msg, 'error');
        }
      }
    });
  }

  // Fissal Relay — mouse-following tooltip on "Not Linked" status
  const relayEl = document.getElementById('fissal-relay-status');
  if (relayEl && relayEl.classList.contains('relay-status-unlinked')) {
    let relayTip = null;
    relayEl.style.cursor = 'help';
    relayEl.addEventListener('mouseenter', () => {
      relayTip = document.createElement('div');
      relayTip.className = 'fissal-relay-tooltip';
      relayTip.textContent = 'Temporarily, please include your Discord ID as your nickname on the Fissal Relay until API synchronization is complete.';
      document.body.appendChild(relayTip);
    });
    relayEl.addEventListener('mousemove', (e) => {
      if (relayTip) {
        requestAnimationFrame(() => {
          if (relayTip) {
            relayTip.style.left = (e.clientX + 12) + 'px';
            relayTip.style.top = (e.clientY + 12) + 'px';
          }
        });
      }
    });
    relayEl.addEventListener('mouseleave', () => {
      if (relayTip) { relayTip.remove(); relayTip = null; }
    });
  }
}

// ══════════════════════════════════════════════════════════════════════════
// TRADER PERSONA PANEL — slides in from right on ESO tag click
// ══════════════════════════════════════════════════════════════════════════

function toggleTraderPersona() {
  const feedPanel = document.getElementById('live-feed-panel');
  if (feedPanel?.classList.contains('open') && getFeedMode() !== 'dock') {
    closeLiveFeed();
  }
  const panel = document.getElementById('trader-persona-panel');
  const backdrop = document.getElementById('trader-persona-backdrop');
  const isOpen = panel.classList.contains('open');

  if (!isOpen) {
    renderTraderPersonaContent();
  }
  panel.classList.toggle('open');
  backdrop.classList.toggle('open');
}

async function renderTraderPersonaContent() {
  const panel = document.getElementById('trader-persona-panel');
  const tag = currentUser?.esoTag || localStorage.getItem('redfur-claimed-tag') || '';

  panel.innerHTML = `
    <div class="trader-persona-header">
      <span class="trader-persona-title">${ESO_ICON_SVG} ESO Link</span>
      <button type="button" class="trader-persona-close" data-ui-action="toggle-trader-persona">✕</button>
    </div>
    <div class="trader-section">
      <div class="loading-spinner" style="font-size:var(--fs-xs);">Loading trader data...</div>
    </div>
  `;

  try {
    const data = await api('/api/me/trader-summary');

    // Recent sales
    let salesHtml = '';
    if (data.recentSales && data.recentSales.length) {
      salesHtml = data.recentSales.map(s => {
        const nm = s.itemDesc || 'Unknown';
        const safe = escapeHtml(nm);
        return `
        <div class="trader-sale-row">
          <span class="trader-sale-item clickable-item" data-item="${safe}">${safe}</span>
          <span class="trader-sale-gold">${gold(s.price)}</span>
        </div>`;
      }).join('');
    } else {
      salesHtml = '<div style="color:var(--t-dim);font-size:var(--fs-xs);padding:0.3rem 0;">No recent sales found.</div>';
    }

    // Guild ranks
    let ranksHtml = '';
    if (data.guildRanks && data.guildRanks.length) {
      ranksHtml = data.guildRanks.map(r => `
        <div class="trader-rank-card">
          <span class="trader-rank-guild">${escapeHtml(r.guildName)}</span>
          <span class="trader-rank-value">${escapeHtml(r.rank || 'Member')}</span>
        </div>
      `).join('');
    } else {
      ranksHtml = '<div style="color:var(--t-dim);font-size:var(--fs-xs);padding:0.3rem 0;">No guild data.</div>';
    }

    // Trading advice
    const adviceHtml = data.advice
      ? `<div class="trader-advice">${escapeHtml(data.advice)}</div>`
      : '<div style="color:var(--t-dim);font-size:var(--fs-xs);font-style:italic;padding:0.3rem 0;">Fissal is contemplating your trading patterns...</div>';

    // Weekly stats
    const weeklyGold = data.weeklyGold || 0;
    const weeklySales = data.weeklySales || 0;

    panel.innerHTML = `
      <div class="trader-persona-header">
        <span class="trader-persona-title">${ESO_ICON_SVG} ESO Link</span>
        <button type="button" class="trader-persona-close" data-ui-action="toggle-trader-persona">✕</button>
      </div>

      <div class="trader-section">
        <div class="trader-section-title">📊 This Week</div>
        <div style="display:flex;gap:1rem;font-family:var(--font-mono);font-size:var(--fs-xs);">
          <div><span style="color:var(--t-dim);">Sales:</span> <span style="color:var(--t-green);">${weeklySales.toLocaleString()}</span></div>
          <div><span style="color:var(--t-dim);">Gold:</span> <span style="color:var(--gold);">${gold(weeklyGold)}</span></div>
        </div>
      </div>

      <div class="trader-section">
        <div class="trader-section-title">🏛️ Guild Ranks</div>
        ${ranksHtml}
      </div>

      <div class="trader-section">
        <div class="trader-section-title">📜 Recent Sales</div>
        ${salesHtml}
      </div>

      <div class="trader-section">
        <div class="trader-section-title">🐱 Fissal's Trading Advice</div>
        ${adviceHtml}
      </div>
    `;
  } catch (err) {
    panel.innerHTML = `
      <div class="trader-persona-header">
        <span class="trader-persona-title">${ESO_ICON_SVG} ESO Link</span>
        <button type="button" class="trader-persona-close" data-ui-action="toggle-trader-persona">✕</button>
      </div>
      <div class="trader-section">
        <div style="color:var(--t-dim);font-size:var(--fs-xs);">Unable to load trader data. ${currentUser ? '' : 'Please log in to see your persona.'}</div>
      </div>
    `;
  }
}

// ── API Helpers ──────────────────────────────────────────────────────────
// TTL-based response cache for expensive GET endpoints
const _apiCache = new Map();
const API_CACHE_TTL = 60_000; // 60 seconds

function cachedApi(path) {
  const now = Date.now();
  const entry = _apiCache.get(path);
  if (entry && (now - entry.ts) < API_CACHE_TTL) return Promise.resolve(entry.data);
  return api(path).then(data => {
    if (data !== null) _apiCache.set(path, { data, ts: now });
    return data;
  });
}

// Bust cache for a specific path or prefix
function bustCache(pathOrPrefix) {
  if (!pathOrPrefix) { _apiCache.clear(); return; }
  for (const key of _apiCache.keys()) {
    if (key.startsWith(pathOrPrefix)) _apiCache.delete(key);
  }
}

async function api(path, { timeout = 15000 } = {}) {
  const ctrl = new AbortController();
  const timer = timeout > 0 ? setTimeout(() => ctrl.abort(), timeout) : null;
  try {
    const res = await fetch(path, { signal: ctrl.signal });
    if (res.status === 401) { currentUser = null; renderDualIdentity(); return null; }
    if (!res.ok) throw new Error(`API ${res.status}`);
    return res.json();
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Request timed out — the server took too long to respond.');
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function apiPost(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API ${res.status}`);
  }
  return res.json();
}

async function apiDelete(path) {
  const res = await fetch(path, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API ${res.status}`);
  }
  return res.json();
}

async function apiPatch(path, body) {
  const res = await fetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `API ${res.status}`);
  }
  return res.json();
}

// ── SSE: Real-Time Sync Stream ──────────────────────────────────────────
let _sseSource = null;
let _syncNotification = null;
let _syncSessionId = null;
let _syncStartTime = 0;
let _syncElapsedTimer = null;
let _syncFadeoutTimer = null;
// Sprint 8.8: track active syncers and data sources across the current session
let _syncSyncers = new Set();
let _syncSources = new Set();

function initSSE() {
  if (_sseSource) return;
  try {
    _sseSource = new EventSource('/api/sync/stream');

    _sseSource.addEventListener('syncStarted', (e) => {
      const data = JSON.parse(e.data);
      if (!data.syncer || data.syncer === 'Startup' || data.syncer === 'System') return;
      _syncSessionId = data.sessionId || Date.now().toString();
      if (data.syncer) _syncSyncers.add(data.syncer);
      if (data.type) _syncSources.add(String(data.type).toUpperCase());
      showSyncNotification(data.syncer, data.type, data.totalFiles);
    });

    let _syncProgressPending = null;
    let _syncProgressTimer = null;
    _sseSource.addEventListener('syncProgress', (e) => {
      const data = JSON.parse(e.data);
      _syncProgressPending = data;
      if (!_syncProgressTimer) {
        _syncProgressTimer = setTimeout(() => {
          if (_syncProgressPending) updateSyncNotification(_syncProgressPending);
          _syncProgressPending = null;
          _syncProgressTimer = null;
        }, 2000);
      }
    });

    _sseSource.addEventListener('syncComplete', async (e) => {
      const data = JSON.parse(e.data);
      completeSyncNotification(data);
      _syncSessionId = null;
      _syncSyncers.clear();
      _syncSources.clear();
      // Refresh sync status in footer
      loadSyncStatus();
      // Bust client-side cached week labels so filters stay current after a sync
      bustCache('/api/weeks');
      // If user is on a page that should update, refresh it
      const route = (window.location.hash.slice(1) || '/').split('?')[0];
      if (['/', '/leaderboard', '/sync', '/sales'].includes(route)) {
        const renderer = routes[route];
        // Pass seamless=true so the dashboard doesn't flash the crystal-ball
        // spinner while the user is idling on the page during a sync. Other
        // route renderers ignore unknown options so this is safe.
        if (renderer) await renderer({ seamless: true });
      }
      // Check for new sales for the current user
      checkForNewSales();
      // Refresh live feed historical data so new sales appear immediately
      loadFeedData(true);
    });

    _sseSource.onerror = () => {
      // Sprint 10.0: exponential backoff 1s → 30s cap so live feed recovers
      // quickly from transient network hiccups without hammering the server
      // during longer outages.
      _sseSource.close();
      _sseSource = null;
      _sseRetryMs = Math.min((_sseRetryMs || 500) * 2, 30_000);
      setTimeout(initSSE, _sseRetryMs);
    };
    // Successful open resets backoff
    _sseSource.addEventListener('open', () => { _sseRetryMs = 500; });
  } catch { /* SSE not supported, degrade gracefully */ }
}

// Sprint 8.8: render active syncer list + source badges from tracking sets
function renderSyncNotifSyncers(el) {
  const target = el.querySelector('.sync-notif-syncer');
  if (!target) return;
  const list = Array.from(_syncSyncers);
  if (list.length === 0) return;
  if (list.length === 1) {
    target.textContent = `${list[0]} is syncing`;
  } else {
    target.innerHTML = `<span class="sync-notif-syncer-count">${list.length} syncers active</span> ` +
      list.map(s => `<span class="sync-notif-syncer-chip">${escapeHtml(s)}</span>`).join(' ');
  }
}

function renderSyncNotifBadges(el) {
  const wrap = el.querySelector('.sync-notif-src-badges');
  if (!wrap) return;
  const badges = [];
  if (_syncSources.has('MM')) badges.push('<span class="sync-notif-src-badge sync-notif-src-badge-mm" title="Fissal Relay">MM</span>');
  if (_syncSources.has('TTC')) badges.push('<span class="sync-notif-src-badge sync-notif-src-badge-ttc" title="Tamriel Trade Centre">TTC</span>');
  wrap.innerHTML = badges.join('');
}

function applySyncNotifSourceClasses(el) {
  el.classList.toggle('sync-notif-src-mm', _syncSources.has('MM'));
  el.classList.toggle('sync-notif-src-ttc', _syncSources.has('TTC'));
}

function showSyncNotification(syncer, type, totalFiles) {
  if (isLiveFeedOpen()) return;
  // Reuse existing notification if same session — accumulate syncers/sources
  const existing = document.getElementById('sync-live-notification');
  if (existing) {
    applySyncNotifSourceClasses(existing);
    renderSyncNotifSyncers(existing);
    renderSyncNotifBadges(existing);
    return;
  }
  // Remove any stale notification
  if (_syncNotification) _syncNotification.remove();

  _syncStartTime = Date.now();
  const isMe = currentUser && (currentUser.displayName === syncer || currentUser.username === syncer);
  const el = document.createElement('div');
  el.className = 'sync-notification' + (isMe ? ' sync-notification-mine' : '');
  el.id = 'sync-live-notification';
  if (totalFiles) el.dataset.totalFiles = totalFiles;
  el.innerHTML = `
    <button class="sync-notif-close" data-ui-action="close-sync-notification" title="Dismiss">✕</button>
    <div class="sync-notif-header">
      <span class="sync-notif-icon">🐱</span>
      <span class="sync-notif-title">Data Sync In Progress</span>
      <span class="sync-notif-src-badges"></span>
      <span class="sync-notif-elapsed" id="sync-notif-elapsed">0s</span>
    </div>
    <div class="sync-notif-body">
      <div class="sync-notif-syncer"></div>
      <div class="sync-notif-stats" id="sync-notif-stats">
        <span class="sync-notif-counter" id="sync-notif-items">📦 0 items</span>
        <span class="sync-notif-counter" id="sync-notif-sales">🪙 0 sales</span>
        <span class="sync-notif-counter" id="sync-notif-files">📂 0 files</span>
      </div>
      <div class="sync-notif-bar"><div class="sync-notif-bar-fill" id="sync-notif-bar-fill"></div></div>
    </div>
  `;
  applySyncNotifSourceClasses(el);
  renderSyncNotifSyncers(el);
  renderSyncNotifBadges(el);

  // Mouseover: pause fadeout + restore full opacity
  el.addEventListener('mouseenter', () => {
    el.style.opacity = '1';
    if (_syncFadeoutTimer) { clearTimeout(_syncFadeoutTimer); _syncFadeoutTimer = null; }
  });
  el.addEventListener('mouseleave', () => {
    el.style.opacity = '';
    // If sync is complete, resume fadeout
    if (el.classList.contains('sync-complete')) {
      _syncFadeoutTimer = setTimeout(() => {
        el.classList.remove('visible');
        setTimeout(() => { el.remove(); _syncNotification = null; }, 500);
      }, 4000);
    }
  });

  document.body.appendChild(el);
  _syncNotification = el;
  // Animate in
  requestAnimationFrame(() => el.classList.add('visible'));

  // Start elapsed timer
  if (_syncElapsedTimer) clearInterval(_syncElapsedTimer);
  const _elapsedRef = document.getElementById('sync-notif-elapsed');
  _syncElapsedTimer = setInterval(() => {
    const el = _elapsedRef || document.getElementById('sync-notif-elapsed');
    if (el) {
      const sec = Math.floor((Date.now() - _syncStartTime) / 1000);
      el.textContent = sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
    }
  }, 1000);
}

function updateSyncNotification(data) {
  const el = document.getElementById('sync-live-notification');
  if (!el) return;
  const items = document.getElementById('sync-notif-items');
  const sales = document.getElementById('sync-notif-sales');
  const files = document.getElementById('sync-notif-files');
  const barFill = document.getElementById('sync-notif-bar-fill');
  if (items) items.textContent = `📦 ${(data.itemsAdded || 0).toLocaleString()} items`;
  if (sales) sales.textContent = `🪙 ${(data.salesAdded || 0).toLocaleString()} sales`;
  if (files) files.textContent = `📂 ${data.filesProcessed || 0} files`;
  if (barFill) {
    const total = parseInt(el.dataset.totalFiles) || data.totalFiles || 0;
    const pct = total > 0 ? Math.min(100, Math.round((data.filesProcessed / total) * 100)) : 0;
    barFill.style.width = total > 0 ? `${pct}%` : '100%';
    barFill.title = total > 0 ? `${pct}% (${data.filesProcessed}/${total})` : '';
  }
}

function completeSyncNotification(data) {
  // Stop elapsed timer
  if (_syncElapsedTimer) { clearInterval(_syncElapsedTimer); _syncElapsedTimer = null; }

  const el = document.getElementById('sync-live-notification');
  if (el) {
    el.querySelector('.sync-notif-title').textContent = '✅ Sync Complete!';
    const elapsedSec = Math.floor((Date.now() - _syncStartTime) / 1000);
    const elapsedEl = el.querySelector('.sync-notif-elapsed');
    if (elapsedEl) elapsedEl.textContent = elapsedSec < 60 ? `${elapsedSec}s` : `${Math.floor(elapsedSec / 60)}m ${elapsedSec % 60}s`;
    el.querySelector('.sync-notif-body').innerHTML = `
      <div class="sync-notif-syncer">🐱 ${data.syncers.map(escapeHtml).join(', ')}</div>
      <div class="sync-notif-stats">
        <span class="sync-notif-counter">📦 +${(data.mm.itemsInserted || 0).toLocaleString()} items</span>
        <span class="sync-notif-counter">🪙 +${(data.mm.salesInserted || 0).toLocaleString()} sales</span>
        <span class="sync-notif-counter">📂 ${data.filesProcessed} files</span>
      </div>
    `;
    el.classList.add('sync-complete');
    // Fade out after 8s (pausable on hover)
    _syncFadeoutTimer = setTimeout(() => {
      el.classList.remove('visible');
      setTimeout(() => { el.remove(); _syncNotification = null; }, 500);
    }, 8000);
  }

  // Add to notification center — include full sync data payload for modal detail view
  const syncersStr = data.syncers.map(escapeHtml).join(', ');
  addNotification('sync', 'Sync Complete',
    `${syncersStr} — +${(data.mm.itemsInserted || 0).toLocaleString()} items, +${(data.mm.salesInserted || 0).toLocaleString()} sales`,
    null, data);

  // Points notifications for every earner in this sync.
  // All-Traders Activity section shows all earners; Personal view filters to just the viewer.
  for (const [name, pts] of Object.entries(data.pointsSummary || {})) {
    if (!pts || pts.earned <= 0) continue;
    addNotification('points',
      `⭐ ${name} +${pts.earned} Sync Points`,
      `Total: ${(pts.total || 0).toLocaleString()}${pts.noNewData ? ' (no new data)' : ''}`,
      null, { earner: name, total: pts.total || 0 });
  }

  // If the current user is the syncer, show a personal sync modal (feed must be closed)
  const isMe = currentUser && data.syncers.some(s => s === currentUser.displayName || s === currentUser.username);
  if (isMe && !isLiveFeedOpen()) {
    showPersonalSyncModal(data);
  }

  // Launch floating item balloon reactions for all connected users
  if (data.highlightItems && data.highlightItems.length > 0) {
    launchSyncBalloons(data.highlightItems);
  }
}

// ── Sync balloon reactions — floating item bubbles on sync completion ────────
const _BALLOON_BADGE = {
  heist:   { emoji: '🏆', label: 'Highest Single Sale',   desc: 'The biggest individual sale price in this sync.',    color: '#f59e0b' },
  volume:  { emoji: '📦', label: 'Most Items Moved',       desc: 'Highest total quantity sold across all transactions.', color: '#38bdf8' },
  count:   { emoji: '🔄', label: 'Most Transactions',      desc: 'Appeared in the most individual sale records.',       color: '#a78bfa' },
  gold:    { emoji: '💰', label: 'Top Revenue',            desc: 'Generated the most total gold revenue.',              color: '#4ade80' },
  notable: { emoji: '⭐', label: 'Notable Sale',           desc: 'Among the top items brought in by this sync.',        color: '#74d8a6' },
};

function launchSyncBalloons(items) {
  if (!items || items.length === 0) return;

  // One shared tooltip element, repositioned on each hover
  let tipEl = document.getElementById('sync-balloon-tip');
  if (!tipEl) {
    tipEl = document.createElement('div');
    tipEl.id = 'sync-balloon-tip';
    tipEl.className = 'sync-balloon-tip';
    tipEl.style.display = 'none';
    document.body.appendChild(tipEl);
  }

  let container = document.getElementById('sync-balloon-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'sync-balloon-container';
    container.className = 'sync-balloon-container';
    document.body.appendChild(container);
  }

  items.forEach((item, i) => {
    const badge = _BALLOON_BADGE[item.badge] || _BALLOON_BADGE.notable;
    const delay  = i * 550 + Math.random() * 250;       // stagger each balloon
    const xPct   = 60 + Math.random() * 30;              // right 60–90% of screen
    const drift  = (Math.random() - 0.5) * 70;           // ±35px horizontal drift

    const el = document.createElement('div');
    el.className = 'sync-balloon';
    el.style.cssText = [
      `--balloon-x:${xPct.toFixed(1)}%`,
      `--balloon-drift:${drift.toFixed(0)}px`,
      `animation-delay:${delay.toFixed(0)}ms`,
      `border-color:${badge.color}`,
      `box-shadow:0 2px 12px rgba(0,0,0,0.55),0 0 10px ${badge.color}44`,
    ].join(';');

    const iconHtml = item.itemIcon
      ? `<img class="sync-balloon-icon" src="${escapeHtml(item.itemIcon)}" onerror="this.onerror=null;this.style.display='none'" alt="">`
      : '<span class="sync-balloon-icon-ph">📦</span>';

    el.innerHTML = `
      ${iconHtml}
      <span class="sync-balloon-badge" style="background:${badge.color};">${badge.emoji}</span>
    `;

    // ── Hover: freeze + show rich tooltip ────────────────────────────────
    el.addEventListener('mouseenter', (e) => {
      el.style.animationPlayState = 'paused';
      el.classList.add('sync-balloon-frozen');

      // Build tooltip content
      const nameLine  = escapeHtml(item.itemName || 'Unknown Item');
      const priceLine = item.maxPrice    ? `Top price: ${item.maxPrice.toLocaleString()}g` : '';
      const volLine   = item.totalVolume ? `Volume: ${item.totalVolume.toLocaleString()} units` : '';
      const cntLine   = item.saleCount   ? `Transactions: ${item.saleCount.toLocaleString()}` : '';
      const goldLine  = item.totalGold   ? `Revenue: ${item.totalGold.toLocaleString()}g` : '';
      const stats     = [priceLine, volLine, cntLine, goldLine].filter(Boolean);

      tipEl.innerHTML = `
        <div class="sbt-title">${nameLine}</div>
        <div class="sbt-badge" style="color:${badge.color};">${badge.emoji} ${escapeHtml(badge.label)}</div>
        <div class="sbt-desc">${escapeHtml(badge.desc)}</div>
        ${stats.map(s => `<div class="sbt-stat">${escapeHtml(s)}</div>`).join('')}
        <div class="sbt-scope">⚠ This sync only</div>
      `;

      // Position near balloon
      const r = el.getBoundingClientRect();
      const tipLeft = Math.min(r.left + r.width / 2 - 90, window.innerWidth - 210);
      const tipTop  = r.top - 8;
      tipEl.style.left    = Math.max(8, tipLeft) + 'px';
      tipEl.style.top     = tipTop + 'px';
      tipEl.style.display = 'block';
      // If too close to top, flip below
      if (r.top < 160) {
        tipEl.style.top  = (r.bottom + 8) + 'px';
      } else {
        tipEl.style.top  = (r.top - tipEl.offsetHeight - 8) + 'px';
      }
    });

    el.addEventListener('mouseleave', () => {
      el.style.animationPlayState = 'running';
      el.classList.remove('sync-balloon-frozen');
      tipEl.style.display = 'none';
    });

    // Auto-remove when animation ends (or after hard timeout)
    const lifetime = delay + 7000;
    el.addEventListener('animationend', () => { if (!el.classList.contains('sync-balloon-frozen')) el.remove(); });
    setTimeout(() => { if (el.parentNode && !el.classList.contains('sync-balloon-frozen')) el.remove(); }, lifetime + 2000);

    container.appendChild(el);
  });
}

function showPersonalSyncModal(data) {
  // Find this user's points
  const myName = currentUser.displayName || currentUser.username;
  const pts = data.pointsSummary?.[myName];

  const modal = document.createElement('div');
  modal.className = 'sync-modal-overlay';
  modal.innerHTML = `
    <div class="sync-modal">
      <div class="sync-modal-header">
        <span>⭐ Your Sync Results</span>
        <button class="sync-modal-close" data-ui-action="close-sync-modal">✕</button>
      </div>
      <div class="sync-modal-body">
        <div class="sync-modal-grid">
          <div class="sync-modal-stat"><span class="label">Items Contributed</span><span class="value">${(data.mm.itemsInserted || 0).toLocaleString()}</span></div>
          <div class="sync-modal-stat"><span class="label">Sales Contributed</span><span class="value">${(data.mm.salesInserted || 0).toLocaleString()}</span></div>
          <div class="sync-modal-stat"><span class="label">TTC Updated</span><span class="value">${data.ttc.versionUpdated ? '✓ v' + data.ttc.newVersion : '—'}</span></div>
          <div class="sync-modal-stat"><span class="label">Files Processed</span><span class="value">${data.filesProcessed}</span></div>
          <div class="sync-modal-stat"><span class="label">Duration</span><span class="value">${(data.duration / 1000).toFixed(1)}s</span></div>
          ${pts ? `
            <div class="sync-modal-stat sync-modal-points">
              <span class="label">Points Earned</span>
              <span class="value gold">+${pts.earned}${pts.noNewData ? ' (no new data)' : ''}</span>
            </div>
            <div class="sync-modal-stat"><span class="label">Total Points</span><span class="value">${(pts.total || 0).toLocaleString()}</span></div>
          ` : ''}
        </div>
      </div>
      <div class="sync-modal-footer">
        <button class="btn btn-primary" data-ui-action="close-sync-modal">OK</button>
      </div>
    </div>
  `;
  // Click-off to dismiss
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });
  document.body.appendChild(modal);
  requestAnimationFrame(() => modal.classList.add('visible'));
}

// ── Live Feed: sync detail modal (Sprint 8.5) ───────────────────────────
// Opens when a user clicks a 'sync' entry in the Live Feed. Shows the full
// sync payload that was stored on the notification at emission time.
function showSyncDetailModal(notif) {
  const data = notif.extra;
  if (!data) return;
  const syncers = Array.isArray(data.syncers) ? data.syncers : [];
  const pointsRows = data.pointsSummary
    ? Object.entries(data.pointsSummary).map(([name, pts]) => `
        <tr>
          <td class="text-mono">${escapeHtml(name)}</td>
          <td class="text-right text-mono">+${pts.earned || 0}${pts.noNewData ? ' <span class="text-muted">(no new data)</span>' : ''}</td>
          <td class="text-right text-mono">${(pts.total || 0).toLocaleString()}</td>
        </tr>`).join('')
    : '';

  const modal = document.createElement('div');
  modal.className = 'sync-modal-overlay';
  modal.innerHTML = `
    <div class="sync-modal">
      <div class="sync-modal-header">
        <span>📡 Sync Detail</span>
        <button class="sync-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="sync-modal-body">
        <div class="text-muted" style="margin-bottom:0.6rem;font-size:var(--fs-xs);">
          ${relativeTime(notif.time)} — ${escapeHtml(syncers.join(', ') || 'unknown')}
        </div>
        <div class="sync-modal-grid">
          <div class="sync-modal-stat"><span class="label">Items Inserted</span><span class="value">${(data.mm?.itemsInserted || 0).toLocaleString()}</span></div>
          <div class="sync-modal-stat"><span class="label">Sales Inserted</span><span class="value">${(data.mm?.salesInserted || 0).toLocaleString()}</span></div>
          <div class="sync-modal-stat"><span class="label">Files Processed</span><span class="value">${data.filesProcessed ?? 0}</span></div>
          <div class="sync-modal-stat"><span class="label">TTC Updated</span><span class="value">${data.ttc?.versionUpdated ? '✓ v' + data.ttc.newVersion : '—'}</span></div>
          ${data.duration != null ? `<div class="sync-modal-stat"><span class="label">Duration</span><span class="value">${(data.duration / 1000).toFixed(1)}s</span></div>` : ''}
        </div>
        ${pointsRows ? `
          <div style="margin-top:0.75rem;">
            <div style="font-weight:600;margin-bottom:0.3rem;">Points Awarded</div>
            <div class="table-wrap">
              <table class="data-table">
                <thead><tr><th>Syncer</th><th class="text-right">Earned</th><th class="text-right">Total</th></tr></thead>
                <tbody>${pointsRows}</tbody>
              </table>
            </div>
          </div>` : ''}
      </div>
      <div class="sync-modal-footer">
        <button class="btn" data-action="goto-sync">Open Sync Page</button>
        <button class="btn btn-primary" data-action="close">Close</button>
      </div>
    </div>
  `;
  const close = () => modal.remove();
  modal.addEventListener('click', (e) => {
    if (e.target === modal) return close();
    const btn = e.target.closest('button');
    if (!btn) return;
    if (btn.classList.contains('sync-modal-close') || btn.dataset.action === 'close') return close();
    if (btn.dataset.action === 'goto-sync') { close(); closeLiveFeed(); navigate('#/sync-stats'); }
  });
  document.body.appendChild(modal);
  requestAnimationFrame(() => modal.classList.add('visible'));
}

// ── Formatters ───────────────────────────────────────────────────────────
function gold(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString() + '<span class="gold-coin" aria-label="gold"></span>';
}

// Plain-text gold for use inside title="" attributes, textContent, etc.
function goldText(n) {
  if (n == null) return '—';
  return Number(n).toLocaleString() + 'g';
}

// Format a sell frequency value (defensively handles null/undefined/NaN).
function freqText(n) {
  if (n == null || !isFinite(Number(n))) return '—';
  return Number(n).toLocaleString() + '/day';
}

function shortDate(ts) {
  if (!ts) return '—';
  // Respect relative timestamp preference
  if (localStorage.getItem('redfur-relative-ts') !== '0') {
    return relativeTime(ts);
  }
  const d = new Date(ts * 1000);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function relativeTime(ts) {
  if (!ts) return 'never';
  const ms = ts < 1e12 ? ts * 1000 : ts; // handle both seconds and milliseconds
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'Just Now';
  if (diff < 3_600_000) return Math.floor(diff / 60_000) + 'm ago';
  if (diff < 86_400_000) return Math.floor(diff / 3_600_000) + 'h ago';

  const totalDays = Math.floor(diff / 86_400_000);
  if (totalDays < 30) return totalDays + 'd ago';

  const years = Math.floor(totalDays / 365);
  const remainingAfterYears = totalDays % 365;
  const months = Math.floor(remainingAfterYears / 30);
  const days = remainingAfterYears % 30;

  const parts = [];
  if (years > 0) parts.push(years + (years === 1 ? ' year' : ' years'));
  if (months > 0) parts.push(months + (months === 1 ? ' month' : ' months'));
  if (parts.length === 0 && days > 0) parts.push(days + 'd');
  return (parts.join(', ') || '1 month') + ' ago';
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ── Reusable Tooltip System ─────────────────────────────────────────────
let _activeTooltip = null;
function showTooltip(el, text) {
  hideTooltip();
  const rect = el.getBoundingClientRect();
  const tip = document.createElement('div');
  tip.className = 'rf-tooltip';
  tip.innerHTML = text;
  tip.style.position = 'fixed';
  // Must be above .profile-modal (z:1000) and any overlays — Sprint 8.9
  tip.style.zIndex = '1100';
  document.body.appendChild(tip);
  // Position above the element, centered
  const tipRect = tip.getBoundingClientRect();
  let top = rect.top - tipRect.height - 6;
  let left = rect.left + (rect.width / 2) - (tipRect.width / 2);
  if (top < 4) top = rect.bottom + 6; // flip below if no room
  if (left < 4) left = 4;
  if (left + tipRect.width > window.innerWidth - 4) left = window.innerWidth - tipRect.width - 4;
  tip.style.top = top + 'px';
  tip.style.left = left + 'px';
  _activeTooltip = tip;
}
function hideTooltip() {
  if (_activeTooltip) { _activeTooltip.remove(); _activeTooltip = null; }
}

// ── Unified Item Hover Card (Sprint 8.6) ────────────────────────────────
// Shows MM + TTC + Fissal summary on hover. Works across Leaderboard,
// Prices, user profiles, live log, and anywhere itemLink/itemCell is used.
const _itemHoverCache = new Map();
const ITEM_HOVER_TTL = 5 * 60 * 1000;
const ITEM_HOVER_DELAY = 350;
let _itemHoverTimer = null;
let _itemHoverEl = null;
let _itemHoverTargetEl = null;
let _itemHoverAbort = null;

function scheduleItemHover(el, name) {
  clearTimeout(_itemHoverTimer);
  _itemHoverTargetEl = el;
  _itemHoverTimer = setTimeout(() => showItemHover(el, name), ITEM_HOVER_DELAY);
}

function cancelItemHover() {
  clearTimeout(_itemHoverTimer);
  _itemHoverTimer = null;
  hideItemHover();
  _itemHoverTargetEl = null;
}

function hideItemHover() {
  if (_itemHoverEl) { _itemHoverEl.remove(); _itemHoverEl = null; }
  if (_itemHoverAbort) { try { _itemHoverAbort.abort(); } catch { /* ignore */ } _itemHoverAbort = null; }
}

async function showItemHover(el, name) {
  if (!name || !document.body.contains(el)) return;
  hideItemHover();
  const card = document.createElement('div');
  card.className = 'item-hover-card';
  card.innerHTML = renderItemHoverLoading(name);
  document.body.appendChild(card);
  _itemHoverEl = card;
  positionItemHoverCard(card, el);

  const key = name.toLowerCase();
  const now = Date.now();
  const cached = _itemHoverCache.get(key);
  if (cached && now - cached.ts < ITEM_HOVER_TTL) {
    card.innerHTML = renderItemHoverBody(cached.data);
    positionItemHoverCard(card, el);
    return;
  }

  try {
    _itemHoverAbort = new AbortController();
    const res = await fetch(`/api/items/${encodeURIComponent(name)}/hover`, { signal: _itemHoverAbort.signal });
    if (!res.ok) throw new Error('hover_failed');
    const data = await res.json();
    _itemHoverCache.set(key, { ts: now, data });
    if (data.icon) cacheItemIcon(data.name || name, data.icon);
    if (_itemHoverEl === card) {
      card.innerHTML = renderItemHoverBody(data);
      positionItemHoverCard(card, el);
    }
  } catch (err) {
    if (err.name === 'AbortError') return;
    if (_itemHoverEl === card) {
      card.innerHTML = `<div class="ihc-head"><div class="ihc-name">${escapeHtml(name)}</div></div>
        <div class="ihc-error">*Fissal shrugs* — no data found.</div>`;
    }
  }
}

function positionItemHoverCard(card, el) {
  const rect = el.getBoundingClientRect();
  const cardRect = card.getBoundingClientRect();
  const gap = 8;
  let top = rect.bottom + gap;
  let left = rect.left + rect.width / 2 - cardRect.width / 2;
  if (top + cardRect.height > window.innerHeight - gap) {
    const above = rect.top - cardRect.height - gap;
    if (above >= gap) top = above;
    else top = Math.max(gap, window.innerHeight - cardRect.height - gap);
  }
  if (left < gap) left = gap;
  if (left + cardRect.width > window.innerWidth - gap) left = window.innerWidth - cardRect.width - gap;
  card.style.top = top + 'px';
  card.style.left = left + 'px';
}

function renderItemHoverLoading(name) {
  return `<div class="ihc-head"><div class="ihc-name">${escapeHtml(name)}</div></div>
    <div class="ihc-loading">Consulting the merchant scrolls…</div>`;
}

function renderItemHoverBody(d) {
  const QUALITY_COLORS = { worn:'#9a9a9a', white:'#e8e8e8', green:'#5bffa0', blue:'#7ab8ff', purple:'#d09cff', gold:'#ffcc40' };
  const qColor = QUALITY_COLORS[d.quality] || 'var(--t-secondary)';
  const iconSrc = d.iconUrl || (d.icon ? esoIconUrl(d.icon) : null);
  const iconHtml = iconSrc
    ? `<img class="ihc-icon" src="${iconSrc}" alt="" onerror="this.onerror=null;this.src='${ESO_ICON_FALLBACK}'">`
    : '';
  const traitBadge = d.trait ? `<span class="ihc-trait">${traitIconImg(d.trait)} ${escapeHtml(d.trait)}</span>` : '';
  const mm = d.mm || { hasData: false };
  const ttc = d.ttc || { hasData: false };
  const fi = d.fissal || {};

  const mmBlock = mm.hasData
    ? `<div class="ihc-price-main">${gold(mm.avg)}<span class="ihc-price-label">avg</span></div>
       <div class="ihc-stat-sub">${mm.saleCount.toLocaleString()} sales · ${mm.totalVolume.toLocaleString()} vol</div>
       <div class="ihc-stat-sub">Range ${gold(mm.min)} – ${gold(mm.max)}</div>
       <div class="ihc-stat-sub">Last ${mm.lastSeen ? relativeTime(mm.lastSeen) : '—'}</div>`
    : `<div class="ihc-nodata">No guild sales recorded.</div>`;

  const ttcPrimary = ttc.hasData ? (ttc.suggested > 0 ? ttc.suggested : ttc.avg) : 0;
  const ttcLabel = ttc.hasData && ttc.suggested > 0 ? 'suggested' : 'avg';
  const ttcBlock = ttc.hasData
    ? `<div class="ihc-price-main">${ttcPrimary > 0 ? gold(ttcPrimary) : '—'}<span class="ihc-price-label">${ttcLabel}</span></div>
       <div class="ihc-stat-sub">${(ttc.saleCount || 0).toLocaleString()} TTC sales${ttc.variantCount > 1 ? ` · ${ttc.variantCount} variants` : ''}</div>
       ${(ttc.variantCount > 1 && ttc.topQuality) || ttc.topTrait ? `<div class="ihc-stat-sub">Top ${ttc.topQuality || '—'}${ttc.topTrait ? ' · ' + ttc.topTrait : ''}</div>` : ''}`
    : `<div class="ihc-nodata">No TTC listings.</div>`;

  const fissalSrc = mm.hasData && ttc.hasData ? 'both' : mm.hasData ? 'mm' : ttc.hasData ? 'ttc' : 'none';
  const fissalBlock = fi.suggested
    ? `<div class="ihc-fissal ihc-fissal-src-${fissalSrc}"><span class="icon-fissal"></span> Fissal suggests <strong>${gold(fi.suggested)}</strong> <span class="ihc-confidence ihc-conf-${escapeHtml(fi.confidence)}">${escapeHtml(fi.confidence)}</span></div>`
    : '';

  return `
    <div class="ihc-head">
      ${iconHtml}
      <div class="ihc-name-wrap">
        <div class="ihc-name" style="color:${qColor};">${escapeHtml(d.name)}</div>
        ${traitBadge}
      </div>
    </div>
    <div class="ihc-body">
      <div class="ihc-col ihc-col-mm${mm.hasData ? '' : ' ihc-col-empty'}">
        <div class="ihc-col-header"><span class="icon-mm"></span> Master Merchant</div>
        ${mmBlock}
      </div>
      <div class="ihc-col ihc-col-ttc${ttc.hasData ? '' : ' ihc-col-empty'}">
        <div class="ihc-col-header"><span class="icon-ttc"></span> Tamriel Trade Centre</div>
        ${ttcBlock}
      </div>
    </div>
    ${fissalBlock}
    <div class="ihc-foot">Click for full details</div>
  `;
}

// Delegated listeners — fires for any [data-item] across Leaderboard, Prices,
// Live Log, user profiles, sync attribution drill-downs, etc.
document.addEventListener('mouseover', (e) => {
  const el = e.target.closest('[data-item]');
  if (!el) return;
  const name = el.getAttribute('data-item');
  if (!name) return;
  if (_itemHoverTargetEl === el && (_itemHoverEl || _itemHoverTimer)) return;
  scheduleItemHover(el, name);
}, true);

document.addEventListener('mouseout', (e) => {
  const el = e.target.closest('[data-item]');
  if (!el) return;
  const rt = e.relatedTarget;
  if (rt && (el.contains(rt) || rt === _itemHoverEl || (_itemHoverEl && _itemHoverEl.contains(rt)))) return;
  cancelItemHover();
}, true);

window.addEventListener('hashchange', cancelItemHover);
document.addEventListener('click', cancelItemHover, true);

// ── Unified Guild Hover Card (Sprint 8.6) ───────────────────────────────
const _guildHoverCache = new Map();
const GUILD_HOVER_TTL = 5 * 60 * 1000;
let _guildHoverTimer = null;
let _guildHoverEl = null;
let _guildHoverTargetEl = null;
let _guildHoverAbort = null;

function scheduleGuildHover(el, name) {
  clearTimeout(_guildHoverTimer);
  _guildHoverTargetEl = el;
  _guildHoverTimer = setTimeout(() => showGuildHover(el, name), ITEM_HOVER_DELAY);
}

function cancelGuildHover() {
  clearTimeout(_guildHoverTimer);
  _guildHoverTimer = null;
  if (_guildHoverEl) { _guildHoverEl.remove(); _guildHoverEl = null; }
  if (_guildHoverAbort) { try { _guildHoverAbort.abort(); } catch { /* ignore */ } _guildHoverAbort = null; }
  _guildHoverTargetEl = null;
}

async function showGuildHover(el, name) {
  if (!name || !document.body.contains(el)) return;
  cancelGuildHover();
  _guildHoverTargetEl = el;
  const card = document.createElement('div');
  card.className = 'guild-hover-card';
  card.innerHTML = `<div class="ghc-head"><div class="ghc-name">${escapeHtml(name)}</div></div>
    <div class="ihc-loading">Consulting the merchant scrolls…</div>`;
  document.body.appendChild(card);
  _guildHoverEl = card;
  positionItemHoverCard(card, el);

  const key = name.toLowerCase();
  const now = Date.now();
  const cached = _guildHoverCache.get(key);
  if (cached && now - cached.ts < GUILD_HOVER_TTL) {
    card.innerHTML = renderGuildHoverBody(cached.data);
    positionItemHoverCard(card, el);
    return;
  }

  try {
    _guildHoverAbort = new AbortController();
    const res = await fetch(`/api/guilds/${encodeURIComponent(name)}/info`, { signal: _guildHoverAbort.signal });
    if (!res.ok) throw new Error('guild_failed');
    const data = await res.json();
    _guildHoverCache.set(key, { ts: now, data });
    if (_guildHoverEl === card) {
      card.innerHTML = renderGuildHoverBody(data);
      positionItemHoverCard(card, el);
    }
  } catch (err) {
    if (err.name === 'AbortError') return;
    if (_guildHoverEl === card) {
      card.innerHTML = `<div class="ghc-head"><div class="ghc-name">${escapeHtml(name)}</div></div>
        <div class="ihc-error">*Fissal shrugs* — no guild data found.</div>`;
    }
  }
}

function renderGuildHoverBody(g) {
  const info = GUILD_COLOR_MAP[(g.guildName || '').toLowerCase()];
  const accent = info ? info.color : 'var(--t-secondary)';
  const dot = info ? `<span class="guild-dot" style="background:${info.color};"></span>` : '';
  const founded = g.foundedDate ? `<div class="ghc-meta-row"><span class="ghc-meta-label">Founded</span><span>${escapeHtml(g.foundedDate)}</span></div>` : '';
  const hall = g.guildHall ? `<div class="ghc-meta-row"><span class="ghc-meta-label">Hall</span><span>${escapeHtml(g.guildHall)}</span></div>` : '';
  const creator = g.creator ? `<div class="ghc-meta-row"><span class="ghc-meta-label">Creator</span><span>${escapeHtml(g.creator)}</span></div>` : '';
  const memberPct = g.maxMembers ? Math.min(100, Math.round(((g.totalSellers || 0) / g.maxMembers) * 100)) : 0;

  // Kiosk / trader line — compact indicator for hover. Full detail is shown in the click modal.
  let kioskHtml = '';
  if (g.kiosk && g.kiosk.zone) {
    const k = g.kiosk;
    const ws = k.hasWayshrine
      ? `<span class="ghc-kiosk-ws" title="Wayshrine ${escapeHtml(k.wayshrineDistance || 'nearby')}">🕯</span>`
      : '';
    const dlc = k.isDLC ? '<span class="ghc-kiosk-dlc" title="DLC zone">DLC</span>' : '';
    const weeks = k.weeksHeld != null
      ? ` <span class="ghc-kiosk-weeks">· ${k.weeksHeld}w</span>` : '';
    kioskHtml = `
      <div class="ghc-kiosk">
        <span class="ghc-kiosk-icon">📍</span>
        <span class="ghc-kiosk-text">
          <strong>${escapeHtml(k.trader || 'Trader')}</strong>
          <span class="ghc-kiosk-loc">${escapeHtml(k.city || '—')}, ${escapeHtml(k.zone)}</span>
        </span>
        ${ws}${dlc}${weeks}
      </div>`;
  }

  return `
    <div class="ghc-head" style="border-color:${accent}44;">
      ${dot}
      <div class="ghc-name" style="color:${accent};">${escapeHtml(g.guildName)}</div>
    </div>
    <div class="ghc-stats">
      <div class="ghc-stat"><span class="ghc-stat-val">${gold(g.weeklyGold || 0)}</span><span class="ghc-stat-label">7d gold</span></div>
      <div class="ghc-stat"><span class="ghc-stat-val">${(g.weeklySales || 0).toLocaleString()}</span><span class="ghc-stat-label">7d sales</span></div>
      <div class="ghc-stat"><span class="ghc-stat-val">${(g.activeSellers || 0)}<span class="ghc-stat-sub">/${g.maxMembers || 500}</span></span><span class="ghc-stat-label">active / roster</span></div>
    </div>
    ${kioskHtml}
    ${g.maxMembers ? `<div class="ghc-capacity">
      <div class="ghc-capacity-bar"><div class="ghc-capacity-fill" style="width:${memberPct}%;background:${accent};"></div></div>
      <div class="ghc-capacity-label">${(g.totalSellers || 0).toLocaleString()} recorded · cap ${g.maxMembers.toLocaleString()}</div>
    </div>` : ''}
    ${founded || hall || creator ? `<div class="ghc-meta">${creator}${founded}${hall}</div>` : ''}
    <div class="ihc-foot">Click for full details</div>
  `;
}

document.addEventListener('mouseover', (e) => {
  const el = e.target.closest('[data-guild]');
  if (!el) return;
  const name = el.getAttribute('data-guild');
  if (!name) return;
  if (_guildHoverTargetEl === el && (_guildHoverEl || _guildHoverTimer)) return;
  scheduleGuildHover(el, name);
}, true);

document.addEventListener('mouseout', (e) => {
  const el = e.target.closest('[data-guild]');
  if (!el) return;
  const rt = e.relatedTarget;
  if (rt && (el.contains(rt) || rt === _guildHoverEl || (_guildHoverEl && _guildHoverEl.contains(rt)))) return;
  cancelGuildHover();
}, true);

window.addEventListener('hashchange', cancelGuildHover);
document.addEventListener('click', cancelGuildHover, true);

// ── Unified User/Seller Hover Card (Sprint 8.6) ─────────────────────────
const _userHoverCache = new Map();
const USER_HOVER_TTL = 3 * 60 * 1000;
let _userHoverTimer = null;
let _userHoverEl = null;
let _userHoverTargetEl = null;
let _userHoverAbort = null;

function scheduleUserHover(el, tag) {
  clearTimeout(_userHoverTimer);
  _userHoverTargetEl = el;
  _userHoverTimer = setTimeout(() => showUserHover(el, tag), ITEM_HOVER_DELAY);
}

function cancelUserHover() {
  clearTimeout(_userHoverTimer);
  _userHoverTimer = null;
  if (_userHoverEl) { _userHoverEl.remove(); _userHoverEl = null; }
  if (_userHoverAbort) { try { _userHoverAbort.abort(); } catch { /* ignore */ } _userHoverAbort = null; }
  _userHoverTargetEl = null;
}

async function showUserHover(el, tag) {
  if (!tag || !document.body.contains(el)) return;
  // Skip generic/system syncers
  const lower = tag.toLowerCase();
  if (lower === 'system' || lower === 'startup' || lower === 'unknown') return;
  cancelUserHover();
  _userHoverTargetEl = el;
  const card = document.createElement('div');
  card.className = 'user-hover-card';
  card.innerHTML = `<div class="uhc-head"><div class="uhc-name">${escapeHtml(tag)}</div></div>
    <div class="ihc-loading">Summoning courier…</div>`;
  document.body.appendChild(card);
  _userHoverEl = card;
  positionItemHoverCard(card, el);

  const key = lower;
  const now = Date.now();
  const cached = _userHoverCache.get(key);
  if (cached && now - cached.ts < USER_HOVER_TTL) {
    card.innerHTML = renderUserHoverBody(cached.data, tag);
    positionItemHoverCard(card, el);
    return;
  }

  try {
    _userHoverAbort = new AbortController();
    const normalTag = tag.startsWith('@') ? tag : '@' + tag;
    const res = await fetch(`/api/users/${encodeURIComponent(normalTag)}/profile`, { signal: _userHoverAbort.signal });
    if (!res.ok) throw new Error('user_failed');
    const data = await res.json();
    _userHoverCache.set(key, { ts: now, data });
    if (_userHoverEl === card) {
      card.innerHTML = renderUserHoverBody(data, tag);
      positionItemHoverCard(card, el);
    }
  } catch (err) {
    if (err.name === 'AbortError') return;
    if (_userHoverEl === card) {
      card.innerHTML = `<div class="uhc-head"><div class="uhc-name">${escapeHtml(tag)}</div></div>
        <div class="ihc-error">*Fissal shrugs* — no profile found.</div>`;
    }
  }
}

function renderUserHoverBody(d, fallbackTag) {
  const tag = d.tag || fallbackTag;
  const display = d.anonymous ? '<span class="uhc-anon">🔒 Anonymous</span>' : (d.displayName ? escapeHtml(d.displayName) : '');
  const avatar = (!d.anonymous && d.avatar)
    ? `<img class="uhc-avatar" src="${escapeHtml(d.avatar)}" alt="" onerror="this.style.display='none'">`
    : `<div class="uhc-avatar-placeholder">${escapeHtml((tag || '?').charAt(tag?.startsWith('@') ? 1 : 0) || '?')}</div>`;
  const ws = d.weeklyStats || {};
  const rankBadge = d.leaderboardRank
    ? `<span class="uhc-rank uhc-rank-${d.leaderboardRank <= 3 ? 'top' : 'mid'}">#${d.leaderboardRank}</span>`
    : '';
  const guildBadges = (d.guilds || [])
    .map(g => {
      const info = GUILD_COLOR_MAP[(g || '').toLowerCase()];
      if (!info) return '';
      return `<span class="uhc-guild-dot" style="background:${info.color};" title="${escapeHtml(g)}"></span>`;
    }).join('');
  const sync = d.sync;
  const syncRow = sync && sync.totalPoints
    ? `<div class="uhc-sync"><span class="icon-fissal"></span> ${Number(sync.totalPoints).toLocaleString()} sync pts${sync.lastSync ? ` · last ${relativeTime(sync.lastSync)}` : ''}</div>`
    : '';

  return `
    <div class="uhc-head">
      ${avatar}
      <div class="uhc-name-wrap">
        <div class="uhc-name">${escapeHtml(tag)} ${rankBadge}</div>
        ${display ? `<div class="uhc-display">${display}</div>` : ''}
        ${guildBadges ? `<div class="uhc-guilds">${guildBadges}</div>` : ''}
      </div>
    </div>
    <div class="uhc-stats">
      <div class="uhc-stat"><span class="uhc-stat-val">${gold(ws.totalGold || 0)}</span><span class="uhc-stat-label">week gold</span></div>
      <div class="uhc-stat"><span class="uhc-stat-val">${(ws.totalSales || 0).toLocaleString()}</span><span class="uhc-stat-label">week sales</span></div>
      <div class="uhc-stat"><span class="uhc-stat-val">${(ws.totalVolume || 0).toLocaleString()}</span><span class="uhc-stat-label">volume</span></div>
    </div>
    ${ws.biggestSale ? `<div class="uhc-best"><span class="uhc-best-label">Biggest</span> ${escapeHtml(ws.biggestSale)} — ${gold(ws.biggestSaleGold || 0)}</div>` : ''}
    ${syncRow}
    <div class="ihc-foot">Click for full profile</div>
  `;
}

document.addEventListener('mouseover', (e) => {
  const el = e.target.closest('[data-user]');
  if (!el) return;
  const tag = el.getAttribute('data-user');
  if (!tag || tag === '—') return;
  if (_userHoverTargetEl === el && (_userHoverEl || _userHoverTimer)) return;
  scheduleUserHover(el, tag);
}, true);

document.addEventListener('mouseout', (e) => {
  const el = e.target.closest('[data-user]');
  if (!el) return;
  const rt = e.relatedTarget;
  if (rt && (el.contains(rt) || rt === _userHoverEl || (_userHoverEl && _userHoverEl.contains(rt)))) return;
  cancelUserHover();
}, true);

window.addEventListener('hashchange', cancelUserHover);
document.addEventListener('click', cancelUserHover, true);

// Sprint 10.6 — one passive rAF-throttled handler cancels all three hover
// cards on scroll/resize, instead of 6 individual un-throttled listeners.
// Scroll listener is scoped to window (capture) so child scrollers also fire.
(function installUnifiedHoverCancellers() {
  let pending = false;
  const flush = () => {
    pending = false;
    try { cancelItemHover();  } catch {}
    try { cancelGuildHover(); } catch {}
    try { cancelUserHover();  } catch {}
  };
  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(flush);
  };
  window.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
})();

// ── Guild color-coding ──────────────────────────────────────────────────
const GUILD_COLOR_MAP = {
  'redfur trading post':    { cls: 'guild-color-post',    color: '#ef4444', key: 'post' },
  'redfur trading caravan': { cls: 'guild-color-caravan', color: '#f59e0b', key: 'caravan' },
  'redfur dealers':         { cls: 'guild-color-dealers', color: '#fb923c', key: 'dealers' },
};
const REDFUR_GUILD_NAMES = Object.keys(GUILD_COLOR_MAP);

function getGuildColorClass(guilds) {
  if (!guilds || !guilds.length) return '';
  const lower = guilds.map(g => (g || '').toLowerCase());
  const redfurCount = REDFUR_GUILD_NAMES.filter(n => lower.includes(n)).length;
  if (redfurCount >= 3) return 'guild-color-all';
  // Return color of first matched Redfur guild
  for (const g of lower) {
    if (GUILD_COLOR_MAP[g]) return GUILD_COLOR_MAP[g].cls;
  }
  return '';
}

function getGuildBadgesHtml(guilds) {
  if (!guilds || !guilds.length) return '';
  const badges = [];
  for (const g of guilds) {
    const info = GUILD_COLOR_MAP[(g || '').toLowerCase()];
    if (info) badges.push(`<span class="profile-guild-badge badge-${info.key}">${escapeHtml(g)}</span>`);
  }
  return badges.length ? `<div class="profile-guilds-row">${badges.join('')}</div>` : '';
}

// ── Toast ────────────────────────────────────────────────────────────────
function toast(msg, type = '', options = {}) {
  if (options.feedAware && isLiveFeedOpen()) return;
  const emoji = type === 'success' ? '✨ ' : type === 'error' ? '⚠️ ' : '';
  const el = document.createElement('div');
  el.className = `toast ${type}`;
  el.textContent = emoji + msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3000);
}

// ── Fissal toast — positioned away from the bubble, longer duration ─────
function fissalToast(msg) {
  const el = document.createElement('div');
  el.className = 'toast error fissal-toast';
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => {
    el.style.opacity = '0';
    el.style.transition = 'opacity 0.5s ease';
    setTimeout(() => el.remove(), 500);
  }, 5000);
}

// ══════════════════════════════════════════════════════════════════════════
//   NOTIFICATION CENTER — Fissal-themed notification history
// ══════════════════════════════════════════════════════════════════════════
const NOTIF_MAX = 50;
const NOTIF_STORAGE_KEY = 'redfur-notifications';
let _notifications = [];
let _notifUnread = 0;

function loadNotifications() {
  try {
    const raw = localStorage.getItem(NOTIF_STORAGE_KEY);
    if (raw) _notifications = JSON.parse(raw).slice(0, NOTIF_MAX);
  } catch { _notifications = []; }
  _notifUnread = _notifications.filter(n => !n.read).length;
  updateNotifBadge();
}

function saveNotifications() {
  try {
    // Strip `extra` payloads before persisting — they can be large (sync data
    // includes pointsSummary for every user) and are only useful in-session
    // for the detail modal. On reload, sync entries fall back to navigation.
    const slim = _notifications.slice(0, NOTIF_MAX).map(n => {
      if (!n.extra) return n;
      const { extra, ...rest } = n;
      return rest;
    });
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(slim));
  } catch { /* storage full */ }
}

function addNotification(type, title, body, icon, extra) {
  const notif = {
    id: Date.now() + '-' + Math.random().toString(36).slice(2, 6),
    type, // 'sync' | 'sale' | 'bigsale' | 'points' | 'system' | 'info'
    title,
    body,
    icon: icon || notifTypeIcon(type),
    time: Date.now(),
    read: false,
    extra: extra || null, // Optional typed payload (e.g. sync data for modal detail view)
  };
  _notifications.unshift(notif);
  if (_notifications.length > NOTIF_MAX) _notifications.length = NOTIF_MAX;
  _notifUnread++;
  saveNotifications();
  updateNotifBadge();
  renderNotifPanel();
  return notif;
}

function isLiveFeedOpen() {
  const panel = document.getElementById('live-feed-panel');
  return !!panel?.classList.contains('open');
}

function notifTypeIcon(type) {
  switch (type) {
    case 'sync': return '📡';
    case 'sale': return '💰';
    case 'bigsale': return '🔥';
    case 'points': return '⭐';
    case 'system': return '⚙️';
    case 'info': return 'ℹ️';
    default: return '🔔';
  }
}

function markAllNotifRead() {
  _notifications.forEach(n => n.read = true);
  _notifUnread = 0;
  saveNotifications();
  updateNotifBadge();
  renderNotifPanel();
}

function clearAllNotifications() {
  _notifications = [];
  _notifUnread = 0;
  saveNotifications();
  updateNotifBadge();
  renderNotifPanel();
}

function updateNotifBadge() {
  const badge = document.getElementById('notif-badge');
  if (!badge) return;
  if (_notifUnread > 0) {
    badge.textContent = _notifUnread > 99 ? '99+' : _notifUnread;
    badge.style.display = '';
  } else {
    badge.style.display = 'none';
  }
}

function renderNotifPanel() {
  const body = document.getElementById('notif-panel-body');
  if (!body) return;
  if (_notifications.length === 0) {
    body.innerHTML = '<div class="notif-empty">No notifications yet</div>';
    return;
  }
  body.innerHTML = _notifications.map(n => {
    const ago = relativeTime(n.time);
    const typeClass = `notif-item-${n.type || 'info'}`;
    const readClass = n.read ? '' : ' notif-unread';
    return `<div class="notif-item ${typeClass}${readClass}" data-id="${n.id}" data-type="${n.type || 'info'}">
      <span class="notif-item-icon">${n.icon}</span>
      <div class="notif-item-content">
        <div class="notif-item-title">${escapeHtml(n.title)}</div>
        ${n.body ? `<div class="notif-item-body">${escapeHtml(n.body)}</div>` : ''}
      </div>
      <span class="notif-item-time">${ago}</span>
      <button class="notif-item-close" data-dismiss="${n.id}" title="Dismiss">✕</button>
    </div>`;
  }).join('');

  // Event delegation for dismiss + navigate (single listener instead of N)
  body.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('.notif-item-close');
    if (closeBtn) {
      e.stopPropagation();
      const id = closeBtn.dataset.dismiss;
      _notifications = _notifications.filter(n => n.id !== id);
      _notifUnread = _notifications.filter(n => !n.read).length;
      saveNotifications();
      updateNotifBadge();
      renderNotifPanel();
      return;
    }
    const item = e.target.closest('.notif-item');
    if (item) {
      const type = item.dataset.type;
      const panel = document.getElementById('notif-panel');
      if (panel) panel.classList.remove('open');
      if (type === 'sale') navigate('#/sales');
      else if (type === 'bigsale') navigate('#/sales');
      else if (type === 'sync') navigate('#/sync-stats');
      else if (type === 'points') navigate('#/sync-stats');
      else if (type === 'system') navigate('#/');
      else navigate('#/');
    }
  });

  // Sprint 8.3: also refresh live feed panel if open
  const lfPanel = document.getElementById('live-feed-panel');
  if (lfPanel && lfPanel.classList.contains('open') && typeof renderLiveFeed === 'function') {
    renderLiveFeed();
  }
}

function initNotifCenter() {
  loadNotifications();
  const btn = document.getElementById('notif-bell-btn');
  const panel = document.getElementById('notif-panel');
  const clearBtn = document.getElementById('notif-clear-btn');
  if (btn && panel) {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      // Bell now opens the Live Feed panel (notif-panel kept as fallback for legacy code paths)
      if (typeof toggleLiveFeed === 'function') {
        toggleLiveFeed();
        markAllNotifRead();
        return;
      }
      const isOpen = panel.classList.toggle('open');
      if (isOpen) {
        renderNotifPanel();
        markAllNotifRead();
      }
    });
    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!panel.contains(e.target) && e.target !== btn) {
        panel.classList.remove('open');
      }
    });
  }
  if (clearBtn) {
    clearBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      clearAllNotifications();
    });
  }
}

// ══════════════════════════════════════════════════════════════════════════
//   LIVE FEED PANEL — unified activity stream (Sprint 8.3)
//   Modes: 'slide' (default) | 'dock' | 'float'
//   Data source: reuses _notifications array (populated by addNotification)
// ══════════════════════════════════════════════════════════════════════════
const FEED_MODE_KEY = 'redfur-feed-mode';
const FEED_FILTER_KEY = 'redfur-feed-filter';
const FEED_PERSPECTIVE_KEY = 'redfur-feed-perspective';
const FEED_OPEN_KEY = 'redfur-feed-open'; // Sprint 9.2: persist open/closed state
const VALID_FEED_MODES = ['slide', 'dock', 'float'];
const FEED_DATA_TTL = 15_000; // ms before reload
let _feedFilter = 'all';
let _feedPerspective = 'all-traders'; // 'personal' | 'all-traders'
let _feedPollTimer = null;
let _feedAllSales = [];      // flat sale rows from /api/feed/recent
let _feedPersonalSales = []; // flat sale rows from /api/me/sales
let _feedDataLoading = false;
let _feedDataTs = 0;
let _feedAllLoading = false;      // true while /api/feed/recent is in-flight
let _feedPersonalLoading = false; // true while /api/me/sales is in-flight
let _feedSeenSaleKeys  = new Set(); // composite keys of all previously-rendered sales
let _feedNewSaleKeys   = new Set(); // keys that are new this render cycle
let _feedHasRenderedOnce = false;   // suppresses entrance animation on initial load
const FEED_PAGE_SIZE = 20;          // item groups / sale rows added per scroll-load
let _feedVisibleGroups   = 20;      // all-traders: item groups initially shown
let _feedVisiblePersonal = 25;      // personal: sale rows initially shown
let _feedScrollObserver  = null;    // IntersectionObserver for lazy-load sentinel (flat mode)
const _feedObservers     = new Map();  // lane-key → IntersectionObserver (lane mode)
// Per-lane layout preferences — persisted to localStorage under 'redfur-feed-lanes'.
// `collapsed`      — whether the lane header is collapsed (body hidden).
// `rowsCompact`    — render rows in single-line compact style.
// `rowsCollapsed`  — (sales only) item groups start with seller sub-rows hidden.
// `flexGrow`       — relative height weight when multiple lanes are visible.
const _FEED_LANE_ORDER_DEFAULT = ['sales', 'syncs', 'activity'];
const _FEED_LANE_DEFAULTS = {
  sales:    { collapsed: false, rowsCompact: true, rowsCollapsed: true, flexGrow: 2 },
  syncs:    { collapsed: false, rowsCompact: true, flexGrow: 1 },
  activity: { collapsed: false, rowsCompact: true, flexGrow: 1 },
};
function _loadFeedLaneSettings() {
  try {
    const s = JSON.parse(localStorage.getItem('redfur-feed-lanes') || 'null');
    const base = {
      laneOrder: [..._FEED_LANE_ORDER_DEFAULT],
      sales:    { ..._FEED_LANE_DEFAULTS.sales },
      syncs:    { ..._FEED_LANE_DEFAULTS.syncs },
      activity: { ..._FEED_LANE_DEFAULTS.activity },
    };
    if (!s) return base;
    // Validate stored order — must contain exactly the three known keys
    const validOrder = Array.isArray(s.laneOrder) &&
      s.laneOrder.length === 3 &&
      ['sales','syncs','activity'].every(k => s.laneOrder.includes(k));
    return {
      laneOrder: validOrder ? s.laneOrder : [..._FEED_LANE_ORDER_DEFAULT],
      sales:    { ..._FEED_LANE_DEFAULTS.sales,    ...(s.sales    || {}) },
      syncs:    { ..._FEED_LANE_DEFAULTS.syncs,    ...(s.syncs    || {}) },
      activity: { ..._FEED_LANE_DEFAULTS.activity, ...(s.activity || {}) },
    };
  } catch {
    return {
      laneOrder: [..._FEED_LANE_ORDER_DEFAULT],
      sales:    { ..._FEED_LANE_DEFAULTS.sales },
      syncs:    { ..._FEED_LANE_DEFAULTS.syncs },
      activity: { ..._FEED_LANE_DEFAULTS.activity },
    };
  }
}
function _saveFeedLaneSettings() {
  try { localStorage.setItem('redfur-feed-lanes', JSON.stringify(_feedLaneSettings)); } catch { /* ignore */ }
}
let _feedLaneSettings    = _loadFeedLaneSettings();
let _feedCollapsedItems  = new Set();  // item group keys user has individually toggled
let _feedVisibleSyncs    = 15;
let _feedVisibleActivity = 15;

// Stable composite key for a sale row (all-traders has no saleID in the API response)
function _saleKey(sale) {
  return `${sale.timestamp}_${sale.price}_${sale.quant || 1}`;
}

// Deterministic per-player HSL colour.  Uses a DJB2-style hash spread across
// the golden angle so names produce evenly-distributed, distinct hues.
function playerColor(name) {
  if (!name) return '#4ade80';
  let h = 5381;
  for (let i = 0; i < name.length; i++) h = ((h << 5) + h) ^ name.charCodeAt(i);
  h = Math.abs(h);
  const hue = Math.round((h * 137.508) % 360);
  return `hsl(${hue}, 62%, 68%)`;
}

function updateLayoutChromeOffsets() {
  const header = document.getElementById('app-header');
  const footer = document.getElementById('app-footer');
  const headerH = header ? Math.ceil(header.getBoundingClientRect().height) : 48;
  const footerH = footer ? Math.ceil(footer.getBoundingClientRect().height) : 28;
  document.documentElement.style.setProperty('--app-header-h', `${headerH}px`);
  document.documentElement.style.setProperty('--app-footer-h', `${footerH}px`);
}

function getFeedMode() {
  // Sprint 12: Restored true docking. The previous Sprint 10.7 hack forced
  // 'slide' for everyone, which made the panel overlay content with a blur
  // backdrop. Dock mode reserves a grid column on desktop, physically
  // pushing the main content left. Slide remains the mobile default.
  const stored = localStorage.getItem(FEED_MODE_KEY);
  if (VALID_FEED_MODES.includes(stored)) return stored;
  // Default by viewport: dock on desktop, slide on mobile/tablet.
  return window.matchMedia('(max-width: 1099px)').matches ? 'slide' : 'dock';
}

function setFeedMode(mode) {
  if (!VALID_FEED_MODES.includes(mode)) mode = 'slide';
  localStorage.setItem(FEED_MODE_KEY, mode);
  applyFeedMode();
}

function applyFeedMode() {
  updateLayoutChromeOffsets();
  const panel = document.getElementById('live-feed-panel');
  const backdrop = document.getElementById('live-feed-backdrop');
  if (!panel) return;
  // Clear any legacy inline top from earlier fixed-positioning mode so the
  // CSS (sticky top:0 when docked; var(--app-header-h) otherwise) wins.
  panel.style.top = '';
  let mode = getFeedMode();
  // Sprint 10.6 — never dock below the tablet breakpoint. The sidebar goes
  // position:fixed <768px and the grid override would otherwise reserve a
  // 240px black column. Preserve the user's stored preference for desktop.
  const narrowVp = window.matchMedia('(max-width: 1099px)').matches;
  if (narrowVp && mode === 'dock') mode = 'slide';
  panel.classList.remove('mode-slide', 'mode-dock', 'mode-float');
  panel.classList.add(`mode-${mode}`);

  // Dock button indicates current mode
  const dockBtn = document.getElementById('live-feed-dock-btn');
  const popBtn = document.getElementById('live-feed-pop-btn');
  if (dockBtn) dockBtn.classList.toggle('active', mode === 'dock');
  if (popBtn) popBtn.classList.toggle('active', mode === 'float');

  // Sprint 12: Apply feed-docked class so #terminal-shell grid reserves a
  // column for the panel on desktop. In slide/float mode we clear it.
  const isOpen = panel.classList.contains('open');
  document.body.classList.toggle('feed-docked', mode === 'dock' && isOpen);

  // Backdrop only for slide mode on mobile (never for dock — it's pushing,
  // not overlaying). Float never gets a backdrop.
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const needsBackdrop = isOpen && mode === 'slide' && isMobile;
  if (backdrop) backdrop.classList.toggle('open', needsBackdrop);
}

function openLiveFeed() {
  const panel = document.getElementById('live-feed-panel');
  if (!panel) return;
  panel.classList.add('open');
  localStorage.setItem(FEED_OPEN_KEY, '1');
  applyFeedMode();
  renderLiveFeed();
  // Mark notif as read (bell badge)
  markAllNotifRead();
}

function closeLiveFeed() {
  const panel = document.getElementById('live-feed-panel');
  const backdrop = document.getElementById('live-feed-backdrop');
  if (panel) panel.classList.remove('open');
  if (backdrop) backdrop.classList.remove('open');
  document.body.classList.remove('feed-docked');
  localStorage.setItem(FEED_OPEN_KEY, '0');
}

function toggleLiveFeed() {
  const panel = document.getElementById('live-feed-panel');
  if (!panel) return;
  if (panel.classList.contains('open')) closeLiveFeed();
  else openLiveFeed();
}

function feedTypeMeta(type) {
  switch (type) {
    case 'sale': return { cls: 'feed-type-sale', icon: '💰', route: '#/sales' };
    case 'bigsale': return { cls: 'feed-type-bigsale', icon: '🔥', route: '#/sales' };
    case 'sync': return { cls: 'feed-type-sync', icon: '📡', route: '#/sync' };
    case 'points': return { cls: 'feed-type-points', icon: '⭐', route: '#/sync' };
    case 'system': return { cls: '', icon: '⚙️', route: '#/' };
    default: return { cls: '', icon: '🔔', route: '#/' };
  }
}

// ── Internal helper: compact one-line sync row (used in lane mode) ──────────
function _renderSyncEntryCompact(n) {
  const ago = relativeTime(n.time);
  const readCls = n.read ? '' : ' feed-unread';
  return `<div class="feed-sync-compact${readCls}" data-id="${escapeHtml(n.id)}" data-type="sync">
    <span class="feed-sync-compact-icon">\ud83d\udce1</span>
    <span class="feed-sync-compact-body">${escapeHtml(n.body || n.title)}</span>
    <span class="feed-sync-compact-time" data-ts="${n.time}">${ago}</span>
  </div>`;
}

// Compact single-line activity/notification row (used in Activity lane when rowsCompact is on)
function _renderNotifEntryCompact(n) {
  const meta = feedTypeMeta(n.type);
  const ago = relativeTime(n.time);
  const readCls = n.read ? '' : ' feed-unread';
  const icon = n.icon || meta.icon || '⭐';
  let body = n.body || n.title || '';
  // For points entries show who earned them (prepend earner if not already in the text)
  if (n.type === 'points' && n.extra?.earner && !body.startsWith(n.extra.earner)) {
    body = n.extra.earner + ': ' + body;
  }
  return `<div class="feed-sync-compact${readCls}" data-id="${escapeHtml(n.id)}" data-type="${escapeHtml(n.type || 'info')}">
    <span class="feed-sync-compact-icon">${icon}</span>
    <span class="feed-sync-compact-body">${escapeHtml(body)}</span>
    <span class="feed-sync-compact-time" data-ts="${n.time}">${ago}</span>
  </div>`;
}

// ── Internal helper: render a single notification entry ─────────────────
function _renderNotifEntry(n) {
  const meta = feedTypeMeta(n.type);
  const ago = relativeTime(n.time);
  const readCls = n.read ? '' : ' feed-unread';
  return `<div class="live-feed-entry ${meta.cls}${readCls}" data-id="${escapeHtml(n.id)}" data-type="${escapeHtml(n.type || 'info')}">
    <span class="live-feed-entry-icon">${n.icon || meta.icon}</span>
    <div class="live-feed-entry-body">
      <div class="live-feed-entry-title">${escapeHtml(n.title)}</div>
      ${n.body ? `<div class="live-feed-entry-text">${escapeHtml(n.body)}</div>` : ''}
      <div class="live-feed-entry-time" data-ts="${n.time}">${ago}</div>
    </div>
  </div>`;
}

// ── Group sync+points notifications into per-syncer events ─────────────────
// Pairs each 'sync' notification with its matching 'points' notifications
// (same earner names, timestamps within 10 s) so they render as one row per person.
function _groupSyncEvents(notifs) {
  const WINDOW_MS = 10000;
  const usedIds = new Set();
  const events = [];

  function syncerNames(n) {
    if (n.extra?.syncers?.length) return n.extra.syncers;
    // Post-reload fallback: parse "Alice, Bob \u2014 +N items, +N sales"
    const sep = (n.body || '').indexOf(' \u2014 ');
    if (sep > 0) return n.body.slice(0, sep).split(', ').map(s => s.trim()).filter(Boolean);
    return [];
  }

  function earnerName(n) {
    if (n.extra?.earner) return n.extra.earner;
    // Post-reload fallback: parse "\u2b50 Alice +5 Sync Points"
    const m = (n.title || '').match(/^\u2b50\s+(.+?)\s+\+[\d,]+\s+Sync Points/);
    return m?.[1]?.trim() || null;
  }

  function ptsEarnedFromNotif(n) {
    const m = (n.title || '').match(/\+([\d,]+)\s+Sync Points/);
    return m ? parseInt(m[1].replace(/,/g, ''), 10) : 0;
  }

  for (const n of notifs) {
    if (n.type !== 'sync') continue;
    const names = syncerNames(n);
    const matchedPts = {};
    for (const pn of notifs) {
      if (pn.type !== 'points') continue;
      if (usedIds.has(pn.id)) continue;
      if (Math.abs(pn.time - n.time) > WINDOW_MS) continue;
      const earner = earnerName(pn);
      if (!earner || !names.includes(earner)) continue;
      matchedPts[earner] = pn;
      usedIds.add(pn.id);
    }
    usedIds.add(n.id);
    events.push({
      isSyncGroup: true,
      time: n.time,
      syncNotif: n,
      // null syncers = couldn't parse names; falls back to raw render
      syncers: names.length > 0
        ? names.map(name => ({ name, ptsNotif: matchedPts[name] || null, earned: matchedPts[name] ? ptsEarnedFromNotif(matchedPts[name]) : 0 }))
        : null,
    });
  }

  // Pass-through: unmatched points, system, info
  for (const n of notifs) {
    if (usedIds.has(n.id)) continue;
    events.push({ isSyncGroup: false, time: n.time, notif: n });
  }
  events.sort((a, b) => b.time - a.time);

  // ── Coalesce consecutive sync groups within 30 min (chain: each pair) ─────────
  // Uses chain-based logic: each consecutive pair must be within COALESCE_MS of
  // each other (not the batch start). Points per syncer are summed across batches.
  const COALESCE_MS = 1_800_000; // 30 minutes
  const _closeBatch = (batch) => {
    if (batch.groups.length === 1) return batch.groups[0];
    // Sum earned points per syncer name across all batches
    const syncerMap = new Map(); // name → { name, ptsNotif, earned, syncCount }
    for (const g of batch.groups) {
      for (const s of g.syncers || []) {
        if (syncerMap.has(s.name)) {
          const e = syncerMap.get(s.name);
          e.earned += s.earned || 0;
          e.syncCount++;
          if (!e.ptsNotif && s.ptsNotif) e.ptsNotif = s.ptsNotif;
        } else {
          syncerMap.set(s.name, { name: s.name, ptsNotif: s.ptsNotif, earned: s.earned || 0, syncCount: 1 });
        }
      }
    }
    const allSyncers = Array.from(syncerMap.values());
    return {
      isSyncGroup: true,
      isCoalesced: true,
      time: batch.groups[0].time,       // most recent batch time
      oldestTime: batch.groups[batch.groups.length - 1].time,
      syncNotif: batch.groups[0].syncNotif,
      syncers: allSyncers.length > 0 ? allSyncers : null,
      coalescedGroups: batch.groups,
    };
  };
  const coalesced = [];
  let _activeBatch = null;
  for (const ev of events) {
    if (!ev.isSyncGroup) {
      if (_activeBatch) { coalesced.push(_closeBatch(_activeBatch)); _activeBatch = null; }
      coalesced.push(ev);
      continue;
    }
    if (!_activeBatch) {
      _activeBatch = { groups: [ev] };
    } else {
      // Chain: compare to the PREVIOUS item's time, not the batch start
      const prev = _activeBatch.groups[_activeBatch.groups.length - 1];
      if (prev.time - ev.time <= COALESCE_MS) {
        _activeBatch.groups.push(ev);
      } else {
        coalesced.push(_closeBatch(_activeBatch));
        _activeBatch = { groups: [ev] };
      }
    }
  }
  if (_activeBatch) coalesced.push(_closeBatch(_activeBatch));
  return coalesced;
}

// ── Per-syncer compact row inside a grouped sync event ──────────────────────
function _renderSyncGroupRowCompact(syncerInfo, syncNotif) {
  const ago = relativeTime(syncNotif.time);
  const readCls = (syncNotif.read && (!syncerInfo.ptsNotif || syncerInfo.ptsNotif.read)) ? '' : ' feed-unread';
  const ptsText = syncerInfo.earned > 0 ? ` — ⭐ +${syncerInfo.earned} pts` : '';
  const countBadge = syncerInfo.syncCount > 1 ? ` <span class="feed-coalesce-count">${syncerInfo.syncCount} syncs</span>` : '';
  const _me = currentUser?.displayName || currentUser?.username;
  const selfCls = _me && syncerInfo.name === _me ? ' feed-sync-me' : '';
  return `<div class="feed-sync-compact${readCls}${selfCls}" data-id="${escapeHtml(syncNotif.id)}" data-type="sync" data-syncer="${escapeHtml(syncerInfo.name)}">
    <span class="feed-sync-compact-icon">📡</span>
    <span class="feed-sync-compact-body">${escapeHtml(syncerInfo.name)}${escapeHtml(ptsText)}${countBadge}</span>
    <span class="feed-sync-compact-time" data-ts="${syncNotif.time}">${ago}</span>
  </div>`;
}

// ── Per-syncer full card inside a grouped sync event ────────────────────────
function _renderSyncGroupRowFull(syncerInfo, syncNotif) {
  const ago = relativeTime(syncNotif.time);
  const readCls = (syncNotif.read && (!syncerInfo.ptsNotif || syncerInfo.ptsNotif.read)) ? '' : ' feed-unread';
  const ptsBadge = syncerInfo.earned > 0
    ? ` <span class="feed-pts-badge">⭐ +${syncerInfo.earned} pts</span>` : '';
  const countBadge = syncerInfo.syncCount > 1
    ? ` <span class="feed-coalesce-count">${syncerInfo.syncCount} syncs</span>` : '';
  const _me = currentUser?.displayName || currentUser?.username;
  const selfCls = _me && syncerInfo.name === _me ? ' feed-sync-me' : '';
  return `<div class="live-feed-entry feed-type-sync${readCls}${selfCls}" data-id="${escapeHtml(syncNotif.id)}" data-type="sync" data-syncer="${escapeHtml(syncerInfo.name)}">
    <span class="live-feed-entry-icon">📡</span>
    <div class="live-feed-entry-body">
      <div class="live-feed-entry-title">${escapeHtml(syncerInfo.name)}${ptsBadge}${countBadge}</div>
      <div class="live-feed-entry-time" data-ts="${syncNotif.time}">${ago}</div>
    </div>
  </div>`;
}

// ── Render a full sync event group: per-syncer rows, or raw fallback ─────────
function _renderSyncGroup(group, compact) {
  const gid = escapeHtml(group.syncNotif.id);
  // For coalesced groups, override the displayed timestamp with the oldest time
  // so each syncer row shows when the oldest batch started
  const displayNotif = group.isCoalesced
    ? { ...group.syncNotif, time: group.oldestTime ?? group.syncNotif.time }
    : group.syncNotif;
  if (!group.syncers) {
    // Couldn't parse syncer names — fall back to single combined dropdown
    const dropdown = group.isCoalesced
      ? _renderCoalescedDropdownHtml(group)
      : _renderSyncDropdownHtml(group.syncNotif, null);
    const inner = compact ? _renderSyncEntryCompact(displayNotif) : _renderNotifEntry(displayNotif);
    return `<div class="feed-sync-group" data-sync-id="${gid}">${inner}${dropdown}</div>`;
  }
  // Each syncer gets their own row immediately followed by their own dropdown.
  // This lets the click handler open the correct person's data regardless of
  // how many syncers share the same group (coalesced or same-batch).
  const items = group.syncers.map(s => {
    const row = compact
      ? _renderSyncGroupRowCompact(s, displayNotif)
      : _renderSyncGroupRowFull(s, displayNotif);
    const dropdown = _renderSyncerDropdownHtml(s, group);
    return row + dropdown;
  }).join('');
  return `<div class="feed-sync-group" data-sync-id="${gid}">${items}</div>`;
}

// ── Per-syncer dropdown: shows one person's breakdown for any group type ────
// Used when group.syncers is populated; each syncer row gets its own dropdown.
function _renderSyncerDropdownHtml(syncerInfo, group) {
  // Collect per-syncer formula fields across all coalesced batches (or just the one batch)
  const batches = group.isCoalesced ? group.coalescedGroups : [group];
  let files = 0, itemsCount = 0, sales = 0, ttcUpdated = false, earned = 0, total = 0, noNewData = true;
  let hasLiveData = false;

  for (const g of batches) {
    const ps = g.syncNotif?.extra?.pointsSummary?.[syncerInfo.name];
    if (!ps) continue;
    hasLiveData = true;
    files       += ps.files   || 0;
    itemsCount  += ps.items   || 0;
    sales       += ps.sales   || 0;
    ttcUpdated   = ttcUpdated || !!ps.ttcUpdated;
    earned      += ps.earned  || 0;
    total        = ps.total   || total; // keep most recent cumulative total
    if (!ps.noNewData) noNewData = false;
  }

  // Post-reload fallback (no extra payload on any batch)
  if (!hasLiveData) {
    const earnedFallback = syncerInfo.earned || 0;
    const totalFallback  = syncerInfo.ptsNotif?.extra?.total;
    const totalStr = totalFallback != null
      ? ` · total <span class="fsd-pts-total-val">${Number(totalFallback).toLocaleString()}</span>` : '';
    const ptsLine = earnedFallback > 0
      ? `<span class="fsd-pts-result">+${earnedFallback.toLocaleString()} pts${totalStr}</span>`
      : `<span class="fsd-pts-result fsd-pts-zero">+0 pts · no new data</span>`;
    return `<div class="feed-sync-dropdown" data-syncer="${escapeHtml(syncerInfo.name)}" hidden>
      <div class="fsd-meta">${escapeHtml(syncerInfo.name)}</div>
      <div class="fsd-section"><div class="fsd-syncer-row">${ptsLine}</div></div>
      <div class="fsd-stale-note">Detailed breakdown available in active session only.</div>
    </div>`;
  }

  // ── Points formula ───────────────────────────────────────────────────────
  let ptsHtml = '';
  if (noNewData || earned <= 0) {
    ptsHtml = `<div class="fsd-section">
      <div class="fsd-syncer-row"><span class="fsd-pts-result fsd-pts-zero">+0 pts \u00b7 no new data \u00b7 total ${total.toLocaleString()}</span></div>
    </div>`;
  } else {
    const pieces = ['<span class="fsd-pts-piece">+5 base</span>'];
    if (files)      pieces.push(`<span class="fsd-pts-piece">${files} file${files !== 1 ? 's' : ''} \u00d72</span>`);
    if (itemsCount) pieces.push(`<span class="fsd-pts-piece">${itemsCount.toLocaleString()} items \u00d75</span>`);
    if (sales)      pieces.push(`<span class="fsd-pts-piece">${sales.toLocaleString()} sales \u00d71</span>`);
    if (ttcUpdated) pieces.push('<span class="fsd-pts-piece fsd-pts-ttc">TTC +50</span>');
    ptsHtml = `<div class="fsd-section">
      <div class="fsd-pts-formula">${pieces.join('<span class="fsd-sep"> + </span>')}</div>
      <div class="fsd-pts-result">= +${earned.toLocaleString()} pts \u00b7 total <span class="fsd-pts-total-val">${total.toLocaleString()}</span></div>
    </div>`;
  }

  // ── Guild coverage (from the first batch that has live data) ─────────────
  const firstLiveBatch = batches.find(g => g.syncNotif?.extra);
  const syncData = firstLiveBatch?.syncNotif?.extra;
  let guildHtml = '';
  const gfat = syncData?.guildFinishedAt;
  if (gfat) {
    const guildKeys   = ['post', 'caravan', 'dealers'];
    const guildLabels = { post: 'Redfur Post', caravan: 'Redfur Caravan', dealers: 'Redfur Dealers' };
    const guildPills  = { post: 'POST', caravan: 'CRVN', dealers: 'DLRS' };
    guildHtml = `<div class="fsd-section">
      <div class="fsd-section-label">Guild Coverage</div>
      ${guildKeys.map(key => {
        const ts = gfat[key];
        const tsStr = ts ? new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
        return `<div class="fsd-guild-row">
          <span class="feed-guild-pill feed-guild-${key}">${guildPills[key]}</span>
          <span class="fsd-guild-label">${escapeHtml(guildLabels[key])}</span>
          <span class="fsd-guild-ts">${tsStr ? 'last captured: ' + escapeHtml(tsStr) : 'no data yet'}</span>
        </div>`;
      }).join('')}
    </div>`;
  }

  // ── Top items (from the first live batch; shared context for this sync) ──
  const rawMap = syncData?.mm?.insertedItemMap || {};
  const topItems = Object.values(rawMap)
    .filter(e => e.saleCount > 0 && e.itemName)
    .sort((a, b) => b.totalGold - a.totalGold)
    .slice(0, 5);
  let itemsHtml = '';
  if (topItems.length > 0) {
    itemsHtml = `<div class="fsd-section">
      <div class="fsd-section-label">Items Registered <span class="fsd-section-sub">top ${topItems.length} by gold</span></div>
      ${topItems.map(item => `<div class="fsd-item-row">
        ${item.itemIcon
          ? `<img class="fsd-item-icon" src="${escapeHtml(esoIconUrl(item.itemIcon))}" onerror="this.onerror=null;this.style.display='none'" alt="">`
          : '<span class="fsd-item-icon-ph">\ud83d\udce6</span>'}
        <span class="fsd-item-name clickable-item" data-item="${escapeHtml(item.itemName)}">${escapeHtml(item.itemName)}</span>
        <span class="fsd-item-count">\u00d7${item.saleCount}</span>
        <span class="fsd-item-top-price">${gold(item.maxPrice)} top</span>
        <span class="fsd-item-gold">${gold(item.totalGold)}</span>
      </div>`).join('')}
    </div>`;
  }

  const metaTime = new Date(group.syncNotif.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `<div class="feed-sync-dropdown" data-syncer="${escapeHtml(syncerInfo.name)}" hidden>
    <div class="fsd-meta">${escapeHtml(syncerInfo.name + ' \u00b7 ' + metaTime)}</div>
    ${ptsHtml}${guildHtml}${itemsHtml}
  </div>`;
}

// ── Coalesced dropdown: per-batch summary for merged sync groups ──────────────
function _renderCoalescedDropdownHtml(group) {
  const batches = group.coalescedGroups;
  const batchCount = batches.length;
  const hasLiveData = batches.some(g => g.syncNotif?.extra);

  if (!hasLiveData) {
    // Post-reload fallback — no extra payload, parse what we can from body text
    const ptsRows = (group.syncers || []).map(s => {
      if (s.earned <= 0) return `<div class="fsd-syncer-row"><span class="fsd-syncer-name">${escapeHtml(s.name)}</span><span class="fsd-pts-result fsd-pts-zero">+0 pts · no new data</span></div>`;
      return `<div class="fsd-syncer-row"><span class="fsd-syncer-name">${escapeHtml(s.name)}</span><span class="fsd-pts-result">+${s.earned.toLocaleString()} pts total</span></div>`;
    }).join('');
    const body = group.syncNotif?.body || '';
    const di = body.indexOf(' — ');
    const statsStr = di >= 0 ? body.slice(di + 3) : body;
    return `<div class="feed-sync-dropdown" hidden>
      ${statsStr ? `<div class="fsd-section"><div class="fsd-section-label">Summary · ${batchCount} sync${batchCount !== 1 ? 's' : ''}</div><div class="fsd-stale-summary">${escapeHtml(statsStr)}</div></div>` : ''}
      ${ptsRows ? `<div class="fsd-section"><div class="fsd-section-label">Points Earned</div>${ptsRows}</div>` : ''}
      <div class="fsd-stale-note">Guild coverage and item breakdown available in active session only.</div>
    </div>`;
  }

  // ── Aggregate totals across all batches ─────────────────────────────────
  let totalFiles = 0, totalItems = 0, totalSales = 0, totalDurationMs = 0;
  for (const g of batches) {
    const d = g.syncNotif?.extra;
    if (!d) continue;
    totalFiles   += d.filesProcessed      || 0;
    totalItems   += d.mm?.itemsInserted   || 0;
    totalSales   += d.mm?.salesInserted   || 0;
    totalDurationMs += d.duration         || 0;
  }
  const metaParts = [
    `${batchCount} sync${batchCount !== 1 ? 's' : ''}`,
    totalFiles  > 0 ? `${totalFiles} file${totalFiles !== 1 ? 's' : ''}` : null,
    totalItems  > 0 ? `+${totalItems.toLocaleString()} items`            : null,
    totalSales  > 0 ? `+${totalSales.toLocaleString()} sales`            : null,
    totalDurationMs > 0 ? `${(totalDurationMs / 1000).toFixed(1)}s total` : null,
  ].filter(Boolean);

  // ── Guild coverage — latest captured timestamp per guild ─────────────────
  const guildKeys   = ['post', 'caravan', 'dealers'];
  const guildLabels = { post: 'Redfur Post', caravan: 'Redfur Caravan', dealers: 'Redfur Dealers' };
  const guildPills  = { post: 'POST', caravan: 'CRVN', dealers: 'DLRS' };
  const latestGuildTs = {};
  for (const g of batches) {
    const gfat = g.syncNotif?.extra?.guildFinishedAt;
    if (!gfat) continue;
    for (const key of guildKeys) {
      if (gfat[key] && (!latestGuildTs[key] || gfat[key] > latestGuildTs[key])) latestGuildTs[key] = gfat[key];
    }
  }
  let guildHtml = '';
  if (Object.keys(latestGuildTs).length > 0) {
    guildHtml = `<div class="fsd-section">
      <div class="fsd-section-label">Guild Coverage</div>
      ${guildKeys.map(key => {
        const ts = latestGuildTs[key];
        const tsStr = ts ? new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null;
        return `<div class="fsd-guild-row">
          <span class="feed-guild-pill feed-guild-${key}">${guildPills[key]}</span>
          <span class="fsd-guild-label">${escapeHtml(guildLabels[key])}</span>
          <span class="fsd-guild-ts">${tsStr ? 'last captured: ' + escapeHtml(tsStr) : 'no data yet'}</span>
        </div>`;
      }).join('')}
    </div>`;
  }

  // ── Per-syncer points — sum formula fields across all batches ────────────
  const syncerFormulas = new Map(); // name → { files, items, sales, ttcUpdated, earned, total, noNewData }
  for (const g of batches) {
    const ps = g.syncNotif?.extra?.pointsSummary;
    if (!ps) continue;
    for (const [name, p] of Object.entries(ps)) {
      if (!syncerFormulas.has(name)) {
        syncerFormulas.set(name, { files: 0, items: 0, sales: 0, ttcUpdated: false, earned: 0, total: 0, noNewData: true });
      }
      const acc = syncerFormulas.get(name);
      acc.files      += p.files  || 0;
      acc.items      += p.items  || 0;
      acc.sales      += p.sales  || 0;
      acc.ttcUpdated  = acc.ttcUpdated || !!p.ttcUpdated;
      acc.earned     += p.earned || 0;
      acc.total       = p.total  || acc.total; // most recent cumulative total
      if (!p.noNewData) acc.noNewData = false;
    }
  }
  let ptsHtml = '';
  if (syncerFormulas.size > 0) {
    const ptRows = Array.from(syncerFormulas.entries()).map(([name, p]) => {
      if (p.noNewData || p.earned <= 0) {
        return `<div class="fsd-syncer-row">
          <span class="fsd-syncer-name">${escapeHtml(name)}</span>
          <span class="fsd-pts-result fsd-pts-zero">+0 pts · no new data · total ${(p.total || 0).toLocaleString()}</span>
        </div>`;
      }
      const pieces = ['<span class="fsd-pts-piece">+5 base</span>'];
      if (p.files)      pieces.push(`<span class="fsd-pts-piece">${p.files} file${p.files !== 1 ? 's' : ''} ×2</span>`);
      if (p.items)      pieces.push(`<span class="fsd-pts-piece">${p.items.toLocaleString()} items ×5</span>`);
      if (p.sales)      pieces.push(`<span class="fsd-pts-piece">${p.sales.toLocaleString()} sales ×1</span>`);
      if (p.ttcUpdated) pieces.push('<span class="fsd-pts-piece fsd-pts-ttc">TTC +50</span>');
      return `<div class="fsd-syncer-row">
        <span class="fsd-syncer-name">${escapeHtml(name)}</span>
        <div class="fsd-pts-formula">${pieces.join('<span class="fsd-sep"> + </span>')}</div>
        <div class="fsd-pts-result">= +${p.earned.toLocaleString()} pts · total <span class="fsd-pts-total-val">${p.total.toLocaleString()}</span></div>
      </div>`;
    }).join('');
    ptsHtml = `<div class="fsd-section"><div class="fsd-section-label">Points Earned</div>${ptRows}</div>`;
  }

  // ── Items registered — combine & dedupe by item name, sort by gold ───────
  const combinedItemMap = {};
  for (const g of batches) {
    const rawMap = g.syncNotif?.extra?.mm?.insertedItemMap || {};
    for (const [id, e] of Object.entries(rawMap)) {
      if (!e.saleCount || !e.itemName) continue;
      if (combinedItemMap[id]) {
        combinedItemMap[id].saleCount += e.saleCount;
        combinedItemMap[id].totalGold += e.totalGold || 0;
        if ((e.maxPrice || 0) > (combinedItemMap[id].maxPrice || 0)) combinedItemMap[id].maxPrice = e.maxPrice;
      } else {
        combinedItemMap[id] = { ...e };
      }
    }
  }
  const items = Object.values(combinedItemMap)
    .filter(e => e.saleCount > 0 && e.itemName)
    .sort((a, b) => b.totalGold - a.totalGold)
    .slice(0, 8);
  let itemsHtml = '';
  if (items.length > 0) {
    itemsHtml = `<div class="fsd-section">
      <div class="fsd-section-label">Items Registered <span class="fsd-section-sub">top ${items.length} by gold</span></div>
      ${items.map(item => `<div class="fsd-item-row">
        ${item.itemIcon
          ? `<img class="fsd-item-icon" src="${escapeHtml(esoIconUrl(item.itemIcon))}" onerror="this.onerror=null;this.style.display='none'" alt="">`
          : '<span class="fsd-item-icon-ph">\ud83d\udce6</span>'}
        <span class="fsd-item-name clickable-item" data-item="${escapeHtml(item.itemName)}">${escapeHtml(item.itemName)}</span>
        <span class="fsd-item-count">\u00d7${item.saleCount}</span>
        <span class="fsd-item-top-price">${gold(item.maxPrice)} top</span>
        <span class="fsd-item-gold">${gold(item.totalGold)}</span>
      </div>`).join('')}
    </div>`;
  }

  return `<div class="feed-sync-dropdown" hidden>
    <div class="fsd-meta">${escapeHtml(metaParts.join(' · '))}</div>
    ${guildHtml}${ptsHtml}${itemsHtml}
  </div>`;
}

// ── Sync inline dropdown: detailed breakdown per sync event ──────────────────
function _renderSyncDropdownHtml(syncNotif, syncers) {
  const data = syncNotif.extra;
  if (!data) {
    // Post-reload fallback — extra payload was stripped on save, parse what we can
    const absTime = new Date(syncNotif.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const dateStr = new Date(syncNotif.time).toLocaleDateString([], { month: 'short', day: 'numeric' });
    const body = syncNotif.body || '';
    const di = body.indexOf(' — ');
    const statsStr = di >= 0 ? body.slice(di + 3) : body;
    const statsHtml = statsStr
      ? `<div class="fsd-section"><div class="fsd-section-label">Summary</div><div class="fsd-stale-summary">${escapeHtml(statsStr)}</div></div>`
      : '';
    let ptsHtml = '';
    if (syncers?.length) {
      const ptRows = syncers.map(s => {
        if (s.earned <= 0) {
          return `<div class="fsd-syncer-row"><span class="fsd-syncer-name">${escapeHtml(s.name)}</span><span class="fsd-pts-result fsd-pts-zero">+0 pts · no new data</span></div>`;
        }
        const total = s.ptsNotif?.extra?.total;
        const totalStr = total != null ? ` · total <span class="fsd-pts-total-val">${total.toLocaleString()}</span>` : '';
        return `<div class="fsd-syncer-row"><span class="fsd-syncer-name">${escapeHtml(s.name)}</span><span class="fsd-pts-result">+${s.earned.toLocaleString()} pts${totalStr}</span></div>`;
      }).join('');
      ptsHtml = `<div class="fsd-section"><div class="fsd-section-label">Points Earned</div>${ptRows}</div>`;
    }
    return `<div class="feed-sync-dropdown" hidden>
      <div class="fsd-meta">${escapeHtml(dateStr + ' · ' + absTime)}</div>
      ${statsHtml}${ptsHtml}
      <div class="fsd-stale-note">Guild coverage and item breakdown available in active session only.</div>
    </div>`;
  }

  const absTime = new Date(syncNotif.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const durationStr = data.duration != null ? (data.duration / 1000).toFixed(1) + 's' : '';
  const metaParts = [
    absTime,
    durationStr ? durationStr + ' elapsed' : null,
    data.filesProcessed != null ? `${data.filesProcessed} files` : null,
    data.mm?.itemsInserted ? `+${data.mm.itemsInserted.toLocaleString()} items` : null,
    data.mm?.salesInserted ? `+${data.mm.salesInserted.toLocaleString()} sales` : null,
    data.ttc?.versionUpdated ? `TTC v${data.ttc.newVersion}` : null,
  ].filter(Boolean);

  // \u2500\u2500 Guild coverage (last captured sale per guild) \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  let guildHtml = '';
  const gfat = data.guildFinishedAt;
  if (gfat) {
    const guilds = [
      { key: 'post',    pill: 'POST', label: 'Redfur Post' },
      { key: 'caravan', pill: 'CRVN', label: 'Redfur Caravan' },
      { key: 'dealers', pill: 'DLRS', label: 'Redfur Dealers' },
    ];
    guildHtml = `<div class="fsd-section">
      <div class="fsd-section-label">Guild Coverage</div>
      ${guilds.map(g => {
        const ts = gfat[g.key];
        const tsStr = ts
          ? new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : null;
        return `<div class="fsd-guild-row">
          <span class="feed-guild-pill feed-guild-${g.key}">${g.pill}</span>
          <span class="fsd-guild-label">${escapeHtml(g.label)}</span>
          <span class="fsd-guild-ts">${tsStr ? 'last captured: ' + escapeHtml(tsStr) : 'no data yet'}</span>
        </div>`;
      }).join('')}
    </div>`;
  }

  // \u2500\u2500 Per-syncer points breakdown \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  let ptsHtml = '';
  const ptsEntries = Object.entries(data.pointsSummary || {});
  if (ptsEntries.length > 0) {
    ptsHtml = `<div class="fsd-section">
      <div class="fsd-section-label">Points Earned</div>
      ${ptsEntries.map(([name, p]) => {
        if (p.noNewData) {
          return `<div class="fsd-syncer-row">
            <span class="fsd-syncer-name">${escapeHtml(name)}</span>
            <span class="fsd-pts-result fsd-pts-zero">+0 pts \u00b7 no new data \u00b7 total ${(p.total || 0).toLocaleString()}</span>
          </div>`;
        }
        const pieces = [];
        if (p.earned > 0) {
          pieces.push('<span class="fsd-pts-piece">+5 base</span>');
          if (p.files)      pieces.push(`<span class="fsd-pts-piece">${p.files} file${p.files > 1 ? 's' : ''} \u00d72</span>`);
          if (p.items)      pieces.push(`<span class="fsd-pts-piece">${(p.items || 0).toLocaleString()} items \u00d75</span>`);
          if (p.sales)      pieces.push(`<span class="fsd-pts-piece">${(p.sales || 0).toLocaleString()} sales \u00d71</span>`);
          if (p.ttcUpdated) pieces.push('<span class="fsd-pts-piece fsd-pts-ttc">TTC +50</span>');
        }
        return `<div class="fsd-syncer-row">
          <span class="fsd-syncer-name">${escapeHtml(name)}</span>
          <div class="fsd-pts-formula">${pieces.join('<span class="fsd-sep"> + </span>')}</div>
          <div class="fsd-pts-result">= +${(p.earned || 0).toLocaleString()} pts \u00b7 total <span class="fsd-pts-total-val">${(p.total || 0).toLocaleString()}</span></div>
        </div>`;
      }).join('')}
    </div>`;
  }

  // \u2500\u2500 Items registered \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
  let itemsHtml = '';
  const rawMap = data.mm?.insertedItemMap || {};
  const items = Object.values(rawMap)
    .filter(e => e.saleCount > 0 && e.itemName)
    .sort((a, b) => b.totalGold - a.totalGold)
    .slice(0, 8);
  if (items.length > 0) {
    itemsHtml = `<div class="fsd-section">
      <div class="fsd-section-label">Items Registered <span class="fsd-section-sub">top ${items.length} by gold</span></div>
      ${items.map(item => `<div class="fsd-item-row">
        ${item.itemIcon
          ? `<img class="fsd-item-icon" src="${escapeHtml(esoIconUrl(item.itemIcon))}" onerror="this.onerror=null;this.style.display='none'" alt="">`
          : '<span class="fsd-item-icon-ph">\ud83d\udce6</span>'}
        <span class="fsd-item-name clickable-item" data-item="${escapeHtml(item.itemName)}">${escapeHtml(item.itemName)}</span>
        <span class="fsd-item-count">\u00d7${item.saleCount}</span>
        <span class="fsd-item-top-price">${gold(item.maxPrice)} top</span>
        <span class="fsd-item-gold">${gold(item.totalGold)}</span>
      </div>`).join('')}
    </div>`;
  }

  return `<div class="feed-sync-dropdown" hidden>
    <div class="fsd-meta">${escapeHtml(metaParts.join(' \u00b7 '))}</div>
    ${guildHtml}${ptsHtml}${itemsHtml}
  </div>`;
}

// ── Feed skeleton placeholder (N pulsing rows) ───────────────────────────
function _feedSkeletonHtml(n = 5) {
  return Array.from({ length: n }, () => '<div class="feed-skeleton-row"></div>').join('');
}

// ── Feed refreshing badge (shown while background fetch runs) ─────────────
function _feedRefreshingBadge() {
  return '<div class="feed-refreshing-badge">Refreshing…</div>';
}

// ── Maps a guildName to a short CSS key ('post' | 'caravan' | 'dealers') ──
function _guildKey(name) {
  if (!name) return '';
  const n = name.toLowerCase();
  if (n.includes('post'))    return 'post';
  if (n.includes('caravan')) return 'caravan';
  if (n.includes('dealers')) return 'dealers';
  return '';
}

// ── Internal helper: render the Personal perspective ─────────────────────
function _renderFeedPersonal() {
  if (!currentUser?.esoTag) {
    return '<div class="live-feed-empty">Login with Discord to see your personal feed.</div>';
  }
  if (_feedPersonalLoading && _feedPersonalSales.length === 0) {
    // First-ever load — show skeleton rows
    return _feedSkeletonHtml(4);
  }

  const PERSONAL_NOTIF = ['sale', 'bigsale', 'points'];
  const _myName = currentUser?.displayName || currentUser?.username;
  let mySales = _feedPersonalSales;
  let myNotifs = _notifications.filter(n => {
    if (!PERSONAL_NOTIF.includes(n.type)) return false;
    // Only show points notifications earned by this specific user
    if (n.type === 'points' && n.extra?.earner) return n.extra.earner === _myName;
    return true;
  });

  // Apply type chip filter
  if (_feedFilter === 'sync') {
    // Personal Sync view: render the user's own syncs + points via the same
    // lane pipeline so the layout + highlighting match All→All.
    return _renderFeedLanes('personal');
  }
  if (_feedFilter === 'sale')    { myNotifs = []; }
  if (_feedFilter === 'bigsale') { mySales = mySales.filter(s => (s.price || 0) >= BIGSALE_THRESHOLD); myNotifs = myNotifs.filter(n => n.type === 'bigsale'); }
  if (_feedFilter === 'points')  { mySales = []; myNotifs = myNotifs.filter(n => n.type === 'points'); }

  let html = '';

  if (mySales.length > 0) {
    html += '<div class="feed-section-header fsh-sales">💰 Your Sales This Week</div>';
    const _totalPS = mySales.length;
    const _visiblePS = mySales.slice(0, _feedVisiblePersonal);
    html += _visiblePS.map(sale => {
      const ago = relativeTime(sale.timestamp);
      const isBig = (sale.price || 0) >= BIGSALE_THRESHOLD;
      const psKey = 'ps_' + (sale.saleID || _saleKey(sale));
      const isNew = _feedNewSaleKeys.has(psKey);
      const gk = _guildKey(sale.guildName);
      return `<div class="feed-sale-row${isBig ? ' feed-sale-big' : ''}${isNew ? ' feed-sale-new' : ''}${gk ? ` feed-guild-${gk}` : ''}">
        ${sale.itemIcon
          ? `<img class="feed-sale-icon" src="${escapeHtml(esoIconUrl(sale.itemIcon))}" onerror="this.onerror=null;this.style.display='none'" alt="">`
          : '<span class="feed-sale-icon-ph">📦</span>'}
        <div class="feed-sale-info">
          <span class="feed-sale-item clickable-item" data-item="${escapeHtml(sale.itemName || 'Unknown')}">${escapeHtml(sale.itemName || 'Unknown')}</span>
        </div>
        ${(sale.quant || 1) > 1 ? `<div class="feed-sale-sub"><span class="feed-sale-qty">×${sale.quant}</span><span class="feed-unit-price">${gold(Math.round((sale.price||0)/(sale.quant||1)))} ea</span></div>` : '<div class="feed-sale-sub"></div>'}
        <div class="feed-sale-meta">
          <span class="feed-sale-price">${gold(sale.price || 0)}</span>
          <span class="feed-sale-time" data-ts="${sale.timestamp}">${ago}</span>
        </div>
      </div>`;
    }).join('');
    if (_totalPS > _feedVisiblePersonal) {
      html += `<div class="feed-load-sentinel" data-more="personal"><span class="feed-load-hint">↓ ${_totalPS - _feedVisiblePersonal} older sales</span></div>`;
    }
  }

  if (myNotifs.length > 0) {
    html += '<div class="feed-section-header fsh-activity">⭐ Activity</div>';
    html += myNotifs.map(n => _renderNotifEntry(n)).join('');
  }

  if (!html) {
    html = '<div class="live-feed-empty">No personal sales this week.</div>';
  }
  // Subtle refresh badge at top when data exists but a background fetch is running
  if (_feedPersonalLoading && _feedPersonalSales.length > 0) {
    html = _feedRefreshingBadge() + html;
  }
  return html;
}

// ── Internal helper: render the All + All 3-lane layout ─────────────────
// `perspective` = 'all-traders' (default, shows all guild activity) or
// 'personal' (same layout, scoped to the logged-in user only).
function _renderFeedLanes(perspective = 'all-traders') {
  const ls = _feedLaneSettings;
  const isPersonal = perspective === 'personal';
  const myName = currentUser?.displayName || currentUser?.username;

  // Personal perspective requires a logged-in user
  if (isPersonal && !currentUser?.esoTag) {
    return '<div class="live-feed-empty">Login with Discord to see your personal feed.</div>';
  }

  // Data sources switch by perspective; shape is identical so the same
  // grouping/rendering code works for both.
  const sourceSales   = isPersonal ? _feedPersonalSales   : _feedAllSales;
  const sourceLoading = isPersonal ? _feedPersonalLoading : _feedAllLoading;
  // Personal sales use a 'ps_' key prefix to avoid collisions with the all-traders stream
  const saleKeyOf = (sale) => isPersonal ? ('ps_' + (sale.saleID || _saleKey(sale))) : _saleKey(sale);

  // ── Sales lane ──────────────────────────────────────────────────────────
  let salesHtml = '', salesSentinel = '', salesCount = 0;
  if (sourceLoading && sourceSales.length === 0) {
    salesHtml = _feedSkeletonHtml(5);
  } else {
    const _FEED_QUALITY_RANK = { worn: 0, white: 1, green: 2, blue: 3, purple: 4, gold: 5 };
    const _FEED_QUALITY_COLOR = { worn: '#9a9a9a', white: '#e8e8e8', green: '#5bffa0', blue: '#7ab8ff', purple: '#d09cff', gold: '#ffcc40' };
    const groupMap = {};
    for (const sale of sourceSales) {
      const key = sale.itemName || 'Unknown';
      if (!groupMap[key]) groupMap[key] = { itemName: key, itemIcon: sale.itemIcon, sales: [], lastTs: 0, totalGold: 0, topQuality: null };
      groupMap[key].sales.push(sale);
      groupMap[key].totalGold += (sale.price || 0);
      if ((sale.timestamp || 0) > groupMap[key].lastTs) groupMap[key].lastTs = sale.timestamp;
      // Track highest quality across all sales in the group
      if (sale.quality && (_FEED_QUALITY_RANK[sale.quality] ?? -1) > (_FEED_QUALITY_RANK[groupMap[key].topQuality] ?? -1)) {
        groupMap[key].topQuality = sale.quality;
      }
    }
    const _allGroups = Object.values(groupMap).sort((a, b) => b.lastTs - a.lastTs);
    salesCount = _allGroups.length;
    const groups = _allGroups.slice(0, _feedVisibleGroups);
    salesHtml = groups.map(g => {
      const rows = g.sales.slice().sort((a, b) => b.timestamp - a.timestamp);
      const count = rows.length;
      const groupIsNew = _feedNewSaleKeys.size > 0 && rows.every(s => _feedNewSaleKeys.has(saleKeyOf(s)));
      const groupKey = g.itemName;
      // rowsCollapsed: groups are hidden unless user explicitly expanded (toggled) them;
      // !rowsCollapsed: groups are shown unless user explicitly collapsed them.
      const defaultCollapsed = ls.sales.rowsCollapsed;
      const isCollapsed = defaultCollapsed ? !_feedCollapsedItems.has(groupKey) : _feedCollapsedItems.has(groupKey);
      const compactCls  = ls.sales.rowsCompact ? ' feed-item-group--compact' : '';
      const qColor = _FEED_QUALITY_COLOR[g.topQuality] || '';
      const nameStyle = qColor ? ` style="color:${qColor}"` : '';
      return `<div class="feed-item-group${compactCls}${groupIsNew ? ' feed-sale-new' : ''}${isCollapsed ? ' feed-item-group-collapsed' : ''}" data-group-key="${escapeHtml(groupKey)}">
        <div class="feed-item-group-hdr">
          <span class="feed-group-toggle">${isCollapsed ? '\u25b8' : '\u25be'}</span>
          ${g.itemIcon
            ? `<img class="feed-sale-icon" src="${escapeHtml(esoIconUrl(g.itemIcon))}" onerror="this.onerror=null;this.style.display='none'" alt="">`
            : '<span class="feed-sale-icon-ph">\ud83d\udce6</span>'}
          <span class="feed-item-group-name clickable-item" data-item="${escapeHtml(g.itemName)}"${nameStyle}>${escapeHtml(g.itemName)}</span>
          <span class="feed-item-group-meta">
            ${count > 1 ? `<span class="feed-item-group-count">\u00d7${count}</span>` : ''}
            <span class="feed-item-group-gold">${gold(g.totalGold)}</span>
            <span class="feed-item-group-time" data-ts="${g.lastTs}">${relativeTime(g.lastTs)}</span>
          </span>
        </div>
        <div class="feed-item-group-rows">
          ${rows.map(sale => {
            const ago = relativeTime(sale.timestamp);
            const color = playerColor(sale.sellerName);
            const rowIsNew = _feedNewSaleKeys.has(saleKeyOf(sale));
            const gk = _guildKey(sale.guildName);
            const guildLabel = gk === 'post' ? 'POST' : gk === 'caravan' ? 'CRVN' : gk === 'dealers' ? 'DLRS' : '';
            return `<div class="feed-item-sale-row${rowIsNew ? ' feed-sale-new' : ''}">
              ${gk ? `<span class="feed-guild-pill feed-guild-${gk}" title="${escapeHtml(sale.guildName || '')}">${guildLabel}</span>` : '<span></span>'}
              <span class="feed-seller-tag" style="color:${color}">${escapeHtml(sale.sellerName)}</span>
              <span class="feed-sale-price">${gold(sale.price || 0)}</span>
              ${(sale.quant || 1) > 1 ? `<span class="feed-sale-qty">\u00d7${sale.quant}</span><span class="feed-unit-price">${gold(Math.round((sale.price||0)/(sale.quant||1)))} ea</span>` : '<span></span><span></span>'}
              <span class="feed-sale-time" data-ts="${sale.timestamp}">${ago}</span>
            </div>`;
          }).join('')}
        </div>
      </div>`;
    }).join('');
    if (_allGroups.length > _feedVisibleGroups) {
      salesSentinel = `<div class="feed-load-sentinel" data-more="sales"><span class="feed-load-hint">\u2193 ${_allGroups.length - _feedVisibleGroups} older items</span></div>`;
    }
  }

  // ── Syncs lane (includes points/system/info activity) ─────────────────
  // For personal perspective, only include syncs the user participated in
  // and points earned by the user. System/info notifications are hidden in
  // personal view so the lane is focused on the user's activity only.
  let _allSyncs, _allActivity;
  if (isPersonal) {
    _allSyncs = _notifications.filter(n =>
      n.type === 'sync' && myName && (
        (Array.isArray(n.extra?.syncers) && n.extra.syncers.includes(myName)) ||
        n.extra?.syncer === myName
      )
    );
    _allActivity = _notifications.filter(n =>
      n.type === 'points' && myName && n.extra?.earner === myName
    );
  } else {
    _allSyncs = _notifications.filter(n => n.type === 'sync');
    _allActivity = _notifications.filter(n => ['points', 'system', 'info'].includes(n.type));
  }
  const _allSyncsAndActivity = [..._allSyncs, ..._allActivity].sort((a, b) => b.time - a.time);

  // Logged-in user's running sync point total (from most recent points notification)
  const _mySyncName = currentUser?.displayName || currentUser?.username;
  const _myLatestPts = _mySyncName
    ? _notifications.find(n => n.type === 'points' && n.extra?.earner === _mySyncName)
    : null;
  const _myTotalPts = _myLatestPts?.extra?.total ?? null;
  const _myPtsBanner = (_myTotalPts !== null && !currentUser?._settings?.privacy?.hidePointsTotal)
    ? `<div class="feed-sync-user-total">\u2b50 Your total: <span class="feed-sync-user-pts">${Number(_myTotalPts).toLocaleString()}</span> pts</div>`
    : '';

  const _syncEvents = _groupSyncEvents(_allSyncsAndActivity);
  const syncsHtml = _syncEvents.slice(0, _feedVisibleSyncs).map(ev =>
    ev.isSyncGroup
      ? _renderSyncGroup(ev, ls.syncs.rowsCompact)
      : (ls.syncs.rowsCompact ? _renderNotifEntryCompact(ev.notif) : _renderNotifEntry(ev.notif))
  ).join('');
  const syncsSentinel = _syncEvents.length > _feedVisibleSyncs
    ? `<div class="feed-load-sentinel" data-more="syncs"><span class="feed-load-hint">\u2193 ${_syncEvents.length - _feedVisibleSyncs} older</span></div>` : '';

  const refreshBadge = (sourceLoading && (sourceSales.length > 0 || _notifications.length > 0)) ? _feedRefreshingBadge() : '';

  // ── Build per-lane content map ───────────────────────────────────────────
  const _laneContent = {
    sales: {
      icon: '\ud83d\udcb0', label: 'Sales', count: salesCount,
      html: salesHtml || '<div class="live-feed-empty">No sales yet \u2014 waiting for sync.</div>',
      sentinel: salesSentinel,
    },
    syncs: {
      icon: '\ud83d\udce1', label: 'Syncs', count: _syncEvents.length,
      html: _myPtsBanner + (syncsHtml || '<div class="live-feed-empty">No recent syncs.</div>'),
      sentinel: syncsSentinel,
    },
  };

  // ── Render lanes in user-defined order (activity lane removed) ───────────
  const laneOrder = ls.laneOrder.filter(k => k !== 'activity');
  const nonCollapsed = laneOrder.filter(k => !ls[k]?.collapsed);
  const lastNonCollapsed = nonCollapsed[nonCollapsed.length - 1];

  const lanesHtml = laneOrder.map(laneKey => {
    const lane = ls[laneKey];
    const c = _laneContent[laneKey];
    if (!lane || !c) return '';
    const isCollapsed = lane.collapsed;
    const hdrCls = `fsh-${laneKey}`;
    const flexStyle = isCollapsed ? '' : ` style="flex: ${lane.flexGrow} 1 0"`;
    const toggle = isCollapsed ? '\u25b8' : '\u25be';
    const countBadge = c.count > 0 ? ` <span class="feed-lane-count">${c.count}</span>` : '';
    // Resize handle sits at the bottom of every non-collapsed lane except the last
    const resizeHandle = !isCollapsed && laneKey !== lastNonCollapsed
      ? `<div class="feed-lane-resize-handle" data-resize-lane="${laneKey}" title="Drag to resize lane"></div>`
      : '';
    return `<div class="feed-lane${isCollapsed ? ' collapsed' : ''}" data-lane="${laneKey}"${flexStyle}>
      <div class="feed-lane-hdr feed-section-header ${hdrCls}">
        <span class="feed-lane-toggle">${toggle}</span>
        ${c.icon} ${c.label}${countBadge}
      </div>
      <div class="feed-lane-body" data-lane="${laneKey}">
        ${c.html}
        ${c.sentinel}
      </div>
      ${resizeHandle}
    </div>`;
  }).join('');

  return `<div class="feed-lanes">${refreshBadge}${lanesHtml}</div>`;
}

// ── Internal helper: render the All-Traders perspective ──────────────────
function _renderFeedAllTraders() {
  if (_feedFilter === 'all') return _renderFeedLanes();
  const showSales  = _feedFilter === 'all' || _feedFilter === 'sale';
  const showBig    = _feedFilter === 'bigsale';
  const showSyncs  = _feedFilter === 'all' || _feedFilter === 'sync';
  const showPoints = _feedFilter === 'all' || _feedFilter === 'points';

  let html = '';

  // ── Sales section: item-grouped ──────────────────────────────────────
  if (showSales || showBig) {
    if (_feedAllLoading && _feedAllSales.length === 0) {
      // First-ever load — show skeleton rows instead of blank text
      html += _feedSkeletonHtml(5);
    } else {
      let salesData = _feedAllSales;
      if (showBig) salesData = salesData.filter(s => (s.price || 0) >= BIGSALE_THRESHOLD);

      if (salesData.length > 0) {
        html += '<div class="feed-section-header fsh-sales">💰 Sales</div>';

        // Group by item name, track most-recent timestamp + total gold per group
        const groupMap = {};
        for (const sale of salesData) {
          const key = sale.itemName || 'Unknown';
          if (!groupMap[key]) groupMap[key] = { itemName: key, itemIcon: sale.itemIcon, sales: [], lastTs: 0, totalGold: 0 };
          groupMap[key].sales.push(sale);
          groupMap[key].totalGold += (sale.price || 0);
          if ((sale.timestamp || 0) > groupMap[key].lastTs) groupMap[key].lastTs = sale.timestamp;
        }

        const _allGroups = Object.values(groupMap).sort((a, b) => b.lastTs - a.lastTs);
        const groups = _allGroups.slice(0, _feedVisibleGroups);
        html += groups.map(g => {
          const rows = g.sales.slice().sort((a, b) => b.timestamp - a.timestamp);
          const count = rows.length;
          // A group is "new" when every sale in it arrived since the last render
          const groupIsNew = _feedNewSaleKeys.size > 0 && rows.every(s => _feedNewSaleKeys.has(_saleKey(s)));
          return `<div class="feed-item-group${groupIsNew ? ' feed-sale-new' : ''}">
            <div class="feed-item-group-hdr">
              ${g.itemIcon
                ? `<img class="feed-sale-icon" src="${escapeHtml(esoIconUrl(g.itemIcon))}" onerror="this.onerror=null;this.style.display='none'" alt="">`
                : '<span class="feed-sale-icon-ph">📦</span>'}
              <span class="feed-item-group-name clickable-item" data-item="${escapeHtml(g.itemName)}">${escapeHtml(g.itemName)}</span>
              <span class="feed-item-group-meta">
                ${count > 1 ? `<span class="feed-item-group-count">×${count}</span>` : ''}
                <span class="feed-item-group-gold">${gold(g.totalGold)}</span>
              </span>
            </div>
            <div class="feed-item-group-rows">
              ${rows.map(sale => {
                const ago = relativeTime(sale.timestamp);
                const color = playerColor(sale.sellerName);
                const rowIsNew = _feedNewSaleKeys.has(_saleKey(sale));
                const gk = _guildKey(sale.guildName);
                const guildLabel = gk === 'post' ? 'POST' : gk === 'caravan' ? 'CRVN' : gk === 'dealers' ? 'DLRS' : '';
                return `<div class="feed-item-sale-row${rowIsNew ? ' feed-sale-new' : ''}">
                  ${gk ? `<span class="feed-guild-pill feed-guild-${gk}" title="${escapeHtml(sale.guildName || '')}">${guildLabel}</span>` : '<span></span>'}
                  <span class="feed-seller-tag" style="color:${color}">${escapeHtml(sale.sellerName)}</span>
                  <span class="feed-sale-price">${gold(sale.price || 0)}</span>
                  ${(sale.quant || 1) > 1 ? `<span class="feed-sale-qty">×${sale.quant}</span><span class="feed-unit-price">${gold(Math.round((sale.price||0)/(sale.quant||1)))} ea</span>` : '<span></span><span></span>'}
                  <span class="feed-sale-time" data-ts="${sale.timestamp}">${ago}</span>
                </div>`;
              }).join('')}
            </div>
          </div>`;
        }).join('');
        if (_allGroups.length > _feedVisibleGroups) {
          html += `<div class="feed-load-sentinel" data-more="groups"><span class="feed-load-hint">↓ ${_allGroups.length - _feedVisibleGroups} older items</span></div>`;
        }
      } else if (_feedFilter !== 'all') {
        html += '<div class="live-feed-empty">No sales this week.</div>';
      }
    }
  }

  // ── Syncs section (includes points/system/info activity) ────────────────
  if (showSyncs || showPoints) {
    const syncs = _notifications.filter(n => n.type === 'sync');
    const activity = _notifications.filter(n => ['points', 'system', 'info'].includes(n.type));
    const combined = [...syncs, ...activity].sort((a, b) => b.time - a.time);
    if (combined.length > 0) {
      html += '<div class="feed-section-header fsh-syncs">📡 Syncs</div>';
      const _mySyncName2 = currentUser?.displayName || currentUser?.username;
      const _myLatestPts2 = _mySyncName2
        ? _notifications.find(n => n.type === 'points' && n.extra?.earner === _mySyncName2)
        : null;
      const _myTotalPts2 = _myLatestPts2?.extra?.total ?? null;
      if (_myTotalPts2 !== null && !currentUser?._settings?.privacy?.hidePointsTotal) {
        html += `<div class="feed-sync-user-total">\u2b50 Your total: <span class="feed-sync-user-pts">${Number(_myTotalPts2).toLocaleString()}</span> pts</div>`;
      }
      const _filteredEvents = _groupSyncEvents(combined);
      // Sprint 10.4: use compact rows (same as lane-mode All→All) so Sync→All
      // entries line up consistently and don't blow out the single-column layout.
      html += _filteredEvents.map(ev =>
        ev.isSyncGroup ? _renderSyncGroup(ev, true) : _renderNotifEntryCompact(ev.notif)
      ).join('');
    }
  }

  // Subtle refresh badge at top when data exists but a background fetch is running
  if (_feedAllLoading && (_feedAllSales.length > 0 || _notifications.length > 0)) {
    html = _feedRefreshingBadge() + html;
  }
  return html || '<div class="live-feed-empty">No activity yet — waiting for the next sync.</div>';
}

// ── Lane drag: resize (bottom handle) + reorder (header drag) ────────────
// Attached once in initLiveFeed; uses event delegation on the persistent
// #live-feed-body element so re-renders don't invalidate the listeners.
function _initFeedLaneDrag(bodyEl) {
  if (!bodyEl) return;
  let _ds = null; // drag state

  bodyEl.addEventListener('pointerdown', (e) => {
    // ── Resize: pointerdown on .feed-lane-resize-handle ──────────────────
    const resizeHandle = e.target.closest('.feed-lane-resize-handle');
    if (resizeHandle) {
      e.preventDefault();
      const laneEl = resizeHandle.closest('.feed-lane');
      const lanesEl = laneEl?.parentElement;
      if (!laneEl || !lanesEl) return;
      const laneKey = laneEl.dataset.lane;
      // Find the next non-collapsed sibling lane (by stored order)
      const ls = _feedLaneSettings;
      const order = ls.laneOrder;
      const idx = order.indexOf(laneKey);
      let nextKey = null, nextEl = null;
      for (let i = idx + 1; i < order.length; i++) {
        if (!ls[order[i]]?.collapsed) {
          nextKey = order[i];
          nextEl = lanesEl.querySelector(`.feed-lane[data-lane="${nextKey}"]`);
          break;
        }
      }
      if (!nextKey || !nextEl) return;
      _ds = {
        type: 'resize',
        laneKey, nextKey, laneEl, nextEl, lanesEl,
        startY: e.clientY,
        startFlexA: ls[laneKey].flexGrow,
        startFlexB: ls[nextKey].flexGrow,
        startHeightA: laneEl.getBoundingClientRect().height,
        startHeightB: nextEl.getBoundingClientRect().height,
        pendingFlexA: null, pendingFlexB: null,
      };
      bodyEl.setPointerCapture(e.pointerId);
      lanesEl.classList.add('feed-lanes--resizing');
      return;
    }

    // ── Reorder: pointerdown on .feed-lane-hdr ───────────────────────────
    const laneHdr = e.target.closest('.feed-lane-hdr');
    if (laneHdr) {
      const laneEl = laneHdr.closest('.feed-lane');
      const laneKey = laneEl?.dataset.lane;
      if (!laneKey) return;
      _ds = {
        type: 'reorder-pending',
        laneKey, laneEl,
        startX: e.clientX, startY: e.clientY,
        pointerId: e.pointerId,
        ghost: null, dropTarget: null,
      };
      // Don't capture yet — let click handler fire if no movement threshold crossed
    }
  });

  bodyEl.addEventListener('pointermove', (e) => {
    if (!_ds) return;

    if (_ds.type === 'resize') {
      const dy = e.clientY - _ds.startY;
      const totalH = _ds.startHeightA + _ds.startHeightB;
      const minH = 36;
      const newHA = Math.max(minH, Math.min(totalH - minH, _ds.startHeightA + dy));
      const newHB = totalH - newHA;
      const totalFlex = _ds.startFlexA + _ds.startFlexB;
      const newFlexA = Math.max(0.1, Math.round((newHA / totalH) * totalFlex * 100) / 100);
      const newFlexB = Math.max(0.1, Math.round((newHB / totalH) * totalFlex * 100) / 100);
      _ds.laneEl.style.flex = `${newFlexA} 1 0`;
      _ds.nextEl.style.flex = `${newFlexB} 1 0`;
      _ds.pendingFlexA = newFlexA;
      _ds.pendingFlexB = newFlexB;
      return;
    }

    if (_ds.type === 'reorder-pending') {
      const dy = Math.abs(e.clientY - _ds.startY);
      const dx = Math.abs(e.clientX - _ds.startX);
      if (dy > 6 || dx > 6) {
        // Upgrade to active reorder drag
        _ds.type = 'reorder';
        bodyEl.setPointerCapture(_ds.pointerId);
        _ds.laneEl.classList.add('feed-lane--dragging');
        const ghost = document.createElement('div');
        ghost.className = 'feed-lane-drag-ghost';
        const hdrText = _ds.laneEl.querySelector('.feed-lane-hdr')?.textContent?.trim() || _ds.laneKey;
        // Strip count badge digits for cleaner ghost label
        ghost.textContent = '\u2261 ' + hdrText.replace(/\s*\d+\s*$/, '').trim();
        ghost.style.top  = (e.clientY - 14) + 'px';
        ghost.style.left = (e.clientX + 12) + 'px';
        document.body.appendChild(ghost);
        _ds.ghost = ghost;
      }
    }

    if (_ds.type === 'reorder') {
      const ghost = _ds.ghost;
      if (ghost) {
        ghost.style.top  = (e.clientY - 14) + 'px';
        ghost.style.left = (e.clientX + 12) + 'px';
      }
      const lanesEl = bodyEl.querySelector('.feed-lanes');
      if (!lanesEl) return;
      // Clear previous drop indicators
      lanesEl.querySelectorAll('.feed-lane--drop-before, .feed-lane--drop-after').forEach(el => {
        el.classList.remove('feed-lane--drop-before', 'feed-lane--drop-after');
      });
      const otherLanes = Array.from(
        lanesEl.querySelectorAll('.feed-lane:not(.feed-lane--dragging)')
      );
      let dropTarget = null;
      for (const el of otherLanes) {
        const rect = el.getBoundingClientRect();
        if (e.clientY >= rect.top - 10 && e.clientY < rect.bottom + 10) {
          const mid = rect.top + rect.height / 2;
          const pos = e.clientY < mid ? 'before' : 'after';
          el.classList.add(pos === 'before' ? 'feed-lane--drop-before' : 'feed-lane--drop-after');
          dropTarget = { laneEl: el, laneKey: el.dataset.lane, pos };
          break;
        }
      }
      _ds.dropTarget = dropTarget;
    }
  });

  bodyEl.addEventListener('pointerup', (e) => {
    if (!_ds) return;
    const state = _ds;
    _ds = null;

    if (state.type === 'resize') {
      state.lanesEl?.classList.remove('feed-lanes--resizing');
      if (state.pendingFlexA != null) {
        _feedLaneSettings[state.laneKey].flexGrow = state.pendingFlexA;
        _feedLaneSettings[state.nextKey].flexGrow = state.pendingFlexB;
        _saveFeedLaneSettings();
      }
      return;
    }

    if (state.type === 'reorder-pending') {
      // No meaningful drag — click will fire naturally for collapse toggle
      return;
    }

    if (state.type === 'reorder') {
      // Suppress the click that would otherwise fire on pointerup (collapse toggle)
      const suppressClick = (ev) => {
        ev.stopImmediatePropagation();
        ev.preventDefault();
        bodyEl.removeEventListener('click', suppressClick, true);
      };
      bodyEl.addEventListener('click', suppressClick, true);

      if (state.ghost) state.ghost.remove();
      state.laneEl.classList.remove('feed-lane--dragging');
      const lanesEl = bodyEl.querySelector('.feed-lanes');
      if (lanesEl) {
        lanesEl.querySelectorAll('.feed-lane--drop-before, .feed-lane--drop-after').forEach(el => {
          el.classList.remove('feed-lane--drop-before', 'feed-lane--drop-after');
        });
      }
      if (state.dropTarget) {
        const order = _feedLaneSettings.laneOrder;
        const fromIdx = order.indexOf(state.laneKey);
        const toKey = state.dropTarget.laneKey;
        if (fromIdx !== -1 && toKey && toKey !== state.laneKey) {
          order.splice(fromIdx, 1);
          const toIdx = order.indexOf(toKey);
          if (state.dropTarget.pos === 'before') order.splice(toIdx, 0, state.laneKey);
          else order.splice(toIdx + 1, 0, state.laneKey);
          _saveFeedLaneSettings();
          renderLiveFeed();
        }
      }
    }
  });

  bodyEl.addEventListener('pointercancel', () => {
    if (!_ds) return;
    const state = _ds;
    _ds = null;
    if (state.ghost) state.ghost.remove();
    if (state.laneEl) state.laneEl.classList.remove('feed-lane--dragging');
    if (state.laneEl?.style?.flex) state.laneEl.style.flex = '';
    if (state.nextEl?.style?.flex) state.nextEl.style.flex = '';
    if (state.lanesEl) state.lanesEl.classList.remove('feed-lanes--resizing');
  });
}

function renderLiveFeed() {
  const body = document.getElementById('live-feed-body');
  if (!body) return;
  // Lane mode (Sales + Syncs lanes) is now used for BOTH perspectives when
  // the user picks the "All" filter chip. Personal uses the same layout but
  // filtered to the viewer's own sales + sync participation.
  const _isLaneMode = _feedFilter === 'all';
  const savedScroll = body.scrollTop;
  const savedLaneScrolls = {};
  if (_isLaneMode) {
    body.querySelectorAll('.feed-lane-body').forEach(lb => {
      savedLaneScrolls[lb.dataset.lane] = lb.scrollTop;
    });
  }

  // ── Non-disruptive update guard ──────────────────────────────────────────
  // If the user has a sync dropdown open skip the full innerHTML replacement
  // so the interaction is not interrupted. Mark new keys as seen so the next
  // full render (after they close the dropdown) is clean.
  const hasOpenDropdown = !!body.querySelector('.feed-sync-group.fsd-open');
  if (hasOpenDropdown && _feedHasRenderedOnce) {
    for (const s of _feedAllSales)      _feedSeenSaleKeys.add(_saleKey(s));
    for (const s of _feedPersonalSales) _feedSeenSaleKeys.add('ps_' + (s.saleID || _saleKey(s)));
    _feedNewSaleKeys = new Set();
    return;
  }

  body.innerHTML = _feedFilter === 'all'
    ? _renderFeedLanes(_feedPerspective)
    : (_feedPerspective === 'personal' ? _renderFeedPersonal() : _renderFeedAllTraders());
  // Restore scroll
  if (_isLaneMode) {
    body.querySelectorAll('.feed-lane-body').forEach(lb => {
      const s = savedLaneScrolls[lb.dataset.lane];
      if (s) lb.scrollTop = s;
    });
  } else {
    body.scrollTop = savedScroll;
  }
  // Update seen-key registry and mark first render complete
  _feedHasRenderedOnce = true;
  for (const s of _feedAllSales)      _feedSeenSaleKeys.add(_saleKey(s));
  for (const s of _feedPersonalSales) _feedSeenSaleKeys.add('ps_' + (s.saleID || _saleKey(s)));
  _feedNewSaleKeys = new Set(); // consumed — clear for next cycle
  // Set up IntersectionObserver(s) on load-more sentinels
  if (_feedScrollObserver) { _feedScrollObserver.disconnect(); _feedScrollObserver = null; }
  _feedObservers.forEach(obs => obs.disconnect()); _feedObservers.clear();
  if (_isLaneMode) {
    body.querySelectorAll('.feed-lane-body').forEach(laneBody => {
      const sentinel = laneBody.querySelector('.feed-load-sentinel');
      if (!sentinel) return;
      const laneKey = laneBody.dataset.lane;
      const obs = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect(); _feedObservers.delete(laneKey);
        if (laneKey === 'sales')    _feedVisibleGroups   += FEED_PAGE_SIZE;
        if (laneKey === 'syncs')    _feedVisibleSyncs    += FEED_PAGE_SIZE;
        if (laneKey === 'activity') _feedVisibleActivity += FEED_PAGE_SIZE;
        renderLiveFeed();
      }, { root: laneBody, threshold: 0.1 });
      obs.observe(sentinel);
      _feedObservers.set(laneKey, obs);
    });
  } else {
    const _sentinel = body.querySelector('.feed-load-sentinel');
    if (_sentinel) {
      _feedScrollObserver = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        _feedScrollObserver.disconnect(); _feedScrollObserver = null;
        if (_sentinel.dataset.more === 'groups')   _feedVisibleGroups   += FEED_PAGE_SIZE;
        if (_sentinel.dataset.more === 'personal') _feedVisiblePersonal += FEED_PAGE_SIZE;
        renderLiveFeed();
      }, { root: body, threshold: 0.1 });
      _feedScrollObserver.observe(_sentinel);
    }
  }
}

// Fetch/refresh live feed data from the server, then re-render if open.
// Both streams (all-traders + personal) are fetched in parallel. Each has
// its own in-flight flag so the UI can show a skeleton vs. a refresh badge
// independently, and perspective switches never block on the mutex.
async function loadFeedData(force = false) {
  const allStale  = force || _feedAllSales.length === 0 || _feedDataTs === 0 || (Date.now() - _feedDataTs >= FEED_DATA_TTL);
  const perStale  = force || _feedPersonalSales.length === 0 || _feedDataTs === 0 || (Date.now() - _feedDataTs >= FEED_DATA_TTL);
  if (!allStale && !perStale) return;

  // Mark per-stream loading flags and re-render immediately so skeletons / badges appear
  if (allStale  && !_feedAllLoading)      { _feedAllLoading      = true; if (isLiveFeedOpen()) renderLiveFeed(); }
  if (perStale  && !_feedPersonalLoading) { _feedPersonalLoading = true; if (isLiveFeedOpen()) renderLiveFeed(); }

  try {
    const [allResp, myResp] = await Promise.all([
      allStale ? api('/api/feed/recent').catch(() => null) : Promise.resolve(null),
      (perStale && currentUser?.esoTag) ? api('/api/me/sales?weeksAgo=0').catch(() => null) : Promise.resolve(null),
    ]);

    if (allResp?.sales) _feedAllSales = allResp.sales;

    if (myResp?.sellers) {
      _feedPersonalSales = [];
      for (const seller of myResp.sellers) {
        for (const sale of (seller.sales || [])) {
          // Ensure sellerName is attached so the lane-view expanded row renders it
          _feedPersonalSales.push(sale.sellerName ? sale : { ...sale, sellerName: seller.sellerName });
        }
      }
      _feedPersonalSales.sort((a, b) => b.timestamp - a.timestamp);
    }

    // Auto-default to personal if this is the first visit and user has recent sales
    if (!localStorage.getItem(FEED_PERSPECTIVE_KEY) && _feedPersonalSales.length > 0) {
      const cutoff = Date.now() / 1000 - 86_400; // 24h
      if (_feedPersonalSales.some(s => s.timestamp > cutoff)) {
        _feedPerspective = 'personal';
        const row = document.getElementById('live-feed-perspective');
        if (row) row.querySelectorAll('[data-perspective]').forEach(b =>
          b.classList.toggle('active', b.dataset.perspective === 'personal')
        );
      }
    }

    _feedDataTs = Date.now();

    // Compute which sales arrived since the last render.
    // Skipped on the very first load so the initial paint doesn't bulk-animate everything.
    if (_feedHasRenderedOnce) {
      const nk = new Set();
      for (const s of _feedAllSales) {
        const k = _saleKey(s);
        if (!_feedSeenSaleKeys.has(k)) nk.add(k);
      }
      for (const s of _feedPersonalSales) {
        const k = 'ps_' + (s.saleID || _saleKey(s));
        if (!_feedSeenSaleKeys.has(k)) nk.add(k);
      }
      _feedNewSaleKeys = nk;
    }
  } catch (err) {
    console.warn('[feed] loadFeedData error:', err);
  } finally {
    _feedAllLoading      = false;
    _feedPersonalLoading = false;
    _feedDataLoading     = false; // keep legacy flag in sync
  }
  if (isLiveFeedOpen()) renderLiveFeed();
}

function initLiveFeed() {
  const panel = document.getElementById('live-feed-panel');
  if (!panel) return;

  updateLayoutChromeOffsets();

  _feedFilter = localStorage.getItem(FEED_FILTER_KEY) || 'sale';
  _feedPerspective = localStorage.getItem(FEED_PERSPECTIVE_KEY) || 'all-traders';

  // Apply saved mode on init (but keep closed)
  applyFeedMode();

  // Sprint 12: Auto-open on desktop only. Mobile viewports start CLOSED so
  // the live feed doesn't cover the entire interface unexpectedly. Users tap
  // the radar-dish button in the header to open the fullscreen drawer.
  const isMobileViewport = window.matchMedia('(max-width: 768px)').matches;
  const storedOpen = localStorage.getItem(FEED_OPEN_KEY);
  const shouldAutoOpen = !isMobileViewport && storedOpen !== '0';
  if (shouldAutoOpen) {
    setTimeout(() => { openLiveFeed(); loadFeedData(true); }, 150);
  } else {
    // Still warm the feed cache so the first tap is instant.
    setTimeout(() => { loadFeedData(true); }, 600);
  }

  // Tick every 30s: update all visible timestamp spans in-place without re-rendering.
  // Transitions: 'Just Now' (0–59s) → '1m ago' → '2m ago' → … → 'Xh ago' etc.
  setInterval(() => {
    const panel = document.getElementById('live-feed-panel');
    if (!panel) return;
    panel.querySelectorAll('[data-ts]').forEach(el => {
      const ts = +el.dataset.ts;
      if (ts) el.textContent = relativeTime(ts);
    });
  }, 30_000);

  // Auto-refresh feed data in the background so perspective switches are instant.
  setInterval(() => { if (isLiveFeedOpen()) loadFeedData(); }, FEED_DATA_TTL);

  // Header buttons
  const clearBtn = document.getElementById('live-feed-clear-btn');
  const logBtn = document.getElementById('live-feed-log-btn');
  const dockBtn = document.getElementById('live-feed-dock-btn');
  const popBtn = document.getElementById('live-feed-pop-btn');

  if (clearBtn) clearBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    clearAllNotifications();
    renderLiveFeed();
  });
  if (logBtn) logBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    closeLiveFeed();
    navigate('#/log');
  });
  // Sprint 10.2: Dock/pop buttons hidden; feed always docked
  // if (dockBtn) dockBtn.addEventListener('click', () => {
  //   const next = getFeedMode() === 'dock' ? 'slide' : 'dock';
  //   setFeedMode(next);
  // });
  // if (popBtn) popBtn.addEventListener('click', () => {
  //   const next = getFeedMode() === 'float' ? 'slide' : 'float';
  //   setFeedMode(next);
  // });

  // Filter chips (type row)
  const filterRow = document.getElementById('live-feed-filters');
  if (filterRow) {
    // Restore active filter
    filterRow.querySelectorAll('[data-feed-filter]').forEach(b => {
      b.classList.toggle('active', b.dataset.feedFilter === _feedFilter);
    });
    filterRow.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-feed-filter]');
      if (!btn) return;
      e.stopPropagation();
      _feedFilter = btn.dataset.feedFilter;
      _feedVisibleGroups = 20; _feedVisiblePersonal = 25; _feedVisibleSyncs = 15; _feedVisibleActivity = 15;
      localStorage.setItem(FEED_FILTER_KEY, _feedFilter);
      filterRow.querySelectorAll('[data-feed-filter]').forEach(b => {
        b.classList.toggle('active', b === btn);
      });
      renderLiveFeed();
    });
  }

  // Perspective chips (personal vs all-traders row)
  const perspectiveRow = document.getElementById('live-feed-perspective');
  if (perspectiveRow) {
    // Restore active perspective
    perspectiveRow.querySelectorAll('[data-perspective]').forEach(b => {
      b.classList.toggle('active', b.dataset.perspective === _feedPerspective);
    });
    perspectiveRow.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-perspective]');
      if (!btn) return;
      e.stopPropagation();
      _feedPerspective = btn.dataset.perspective;
      _feedVisibleGroups = 20; _feedVisiblePersonal = 25; _feedVisibleSyncs = 15; _feedVisibleActivity = 15;
      localStorage.setItem(FEED_PERSPECTIVE_KEY, _feedPerspective);
      perspectiveRow.querySelectorAll('[data-perspective]').forEach(b => {
        b.classList.toggle('active', b === btn);
      });
      renderLiveFeed();   // switch view immediately — data stays fresh via background interval
    });
  }

  // Body: click entry → toggle inline dropdown (sync) or navigate (others)
  const bodyEl = document.getElementById('live-feed-body');
  if (bodyEl) {
    bodyEl.addEventListener('click', (e) => {
      // Clicks inside an open sync dropdown pass through to item/guild handlers only
      if (e.target.closest('.feed-sync-dropdown')) return;
      // Sync rows (compact or full) → toggle inline breakdown dropdown
      const compactSync = e.target.closest('.feed-sync-compact');
      const fullSync    = e.target.closest('.live-feed-entry[data-type="sync"]');
      const syncRow = compactSync || fullSync;
      if (syncRow) {
        const group = syncRow.closest('.feed-sync-group');
        if (group) {
          // Find the dropdown for the specific syncer that was clicked.
          // Each syncer row carries data-syncer="Name"; each dropdown is
          // keyed the same way so clicking SlumberCat's row opens only
          // SlumberCat's dropdown, not WenisSwindlehurst's.
          const syncerName = syncRow.dataset.syncer;
          const allDropdowns = Array.from(group.querySelectorAll('.feed-sync-dropdown'));
          const dropdown = syncerName
            ? (allDropdowns.find(d => d.dataset.syncer === syncerName) || allDropdowns[0])
            : allDropdowns[0];
          if (dropdown) {
            // Close sibling dropdowns (only one open at a time in a group)
            allDropdowns.forEach(d => { if (d !== dropdown) d.hidden = true; });
            const willOpen = dropdown.hidden;
            dropdown.hidden = !willOpen;
            // fsd-open reflects whether ANY dropdown in this group is now visible
            group.classList.toggle('fsd-open', allDropdowns.some(d => !d.hidden));
          }
          return;
        }
        navigate('#/sync');
        return;
      }
      const entry = e.target.closest('.live-feed-entry');
      if (!entry) return;
      const type = entry.dataset.type;
      if (type === 'sync') return; // handled above
      const meta = feedTypeMeta(type);
      closeLiveFeed();
      navigate(meta.route);
    });
  }

  // Lane header and item-group collapse toggles (delegated from body)
  if (bodyEl) {
    bodyEl.addEventListener('click', (e) => {
      const laneHdr = e.target.closest('.feed-lane-hdr');
      if (laneHdr) {
        e.stopPropagation();
        const lane = laneHdr.closest('[data-lane]');
        if (lane && Object.prototype.hasOwnProperty.call(_feedLaneSettings, lane.dataset.lane)) {
          _feedLaneSettings[lane.dataset.lane].collapsed = !_feedLaneSettings[lane.dataset.lane].collapsed;
          _saveFeedLaneSettings();
          renderLiveFeed();
          return;
        }
      }
      const groupHdr = e.target.closest('.feed-item-group-hdr');
      if (groupHdr && !e.target.closest('.feed-item-group-name')) {
        e.stopPropagation();
        const group = groupHdr.closest('[data-group-key]');
        if (group) {
          const key = group.dataset.groupKey;
          if (_feedCollapsedItems.has(key)) _feedCollapsedItems.delete(key);
          else _feedCollapsedItems.add(key);
          renderLiveFeed();
          return;
        }
      }
    });
  }

  // Backdrop click closes
  const backdrop = document.getElementById('live-feed-backdrop');
  if (backdrop) backdrop.addEventListener('click', closeLiveFeed);

  // Escape key closes
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel.classList.contains('open')) closeLiveFeed();
  });

  // Outside click closes slide/float feed so header controls remain usable.
  document.addEventListener('click', (e) => {
    if (!panel.classList.contains('open')) return;
    if (getFeedMode() === 'dock') return;
    const bell = document.getElementById('notif-bell-btn');
    if (panel.contains(e.target)) return;
    if (bell && bell.contains(e.target)) return;
    closeLiveFeed();
  });

  // Responsive: central viewport-change handler re-applies feed mode + any
  // breakpoint-sensitive layout + resizes live Chart.js instances. The
  // matchMedia listeners fire immediately on breakpoint crossings; the
  // debounced resize listener is a safety net for continuous drags.
  // Lane drag: resize handles + header reorder
  _initFeedLaneDrag(bodyEl);

  if (typeof onViewportChange === 'function') {
    const mqWide = window.matchMedia('(min-width: 1280px)');
    const mqNarrow = window.matchMedia('(max-width: 768px)');
    try {
      mqWide.addEventListener('change', onViewportChange);
      mqNarrow.addEventListener('change', onViewportChange);
    } catch {
      // Safari < 14 fallback
      mqWide.addListener?.(onViewportChange);
      mqNarrow.addListener?.(onViewportChange);
    }
    let _resizeDebounce = null;
    let _resizeFollowUp = null;
    window.addEventListener('resize', () => {
      clearTimeout(_resizeDebounce);
      clearTimeout(_resizeFollowUp);
      _resizeDebounce = setTimeout(() => {
        onViewportChange();
        // Second pass at 350ms to catch late-settling layout after abrupt viewport
        // jumps (e.g. DevTools device toolbar toggled off). The first pass runs
        // mid-transition; this one runs after the browser has fully committed.
        _resizeFollowUp = setTimeout(onViewportChange, 350);
      }, 50);
    });
    // Apply once on init so mobile classes/state are correct before first
    // user interaction (no need to wait for a resize/breakpoint event).
    onViewportChange();
  } else {
    // Fallback if helper not yet defined at script parse time
    window.addEventListener('resize', () => {
      if (panel.classList.contains('open')) applyFeedMode();
    });
  }
}

// ══════════════════════════════════════════════════════════════════════════
// Sprint 10.0 — central viewport-change handler
// Single source of truth for responsive re-layout. Invoked on breakpoint
// crossings (matchMedia) and via a debounced window.resize listener.
// Keeps feed mode, sidebar backdrop, and live Chart.js instances in sync
// with the active viewport.
// ══════════════════════════════════════════════════════════════════════════
function onViewportChange() {
  try {
    // Runtime safety rail: some mobile browsers can drift across breakpoints
    // due to URL bar/chrome resizing. Keep a deterministic mobile class.
    const viewportW = Math.max(0, document.documentElement?.clientWidth || window.innerWidth || 0);
    const isNarrowViewport = viewportW <= 900;
    document.body.classList.toggle('viewport-narrow', isNarrowViewport);

    updateLayoutChromeOffsets();
    if (typeof applyFeedMode === 'function') {
      // If body.feed-docked is already set, a breakpoint crossing (e.g. DevTools
      // mobile simulation toggled off) changes the computed margin-right via the
      // media query override but never fires a CSS transition because no class
      // changes occurred. Force a genuine remove→reflow→re-add cycle so the
      // browser re-evaluates margin-right from the current computed value.
      if (document.body.classList.contains('feed-docked')) {
        document.body.classList.remove('feed-docked');
        void document.body.offsetHeight; // flush style recalculation
      }
      if (isNarrowViewport) {
        document.body.classList.remove('feed-docked');
      }
      applyFeedMode();
    }
  } catch (err) { console.warn('[viewport] applyFeedMode failed:', err); }

  // Re-layout any live Chart.js instances so they pick up the new
  // container width. Chart.js v3/v4 exposes a registry under Chart.instances
  // (object keyed by numeric id).
  try {
    if (typeof Chart !== 'undefined' && Chart.instances) {
      const instances = Object.values(Chart.instances);
      for (const c of instances) {
        if (c && typeof c.resize === 'function') {
          try { c.resize(); } catch { /* swallow per-chart resize errors */ }
        }
      }
    }
  } catch (err) { console.warn('[viewport] chart resize failed:', err); }
}
if (typeof window !== 'undefined') window.onViewportChange = onViewportChange;

let _uiActionDelegationBound = false;
function initUiActionDelegation() {
  if (_uiActionDelegationBound) return;
  _uiActionDelegationBound = true;

  // ── Sprint 12 Phase 3: generic sortable headers for any .data-table ────
  // Click a <th> inside a `.data-table` to sort its <tbody> rows by that
  // column. Toggles asc/desc on repeat click; numeric detection is
  // automatic. Tables can opt-out by adding `data-no-sort` to the table
  // element. Cell sort values may be overridden via `data-sort-val`.
  document.addEventListener('click', (e) => {
    const th = e.target.closest('table.data-table thead th');
    if (!th) return;
    const table = th.closest('table.data-table');
    if (!table || table.hasAttribute('data-no-sort')) return;
    // Skip if author opted this column out (e.g. action-button column)
    if (th.hasAttribute('data-sort-skip')) return;
    const tbody = table.querySelector('tbody');
    if (!tbody) return;
    const rows = Array.from(tbody.rows).filter(r => !r.classList.contains('no-sort'));
    if (rows.length < 2) return;

    const headerRow = th.parentElement;
    const colIdx = Array.from(headerRow.children).indexOf(th);
    if (colIdx < 0) return;

    // Toggle direction. Track per-table active column on the table dataset.
    const prevCol = parseInt(table.dataset.sortCol || '-1', 10);
    const prevDir = table.dataset.sortDir === 'desc' ? 'desc' : 'asc';
    const dir = (prevCol === colIdx && prevDir === 'asc') ? 'desc' : 'asc';

    // Read raw cell value (data-sort-val override > textContent).
    const cellVal = (row) => {
      const cell = row.children[colIdx];
      if (!cell) return '';
      return cell.dataset.sortVal != null ? cell.dataset.sortVal : (cell.textContent || '').trim();
    };

    // Detect numeric: every non-empty value parses as a finite number after
    // stripping commas, currency, and trailing units.
    const numRe = /^-?[\d,]+(?:\.\d+)?[%×x]?$/;
    const allNumeric = rows.every(r => {
      const v = cellVal(r).replace(/[,\s]/g, '').replace(/[×x%]$/, '');
      return v === '' || numRe.test(cellVal(r).replace(/[,\s]/g, ''));
    });
    const cmpStr = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

    rows.sort((a, b) => {
      const va = cellVal(a);
      const vb = cellVal(b);
      let cmp;
      if (allNumeric) {
        const na = parseFloat(va.replace(/[,\s×x%]/g, ''));
        const nb = parseFloat(vb.replace(/[,\s×x%]/g, ''));
        cmp = (isNaN(na) ? 0 : na) - (isNaN(nb) ? 0 : nb);
      } else {
        cmp = cmpStr(va, vb);
      }
      return dir === 'asc' ? cmp : -cmp;
    });

    // Re-attach in new order — DocumentFragment for one reflow.
    const frag = document.createDocumentFragment();
    rows.forEach(r => frag.appendChild(r));
    tbody.appendChild(frag);

    // Update header indicators.
    headerRow.querySelectorAll('th').forEach(h => {
      h.classList.remove('sort-asc', 'sort-desc', 'sort-active');
      const arr = h.querySelector('.sort-arrow-auto');
      if (arr) arr.textContent = '';
    });
    th.classList.add('sort-active', dir === 'asc' ? 'sort-asc' : 'sort-desc');
    let arrowEl = th.querySelector('.sort-arrow-auto');
    if (!arrowEl) {
      arrowEl = document.createElement('span');
      arrowEl.className = 'sort-arrow-auto';
      th.appendChild(arrowEl);
    }
    arrowEl.textContent = dir === 'asc' ? ' ▲' : ' ▼';
    table.dataset.sortCol = String(colIdx);
    table.dataset.sortDir = dir;
  });

  document.addEventListener('click', (e) => {
    // Admin spy buttons live inside [data-user] table cells — must be checked
    // first so clicking a spy button does not also open the user profile.
    const spyEl = e.target.closest('[data-ui-action^="spy-"]');
    if (spyEl) {
      e.preventDefault();
      e.stopPropagation();
      const tag = decodeURIComponent(spyEl.dataset.tag || '');
      if (tag) {
        if (spyEl.dataset.uiAction === 'spy-sales') {
          navigate(`#/admin/user/${encodeURIComponent(tag)}`);
          if (typeof renderAdminUserDeep === 'function') renderAdminUserDeep();
        } else if (spyEl.dataset.uiAction === 'spy-raffle') {
          navigate(`#/admin/raffle?tag=${encodeURIComponent(tag)}`);
          if (typeof renderAdminRaffle === 'function') renderAdminRaffle();
        }
      }
      return;
    }

    // Sprint 10.2: Handle [data-user] clicks (CSP-safe alternative to onclick="showUserProfile(...)")
    const userEl = e.target.closest('[data-user]');
    if (userEl) {
      e.preventDefault();
      const userTag = userEl.dataset.user || '';
      if (userTag) showUserProfile(userTag);
      return;
    }

    // Sprint 10.2: Handle [data-guild] clicks (CSP-safe alternative to onclick="showGuildInfo(...)")
    const guildEl = e.target.closest('[data-guild]');
    if (guildEl) {
      e.preventDefault();
      const guildName = guildEl.dataset.guild || '';
      if (guildName) showGuildInfo(guildName);
      return;
    }

    // CSP-safe item click handling (inline onclick is blocked by script-src 'self').
    const itemEl = e.target.closest('.clickable-item[data-item], .pc-item[data-item], .sync-top-item[data-item]');
    if (itemEl) {
      e.preventDefault();
      const itemName = itemEl.dataset.item || '';
      if (itemName) showItemDetail(itemName);
      return;
    }

    const el = e.target.closest('[data-ui-action]');
    if (!el) return;
    const action = el.dataset.uiAction;

    if (action === 'close-modal') {
      e.preventDefault();
      closeProfileModal();
      return;
    }
    if (action === 'open-admin-drill') {
      e.preventDefault();
      const href = el.getAttribute('href');
      closeProfileModal();
      if (href) navigate(href.replace(/^#/, ''));
      return;
    }
    if (action === 'modal-back') {
      e.preventDefault();
      popModal();
      return;
    }
    if (action === 'close-sync-notification') {
      e.preventDefault();
      const notif = el.closest('.sync-notification');
      if (notif) notif.remove();
      return;
    }
    if (action === 'close-sync-modal') {
      e.preventDefault();
      const overlay = el.closest('.sync-modal-overlay');
      if (overlay) overlay.remove();
      return;
    }
    if (action === 'toggle-settings-panel') {
      e.preventDefault();
      toggleSettingsPanel();
      return;
    }
    if (action === 'close-settings-panel') {
      toggleSettingsPanel();
      return;
    }
    if (action === 'toggle-trader-persona') {
      e.preventDefault();
      toggleTraderPersona();
      return;
    }
    if (action === 'retry-item-detail') {
      e.preventDefault();
      const itemName = el.dataset.item || '';
      if (itemName) showItemDetail(itemName);
    }
    if (action === 'open-item-page') {
      e.preventDefault();
      closeProfileModal();
      const route = el.dataset.route || '';
      if (route) navigate('#' + route);
      return;
    }
  });
}

// ── Sale Alert — check for new sales after sync ─────────────────────────
let _lastKnownSaleCount = null;
let _lastSeenSaleIds = new Set();
const BIGSALE_THRESHOLD = 50000;

async function checkForNewSales() {
  // Only if logged in with an esoTag
  if (!currentUser?.esoTag) return;
  try {
    // Use all-time data so newly synced historical sales show up immediately
    // instead of only counting sales from the current trade week.
    const data = await api('/api/me/sales?weeksAgo=-1');
    if (!data?.sellers) return;
    // Count total sales across all guilds + scan for big-sales
    let totalSales = 0;
    const bigSales = [];
    for (const seller of data.sellers) {
      for (const s of (seller.sales || [])) {
        totalSales++;
        const sid = s.id || `${s.timestamp}-${s.itemName}-${s.price}`;
        if (!_lastSeenSaleIds.has(sid) && (s.price || 0) >= BIGSALE_THRESHOLD) {
          bigSales.push({ ...s, _sid: sid, guildName: seller.guildName });
        }
        _lastSeenSaleIds.add(sid);
      }
    }
    if (_lastKnownSaleCount !== null && totalSales > _lastKnownSaleCount) {
      const newCount = totalSales - _lastKnownSaleCount;
      addNotification('sale', `${newCount} New Sale${newCount > 1 ? 's' : ''}!`,
        `You have ${newCount} new sale${newCount > 1 ? 's' : ''} this week. Total: ${totalSales.toLocaleString()}`);
      // Show a brief toast too
      toast(`💰 ${newCount} new sale${newCount > 1 ? 's' : ''} detected!`, 'success', { feedAware: true });
    }
    // Emit a bigsale entry for each (only when not first-load seeding)
    if (_lastKnownSaleCount !== null) {
      for (const b of bigSales) {
        // Sprint 8.8 followup: use item icon as the notification avatar for bigsales
        const iconHtml = b.itemName ? getItemIconHtml(b.itemName) : null;
        const wrappedIcon = iconHtml ? `<span class="feed-bigsale-icon-bubble">${iconHtml}</span>` : null;
        addNotification('bigsale', `🔥 Big Sale: ${(b.price || 0).toLocaleString()}g`,
          `${b.itemName || b.itemDesc || 'Item'} sold in ${b.guildName || 'guild'}`,
          wrappedIcon);
      }
    }
    _lastKnownSaleCount = totalSales;
  } catch { /* silent — not critical */ }
}

// ── Clickable username helper ────────────────────────────────────────────
// (defined near bottom of file, after showUserProfile)

function viewUser(tag) {
  showUserProfile(tag);
}

// ── Clickable item name helper ───────────────────────────────────────────
// ── Item icon cache (populated from search/detail results) ───────────────
// ┌─────────────────────────────────────────────────────────────────────┐
// │  TODO (Designers): Place jode-header.png, jone-header.png,          │
// │  fissal-header.png, and the 3 icon PNGs (jode-icon.png,             │
// │  jone-icon.png, fissal-icon.png) into the /public/assets/ folder.   │
// └─────────────────────────────────────────────────────────────────────┘
const _itemIconCache = {};
const ESO_ICON_FALLBACK = 'https://esoicons.uesp.net/esoui/art/icons/housing_bre_con_sack004.png';
function cacheItemIcon(name, icon) { if (name && icon) _itemIconCache[name.toLowerCase()] = icon; }
function esoIconUrl(iconPath) {
  if (!iconPath || typeof iconPath !== 'string') return ESO_ICON_FALLBACK;
  let p = iconPath.trim();
  if (!p) return ESO_ICON_FALLBACK;
  // Normalize backslashes (Windows-style game data paths)
  p = p.replace(/\\/g, '/');
  p = p.toLowerCase();
  if (p.endsWith('.dds')) p = p.slice(0, -4) + '.png';
  else if (!p.endsWith('.png') && !p.endsWith('.jpg')) p += '.png';
  // If already a full URL, rewrite any stale self-hosted icon origin to the canonical UESP host
  if (p.startsWith('http')) {
    return p.replace(/^https?:\/\/[^/]*redfur[^/]*/i, 'https://esoicons.uesp.net');
  }
  // Ensure leading slash for absolute path on esoicons host
  if (!p.startsWith('/')) p = '/esoui/art/icons/' + p;
  return encodeURI(`https://esoicons.uesp.net${p}`);
}
function getItemIconHtml(name) {
  const icon = _itemIconCache[(name || '').toLowerCase()];
  const src = (icon && typeof icon === 'string') ? esoIconUrl(icon) : ESO_ICON_FALLBACK;
  return `<img class="item-icon-inline" src="${src}" alt="" onerror="console.warn('Icon failed to load:', this.src); if(!this.dataset.retried && this.src.endsWith('.png')){this.dataset.retried='1';this.src=this.src.replace('.png','.jpg')}else{this.onerror=null;this.src='${ESO_ICON_FALLBACK}'}">`;
}

function itemLink(name, extra = '') {
  if (!name) return '—';
  const safe = escapeHtml(name);
  const icon = getItemIconHtml(name);
  return `<span class="clickable-item${extra ? ' ' + extra : ''}" data-item="${safe}">${icon}${safe}</span>`;
}

function itemCell(name, extraClasses = '') {
  if (!name) return `<td class="${extraClasses}">—</td>`;
  const safe = escapeHtml(name);
  const icon = getItemIconHtml(name);
  return `<td class="${extraClasses}"><span class="clickable-item" data-item="${safe}">${icon}${safe}</span></td>`;
}

// ── Clickable guild name helper ──────────────────────────────────────────
function guildLink(name, extra = '') {
  if (!name) return '—';
  const safe = escapeHtml(name);
  return `<span class="clickable-guild${extra ? ' ' + extra : ''}" data-guild="${safe}">${safe}</span>`;
}

function guildCell(name, extraClasses = '') {
  if (!name) return `<td class="${extraClasses}">—</td>`;
  const safe = escapeHtml(name);
  return `<td class="clickable-guild ${extraClasses}" data-guild="${safe}">${safe}</td>`;
}

// ── Guild filter options helper ──────────────────────────────────────────
const GUILDS = [
  { value: 'post', label: 'Redfur Trading Post' },
  { value: 'caravan', label: 'Redfur Trading Caravan' },
  { value: 'dealers', label: 'Redfur Dealers' },
];

// ── Autocomplete helper (reusable for any text input) ────────────────────
function attachAutocomplete(inputEl, getItems) {
  if (!inputEl) return;
  const wrap = document.createElement('div');
  wrap.className = 'autocomplete-wrap';
  inputEl.parentNode.insertBefore(wrap, inputEl);
  wrap.appendChild(inputEl);

  const dropdown = document.createElement('div');
  dropdown.className = 'autocomplete-dropdown';
  dropdown.style.display = 'none';
  wrap.appendChild(dropdown);

  let debounce = null;
  let skipNextDropdown = false;

  inputEl.addEventListener('input', () => {
    if (skipNextDropdown) { skipNextDropdown = false; return; }
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      const q = inputEl.value.trim().toLowerCase();
      if (q.length < 2) { dropdown.style.display = 'none'; return; }
      const items = getItems(q).slice(0, 10);
      if (!items.length) { dropdown.style.display = 'none'; return; }
      dropdown.innerHTML = items.map(item =>
        `<div class="autocomplete-option" data-value="${escapeHtml(item)}">${escapeHtml(item)}</div>`
      ).join('');
      dropdown.style.display = 'block';
    }, 150);
  });

  dropdown.addEventListener('click', (e) => {
    const opt = e.target.closest('.autocomplete-option');
    if (!opt) return;
    inputEl.value = opt.dataset.value;
    dropdown.style.display = 'none';
    skipNextDropdown = true;
    inputEl.dispatchEvent(new Event('input', { bubbles: true }));
  });

  inputEl.addEventListener('blur', () => {
    setTimeout(() => { dropdown.style.display = 'none'; }, 200);
  });

  // Dismiss on outside click (handles edge cases where blur doesn't fire,
  // e.g. clicking a non-focusable element elsewhere in the page).
  document.addEventListener('click', (e) => {
    if (dropdown.style.display === 'none') return;
    if (!wrap.contains(e.target)) dropdown.style.display = 'none';
  });

  inputEl.addEventListener('keydown', (e) => {
    if (dropdown.style.display === 'none') return;
    const opts = [...dropdown.querySelectorAll('.autocomplete-option')];
    const active = dropdown.querySelector('.autocomplete-option.active');
    let idx = opts.indexOf(active);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (active) active.classList.remove('active');
      idx = (idx + 1) % opts.length;
      opts[idx].classList.add('active');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (active) active.classList.remove('active');
      idx = idx <= 0 ? opts.length - 1 : idx - 1;
      opts[idx].classList.add('active');
    } else if (e.key === 'Enter' && active) {
      e.preventDefault();
      inputEl.value = active.dataset.value;
      dropdown.style.display = 'none';
      skipNextDropdown = true;
      inputEl.dispatchEvent(new Event('input', { bubbles: true }));
    } else if (e.key === 'Escape') {
      dropdown.style.display = 'none';
    }
  });
}

// ── Trait icons (Discord custom emoji IDs → CDN URLs) ────────────────────
const TRAIT_ICONS = {
  arcane: '1215844656558899302',
  bloodthirsty: '1215844658026905740',
  charged: '1215844659650367568',
  decisive: '1215844660920975450',
  defending: '1215844661961297973',
  divines: '1215844663005675662',
  harmony: '1215844664700047412',
  healthy: '1215844665719525557',
  impenetrable: '1215845382739984485',
  infused: '1215844671369121852',
  intricate: '1215845389077577828',
  invigorating: '1215844674560983062',
  nirnhoned: '1215844678906413187',
  ornate: '1215845391275130950',
  powered: '1215844681586442300',
  precise: '1215845392990605386',
  protective: '1215844684967055380',
  reinforced: '1215845394689425469',
  robust: '1215844688368762912',
  sharpened: '1215845395671027802',
  sturdy: '1215844692651151460',
  swift: '1215845396593643551',
  training: '1215844696253923328',
  triune: '1215844700469330010',
  'well-fitted': '1215845438784147467',
};

function traitIconUrl(traitName) {
  const id = TRAIT_ICONS[(traitName || '').toLowerCase()];
  return id ? `https://cdn.discordapp.com/emojis/${id}.png?size=32` : '';
}

function traitIconImg(traitName) {
  const url = traitIconUrl(traitName);
  return url ? `<img class="trait-icon" src="${url}" alt="${escapeHtml(traitName)}" onerror="this.style.display='none'">` : '';
}

// ── Quality colors + CSS dot helper ──────────────────────────────────────
const QUALITY_COLORS = { white: '#e8e8e8', green: '#5bffa0', blue: '#7ab8ff', purple: '#d09cff', gold: '#ffcc40' };

// ── Centralized icon/color config ────────────────────────────────────────
const ICON_MAP = {
  traits: TRAIT_ICONS,
  qualities: QUALITY_COLORS,
  entities: { player: '#22d3ee', item: '#fbbf24', guild: '#4ade80' },
  emotes: {
    '0//0': { label: 'Blushing', color: '#f87171' },
    '#~#':  { label: 'Processing', color: '#6b7c6b' },
    '!,,!': { label: 'Excited', color: '#4ade80' },
    '~,,~': { label: 'Annoyed', color: '#fb923c' },
    '!,,~': { label: 'Cheeky', color: '#c084fc' },
    '0~0':  { label: 'Curious', color: '#22d3ee' },
    '^,,^': { label: 'Happy', color: '#fbbf24' },
  },
};

function qualityDot(quality) {
  const c = QUALITY_COLORS[(quality || '').toLowerCase()];
  return c ? `<span class="quality-dot quality-${(quality || 'none').toLowerCase()}" style="background:${c};" title="${quality}"></span>` : '';
}

function guildOptions() {
  return `
    <option value="">All Redfur Guilds</option>
    ${GUILDS.map(g => `<option value="${g.value}">${g.label}</option>`).join('')}`;
}

/** Multi-select guild checkboxes for advanced filter bars */
function guildCheckboxes(selectedArr = []) {
  return GUILDS.map(g =>
    `<label class="filter-checkbox">
       <input type="checkbox" value="${g.value}" ${selectedArr.includes(g.value) ? 'checked' : ''}> ${g.label}
     </label>`
  ).join('');
}

/** Date range picker pair */
function dateRangePicker(startId, endId, startVal = '', endVal = '') {
  return `
    <div class="filter-group">
      <label>From</label>
      <input type="date" class="form-input" id="${startId}" value="${startVal}">
    </div>
    <div class="filter-group">
      <label>To</label>
      <input type="date" class="form-input" id="${endId}" value="${endVal}">
    </div>`;
}

/** Sort direction toggle */
function sortToggle(id, currentSort = 'gold-desc') {
  return `
    <div class="filter-group">
      <label>Sort By</label>
      <select class="form-input" id="${id}" style="min-width:110px;">
        <option value="gold-desc" ${currentSort === 'gold-desc' ? 'selected' : ''}>Gold ↓</option>
        <option value="gold-asc" ${currentSort === 'gold-asc' ? 'selected' : ''}>Gold ↑</option>
        <option value="sales-desc" ${currentSort === 'sales-desc' ? 'selected' : ''}>Sales ↓</option>
        <option value="sales-asc" ${currentSort === 'sales-asc' ? 'selected' : ''}>Sales ↑</option>
        <option value="items-desc" ${currentSort === 'items-desc' ? 'selected' : ''}>Items ↓</option>
        <option value="items-asc" ${currentSort === 'items-asc' ? 'selected' : ''}>Items ↑</option>
        <option value="lastsale-desc" ${currentSort === 'lastsale-desc' ? 'selected' : ''}>Last Sale ↓</option>
        <option value="lastsale-asc" ${currentSort === 'lastsale-asc' ? 'selected' : ''}>Last Sale ↑</option>
        <option value="name-asc" ${currentSort === 'name-asc' ? 'selected' : ''}>Name A-Z</option>
        <option value="name-desc" ${currentSort === 'name-desc' ? 'selected' : ''}>Name Z-A</option>
        <option value="guild-asc" ${currentSort === 'guild-asc' ? 'selected' : ''}>Guild A-Z</option>
        <option value="guild-desc" ${currentSort === 'guild-desc' ? 'selected' : ''}>Guild Z-A</option>
      </select>
    </div>`;
}

/** Client-side sort helper — supports multi-key sort via comma-separated keys (e.g. "gold-desc,name-asc") */
function sortData(arr, sortKey, latestSaleMap) {
  const keys = (sortKey || 'gold-desc').split(',').map(k => {
    const [field, dir] = k.trim().split('-');
    return { field, mul: dir === 'asc' ? 1 : -1 };
  });
  return [...arr].sort((a, b) => {
    for (const { field, mul } of keys) {
      let cmp = 0;
      if (field === 'gold') cmp = ((a.totalGold || a.price || a.profit || 0) - (b.totalGold || b.price || b.profit || 0));
      else if (field === 'sales') cmp = ((a.totalSales || a.saleCount || 0) - (b.totalSales || b.saleCount || 0));
      else if (field === 'items') cmp = ((a.totalItems || 0) - (b.totalItems || 0));
      else if (field === 'lastsale') {
        const tsA = latestSaleMap ? (latestSaleMap[a.sellerName + '|' + a.guildName] || 0) : 0;
        const tsB = latestSaleMap ? (latestSaleMap[b.sellerName + '|' + b.guildName] || 0) : 0;
        cmp = tsA - tsB;
      }
      else if (field === 'name') cmp = (a.sellerName || a.itemName || '').localeCompare(b.sellerName || b.itemName || '');
      else if (field === 'guild') cmp = (a.guildName || '').localeCompare(b.guildName || '');
      if (cmp !== 0) return mul * cmp;
    }
    return 0;
  });
}

// ── Generic table pagination (client-side, default 25 rows) ────────────
function applyAutoTablePagination(root = document) {
  if (!root) return;
  const tables = root.querySelectorAll('.data-table tbody');
  for (const tbody of tables) {
    const table = tbody.closest('table');
    if (!table || table.classList.contains('log-table')) continue; // log has explicit server-side pagination
    if (table.dataset.autoPaginate === 'off') continue;
    if (table.dataset.autoPaginateReady === '1') continue;

    const rows = Array.from(tbody.querySelectorAll('tr'));
    // Skip empty placeholder-only tables
    if (rows.length <= 1 && rows[0]?.querySelector('.empty-state')) continue;

    const pageSize = 25;
    const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
    if (totalPages <= 1) continue;

    let page = 1;
    const pager = document.createElement('div');
    pager.className = 'table-auto-pager';

    function renderPage() {
      const from = (page - 1) * pageSize;
      const to = from + pageSize;
      rows.forEach((r, i) => { r.style.display = (i >= from && i < to) ? '' : 'none'; });
      const info = pager.querySelector('.tap-info');
      const input = pager.querySelector('.tap-input');
      const prev = pager.querySelector('.tap-prev');
      const next = pager.querySelector('.tap-next');
      if (info) info.textContent = `Page ${page} / ${totalPages}`;
      if (input) input.value = String(page);
      if (prev) prev.disabled = page <= 1;
      if (next) next.disabled = page >= totalPages;
    }

    pager.innerHTML = `
      <button class="btn btn-sm tap-prev">‹</button>
      <span class="tap-info text-mono"></span>
      <input class="form-input tap-input" type="number" min="1" max="${totalPages}" style="width:4.2rem;min-width:4.2rem;">
      <button class="btn btn-sm tap-next">›</button>`;

    pager.querySelector('.tap-prev')?.addEventListener('click', () => {
      if (page > 1) { page--; renderPage(); }
    });
    pager.querySelector('.tap-next')?.addEventListener('click', () => {
      if (page < totalPages) { page++; renderPage(); }
    });
    pager.querySelector('.tap-input')?.addEventListener('change', (e) => {
      const p = clampIntClient(parseInt(e.target.value, 10), 1, totalPages, page);
      page = p;
      renderPage();
    });

    const wrap = table.closest('.table-wrap') || table.parentElement;
    if (wrap) wrap.insertAdjacentElement('afterend', pager);
    table.dataset.autoPaginateReady = '1';
    renderPage();
  }
}

// ── Week picker helper ───────────────────────────────────────────────────
function weekPicker(id, weeksAgo) {
  const noneOpt = `<option value="-1" ${weeksAgo === -1 ? 'selected' : ''}>All Time</option>`;
  const _wkPrefix = i => i === 0 ? 'This Week' : i === 1 ? 'Last Week' : i === 2 ? 'Prior Week' : null;
  const options = weekLabels.length > 0
    ? weekLabels.map((w, i) => {
        const prefix = _wkPrefix(i);
        const label  = prefix ? `${prefix}  (${w.label})` : w.label;
        return `<option value="${i}" ${weeksAgo === i ? 'selected' : ''}>${label}</option>`;
      }).join('')
    : `<option value="0" ${weeksAgo === 0 ? 'selected' : ''}>This Week</option>
       ${Array.from({length: 11}, (_, i) => i + 1).map(w =>
         `<option value="${w}" ${weeksAgo === w ? 'selected' : ''}>${w === 1 ? 'Last Week' : w === 2 ? 'Prior Week' : `-${w} weeks`}</option>`
       ).join('')}`;
  return `
    <div class="filter-group">
      <label>FILTER TRADE WEEK</label>
      <select class="form-input" id="${id}" style="min-width:130px;">
        ${noneOpt}
        ${options}
      </select>
    </div>`;
}

// ── Router ───────────────────────────────────────────────────────────────
const routes = {
  '/':            renderOverview,
  '/prices':      renderItemPricingAndAnalysis,
  '/sales':       renderMySales,
  '/leaderboard': renderLeaderboard,
  '/settings':    renderSettings,
  '/fissal':      renderFissal,
  '/sync':        renderSyncAttribution,
  '/sync-stats':  renderSyncAttribution,
  '/log':         renderAdminLog,
  '/api-docs':    renderApiDocs,
  // Admin routes
  '/admin/guild-audit':  renderGuildAudit,
  '/admin/sales':       renderAdminSales,
  '/admin/members':     renderAdminMembers,
  '/admin/items':       renderItemPricingAndAnalysis,
  '/items':             renderItemPricingAndAnalysis,  // legacy alias → merged Item Pricing & Analysis page (Analysis tab via ?tab=analysis)
  '/admin/leaderboard': renderAdminLeaderboard, // legacy, kept as alias
  '/admin/log':         renderAdminLog,
  // Sprint 13: Storage + Diagnostics merged under /admin/perf as tabs.
  '/admin/diagnostics': renderAdminPerformance,
  '/admin/perf':        renderAdminPerformance,
  '/admin/raffle':      renderAdminRaffle,  // legacy alias kept for deep links
  '/raffle':            renderAdminRaffle,
  '/admin/memories':    renderAdminMemories,
  '/admin/fissal':      renderAdminFissal,
  '/admin/storage':     renderAdminPerformance,
  '/admin/tools':       renderAdminTools,
  '/admin/traders':     renderAdminTraders,
};

function navigate(path) {
  window.location.hash = path;
}

function handleRoute() {
  const hash = window.location.hash.slice(1) || '/';
  const route = hash.split('?')[0];
  const prevRoute = currentRoute;
  currentRoute = hash;

  // Remove fissal page body class when navigating away
  document.body.classList.toggle('on-fissal-page', route === '/fissal');

  // Show Fissal transition overlay on real navigations (not first load, not same route)
  const isFirstLoad = prevRoute === '/' && !window.history.state;
  const isSameRoute = prevRoute === hash;
  if (!isFirstLoad && !isSameRoute) {
    _showFissalTransition();
    _fissalTransTimer = setTimeout(_hideFissalTransition, 600);
  }

  // Check for dynamic admin/user route
  const userMatch = route.match(/^\/admin\/user\/(.+)$/);
  // Check for dedicated item page route
  const itemMatch = route.match(/^\/item\/(.+)$/);

  // Update active sidebar links
  updateSidebarActive();

  if (userMatch) {
    renderUserDetail(decodeURIComponent(userMatch[1]));
  } else if (itemMatch) {
    renderItemPage(decodeURIComponent(itemMatch[1]));
  } else if (routes[route]) {
    routes[route]();
  } else {
    $content.innerHTML = '<div class="empty-state">Page not found.</div>';
  }

  // Give async renderers a microtask/frame to paint, then paginate visible tables.
  setTimeout(() => applyAutoTablePagination($content), 0);
  requestAnimationFrame(() => applyAutoTablePagination($content));
}

window.addEventListener('hashchange', handleRoute);

// ── Auth & Init ──────────────────────────────────────────────────────────
async function init() {
  try {
    currentUser = await api('/auth/me');
    // Fetch user settings for privacy toggle state
    if (currentUser) {
      try {
        const settings = await api('/api/me/settings');
        currentUser._settings = settings || {};
      } catch { currentUser._settings = {}; }
      // Fetch Fissal's live bot avatar in the background — non-blocking
      _fetchFissalAvatar();
    }
  } catch { currentUser = null; }

  // Load trade week labels
  try {
    weekLabels = await cachedApi('/api/weeks') || [];
  } catch { weekLabels = []; }

  // New layout: sidebar + clock + dual identity
  renderSidebar();
  renderDualIdentity();
  initUiActionDelegation();
  renderLunarClock();

  // Handle ?item= deep-link from external URLs (e.g. /price?item=Iron%20Sword)
  const _qs = new URLSearchParams(window.location.search);
  const _itemParam = _qs.get('item');
  if (_itemParam && !window.location.hash) {
    window.history.replaceState(null, '', window.location.pathname);
    window.location.hash = '#/item/' + encodeURIComponent(_itemParam);
  }

  handleRoute();
  loadSyncStatus();
  initSSE();
  initNotifCenter();
  initLiveFeed();
  checkForNewSales();
  initFissalPageLinks();

  // Sidebar toggle (mobile)
  const sidebarToggle = document.getElementById('sidebar-toggle');
  if (sidebarToggle) sidebarToggle.addEventListener('click', toggleSidebar);
  const sidebarBackdrop = document.getElementById('sidebar-backdrop');
  if (sidebarBackdrop) sidebarBackdrop.addEventListener('click', toggleSidebar);

  // Settings panel backdrop close
  const settingsBackdrop = document.getElementById('settings-panel-backdrop');
  if (settingsBackdrop) settingsBackdrop.addEventListener('click', toggleSettingsPanel);

  // Trader persona backdrop close
  const traderBackdrop = document.getElementById('trader-persona-backdrop');
  if (traderBackdrop) traderBackdrop.addEventListener('click', toggleTraderPersona);

  // Close panels on outside click (notification bell etc.)
  document.addEventListener('click', (e) => {
    // Close notification panel if open
    const notifPanel = document.getElementById('notif-panel');
    const notifBtn = document.getElementById('notif-bell-btn');
    if (notifPanel && notifPanel.classList.contains('open') && !notifPanel.contains(e.target) && !notifBtn?.contains(e.target)) {
      notifPanel.classList.remove('open');
    }
  });

  // Tooltip delegation for [data-tooltip] elements
  document.addEventListener('mouseenter', (e) => {
    const el = e.target.closest('[data-tooltip]');
    if (el) showTooltip(el, el.dataset.tooltip);
  }, true);
  document.addEventListener('mouseleave', (e) => {
    if (e.target.closest('[data-tooltip]')) hideTooltip();
  }, true);

}

// ── Sprint 12 Phase 3: Profile-modal stack ──────────────────────────────
// Multiple show* functions all share `#profile-modal`. Previously, opening a
// new popup from inside an open one (e.g. clicking a guild name on a player
// card) replaced the content and lost the parent context. We now maintain a
// stack of "renderers" — closures that re-open the previous modal — so the
// header can show a Back arrow that pops one level instead of fully closing.
let _modalStack = [];                  // array of () => Promise<void>
let _currentModalRenderer = null;      // last show* called

function _isProfileModalOpen() {
  const m = document.getElementById('profile-modal');
  return !!(m && m.style.display && m.style.display !== 'none');
}
// Each show* function calls this at entry. If a modal is already open, the
// previous renderer is pushed before being replaced.
function pushModalRenderer(fn) {
  if (_isProfileModalOpen() && _currentModalRenderer && _currentModalRenderer !== fn) {
    _modalStack.push(_currentModalRenderer);
  }
  _currentModalRenderer = fn;
}
function popModal() {
  const prev = _modalStack.pop();
  if (prev) {
    _currentModalRenderer = null;
    try { prev(); } catch { closeProfileModal(); }
  } else {
    closeProfileModal();
  }
}
function closeProfileModal() {
  _modalStack = [];
  _currentModalRenderer = null;
  const m = document.getElementById('profile-modal');
  if (m) m.style.display = 'none';
}
// Returns the markup for the modal's top-right button. Renders a Back arrow
// when a parent modal exists in the stack, or a Close ✕ when this is the
// root modal. Both work on desktop and mobile.
function modalNavBtnHtml() {
  if (_modalStack.length > 0) {
    return `<button class="profile-back" data-ui-action="modal-back" title="Back" aria-label="Back to previous">← Back</button>`;
  }
  return `<button class="profile-close" data-ui-action="close-modal" title="Close" aria-label="Close">✕</button>`;
}
// Sprint 12 Phase 3: Admin drill-down — renders a "Deep Analytics" link to a
// dedicated route, only when the current user is an admin. `kind` is one of
// 'user' | 'guild' to pick the target route.
function modalAdminDrillBtn(kind, key) {
  if (!currentUser?.isAdmin || !key) return '';
  const route = kind === 'guild'
    ? `#/admin/sales?tab=players&guild=${encodeURIComponent(key)}`
    : `#/admin/sales?tab=players&member=${encodeURIComponent(key)}`;
  const label = kind === 'guild' ? '🔬 Guild Deep-Dive' : '🔬 Player Deep-Dive';
  return `<a href="${route}" class="btn btn-primary modal-drill-btn" data-ui-action="open-admin-drill" title="Open dedicated admin page">${label}</a>`;
}

// ── User Profile Popup ───────────────────────────────────────
async function showUserProfile(tag) {
  pushModalRenderer(() => showUserProfile(tag));
  const $modal = document.getElementById('profile-modal');
  const $content = document.getElementById('profile-modal-content');
  $content.innerHTML = '<div class="loading-spinner">🐱 Fissal is looking them up...</div>';
  $modal.style.display = 'flex';

  try {
    const normalTag = tag.startsWith('@') ? tag : '@' + tag;
    const data = await api(`/api/users/${encodeURIComponent(normalTag)}/profile`);

    const avatarHtml = data.avatar
      ? `<img class="profile-avatar" src="${escapeHtml(data.avatar)}" alt="">`
      : '<div class="profile-avatar-placeholder">?</div>';

    const rankBadge = data.leaderboardRank
      ? `<span class="profile-rank">#${data.leaderboardRank}</span>`
      : '';

    const guildColorCls = getGuildColorClass(data.guilds || []);
    const guildBadges = getGuildBadgesHtml(data.guilds || []);
    const nameColor = guildColorCls ? '' : `style="color:${escapeHtml(data.color || '#4ade80')}"`;
    const nameCls = guildColorCls ? `class="profile-name ${guildColorCls}"` : 'class="profile-name"';

    const discordTagHtml = data.displayName && !data.anonymous
      ? `<div class="profile-discord-tag">Discord: ${escapeHtml(data.displayName)}</div>` : '';

    const syncHtml = data.sync ? `
      <div class="profile-section profile-section-guild">
        <h3>Sync Contribution</h3>
        <div class="profile-stats-row">
          <div class="profile-stat"><span class="stat-label">Points</span><span class="stat-value gold">${(data.sync.totalPoints || 0).toLocaleString()}</span></div>
          <div class="profile-stat"><span class="stat-label"><span class="icon-mm"></span> MM Syncs</span><span class="stat-value">${data.sync.mmSyncs || 0}</span></div>
          <div class="profile-stat"><span class="stat-label"><span class="icon-ttc"></span> TTC Syncs</span><span class="stat-value">${data.sync.ttcSyncs || 0}</span></div>
          <div class="profile-stat"><span class="stat-label">Last Sync</span><span class="stat-value">${relativeTime(data.sync.lastSync)}</span></div>
        </div>
      </div>
    ` : '';

    $content.innerHTML = `
      ${modalNavBtnHtml()}
      <div class="profile-header">
        ${avatarHtml}
        <div class="profile-identity">
          <span ${nameCls} ${nameColor}>${escapeHtml(data.tag)}</span>
          ${discordTagHtml}
          ${guildBadges}
          ${rankBadge}
        </div>
      </div>
      <div class="profile-section profile-section-guild guild-section-post">
        <h3>This Week</h3>
        <div class="profile-stats-row">
          <div class="profile-stat"><span class="stat-label">Gold Earned</span><span class="stat-value gold">${gold(data.weeklyStats.totalGold)}</span></div>
          <div class="profile-stat"><span class="stat-label">Sales</span><span class="stat-value">${data.weeklyStats.totalSales}</span></div>
          <div class="profile-stat"><span class="stat-label">Volume</span><span class="stat-value">${data.weeklyStats.totalVolume.toLocaleString()}</span></div>
        </div>
        ${data.weeklyStats.biggestSale ? `<div class="profile-highlight">Biggest: <span class="gold">${gold(data.weeklyStats.biggestSaleGold)}</span> — ${escapeHtml(data.weeklyStats.biggestSale)}</div>` : ''}
      </div>
      <div class="profile-section">
        <h3>All Time</h3>
        <div class="profile-stats-row">
          <div class="profile-stat"><span class="stat-label">Total Gold</span><span class="stat-value gold">${gold(data.allTimeStats.totalGold)}</span></div>
          <div class="profile-stat"><span class="stat-label">Total Sales</span><span class="stat-value">${data.allTimeStats.totalSales.toLocaleString()}</span></div>
        </div>
      </div>
      ${syncHtml}
      ${modalAdminDrillBtn('user', data.tag)}
    `;
  } catch (err) {
    $content.innerHTML = `${modalNavBtnHtml()}
      <div class="error-msg">${escapeHtml(err.message)}</div>`;
  }
}

// ── Guild Info Popup ─────────────────────────────────────────
async function showGuildInfo(guildName) {
  pushModalRenderer(() => showGuildInfo(guildName));
  // Dismiss any lingering hover card so we don't end up with two menus.
  try { cancelGuildHover(); } catch { /* noop */ }

  const $modal = document.getElementById('profile-modal');
  const $mc = document.getElementById('profile-modal-content');
  $mc.innerHTML = '<div class="loading-spinner">Loading guild info...</div>';
  $modal.style.display = 'flex';

  try {
    const data = await api(`/api/guilds/${encodeURIComponent(guildName)}/info`);

    const ageDisplay = data.foundedDate
      ? relativeTime(data.foundedDate).replace(' ago', ' old')
      : '—';

    const activePct = (data.activeSellers != null && data.maxMembers)
      ? Math.round((data.activeSellers / data.maxMembers) * 100)
      : null;

    let adminHtml = '';
    if (currentUser?.isAdmin) {
      adminHtml = `
        <div class="guild-info-admin">
          ${modalAdminDrillBtn('guild', data.guildKey || guildName)}
        </div>`;
    }

    $mc.innerHTML = `
      ${modalNavBtnHtml()}
      <div class="guild-info-header">
        <h2 class="profile-name guild-info-name">${escapeHtml(guildName)}</h2>
      </div>
      <div class="profile-section">
        <h3>Guild Details</h3>
        <div class="guild-info-metrics">
          <div class="guild-info-metric guild-info-metric-active">
            <span class="guild-metric-emoji">🛒</span>
            <span class="guild-metric-value">${data.activeSellers != null ? `${data.activeSellers.toLocaleString()}/${(data.maxMembers || 500).toLocaleString()}` : '—'}</span>
            <span class="guild-metric-label" title="Members in the guild roster proxy (up to capacity) with at least one sale in the last 15 days">Active Members</span>
          </div>
          <div class="guild-info-metric guild-info-metric-total">
            <span class="guild-metric-emoji">👥</span>
            <span class="guild-metric-value">${data.totalSellers != null ? data.totalSellers.toLocaleString() : '—'}</span>
            <span class="guild-metric-label" title="Grand total of every unique user ever recorded with sales in this guild">Total Members</span>
          </div>
          ${activePct != null ? `
          <div class="guild-info-metric guild-info-metric-sales">
            <span class="guild-metric-emoji">📈</span>
            <span class="guild-metric-value">${activePct}%</span>
            <span class="guild-metric-label">Activity Rate</span>
          </div>` : ''}
          <div class="guild-info-metric">
            <span class="guild-metric-emoji">🕰️</span>
            <span class="guild-metric-value">${ageDisplay}</span>
            <span class="guild-metric-label">Guild Age</span>
          </div>
        </div>
        ${data.creator ? `<div class="profile-highlight">👑 Creator: ${escapeHtml(data.creator)}</div>` : ''}
        ${data.guildHall ? `<div class="profile-highlight">🏠 Guild Hall: ${escapeHtml(data.guildHall)}</div>` : ''}
      </div>
      <div class="profile-section">
        <h3>This Week</h3>
        <div class="guild-info-metrics">
          <div class="guild-info-metric guild-info-metric-gold">
            <span class="guild-metric-emoji">💰</span>
            <span class="guild-metric-value gold">${gold(data.weeklyGold)}</span>
            <span class="guild-metric-label">Total Gold</span>
          </div>
          <div class="guild-info-metric guild-info-metric-sales">
            <span class="guild-metric-emoji">📦</span>
            <span class="guild-metric-value">${(data.weeklySales || 0).toLocaleString()}</span>
            <span class="guild-metric-label">Sales</span>
          </div>
          <div class="guild-info-metric guild-info-metric-volume">
            <span class="guild-metric-emoji">📊</span>
            <span class="guild-metric-value">${(data.weeklyVolume || 0).toLocaleString()}</span>
            <span class="guild-metric-label">Volume</span>
          </div>
        </div>
      </div>
      ${_renderGuildKioskSection(data.kiosk)}
      ${adminHtml}
    `;
  } catch (err) {
    $mc.innerHTML = `${modalNavBtnHtml()}
      <div class="error-msg">${escapeHtml(err.message)}</div>`;
  }
}

// ── Sprint 10.5: Kiosk section for the guild info modal ─────────────────
// Shows where the guild's trader is currently parked, plus a wayshrine
// indicator. For admin viewers, expands with tier / prestige / bid band /
// live bid estimate. For regular users, only the location is shown.
function _renderGuildKioskSection(k) {
  if (!k || !k.zone) {
    return `
      <div class="profile-section guild-kiosk-section guild-kiosk-empty">
        <h3>📍 Guild Trader</h3>
        <div class="t-dim" style="padding:0.3rem 0;">
          No trader tracked for this guild yet.
        </div>
      </div>`;
  }

  const ws = k.hasWayshrine
    ? `<span class="kiosk-badge kiosk-badge-ws" title="Wayshrine ${escapeHtml(k.wayshrineDistance || 'nearby')}">🕯 Wayshrine${k.wayshrineDistance ? ' · ' + escapeHtml(k.wayshrineDistance) : ''}</span>`
    : `<span class="kiosk-badge kiosk-badge-ws-far" title="No wayshrine in flagship city">🕯 No wayshrine</span>`;
  const dlc = k.isDLC
    ? '<span class="kiosk-badge kiosk-badge-dlc" title="DLC / Chapter zone">DLC</span>'
    : '<span class="kiosk-badge kiosk-badge-base" title="Base game zone">Base</span>';
  const weeks = k.weeksHeld != null
    ? `<span class="kiosk-badge kiosk-badge-weeks" title="Consecutive weeks in this zone">${k.weeksHeld}w held</span>`
    : '';

  // Public (everyone) — just the location + indicators
  const commonHtml = `
    <div class="guild-kiosk-main">
      <div class="guild-kiosk-line">
        <span class="guild-kiosk-emoji">📍</span>
        <div class="guild-kiosk-where">
          <div class="guild-kiosk-trader"><strong>${escapeHtml(k.trader || 'Guild Trader')}</strong></div>
          <div class="guild-kiosk-loc">${escapeHtml(k.city || '—')} <span class="t-dim">·</span> ${escapeHtml(k.zone)}</div>
        </div>
      </div>
      <div class="guild-kiosk-badges">
        ${ws} ${dlc} ${weeks}
      </div>
    </div>`;

  if (!k.adminDetail) {
    // Regular user: keep it simple
    return `
      <div class="profile-section guild-kiosk-section">
        <h3>📍 Guild Trader</h3>
        ${commonHtml}
      </div>`;
  }

  // Admin: add tier / prestige / bid band / estimate
  const tierCls = `trader-tier trader-tier-${k.tier || 'mid'}`;
  const est = k.bidEstimate || {};
  const estStr = est.estimate != null
    ? `<span class="text-gold">${_fmtGold(est.estimate)}</span> <span class="t-dim">(${_fmtGold(est.low)}–${_fmtGold(est.high)})</span>`
    : '<span class="t-dim">—</span>';
  const confPct = est.confidence != null ? Math.round(est.confidence * 100) + '%' : '—';
  const bankBadge = k.hasBank
    ? '<span class="kiosk-badge kiosk-badge-bank" title="Banker in flagship city">🏦 Bank</span>'
    : '';

  return `
    <div class="profile-section guild-kiosk-section guild-kiosk-admin">
      <h3>📍 Guild Trader <span class="t-dim" style="font-size:var(--fs-xs);font-weight:400;">(admin view)</span></h3>
      ${commonHtml}
      <div class="guild-kiosk-admin-grid">
        <div class="guild-kiosk-stat">
          <span class="kiosk-stat-label">Tier</span>
          <span class="kiosk-stat-value"><span class="${tierCls}">${escapeHtml(k.tier || '—')}</span></span>
        </div>
        <div class="guild-kiosk-stat">
          <span class="kiosk-stat-label">Prestige</span>
          <span class="kiosk-stat-value text-mono">${k.prestigeScore ?? '—'}</span>
        </div>
        <div class="guild-kiosk-stat">
          <span class="kiosk-stat-label">Est. Bid</span>
          <span class="kiosk-stat-value text-mono">${estStr}</span>
        </div>
        <div class="guild-kiosk-stat">
          <span class="kiosk-stat-label">Confidence</span>
          <span class="kiosk-stat-value text-mono">${confPct} <span class="t-dim" style="font-size:var(--fs-xs);">${escapeHtml(est.source || '')}</span></span>
        </div>
        <div class="guild-kiosk-stat">
          <span class="kiosk-stat-label">Community Range</span>
          <span class="kiosk-stat-value text-mono">${
            (k.estBidMin != null && k.estBidMax != null)
              ? `${_fmtGold(k.estBidMin)}–${_fmtGold(k.estBidMax)}`
              : '—'
          }</span>
        </div>
        <div class="guild-kiosk-stat">
          <span class="kiosk-stat-label">Amenities</span>
          <span class="kiosk-stat-value">${bankBadge || '<span class="t-dim">—</span>'}</span>
        </div>
      </div>
    </div>`;
}

function renderNav() {
  // Legacy stub — sidebar handles all navigation now
  renderSidebar();
}

async function loadSyncStatus() {
  // Pull server version from healthz and update the sidebar badge
  try {
    const hz = await fetch('/api/healthz').then(r => r.ok ? r.json() : null);
    if (hz?.version) {
      SITE_VERSION = 'v' + hz.version;
      const badge = document.querySelector('.sidebar-site-version');
      if (badge) badge.textContent = SITE_VERSION;
    }
  } catch { /* non-critical */ }

  try {
    const status = await cachedApi('/api/sync-status');
    if (!status) return;
    const cls = status.isStale ? 'sync-stale' : 'sync-live';
    const label = status.isStale ? 'STALE' : 'LIVE';

    // ── Sprint 12: rich hover tooltips ─────────────────────────────────────
    // Sync tooltip: per-guild MM finishedAt + last syncer.
    const sb = status.syncedBy || {};
    const guilds = status.mm?.guilds || {};
    const fmtAgo = (t) => t ? relativeTime(t) : 'never';
    const fmtBy = (k) => sb[k] ? ` (by ${sb[k]})` : '';
    const syncTip = [
      'Per-guild MM sync:',
      `• Post:    ${fmtAgo(guilds.post)}${fmtBy('post')}`,
      `• Caravan: ${fmtAgo(guilds.caravan)}${fmtBy('caravan')}`,
      `• Dealers: ${fmtAgo(guilds.dealers)}${fmtBy('dealers')}`,
      '',
      'Raffle sync:',
      `• RTP: ${fmtAgo(status.raffle?.rtp)}${fmtBy('rtpRaffle')}`,
      `• RD:  ${fmtAgo(status.raffle?.rd)}${fmtBy('rdRaffle')}`,
    ].join('\n');

    // TTC tooltip: previous "first syncer" history + current.
    const ttcTip = [
      `TTC v${status.ttc?.ttcVersion || '?'}`,
      `Last sync: ${fmtAgo(status.ttc?.finishedAt)}`,
      sb.TTC ? `First syncer: ${sb.TTC}` : 'No TTC syncer recorded yet',
    ].join('\n');

    $syncIndicator.innerHTML =
      `<span class="footer-sync-group" title="${escapeHtml(syncTip)}">` +
        `<span class="${cls}">●</span> Sync: ` +
        `<span class="live-label ${cls === 'sync-live' ? 'live-label-on' : 'live-label-off'}">${label}</span>` +
      `</span>` +
      ` · ` +
      `<span class="footer-ttc-group" title="${escapeHtml(ttcTip)}">` +
        `<span class="icon-ttc"></span> <span class="ttc-text">TTC v${status.ttc?.ttcVersion || '?'}</span>` +
      `</span>`;
    $footerVersion.textContent = `${(status.mm.totalSales || 0).toLocaleString()} sales tracked`;
  } catch { /* silently fail */ }
}

// ── Page Renderers ───────────────────────────────────────────────────────

// ── Sprint 9.1: Dashboard layout persistence (order/collapse/hide) ──────
const DASH_LAYOUT_KEY = 'redfur-dashboard-layout';

function getDashboardLayout() {
  try {
    const raw = localStorage.getItem(DASH_LAYOUT_KEY);
    if (!raw) return { order: [], collapsed: [], hidden: [], added: [] };
    const obj = JSON.parse(raw);
    return {
      order: Array.isArray(obj.order) ? obj.order : [],
      collapsed: Array.isArray(obj.collapsed) ? obj.collapsed : [],
      hidden: Array.isArray(obj.hidden) ? obj.hidden : [],
      added: Array.isArray(obj.added) ? obj.added : [],
    };
  } catch { return { order: [], collapsed: [], hidden: [], added: [] }; }
}

function saveDashboardLayout(layout) {
  try { localStorage.setItem(DASH_LAYOUT_KEY, JSON.stringify(layout)); } catch {}
}

// ── Sprint 10.3: Pluggable dashboard widgets ─────────────────────────────
// Each widget is a compact "summary card" the user can add to their Overview
// page via the "+ Add Panel" picker. Section ids of user-added widgets are
// prefixed with "widget:" so the existing layout machinery (reorder / collapse)
// works generically on both built-ins and widgets without special casing.
//
// Shape:
//   id         — short slug used in localStorage + data-section-id
//   label      — human-readable name in picker & header bar
//   route      — hash route this widget "belongs" to (for the Open → link)
//   requires   — optional 'login' | 'admin' gating for visibility in picker
//   render(host, layoutCtx) — populates the host <div> with the card body.
//                             Receives the widget's direct content host (not
//                             the .overview-category wrapper).
const DASHBOARD_WIDGETS = {
  'price-check': {
    label: 'Price Check',
    icon: '🏷️',
    route: '/prices',
    description: 'Look up current market prices for any item.',
    requires: null,
    render: (host) => {
      host.innerHTML = `
        <div class="panel ov-widget-card">
          <div class="ov-widget-body">
            <p class="ov-widget-desc">Search the TamrielTradeCentre + Fissal Relay price history for any item.</p>
            <form class="ov-widget-quicksearch" data-widget="price-check">
              <input type="text" class="form-input" name="q" placeholder="e.g. Rosin, Nirncrux, Mundane Rune..." autocomplete="off">
              <button type="submit" class="btn btn-primary">Check Prices</button>
            </form>
          </div>
          <div class="ov-widget-footer">
            <a href="#/prices" class="btn btn-sm">Open Price Check →</a>
          </div>
        </div>`;
      const form = host.querySelector('form.ov-widget-quicksearch');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          const q = (form.querySelector('input[name="q"]')?.value || '').trim();
          // Stash for the price-check page to read on mount.
          if (q) { try { sessionStorage.setItem('redfur_prefill_price_q', q); } catch {} }
          location.hash = '#/prices';
        });
      }
    },
  },
};

/**
 * Returns the list of widget ids the current user is allowed to see in the
 * "+ Add Panel" picker (respecting login/admin gates).
 */
function _availableWidgetIds() {
  return Object.keys(DASHBOARD_WIDGETS).filter(id => {
    const w = DASHBOARD_WIDGETS[id];
    if (w.requires === 'admin' && !currentUser?.isAdmin) return false;
    if (w.requires === 'login' && !currentUser) return false;
    return true;
  });
}

function _widgetSectionId(id) { return 'widget:' + id; }
function _parseWidgetSection(sectionId) {
  return sectionId && sectionId.startsWith('widget:') ? sectionId.slice(7) : null;
}

function applyDashboardLayout(root) {
  if (!root) return;
  const sections = Array.from(root.querySelectorAll('.overview-category[data-section-id]'));
  if (sections.length === 0) return;
  const layout = getDashboardLayout();

  // Decorate each section with a header bar (collapse + reorder + hide)
  sections.forEach(sec => {
    const id = sec.dataset.sectionId;
    const label = sec.querySelector('.overview-category-label');
    if (!label || label.dataset.sprint91 === '1') return;
    label.dataset.sprint91 = '1';
    const labelText = label.textContent.replace(/^▸\s*/, '').trim();
    label.innerHTML = `
      <button class="ov-section-toggle" title="Collapse / expand" data-act="toggle">▸</button>
      <span class="ov-section-title">${escapeHtml(labelText)}</span>
      <span class="ov-section-ctrls">
        <button class="ov-section-btn" title="Move up" data-act="up">▲</button>
        <button class="ov-section-btn" title="Move down" data-act="down">▼</button>
        <button class="ov-section-btn" title="Hide section" data-act="hide">✕</button>
      </span>`;

    label.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-act]');
      if (!btn) return;
      e.preventDefault();
      const act = btn.dataset.act;
      const curLayout = getDashboardLayout();
      if (act === 'toggle') {
        const idx = curLayout.collapsed.indexOf(id);
        if (idx >= 0) curLayout.collapsed.splice(idx, 1);
        else curLayout.collapsed.push(id);
        saveDashboardLayout(curLayout);
        _applyDashLayoutState(root);
      } else if (act === 'hide') {
        const widgetId = _parseWidgetSection(id);
        if (widgetId) {
          // Widgets are removed (un-added) rather than hidden — re-add via the
          // "+ Add Panel" picker instead of a restore chip.
          curLayout.added = curLayout.added.filter(x => x !== widgetId);
        } else {
          if (!curLayout.hidden.includes(id)) curLayout.hidden.push(id);
        }
        saveDashboardLayout(curLayout);
        if (widgetId) {
          // Full re-render so the widget DOM is removed cleanly.
          renderOverview({ seamless: true });
        } else {
          _applyDashLayoutState(root);
          _ensureRestoreBar(root);
        }
      } else if (act === 'up' || act === 'down') {
        const visible = Array.from(root.querySelectorAll('.overview-category[data-section-id]'))
          .filter(s => !curLayout.hidden.includes(s.dataset.sectionId));
        const orderIds = visible.map(s => s.dataset.sectionId);
        const i = orderIds.indexOf(id);
        const j = act === 'up' ? i - 1 : i + 1;
        if (j < 0 || j >= orderIds.length) return;
        [orderIds[i], orderIds[j]] = [orderIds[j], orderIds[i]];
        // persist full order including hidden at end in original order
        const hiddenIds = sections.map(s => s.dataset.sectionId).filter(x => curLayout.hidden.includes(x));
        curLayout.order = [...orderIds, ...hiddenIds];
        saveDashboardLayout(curLayout);
        _applyDashLayoutState(root);
      }
    });
  });

  _applyDashLayoutState(root);
  _ensureRestoreBar(root);
}

function _applyDashLayoutState(root) {
  const layout = getDashboardLayout();
  const sections = Array.from(root.querySelectorAll('.overview-category[data-section-id]'));
  // Apply order
  if (layout.order.length) {
    const parent = sections[0]?.parentNode;
    if (parent) {
      const byId = Object.fromEntries(sections.map(s => [s.dataset.sectionId, s]));
      const ordered = [];
      for (const id of layout.order) if (byId[id]) ordered.push(byId[id]);
      for (const s of sections) if (!layout.order.includes(s.dataset.sectionId)) ordered.push(s);
      for (const s of ordered) parent.appendChild(s);
    }
  }
  // Apply collapsed / hidden
  for (const sec of sections) {
    const id = sec.dataset.sectionId;
    const collapsed = layout.collapsed.includes(id);
    const hidden = layout.hidden.includes(id);
    sec.classList.toggle('ov-collapsed', collapsed);
    sec.style.display = hidden ? 'none' : '';
    const toggle = sec.querySelector('.ov-section-toggle');
    if (toggle) toggle.textContent = collapsed ? '▸' : '▾';
  }
}

function _ensureRestoreBar(root) {
  const layout = getDashboardLayout();
  const labels = { personal: 'Personal Metrics', analytics: 'Personal Analytics', sync: 'Server Sync Data' };

  // Widgets eligible to add: registered widgets not currently added, respecting login/admin gates.
  const available = _availableWidgetIds().filter(id => !layout.added.includes(id));

  // Bar is visible whenever there's something to restore OR any widget to add.
  const showBar = layout.hidden.length > 0 || available.length > 0;

  let bar = root.querySelector('.ov-restore-bar');
  if (!showBar) { if (bar) bar.remove(); return; }
  if (!bar) {
    bar = document.createElement('div');
    bar.className = 'ov-restore-bar';
    root.insertBefore(bar, root.firstChild);
  }

  const hiddenHtml = layout.hidden.length
    ? `<span class="ov-restore-group">
        <span class="ov-restore-title">Hidden:</span>
        ${layout.hidden.map(id => `<button class="btn btn-xs ov-restore-btn" data-act="restore" data-id="${escapeHtml(id)}">+ ${escapeHtml(labels[id] || id)}</button>`).join(' ')}
      </span>`
    : '';

  const addPanelHtml = available.length
    ? `<span class="ov-restore-group ov-add-panel-group">
        <button class="btn btn-xs ov-add-panel-btn" data-act="open-picker" title="Add a new panel to your dashboard">+ Add Panel ▾</button>
        <div class="ov-add-panel-menu" hidden>
          ${available.map(id => {
            const w = DASHBOARD_WIDGETS[id];
            return `<button class="ov-add-panel-item" data-act="add" data-id="${escapeHtml(id)}">
              <span class="ov-add-panel-icon">${w.icon || '▸'}</span>
              <span class="ov-add-panel-label">${escapeHtml(w.label)}</span>
              ${w.description ? `<span class="ov-add-panel-desc">${escapeHtml(w.description)}</span>` : ''}
            </button>`;
          }).join('')}
        </div>
      </span>`
    : '';

  bar.innerHTML = hiddenHtml + addPanelHtml;

  bar.onclick = (e) => {
    const btn = e.target.closest('[data-act]');
    if (!btn) return;
    const act = btn.dataset.act;
    const cur = getDashboardLayout();

    if (act === 'restore') {
      cur.hidden = cur.hidden.filter(x => x !== btn.dataset.id);
      saveDashboardLayout(cur);
      _applyDashLayoutState(root);
      _ensureRestoreBar(root);
    } else if (act === 'open-picker') {
      const menu = bar.querySelector('.ov-add-panel-menu');
      if (menu) menu.hidden = !menu.hidden;
    } else if (act === 'add') {
      const id = btn.dataset.id;
      if (id && DASHBOARD_WIDGETS[id] && !cur.added.includes(id)) {
        cur.added.push(id);
        saveDashboardLayout(cur);
        // Re-render so the new widget section is appended + layout applied.
        renderOverview({ seamless: true });
      }
    }
  };

  // Close the picker when clicking outside.
  if (!bar.dataset.outsideClickBound) {
    bar.dataset.outsideClickBound = '1';
    document.addEventListener('click', (e) => {
      if (!bar.isConnected) return;
      if (bar.contains(e.target)) return;
      const menu = bar.querySelector('.ov-add-panel-menu');
      if (menu && !menu.hidden) menu.hidden = true;
    });
  }
}

// ── Generic seamless-refresh helpers (Sprint 10.7) ────────────
// A CPU-cheap snapshot/flash pair used by renderLeaderboard, renderMySales,
// renderAdminSales, and renderSyncAttribution so background refreshes never
// blank the screen and only the cells whose numbers actually changed get
// highlighted. Storage is a plain Map keyed by `row:<name>:<colIndex>` or
// `honor:<label>`; O(n) in cells on the page (typically < 300). No
// MutationObservers, no cloneNode. < 1ms on mid-range mobile.
function _snapshotLeaderboardValues(root) {
  const snap = new Map();
  const $root = root || $content;
  if (!$root) return snap;
  const rows = $root.querySelectorAll('.data-table tbody tr');
  rows.forEach(tr => {
    const nameEl = tr.querySelector('.clickable-name, .clickable-tag, td.rank + td');
    const key = (nameEl && nameEl.textContent.trim()) || String(tr.rowIndex);
    tr.querySelectorAll('td').forEach((td, i) => {
      const t = td.textContent.trim();
      if (t) snap.set('row:' + key + ':' + i, t);
    });
  });
  $root.querySelectorAll('.lb-honor-card').forEach(card => {
    const lbl = card.querySelector('.lb-honor-label')?.textContent?.trim() || '';
    const val = card.querySelector('.lb-honor-value')?.textContent?.trim() || '';
    if (lbl) snap.set('honor:' + lbl, val);
  });
  return snap;
}

function _flashLeaderboardChangedValues(snap, root) {
  if (!snap || snap.size === 0) return;
  const $root = root || $content;
  if (!$root) return;
  const flash = (el) => {
    if (!el) return;
    el.classList.remove('terminal-flash');
    void el.offsetWidth; // retrigger animation if already present
    el.classList.add('terminal-flash');
    setTimeout(() => el.classList.remove('terminal-flash'), 1400);
  };
  const rows = $root.querySelectorAll('.data-table tbody tr');
  rows.forEach(tr => {
    const nameEl = tr.querySelector('.clickable-name, .clickable-tag, td.rank + td');
    const key = (nameEl && nameEl.textContent.trim()) || String(tr.rowIndex);
    tr.querySelectorAll('td').forEach((td, i) => {
      const prev = snap.get('row:' + key + ':' + i);
      const cur  = td.textContent.trim();
      if (prev !== undefined && prev !== cur) flash(td);
    });
  });
  $root.querySelectorAll('.lb-honor-card').forEach(card => {
    const lbl = card.querySelector('.lb-honor-label')?.textContent?.trim() || '';
    const valEl = card.querySelector('.lb-honor-value');
    const prev = snap.get('honor:' + lbl);
    if (prev !== undefined && valEl && prev !== valEl.textContent.trim()) flash(valEl);
  });
}

// ── Overview refresh helpers (seamless idle updates) ─────────
// Snapshot a Map of stat-label → displayed value text for every stat cell
// currently in $content. Used to diff against the post-refresh DOM so only
// changed values get the brief highlight animation.
function _snapshotOverviewValues() {
  const snap = new Map();
  if (!$content) return snap;
  $content.querySelectorAll('.stat-row').forEach(row => {
    const lbl = row.querySelector('.stat-label')?.textContent?.trim();
    const val = row.querySelector('.stat-value')?.textContent?.trim();
    if (lbl) snap.set('row:' + lbl, val);
  });
  $content.querySelectorAll('.stat-big').forEach(row => {
    const lbl = row.querySelector('.label')?.textContent?.trim();
    const val = row.querySelector('.value')?.textContent?.trim();
    if (lbl) snap.set('big:' + lbl, val);
  });
  return snap;
}

// Walk the newly-replaced DOM and flash any cells whose value text differs
// from the snapshot. Adds .value-updated which drives a brief glow animation
// defined in styles.css; the class is auto-removed after the animation runs.
function _flashOverviewChangedValues(snap) {
  if (!snap || !$content || snap.size === 0) return;
  const applyFlash = (el) => {
    if (!el) return;
    el.classList.remove('value-updated'); // restart animation if already flashing
    // Force a reflow so the re-added class retriggers the keyframe
    void el.offsetWidth;
    el.classList.add('value-updated');
    setTimeout(() => el.classList.remove('value-updated'), 1600);
  };
  $content.querySelectorAll('.stat-row').forEach(row => {
    const lbl = row.querySelector('.stat-label')?.textContent?.trim();
    const el  = row.querySelector('.stat-value');
    if (!lbl || !el) return;
    const prev = snap.get('row:' + lbl);
    if (prev !== undefined && prev !== el.textContent.trim()) applyFlash(el);
  });
  $content.querySelectorAll('.stat-big').forEach(row => {
    const lbl = row.querySelector('.label')?.textContent?.trim();
    const el  = row.querySelector('.value');
    if (!lbl || !el) return;
    const prev = snap.get('big:' + lbl);
    if (prev !== undefined && prev !== el.textContent.trim()) applyFlash(el);
  });
}

// ── Overview ─────────────────────────────────────────────────
// Thematic loading messages — Fissal (the AI cat oracle), the terminal/Dwemer
// machinery, and radio-frequency vocabulary. One is picked at random per
// fresh dashboard render so the boot screen feels alive.
const _OVERVIEW_LOADING_MSGS = [
  '📡 Tuning to Fissal\'s frequency…',
  '🐱 Fissal is purring up the receivers…',
  '📻 Locking onto the trade-band signal…',
  '⚡ Spooling Dwemer terminal — stand by…',
  '🛰 Awaiting carrier wave from the kiosks…',
  '📡 Fissal sniffs the airwaves for ledger packets…',
  '🔊 Decoding telegraph chatter from the trading post…',
  '⚙ Warming the resonator coils…',
  '📻 Adjusting whisker-antenna gain…',
  '🐾 Fissal flicks an ear — signal incoming…',
  '📡 Cross-referencing TamrielTradeCentre broadcasts…',
  '⚡ Crystal valves heating to operating voltage…',
];
function _pickOverviewLoadingMsg() {
  return _OVERVIEW_LOADING_MSGS[Math.floor(Math.random() * _OVERVIEW_LOADING_MSGS.length)];
}

// ── Sprint 12 Phase 3: Overview "My Recent Sales" panel ─────────────────
const OVERVIEW_RECENT_PAGE_SIZE = 25;
const _OVERVIEW_QUALITY_NAMES = ['gold', 'purple', 'blue', 'green', 'white', 'worn'];
const _OVERVIEW_QUALITY_COLORS = {
  worn: '#9a9a9a', white: '#e8e8e8', green: '#5bffa0',
  blue: '#7ab8ff', purple: '#d09cff', gold: '#ffcc40',
};
function _overviewParseQuality(adderText) {
  if (!adderText) return null;
  const a = String(adderText).toLowerCase();
  for (const q of _OVERVIEW_QUALITY_NAMES) { if (a.includes(q)) return q; }
  return null;
}
function _renderOverviewRecentSalesShell(total) {
  const pages = Math.max(1, Math.ceil(total / OVERVIEW_RECENT_PAGE_SIZE));
  return `
    <div class="panel ov-recent-sales-panel" data-page="0" data-pages="${pages}" data-total="${total}">
      <div class="ov-recent-header">
        <span class="ov-recent-title">📜 ${total.toLocaleString()} recent sale${total === 1 ? '' : 's'}</span>
        <div class="ov-recent-pager">
          <button class="ov-recent-prev btn btn-sm" aria-label="Previous page" disabled>←</button>
          <span class="ov-recent-page-label">1 / ${pages}</span>
          <button class="ov-recent-next btn btn-sm" aria-label="Next page"${pages > 1 ? '' : ' disabled'}>→</button>
        </div>
      </div>
      <div class="ov-recent-track" role="list"></div>
    </div>`;
}
function _renderOverviewRecentRow(sale) {
  const qual = _overviewParseQuality(sale.itemAdderText);
  const qColor = qual ? _OVERVIEW_QUALITY_COLORS[qual] : '';
  const iconUrl = sale.itemIcon ? esoIconUrl(sale.itemIcon) : '';
  const guildCls = getGuildColorClass([sale.guildName]);
  const ago = relativeTime(sale.timestamp);
  const itemNameSafe = escapeHtml(sale.itemName || 'Unknown');
  const guildSafe = escapeHtml(sale.guildName || '—');
  return `
    <div class="ov-recent-row" role="listitem">
      ${iconUrl
        ? `<img class="ov-recent-icon" src="${escapeHtml(iconUrl)}" alt="" onerror="this.onerror=null;this.style.visibility='hidden'">`
        : `<span class="ov-recent-icon ov-recent-icon-fallback">📦</span>`}
      <span class="ov-recent-qty">×${(sale.quant || 1).toLocaleString()}</span>
      <span class="ov-recent-name clickable-item" data-item="${itemNameSafe}"${qColor ? ` style="color:${qColor}"` : ''}>${itemNameSafe}</span>
      <span class="ov-recent-guild ${guildCls} clickable-guild" data-guild="${guildSafe}">${guildSafe}</span>
      <span class="ov-recent-price gold">${gold(sale.price)}</span>
      <span class="ov-recent-ago" data-ts="${sale.timestamp}">${ago}</span>
    </div>`;
}
function _wireOverviewRecentSales() {
  const panel = document.querySelector('.ov-recent-sales-panel');
  if (!panel) return;
  const track = panel.querySelector('.ov-recent-track');
  const prev  = panel.querySelector('.ov-recent-prev');
  const next  = panel.querySelector('.ov-recent-next');
  const label = panel.querySelector('.ov-recent-page-label');
  const sales = window._overviewRecentSales || [];
  const pages = Math.max(1, Math.ceil(sales.length / OVERVIEW_RECENT_PAGE_SIZE));

  function paint(pageIdx) {
    const start = pageIdx * OVERVIEW_RECENT_PAGE_SIZE;
    const slice = sales.slice(start, start + OVERVIEW_RECENT_PAGE_SIZE);
    track.innerHTML = slice.map(_renderOverviewRecentRow).join('');
    if (label) label.textContent = `${pageIdx + 1} / ${pages}`;
    if (prev) prev.disabled = pageIdx <= 0;
    if (next) next.disabled = pageIdx >= pages - 1;
    panel.dataset.page = String(pageIdx);
  }
  paint(0);
  if (prev) prev.addEventListener('click', () => {
    const cur = parseInt(panel.dataset.page || '0', 10);
    if (cur > 0) paint(cur - 1);
  });
  if (next) next.addEventListener('click', () => {
    const cur = parseInt(panel.dataset.page || '0', 10);
    if (cur < pages - 1) paint(cur + 1);
  });
}

async function renderOverview(opts = {}) {
  const { seamless = false } = opts;
  // If the dashboard is already rendered (e.g. a background SSE refresh while
  // the user is idling on this page), avoid the loading spinner entirely —
  // it's jarring for passive updates. Only show it when the page is genuinely
  // empty (first visit / route change).
  const alreadyRendered = !!$content.querySelector('[data-section-id]');
  if (!alreadyRendered && !seamless) {
    $content.innerHTML = `<div class="loading-spinner">${_pickOverviewLoadingMsg()}</div>`;
  }

  try {
    const [stats, sync] = await Promise.all([
      api('/api/stats/overview'),
      api('/api/sync-status'),
    ]);
    if (!stats) return renderLoginPrompt();

    // ── Category 1: Personal Metrics ──
    let personalHtml = '';
    let recentSalesPanelHtml = '';
    if (currentUser) {
      try {
        const mySales = await api('/api/me/sales?weeksAgo=0');
        if (mySales && mySales.sellers) {
          let totalGold = 0, totalCount = 0;
          for (const s of mySales.sellers) for (const sale of s.sales) { totalGold += sale.price; totalCount++; }
          personalHtml = `
            <div class="panel-grid">
              <div class="stat-big"><span class="value">${gold(totalGold)}</span><span class="label">This Week's Revenue</span></div>
              <div class="stat-big"><span class="value">${totalCount.toLocaleString()}</span><span class="label">Transactions</span></div>
            </div>`;
        }
      } catch { /* silently fail */ }

      // Sprint 12 Phase 3: "My Recent Sales" — paginated horizontal panel
      // (25 items/page) on the Overview. Fetches lifetime sales so the
      // panel still has content for users who haven't sold this week.
      try {
        const allTime = await api('/api/me/sales?weeksAgo=-1');
        const flat = [];
        for (const s of (allTime?.sellers || [])) {
          for (const sale of (s.sales || [])) flat.push(sale);
        }
        flat.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        const top = flat.slice(0, 250); // cap at 250 (10 pages × 25)
        if (top.length) {
          // Stash for client-side paging without re-fetch.
          window._overviewRecentSales = top;
          recentSalesPanelHtml = _renderOverviewRecentSalesShell(top.length);
        }
      } catch { /* silently fail */ }
    }

    // ── Category 2: Personal Analytics (placeholder for Sprint 6.1) ──

    // ── Category 3: Server Sync Data ──
    const syncHtml = `
      <div class="panel overview-sync-section overview-sync-mm">
        <div class="panel-header"><span class="panel-title"><span class="icon-mm"></span> Fissal Relay</span></div>
        <div class="stat-row"><span class="stat-label">Items Catalogued</span><span class="stat-value">${stats.mmItems.toLocaleString()}</span></div>
        <div class="stat-row"><span class="stat-label">Sales Recorded</span><span class="stat-value gold">${stats.mmSales.toLocaleString()}</span></div>
        <div class="stat-row"><span class="stat-label">Users Tracked</span><span class="stat-value">${stats.users.toLocaleString()}</span></div>
        <div class="stat-row"><span class="stat-label">Last Sync</span><span class="stat-value">${relativeTime(sync?.mm?.finishedAt)}</span></div>
      </div>
      <div class="panel overview-sync-section overview-sync-ttc">
        <div class="panel-header"><span class="panel-title"><span class="icon-ttc"></span> TamrielTradeCentre</span></div>
        <div class="stat-row"><span class="stat-label">TTC Items</span><span class="stat-value">${stats.ttcItems.toLocaleString()}</span></div>
        <div class="stat-row"><span class="stat-label">Price Entries</span><span class="stat-value">${stats.ttcPrices.toLocaleString()}</span></div>
        <div class="stat-row"><span class="stat-label">TTC Version</span><span class="stat-value cyan ttc-version-link" onclick="showTtcVersionHistory()" title="Click to view version history" style="cursor:pointer;text-decoration:underline dotted;">v${stats.ttcVersion || '?'}</span></div>
        <div class="stat-row"><span class="stat-label">Last Sync</span><span class="stat-value">${relativeTime(sync?.ttc?.finishedAt)}</span></div>
      </div>
      <div class="panel overview-sync-section">
        <div class="panel-header"><span class="panel-title"><span class="icon-fissal"></span> Sync Attribution</span></div>
        <div class="stat-row"><span class="stat-label">Post</span><span class="stat-value">${escapeHtml(sync?.syncedBy?.post || '—')} · ${relativeTime(sync?.mm?.guilds?.post)}</span></div>
        <div class="stat-row"><span class="stat-label">Caravan</span><span class="stat-value">${escapeHtml(sync?.syncedBy?.caravan || '—')} · ${relativeTime(sync?.mm?.guilds?.caravan)}</span></div>
        <div class="stat-row"><span class="stat-label">Dealers</span><span class="stat-value">${escapeHtml(sync?.syncedBy?.dealers || '—')} · ${relativeTime(sync?.mm?.guilds?.dealers)}</span></div>
        <div class="stat-row"><span class="stat-label"><span class="icon-ttc"></span> TTC</span><span class="stat-value">${escapeHtml(sync?.syncedBy?.TTC || '—')}</span></div>
      </div>`;

    // Snapshot current values by label BEFORE replacing innerHTML, so we can
    // highlight which cells changed after the swap (seamless refresh UX).
    const prevSnapshot = alreadyRendered ? _snapshotOverviewValues() : null;

    // ── User-added widgets (Sprint 10.3) ──
    // Emit a .overview-category wrapper for each enabled widget. The body is
    // a host <div> filled by the widget's render() fn after innerHTML swap.
    const layout = getDashboardLayout();
    const addedWidgets = layout.added.filter(id => {
      const w = DASHBOARD_WIDGETS[id];
      if (!w) return false;
      if (w.requires === 'admin' && !currentUser?.isAdmin) return false;
      if (w.requires === 'login' && !currentUser) return false;
      return true;
    });
    const widgetsHtml = addedWidgets.map(id => {
      const w = DASHBOARD_WIDGETS[id];
      const secId = _widgetSectionId(id);
      return `
      <div class="overview-category ov-widget-section" data-section-id="${escapeHtml(secId)}">
        <div class="overview-category-label">▸ ${escapeHtml(w.icon ? w.icon + ' ' : '')}${escapeHtml(w.label)}</div>
        <div class="ov-widget-host" data-widget-id="${escapeHtml(id)}"></div>
      </div>`;
    }).join('');

    $content.innerHTML = `
      <!-- Personal Metrics -->
      <div class="overview-category" data-section-id="personal">
        <div class="overview-category-label">▸ Personal Metrics</div>
        <div class="panel">
          ${personalHtml || '<div class="empty-state">Login with Discord to see your personal stats.</div>'}
        </div>
      </div>

      ${recentSalesPanelHtml ? `
      <!-- My Recent Sales (Sprint 12 Phase 3) -->
      <div class="overview-category" data-section-id="recent-sales">
        <div class="overview-category-label">▸ My Recent Sales</div>
        ${recentSalesPanelHtml}
      </div>` : ''}

      <!-- Fissal Commentary -->
      <div class="overview-category" data-section-id="analytics">
        <div class="overview-category-label">▸ Personal Analytics</div>
        <div id="personal-analytics-container"></div>
      </div>

      <!-- Server Sync Data -->
      <div class="overview-category" data-section-id="sync">
        <div class="overview-category-label">▸ Server Sync Data</div>
        <div class="panel-grid">
          ${syncHtml}
        </div>
      </div>
      ${widgetsHtml}
    `;

    // Populate each user-added widget's host element.
    for (const id of addedWidgets) {
      const host = $content.querySelector(`.ov-widget-host[data-widget-id="${CSS.escape(id)}"]`);
      if (!host) continue;
      try { DASHBOARD_WIDGETS[id].render(host); }
      catch (err) { host.innerHTML = `<div class="error-msg">Widget failed to load: ${escapeHtml(err.message)}</div>`; }
    }

    // Sprint 9.1: apply user's saved dashboard layout (order, collapsed, hidden)
    applyDashboardLayout($content);

    // Sprint 12 Phase 3: paint first page of My Recent Sales + wire pagination
    if (recentSalesPanelHtml) _wireOverviewRecentSales();

    // On seamless re-renders, flash any stat values that changed so the user
    // can see data updating without the whole page blanking.
    if (alreadyRendered) _flashOverviewChangedValues(prevSnapshot);

    // ── Populate Personal Analytics dashboard (async, non-blocking) ──
    renderPersonalAnalytics();

    // TTC Version History Popup
    window.showTtcVersionHistory = async function() {
      const existing = document.getElementById('ttc-version-popup');
      if (existing) { existing.remove(); return; }
      const popup = document.createElement('div');
      popup.id = 'ttc-version-popup';
      popup.className = 'ttc-version-popup';
      popup.innerHTML = `
        <div class="ttc-version-popup-inner">
          <div class="ttc-version-popup-header">
            <span class="panel-title">📦 TTC Version History</span>
            <button class="btn btn-sm" onclick="document.getElementById('ttc-version-popup')?.remove()">✕</button>
          </div>
          <div id="ttc-version-popup-content"><div class="loading-spinner">Loading...</div></div>
        </div>`;
      document.body.appendChild(popup);
      popup.addEventListener('click', e => { if (e.target === popup) popup.remove(); });
      try {
        const data = await api('/api/ttc/versions');
        const contentEl = document.getElementById('ttc-version-popup-content');
        if (!contentEl) return;
        if (!data.versions?.length) {
          contentEl.innerHTML = '<div class="empty-state" style="padding:1rem;">No version history recorded yet.</div>';
          return;
        }
        let html = '<div class="ttc-version-list">';
        for (const v of data.versions) {
          const syncerNames = v.syncers?.map(s => escapeHtml(s.name)).join(', ') || escapeHtml(v.syncer_name || '—');
          html += `
            <div class="ttc-version-entry">
              <span class="ttc-version-num">v${escapeHtml(String(v.version))}</span>
              <span class="ttc-version-date">${relativeTime(v.first_synced_at)}</span>
              <span class="ttc-version-syncer">${syncerNames}</span>
            </div>`;
        }
        html += '</div>';
        contentEl.innerHTML = html;
      } catch {
        const contentEl = document.getElementById('ttc-version-popup-content');
        if (contentEl) contentEl.innerHTML = '<span class="text-dim">Failed to load version history.</span>';
      }
    };

  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load overview: ${escapeHtml(err.message)}</div>`;
  }
}

function renderLoginPrompt() {
  $content.innerHTML = `
    <div class="panel">
      <div class="empty-state">
        <p>Login with Discord to access your personal dashboard.</p>
        <br>
        <a href="/auth/login" class="btn btn-login">Login with Discord</a>
      </div>
    </div>
  `;
}

// ── Personal Analytics Dashboard ─────────────────────────────
async function renderPersonalAnalytics() {
  const container = document.getElementById('personal-analytics-container');
  if (!container) return;

  // Not logged in — prompt
  if (!currentUser) {
    container.innerHTML = '<div class="panel"><div class="empty-state">Login with Discord to view your analytics.</div></div>';
    return;
  }

  // Internal state
  let paFilterMode = 'tradeweek'; // 'tradeweek' | 'calendar' | 'alltime'
  let paWeeksAgo = 0;
  let paCalStart = '';
  let paCalEnd = '';
  let paGuildFilter = '';  // guild name string or empty
  let paItemFilter = '';
  let paLastData = null;   // cache last API response

  // ── Build shell ──
  container.innerHTML = `
    <div class="panel analytics-dashboard">
      <div class="panel-header" style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:0.5rem;">
        <span class="panel-title">📊 Personal Analytics</span>
        <button class="btn btn-sm analytics-ask-fissal-btn" id="pa-ask-fissal-btn" title="Ask Fissal to analyse this data">🐱 Ask Fissal</button>
      </div>
      <div class="filter-bar analytics-filter-bar">
        <div class="filter-group">
          <label>Time Range</label>
          <div class="term-toggle-group">
            <button class="term-toggle-btn active" data-pa-fm="tradeweek">Trade-Week</button>
            <button class="term-toggle-btn" data-pa-fm="calendar">Calendar</button>
            <button class="term-toggle-btn" data-pa-fm="alltime">All Time</button>
          </div>
        </div>
        <div id="pa-tw-section">
          ${weekPicker('pa-week-select', 0)}
        </div>
        <div id="pa-cal-section" style="display:none;">
          ${dateRangePicker('pa-cal-start', 'pa-cal-end')}
        </div>
        <div class="filter-group">
          <label>Guild</label>
          <select class="form-input" id="pa-guild-select" style="min-width:130px;">
            <option value="">All Redfur Guilds</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Item</label>
          <input type="text" class="form-input pa-item-search" id="pa-item-input" placeholder="Search items..." autocomplete="off">
        </div>
      </div>

      <div id="pa-data-area" class="analytics-data-area">
        <div class="analytics-summary-row">
          <div class="analytics-stat-card"><span class="value gold" id="pa-total-gold">—</span><span class="label">Total Revenue</span></div>
          <div class="analytics-stat-card"><span class="value" id="pa-total-sales">—</span><span class="label">Transactions</span></div>
          <div class="analytics-stat-card"><span class="value" id="pa-avg-sale">—</span><span class="label">Avg Sale Price</span></div>
          <div class="analytics-stat-card"><span class="value" id="pa-top-item">—</span><span class="label">Top Item (by gold)</span></div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Revenue by Guild</div>
          <div id="pa-guild-chart" class="bar-chart"></div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Top Items by Volume</div>
          <div id="pa-items-chart" class="bar-chart"></div>
        </div>

        <div class="analytics-section">
          <div class="analytics-section-title">Sales Timeline</div>
          <div id="pa-trend-chart" class="analytics-trend"></div>
        </div>
      </div>

      <div id="pa-empty-overlay" class="analytics-empty-overlay">
        <div class="analytics-empty-message empty-state-fissal">
          <span class="fissal-emote">🐱</span>
          <div class="fissal-action">*Fissal tilts their head curiously*</div>
          <div class="fissal-line">"No sales found for this frequency yet! But do not let your claws dull—you can still run Price Checks or view the Leaderboards."</div>
          <span class="fissal-attribution">— Fissal</span>
        </div>
      </div>
    </div>
  `;

  // ── Wire filter events ──
  const toggleBtns = container.querySelectorAll('[data-pa-fm]');
  const twSection = document.getElementById('pa-tw-section');
  const calSection = document.getElementById('pa-cal-section');
  const weekSelect = document.getElementById('pa-week-select');
  const calStartIn = document.getElementById('pa-cal-start');
  const calEndIn = document.getElementById('pa-cal-end');
  const guildSelect = document.getElementById('pa-guild-select');
  const itemInput = document.getElementById('pa-item-input');

  toggleBtns.forEach(btn => btn.addEventListener('click', () => {
    toggleBtns.forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    paFilterMode = btn.dataset.paFm;
    twSection.style.display = paFilterMode === 'tradeweek' ? '' : 'none';
    calSection.style.display = paFilterMode === 'calendar' ? 'flex' : 'none';
    paLastData = null; // force refetch on mode change
    loadPA();
  }));

  if (weekSelect) weekSelect.addEventListener('change', () => {
    paWeeksAgo = parseInt(weekSelect.value, 10);
    paLastData = null;
    loadPA();
  });

  if (calStartIn) calStartIn.addEventListener('change', () => { paCalStart = calStartIn.value; paLastData = null; loadPA(); });
  if (calEndIn) calEndIn.addEventListener('change', () => { paCalEnd = calEndIn.value; paLastData = null; loadPA(); });

  if (guildSelect) guildSelect.addEventListener('change', () => { paGuildFilter = guildSelect.value; loadPA(); });

  let itemDebounce = null;
  if (itemInput) itemInput.addEventListener('input', () => {
    clearTimeout(itemDebounce);
    itemDebounce = setTimeout(() => { paItemFilter = itemInput.value.trim(); loadPA(); }, 300);
  });

  // ── Data loading ──
  async function fetchSalesData() {
    let apiWeeks;
    if (paFilterMode === 'tradeweek') apiWeeks = paWeeksAgo;
    else apiWeeks = -1; // all-time or calendar (calendar filters client-side)
    return api(`/api/me/sales?weeksAgo=${apiWeeks}`);
  }

  async function loadPA(forceAllTime) {
    if (forceAllTime) {
      paFilterMode = 'alltime';
      toggleBtns.forEach(b => {
        b.classList.toggle('active', b.dataset.paFm === 'alltime');
      });
      twSection.style.display = 'none';
      calSection.style.display = 'none';
      paLastData = null;
    }

    try {
      const data = paLastData || await fetchSalesData();
      paLastData = data;
      if (!data || !data.sellers) { showEmpty(); return; }

      // Flatten sales
      let allSales = [];
      const guildSet = new Set();
      for (const seller of data.sellers) {
        const gn = seller.guildName || 'Unknown';
        guildSet.add(gn);
        for (const sale of (seller.sales || [])) {
          allSales.push({ ...sale, guildName: sale.guildName || gn });
        }
      }

      // Populate guild dropdown (once per fetch)
      if (guildSelect && guildSelect.options.length <= 1) {
        for (const g of guildSet) {
          const opt = document.createElement('option');
          opt.value = g;
          opt.textContent = g;
          guildSelect.appendChild(opt);
        }
      }

      // Apply guild filter
      if (paGuildFilter) {
        allSales = allSales.filter(s => s.guildName === paGuildFilter);
      }

      // Apply item filter
      if (paItemFilter) {
        const q = paItemFilter.toLowerCase();
        allSales = allSales.filter(s => (s.itemName || s.itemDesc || '').toLowerCase().includes(q));
      }

      // Apply calendar date range filter
      if (paFilterMode === 'calendar' && (paCalStart || paCalEnd)) {
        const startTs = paCalStart ? new Date(paCalStart + 'T00:00:00').getTime() / 1000 : 0;
        const endTs = paCalEnd ? new Date(paCalEnd + 'T23:59:59').getTime() / 1000 : Infinity;
        allSales = allSales.filter(s => {
          const t = s.timestamp || 0;
          return t >= startTs && t <= endTs;
        });
      }

      // If no sales and not already "all time", auto-switch
      if (allSales.length === 0 && paFilterMode !== 'alltime' && !paGuildFilter && !paItemFilter) {
        return loadPA(true);
      }

      if (allSales.length === 0) { showEmpty(); return; }
      showData(allSales, guildSet);

    } catch (err) {
      console.error('[Analytics]', err);
      const dataArea = document.getElementById('pa-data-area');
      if (dataArea) dataArea.innerHTML = `<div class="empty-state">Failed to load analytics: ${escapeHtml(err.message)}</div>`;
    }
  }

  function showEmpty() {
    const dataArea = document.getElementById('pa-data-area');
    const overlay = document.getElementById('pa-empty-overlay');
    if (dataArea) dataArea.classList.add('blurred');
    if (overlay) { overlay.style.display = 'flex'; overlay.classList.add('visible'); }
    // Set placeholder values
    setStatText('pa-total-gold', 'N/A');
    setStatText('pa-total-sales', '0');
    setStatText('pa-avg-sale', 'N/A');
    setStatText('pa-top-item', 'N/A');
    const guildChart = document.getElementById('pa-guild-chart');
    const itemsChart = document.getElementById('pa-items-chart');
    const trendChart = document.getElementById('pa-trend-chart');
    if (guildChart) guildChart.innerHTML = '<div class="empty-state" style="padding:0.5rem">No data</div>';
    if (itemsChart) itemsChart.innerHTML = '<div class="empty-state" style="padding:0.5rem">No data</div>';
    if (trendChart) trendChart.innerHTML = '<div class="empty-state" style="padding:0.5rem">No data</div>';
  }

  function showData(allSales, guildSet) {
    const dataArea = document.getElementById('pa-data-area');
    const overlay = document.getElementById('pa-empty-overlay');
    if (dataArea) dataArea.classList.remove('blurred');
    if (overlay) { overlay.style.display = 'none'; overlay.classList.remove('visible'); }

    // ── Compute metrics ──
    const totalGold = allSales.reduce((s, x) => s + (x.price || 0), 0);
    const totalSales = allSales.length;
    const avgSale = totalSales > 0 ? Math.round(totalGold / totalSales) : 0;

    // Top item by gold
    const itemGoldMap = {};
    const itemCountMap = {};
    for (const s of allSales) {
      const name = s.itemName || s.itemDesc || 'Unknown';
      itemGoldMap[name] = (itemGoldMap[name] || 0) + (s.price || 0);
      itemCountMap[name] = (itemCountMap[name] || 0) + (s.quant || 1);
    }
    const topItemName = Object.entries(itemGoldMap).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';

    // ── Populate summary cards ──
    setStatText('pa-total-gold', goldText(totalGold));
    setStatText('pa-total-sales', totalSales.toLocaleString());
    setStatText('pa-avg-sale', goldText(avgSale));
    setStatText('pa-top-item', topItemName.length > 24 ? topItemName.slice(0, 22) + '…' : topItemName);

    // ── Guild Revenue bar chart ──
    const guildTotals = {};
    for (const s of allSales) {
      const gn = s.guildName || 'Unknown';
      guildTotals[gn] = (guildTotals[gn] || 0) + (s.price || 0);
    }
    const guildChart = document.getElementById('pa-guild-chart');
    if (guildChart) {
      const maxGuildGold = Math.max(...Object.values(guildTotals), 1);
      let html = '';
      for (const [gn, total] of Object.entries(guildTotals).sort((a, b) => b[1] - a[1])) {
        const pct = Math.max(2, (total / maxGuildGold) * 100);
        html += `
          <div class="bar-row">
            <span class="bar-label">${escapeHtml(gn)}</span>
            <div class="bar-track"><div class="bar-fill" style="width:${pct}%"></div></div>
            <span class="bar-value">${gold(total)}</span>
          </div>`;
      }
      guildChart.innerHTML = html || '<div class="empty-state" style="padding:0.5rem">No guild data</div>';
    }

    // ── Top Items by Volume bar chart (top 10) ──
    const itemsSorted = Object.entries(itemCountMap).sort((a, b) => b[1] - a[1]).slice(0, 10);
    const itemsChart = document.getElementById('pa-items-chart');
    if (itemsChart) {
      const maxItemVol = Math.max(...itemsSorted.map(x => x[1]), 1);
      let html = '';
      for (const [name, count] of itemsSorted) {
        const pct = Math.max(2, (count / maxItemVol) * 100);
        const goldVal = itemGoldMap[name] || 0;
        html += `
          <div class="bar-row">
            <span class="bar-label">${escapeHtml(name.length > 30 ? name.slice(0, 28) + '…' : name)}</span>
            <div class="bar-track"><div class="bar-fill" style="width:${pct}%"></div></div>
            <span class="bar-value">${count.toLocaleString()} sold · ${gold(goldVal)}</span>
          </div>`;
      }
      itemsChart.innerHTML = html || '<div class="empty-state" style="padding:0.5rem">No item data</div>';
    }

    // ── Sales Timeline (daily or weekly buckets) ──
    const trendChart = document.getElementById('pa-trend-chart');
    if (trendChart) {
      // Determine if daily or weekly buckets
      const timestamps = allSales.map(s => s.timestamp || 0).filter(t => t > 0);
      if (timestamps.length === 0) {
        trendChart.innerHTML = '<div class="empty-state" style="padding:0.5rem">No timeline data</div>';
      } else {
        const minTs = Math.min(...timestamps);
        const maxTs = Math.max(...timestamps);
        const rangeDays = (maxTs - minTs) / 86400;
        const useWeekly = rangeDays > 30;

        const buckets = {};
        for (const s of allSales) {
          const t = s.timestamp || 0;
          if (t <= 0) continue;
          const d = new Date(t * 1000);
          let key;
          if (useWeekly) {
            // ISO week bucket
            const jan1 = new Date(d.getFullYear(), 0, 1);
            const weekNum = Math.ceil(((d - jan1) / 86400000 + jan1.getDay() + 1) / 7);
            key = d.getFullYear() + '-W' + String(weekNum).padStart(2, '0');
          } else {
            key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
          }
          if (!buckets[key]) buckets[key] = { gold: 0, count: 0 };
          buckets[key].gold += s.price || 0;
          buckets[key].count++;
        }

        const sorted = Object.entries(buckets).sort((a, b) => a[0].localeCompare(b[0]));
        // Limit to last 30 buckets
        const display = sorted.slice(-30);
        const maxBucketGold = Math.max(...display.map(x => x[1].gold), 1);

        let html = '';
        for (const [label, bucket] of display) {
          const pct = Math.max(3, (bucket.gold / maxBucketGold) * 100);
          // Short label
          let shortLabel;
          if (useWeekly) {
            shortLabel = label.split('-W')[1] ? 'W' + label.split('-W')[1] : label;
          } else {
            const parts = label.split('-');
            shortLabel = parts[1] + '/' + parts[2];
          }
          html += `
            <div class="analytics-trend-bar-wrap" title="${label}: ${goldText(bucket.gold)} (${bucket.count} sale${bucket.count !== 1 ? 's' : ''})">
              <div class="analytics-trend-bar" style="height:${pct}%"></div>
              <span class="analytics-trend-bar-label">${shortLabel}</span>
            </div>`;
        }
        trendChart.innerHTML = html;
      }
    }
  }

  function setStatText(id, text) {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  }

  // ── Initial load ──
  await loadPA();

  // ── Ask Fissal button ──
  const paAskBtn = document.getElementById('pa-ask-fissal-btn');
  if (paAskBtn) {
    paAskBtn.addEventListener('click', () => {
      // Build a one-sentence context summary from whatever is currently visible
      const data = paLastData;
      if (!data) {
        fissalToast('Load some data first, then ask Fissal!');
        return;
      }
      const allSalesFlat = (data.sellers || []).flatMap(s => s.sales || []);
      const totalGold = allSalesFlat.reduce((a, s) => a + (s.price || 0), 0);
      const totalSales = allSalesFlat.length;
      const itemGoldMap = {};
      for (const s of allSalesFlat) {
        const n = s.itemName || s.itemDesc || 'Unknown';
        itemGoldMap[n] = (itemGoldMap[n] || 0) + (s.price || 0);
      }
      const topItem = Object.entries(itemGoldMap).sort((a, b) => b[1] - a[1])[0]?.[0] || 'unknown';
      const guildLabel = paGuildFilter ? ` (guild: ${paGuildFilter})` : '';
      const prompt = `My personal analytics show ${totalSales} sales totalling ${totalGold.toLocaleString()} gold${guildLabel}. My top-earning item was "${topItem}". Can you give me a brief trading analysis and one or two tips to improve my sales?`;

      // Open the floating bubble and pre-fill the message
      const chatFloat = document.getElementById('fissal-chat-float');
      const inputEl = document.getElementById('fissal-chat-input');
      if (chatFloat && inputEl) {
        // Trigger greeting if first open
        document.getElementById('fissal-bubble')?.click();
        setTimeout(() => {
          inputEl.value = prompt;
          inputEl.focus();
        }, 80);
      } else {
        fissalToast('Fissal chat is not available. Make sure you are logged in.');
      }
    });
  }
}

// ── My Sales ─────────────────────────────────────────────────
async function renderMySales(opts = {}) {
  if (!currentUser) return renderLoginPrompt();
  const { seamless = false } = opts;
  // Sprint 10.7: skip the spinner on SSE-triggered background refreshes so
  // the old table stays visible while the new one loads.
  const alreadyRendered = !!$content.querySelector('.panel, .data-table');
  const prevSnap = (alreadyRendered && seamless) ? _snapshotLeaderboardValues() : null;
  if (!alreadyRendered) $content.innerHTML = '<div class="loading-spinner">📜 Unrolling the merchant scrolls...</div>';
  else if (!seamless) $content.classList.add('content-loading');

  const params = new URLSearchParams(window.location.hash.split('?')[1]);
  const filterMode = params.get('fm') || 'none'; // 'none', 'tradeweek', 'calendar'
  const weeksAgo = parseInt(params.get('w')) || 0;
  const calStart = params.get('cs') || '';
  const calEnd = params.get('ce') || '';
  const calTimeStart = params.get('ts') || '';
  const calTimeEnd = params.get('te') || '';
  const guildFilter = params.get('guild') || '';
  const itemFilter = params.get('item') || '';
  const sortBy = params.get('sort') || 'gold-desc';

  try {
    // Fetch sales — if no filter, fetch all-time (weeksAgo=-1 means no week filter)
    const apiWeeks = filterMode === 'tradeweek' ? weeksAgo : -1;
    const data = await api(`/api/me/sales?weeksAgo=${apiWeeks}`);
    if (!data) return;

    if (data.message) {
      $content.innerHTML = `
        <div class="panel">
          <div class="panel-header"><span class="panel-title">My Sales</span></div>
          <div class="empty-state">${escapeHtml(data.message)}<br><br>
            <a href="#/settings" class="btn btn-primary">Link ESO Tag</a>
          </div>
        </div>`;
      return;
    }

    // Client-side sub-filters
    let filtered = data.sellers;
    if (guildFilter) {
      filtered = filtered.filter(s => s.guildName?.toLowerCase().includes(guildFilter.toLowerCase()));
    }
    let allSales = [];
    for (const seller of filtered) {
      for (const sale of seller.sales) allSales.push(sale);
    }
    if (itemFilter) {
      allSales = allSales.filter(s => s.itemName?.toLowerCase().includes(itemFilter.toLowerCase()));
    }

    // Apply calendar filter client-side
    if (filterMode === 'calendar' && (calStart || calEnd)) {
      let startTs = calStart ? Math.floor(new Date(calStart).getTime() / 1000) : 0;
      let endTs = calEnd ? Math.floor(new Date(calEnd + 'T23:59:59').getTime() / 1000) : Infinity;
      // Single-day time range
      if (calStart && calStart === calEnd && calTimeStart) {
        startTs = Math.floor(new Date(calStart + 'T' + calTimeStart).getTime() / 1000);
        endTs = calTimeEnd ? Math.floor(new Date(calEnd + 'T' + calTimeEnd).getTime() / 1000) : endTs;
      }
      allSales = allSales.filter(s => s.timestamp >= startTs && s.timestamp <= endTs);
    }

    // Always sort most recent first by default, then apply user sort
    allSales.sort((a, b) => b.timestamp - a.timestamp);

    const totalProfit = allSales.reduce((s, x) => s + x.price, 0);
    const totalCount = allSales.length;

    // Compute metrics
    const itemCounts = {};
    const itemTimestamps = {};
    for (const sale of allSales) {
      const name = sale.itemName || 'Unknown';
      itemCounts[name] = (itemCounts[name] || 0) + sale.quant;
      if (!itemTimestamps[name]) itemTimestamps[name] = [];
      itemTimestamps[name].push(sale.timestamp);
    }

    let mostSoldItem = null;
    let mostSoldQty = 0;
    for (const [name, qty] of Object.entries(itemCounts)) {
      if (qty > mostSoldQty) { mostSoldQty = qty; mostSoldItem = name; }
    }

    // Additional metrics: newest, oldest, biggest sale
    const newestSale = allSales.length ? allSales.reduce((a, b) => a.timestamp > b.timestamp ? a : b) : null;
    const oldestSale = allSales.length ? allSales.reduce((a, b) => a.timestamp < b.timestamp ? a : b) : null;
    const biggestSale = allSales.length ? allSales.reduce((a, b) => a.price > b.price ? a : b) : null;

    // Build guild breakdown chart data
    const guildTotals = {};
    for (const seller of data.sellers) {
      const gn = seller.guildName || 'Unknown';
      guildTotals[gn] = (guildTotals[gn] || 0) + seller.sales.reduce((s, x) => s + x.price, 0);
    }
    const maxGuildGold = Math.max(...Object.values(guildTotals), 1);

    // Apply user sort override
    if (sortBy.startsWith('gold')) {
      allSales.sort((a, b) => sortBy === 'gold-asc' ? a.price - b.price : b.price - a.price);
    } else if (sortBy.startsWith('name')) {
      allSales.sort((a, b) => sortBy === 'name-asc'
        ? (a.itemName || '').localeCompare(b.itemName || '')
        : (b.itemName || '').localeCompare(a.itemName || ''));
    }

    let salesHtml = '';
    for (const sale of allSales) {
      salesHtml += `<tr>
        ${itemCell(sale.itemName)}
        <td class="text-right text-mono text-gold">${gold(sale.price)}</td>
        <td class="text-right text-mono">${sale.quant}</td>
        ${guildCell(sale.guildName)}
        <td class="text-mono">${shortDate(sale.timestamp)}</td>
      </tr>`;
    }

    // Guild bar chart
    let chartHtml = '<div class="bar-chart">';
    for (const [gn, total] of Object.entries(guildTotals)) {
      const pct = Math.max(2, (total / maxGuildGold) * 100);
      chartHtml += `
        <div class="bar-row">
          <span class="bar-label">${escapeHtml(gn)}</span>
          <div class="bar-track"><div class="bar-fill" style="width:${pct}%" title="${goldText(total)}"></div></div>
          <span class="bar-value">${gold(total)}</span>
        </div>`;
    }
    chartHtml += '</div>';

    // Filter mode label
    let filterLabel = 'All Time';
    if (filterMode === 'tradeweek') {
      filterLabel = weekLabels[weeksAgo]?.label || (weeksAgo === 0 ? 'Current Week' : `${weeksAgo} weeks ago`);
    } else if (filterMode === 'calendar' && calStart) {
      filterLabel = calStart === calEnd ? calStart : `${calStart} → ${calEnd || 'now'}`;
    }

    // Check if single-day selected for time range reveal
    const isSingleDay = filterMode === 'calendar' && calStart && calStart === calEnd;

    $content.innerHTML = `
      <div class="panel">
        <div class="panel-header">
          <span class="panel-title">My Sales</span>
          <span class="stat-value" style="font-size:var(--fs-xs);color:var(--t-dim);">${escapeHtml(filterLabel)}</span>
        </div>
        <div class="filter-bar">
          <!-- Time/date filter group -->
          <div class="filter-time-container">
            <div class="filter-group">
              <label>Time Filter</label>
              <div class="term-toggle-group">
                <button class="term-toggle-btn ${filterMode === 'none' ? 'active' : ''}" data-fm="none">None</button>
                <button class="term-toggle-btn ${filterMode === 'tradeweek' ? 'active' : ''}" data-fm="tradeweek">Trade-Week</button>
                <button class="term-toggle-btn ${filterMode === 'calendar' ? 'active' : ''}" data-fm="calendar">Calendar</button>
              </div>
            </div>

            <!-- Trade-week picker (shown if mode = tradeweek) -->
            <div id="ms-tw-section" style="display:${filterMode === 'tradeweek' ? 'flex' : 'none'};gap:0.5rem;">
              <div class="filter-group">
                <label>FILTER TRADE WEEK</label>
                <select class="form-input" id="my-week" style="min-width:130px;">
                  ${weekLabels.length > 0
                    ? weekLabels.map((w, i) =>
                        `<option value="${i}" ${weeksAgo === i ? 'selected' : ''}>${i === 0 ? 'Current' : w.label}</option>`
                      ).join('')
                    : `<option value="0" ${weeksAgo === 0 ? 'selected' : ''}>Current</option>
                       ${Array.from({length: 11}, (_, i) => i + 1).map(w =>
                         `<option value="${w}" ${weeksAgo === w ? 'selected' : ''}>-${w} week${w > 1 ? 's' : ''}</option>`
                       ).join('')}`}
                </select>
              </div>
            </div>

            <!-- Calendar section (shown if mode = calendar) -->
            <div id="ms-cal-section" style="display:${filterMode === 'calendar' ? 'flex' : 'none'};gap:0.5rem;flex-wrap:wrap;align-items:flex-end;">
              <div class="filter-group">
                <label>From</label>
                <input type="date" class="form-input crt-cal-input" id="ms-cal-start" value="${calStart}">
              </div>
              <div class="filter-group">
                <label>To</label>
                <input type="date" class="form-input crt-cal-input" id="ms-cal-end" value="${calEnd}">
              </div>
              <div id="ms-time-range" style="display:${isSingleDay ? 'flex' : 'none'};gap:0.5rem;align-items:flex-end;">
                <div class="filter-group">
                  <label>From Time</label>
                  <input type="time" class="form-input crt-cal-input" id="ms-time-start" value="${calTimeStart}">
                </div>
                <div class="filter-group">
                  <label>To Time</label>
                  <input type="time" class="form-input crt-cal-input" id="ms-time-end" value="${calTimeEnd}">
                </div>
              </div>
            </div>

            <div class="filter-group" style="justify-content:flex-end;align-self:flex-end;">
              <button class="btn btn-primary" id="my-filter-btn" style="min-width:90px;height:2rem;">Apply</button>
            </div>
          </div>

          <div class="filter-group">
            <label>Guild</label>
            <select class="form-input" id="my-guild" style="min-width:140px;">${guildOptions()}</select>
          </div>
          <div class="filter-group">
            <label>Item</label>
            <input type="text" class="form-input" id="my-item" placeholder="Search items..." value="${escapeHtml(itemFilter)}" style="min-width:140px;">
          </div>
          ${sortToggle('my-sort', sortBy)}
        </div>

        <div class="panel-grid" style="margin-bottom: 0.75rem;">
          <div class="stat-big"><span class="value">${gold(totalProfit)}</span><span class="label">Total Revenue</span></div>
          <div class="stat-big"><span class="value">${totalCount.toLocaleString()}</span><span class="label">Total Sales</span></div>
          ${newestSale ? `<div class="stat-big"><span class="value" style="font-size:var(--fs-sm);color:var(--t-green);">${gold(newestSale.price)} <span style="opacity:0.6;">×${newestSale.quant}</span></span><span class="label">Most Recent — ${shortDate(newestSale.timestamp)}</span><span class="stat-sub" style="font-size:var(--fs-xs);color:var(--t-dim);margin-top:0.15rem;">${itemLink(newestSale.itemName || 'Unknown')}</span></div>` : ''}
          ${oldestSale ? `<div class="stat-big"><span class="value" style="font-size:var(--fs-sm);color:var(--t-dim);">${gold(oldestSale.price)} <span style="opacity:0.6;">×${oldestSale.quant}</span></span><span class="label">Oldest — ${shortDate(oldestSale.timestamp)}</span><span class="stat-sub" style="font-size:var(--fs-xs);color:var(--t-dim);margin-top:0.15rem;">${itemLink(oldestSale.itemName || 'Unknown')}</span></div>` : ''}
          ${biggestSale ? `<div class="stat-big"><span class="value" style="font-size:var(--fs-sm);color:var(--gold-bright);">${gold(biggestSale.price)} <span style="opacity:0.6;">×${biggestSale.quant}</span></span><span class="label">Biggest Sale</span><span class="stat-sub" style="font-size:var(--fs-xs);color:var(--t-dim);margin-top:0.15rem;">${itemLink(biggestSale.itemName || 'Unknown')} — ${shortDate(biggestSale.timestamp)}</span></div>` : ''}
          ${mostSoldItem ? `<div class="stat-big"><span class="value" style="font-size:var(--fs-sm);color:var(--t-red-orange);">${itemLink(mostSoldItem)}</span><span class="label">Highest Volume (${mostSoldQty})</span></div>` : ''}
        </div>

        <div class="screen-divider"></div>
        <div class="panel-header"><span class="panel-title">Revenue by Guild</span></div>
        ${chartHtml}

        <div class="screen-divider"></div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Item</th><th class="text-right">Price</th><th class="text-right">Qty</th><th>Guild</th><th>Date</th></tr></thead>
            <tbody>${salesHtml || '<tr><td colspan="5" class="empty-state">No sales found.</td></tr>'}</tbody>
          </table>
        </div>
      </div>`;

    // Set selected guild
    if (guildFilter) {
      const sel = document.getElementById('my-guild');
      for (const opt of sel.options) {
        if (opt.textContent.toLowerCase().includes(guildFilter.toLowerCase())) opt.selected = true;
      }
    }

    // Wire filter mode toggle
    document.querySelectorAll('.term-toggle-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.term-toggle-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const fm = btn.dataset.fm;
        document.getElementById('ms-tw-section').style.display = fm === 'tradeweek' ? 'flex' : 'none';
        document.getElementById('ms-cal-section').style.display = fm === 'calendar' ? 'flex' : 'none';
      });
    });

    // Wire calendar single-day detection → reveal time range
    const calStartEl = document.getElementById('ms-cal-start');
    const calEndEl = document.getElementById('ms-cal-end');
    function checkSingleDay() {
      const s = calStartEl?.value;
      const e = calEndEl?.value;
      const timeRange = document.getElementById('ms-time-range');
      if (timeRange) timeRange.style.display = (s && s === e) ? 'flex' : 'none';
    }
    if (calStartEl) calStartEl.addEventListener('change', checkSingleDay);
    if (calEndEl) calEndEl.addEventListener('change', checkSingleDay);

    // Wire autocomplete on item filter using known items from this page's sales data
    const allItemNames = [...new Set(allSales.map(s => s.itemName).filter(Boolean))].sort();
    attachAutocomplete(document.getElementById('my-item'), (q) =>
      allItemNames.filter(n => n.toLowerCase().includes(q))
    );

    document.getElementById('my-filter-btn').addEventListener('click', () => {
      const activeFm = document.querySelector('.term-toggle-btn.active')?.dataset.fm || 'none';
      const w = document.getElementById('my-week')?.value || '0';
      const g = document.getElementById('my-guild').value;
      const it = document.getElementById('my-item').value.trim();
      const s = document.getElementById('my-sort').value;
      const cs = document.getElementById('ms-cal-start')?.value || '';
      const ce = document.getElementById('ms-cal-end')?.value || '';
      const tStart = document.getElementById('ms-time-start')?.value || '';
      const tEnd = document.getElementById('ms-time-end')?.value || '';

      let hash = `#/sales?fm=${activeFm}`;
      if (activeFm === 'tradeweek') hash += `&w=${w}`;
      if (activeFm === 'calendar') {
        if (cs) hash += `&cs=${cs}`;
        if (ce) hash += `&ce=${ce}`;
        if (cs === ce && tStart) hash += `&ts=${tStart}`;
        if (cs === ce && tEnd) hash += `&te=${tEnd}`;
      }
      if (g) hash += `&guild=${encodeURIComponent(g)}`;
      if (it) hash += `&item=${encodeURIComponent(it)}`;
      if (s !== 'gold-desc') hash += `&sort=${s}`;
      navigate(hash);
      renderMySales();
    });
    // Sprint 10.7: seamless flash of changed cells after swap
    $content.classList.remove('content-loading');
    if (prevSnap) _flashLeaderboardChangedValues(prevSnap);
  } catch (err) {
    $content.classList.remove('content-loading');
    $content.innerHTML = `<div class="error-msg">Failed to load sales: ${escapeHtml(err.message)}</div>`;
  }
}

// ── Seller Leaderboard ───────────────────────────────────────
async function renderLeaderboard(opts = {}) {
  const { seamless = false } = opts;
  // Sprint 10.7 seamless-refresh: on a background SSE-triggered refresh,
  // keep the existing DOM visible and only flash the cells that changed
  // once the new content is swapped in. First-visit path unchanged.
  const alreadyRendered = !!$content.querySelector('.panel');
  const prevSnap = (alreadyRendered && seamless) ? _snapshotLeaderboardValues() : null;
  if (!alreadyRendered) $content.innerHTML = '<div class="loading-spinner">👑 Tallying the gold...</div>';
  else if (!seamless) $content.classList.add('content-loading');

  const params = new URLSearchParams(window.location.hash.split('?')[1]);
  const weeksAgo = parseInt(params.get('w')) || 0;
  const guildFilter = params.get('guild') || '';
  const sortBy = params.get('sort') || 'gold-desc';

  try {
    let url = `/api/seller-leaderboard?weeksAgo=${weeksAgo}`;
    if (guildFilter) url += `&guild=${encodeURIComponent(guildFilter)}`;
    let data = await api(url);
    if (!data) return;

    // Fallback: if current week (weeksAgo=0) has no data, try last week
    let fallbackNotice = '';
    if (weeksAgo === 0 && (!data.sellers || data.sellers.length === 0)) {
      let fbUrl = `/api/seller-leaderboard?weeksAgo=1`;
      if (guildFilter) fbUrl += `&guild=${encodeURIComponent(guildFilter)}`;
      const fbData = await api(fbUrl);
      if (fbData && fbData.sellers && fbData.sellers.length > 0) {
        data = fbData;
        fallbackNotice = `<div class="fallback-notice">No current-week data yet — showing last week's leaderboard.</div>`;
      }
    }

    // Client-side sort
    let sellers = sortData(data.sellers, sortBy);

    // Honors — with enhanced formatting (line breaks, bold, icons)
    const h = data.honors;
    let honorsHtml = '';
    if (h && data.sellers.length > 0) {
      honorsHtml = `<div class="honors-grid">`;
      if (h.highestValueItem?.itemIcon) cacheItemIcon(h.highestValueItem.itemName, h.highestValueItem.itemIcon);
      if (h.highestValueItem) {
        const sellerGuildCls = getGuildColorClass([]); // We don't have guild data for honors sellers yet
        honorsHtml += `
          <div class="honor-badge honor-gold lb-honor-card" title="The Heist — biggest single sale this week. Reward for landing one big flip.">
            <div class="lb-honor-label">The Heist <span class="honor-info-badge" title="The Heist — biggest single sale this week. Reward for landing one big flip.">?</span></div>
            <div class="lb-honor-seller clickable-tag" data-user="${escapeHtml(h.highestValueItem.sellerName)}">${escapeHtml(h.highestValueItem.sellerName)}</div>
            <div class="lb-honor-item">${getItemIconHtml(h.highestValueItem.itemName)} <span class="clickable-item" data-item="${escapeHtml(h.highestValueItem.itemName)}">${escapeHtml(h.highestValueItem.itemName)}</span></div>
            <div class="lb-honor-value text-gold">${gold(h.highestValueItem.price)}</div>
          </div>`;
      }
      if (h.highestSalesCount) {
        honorsHtml += `
          <div class="honor-badge honor-green lb-honor-card" title="The Auctioneer — highest transaction count this week. Consistency over size.">
            <div class="lb-honor-label">The Auctioneer <span class="honor-info-badge" title="The Auctioneer — highest transaction count this week. Consistency over size.">?</span></div>
            <div class="lb-honor-seller clickable-tag" data-user="${escapeHtml(h.highestSalesCount.sellerName)}">${escapeHtml(h.highestSalesCount.sellerName)}</div>
            <div class="lb-honor-value" style="color:var(--t-green);">${h.highestSalesCount.totalSales.toLocaleString()}</div>
            <div class="lb-honor-label">transactions</div>
          </div>`;
      }
      if (h.highestVolume) {
        honorsHtml += `
          <div class="honor-badge honor-cyan lb-honor-card" title="The Caravaneer — most items moved this week. Reward for moving stock in bulk.">
            <div class="lb-honor-label">The Caravaneer <span class="honor-info-badge" title="The Caravaneer — most items moved this week. Reward for moving stock in bulk.">?</span></div>
            <div class="lb-honor-seller clickable-tag" data-user="${escapeHtml(h.highestVolume.sellerName)}">${escapeHtml(h.highestVolume.sellerName)}</div>
            <div class="lb-honor-value" style="color:var(--t-red-orange);">${h.highestVolume.totalVolume.toLocaleString()}</div>
            <div class="lb-honor-label">items moved</div>
          </div>`;
      }
      if (h.vip) {
        const vipTip = 'VIP score blends total gold (25%), sales count (50%), and volume (25%), normalized against the top performer in each category. Sales count is weighted highest to reward consistent activity over one lucky big flip.';
        honorsHtml += `
          <div class="honor-badge honor-vip lb-honor-card" title="${escapeHtml(vipTip)}">
            <div class="lb-honor-label">The VIP <span class="honor-info-badge" title="VIP score blends total gold (25%), sales count (50%), and volume (25%), normalized against the top performer in each category. Sales count is weighted highest to reward consistent activity over one lucky big flip.">?</span></div>
            <div class="lb-honor-seller clickable-tag" data-user="${escapeHtml(h.vip.sellerName)}">${escapeHtml(h.vip.sellerName)}</div>
            <div class="lb-honor-value" style="color:#c084fc;">${h.vip.score}%</div>
            <div class="lb-honor-label">performance score</div>
          </div>`;
      }
      honorsHtml += `</div>`;
    }

    // Split: Top 10 cards + remaining table
    // Sprint 12 Phase 1.3: respect API-supplied `s.rank` so hidden sellers
    // leave a visible gap (public) or render greyed with adjustedRank in
    // parens (admin). Top10 = first 10 entries returned (which may have
    // gappy rank numbers when hidden users sit in slots 1-10 for non-admins).
    const top10 = sellers.slice(0, 10);
    const remaining = sellers.slice(10);

    let top10Html = '';
    const RANK_MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };
    if (top10.length > 0) {
      top10Html = '<div class="lb-top10">';
      for (let i = 0; i < top10.length; i++) {
        const s = top10[i];
        const rank = (typeof s.rank === 'number' && s.rank > 0) ? s.rank : (i + 1);
        const medal = RANK_MEDAL[rank] || '';
        const safeName = escapeHtml(s.sellerName).replace(/'/g, "\\'");
        const guildCls = getGuildColorClass(s.guilds || []);
        const guildSub = (s.guilds && s.guilds.length) ? `<div class="lb-guilds-sub">${s.guilds.map(g => escapeHtml(g.replace(/^Redfur\s+/i, ''))).join(' · ')}</div>` : '';
        const hiddenAttr = s._hiddenFromPublic ? ' data-hidden="1"' : '';
        const hiddenCls  = s._hiddenFromPublic ? ' lb-row-hidden' : '';
        const adjRankHtml = (s._hiddenFromPublic && s.adjustedRank == null)
          ? ` <span class="lb-adj-rank" title="Hidden from public — would not appear on the public leaderboard.">(hidden)</span>`
          : (s._hiddenFromPublic && s.adjustedRank != null
              ? ` <span class="lb-adj-rank" title="Public viewers see this seller at rank #${s.adjustedRank}.">(#${s.adjustedRank})</span>`
              : '');
        top10Html += `
          <div class="lb-top10-card lb-rank-${rank}${hiddenCls}" data-user="${safeName}"${hiddenAttr}>
            <span class="lb-rank-num">${medal || '#' + rank}${adjRankHtml}</span>
            <div class="lb-card-info">
              <div class="lb-card-name ${guildCls}">${escapeHtml(s.sellerName)}${s._hiddenFromPublic ? ' <span class="lb-hidden-badge" title="Hidden from public leaderboard">🔒</span>' : ''}</div>
              ${guildSub}
              <div class="lb-card-stats">
                <div class="lb-card-stat-line"><span class="lb-card-stat-label">Gold</span> <span class="lb-card-stat-val lb-card-gold">${gold(s.totalGold)}</span></div>
                <div class="lb-card-stat-line"><span class="lb-card-stat-label">Sales</span> <span class="lb-card-stat-val">${s.totalSales.toLocaleString()}</span></div>
                <div class="lb-card-stat-line"><span class="lb-card-stat-label">Volume</span> <span class="lb-card-stat-val">${s.totalVolume.toLocaleString()}</span></div>
              </div>
            </div>
          </div>`;
      }
      top10Html += '</div>';
    }

    let remainingRows = '';
    for (let i = 0; i < remaining.length; i++) {
      const s = remaining[i];
      const rank = (typeof s.rank === 'number' && s.rank > 0) ? s.rank : (i + 11);
      const medal = RANK_MEDAL[rank] || '';
      const guildCls = getGuildColorClass(s.guilds || []);
      const guildDots = (s.guilds && s.guilds.length)
        ? s.guilds.map(g => {
            const info = GUILD_COLOR_MAP[(g || '').toLowerCase()];
            return info ? `<span class="guild-dot" style="background:${info.color};" title="${escapeHtml(g)}"></span>` : '';
          }).join('')
        : '';
      const guildSub = (s.guilds && s.guilds.length)
        ? `<div class="lb-guilds-sub">${s.guilds.map(g => escapeHtml(g.replace(/^Redfur\s+/i, ''))).join(' · ')}</div>`
        : '';
      const safeName = escapeHtml(s.sellerName || '—');
      const hiddenCls = s._hiddenFromPublic ? ' lb-row-hidden' : '';
      const adjRankCell = (s._hiddenFromPublic && s.adjustedRank != null)
        ? ` <span class="lb-adj-rank" title="Public viewers see this seller at rank #${s.adjustedRank}.">(#${s.adjustedRank})</span>`
        : (s._hiddenFromPublic ? ' <span class="lb-adj-rank" title="Hidden from public.">(hidden)</span>' : '');
      const lockBadge = s._hiddenFromPublic ? ' <span class="lb-hidden-badge" title="Hidden from public leaderboard">🔒</span>' : '';
      const nameTd = s.sellerName
        ? `<td class="lb-rest-name-cell"><div class="lb-rest-name-line"><span class="lb-rest-dots">${guildDots}</span><span class="clickable-tag ${guildCls}" data-user="${safeName}">${safeName}</span>${lockBadge}</div>${guildSub}</td>`
        : `<td>${safeName}</td>`;
      remainingRows += `<tr class="lb-rest-row${hiddenCls}">
        <td class="leaderboard-rank">${medal || '#' + rank}${adjRankCell}</td>
        ${nameTd}
        <td class="text-right text-mono text-gold">${gold(s.totalGold)}</td>
        <td class="text-right text-mono">${s.totalSales.toLocaleString()}</td>
        <td class="text-right text-mono">${s.totalVolume.toLocaleString()}</td>
      </tr>`;
    }

    const remainingTable = remaining.length > 0 ? `
      <div class="table-wrap">
        <table class="data-table lb-rest-table">
          <thead><tr><th></th><th>Seller</th><th class="text-right">Gold Earned</th><th class="text-right">Sales</th><th class="text-right">Volume</th></tr></thead>
          <tbody>${remainingRows}</tbody>
        </table>
      </div>` : '';

    // Build trader week subtext (Sprint 8.6 polish)
    const wk = weekLabels[weeksAgo];
    const weekLabel = (weeksAgo === -1) ? 'All Time'
      : wk?.label
        ? (weeksAgo === 0 ? `Current Trade Week · ${wk.label}` : `Trade Week · ${wk.label}`)
        : (weeksAgo === 0 ? 'Current Trade Week' : `${weeksAgo} week${weeksAgo > 1 ? 's' : ''} ago`);

    $content.innerHTML = `
      <div class="panel">
        <div class="panel-header">
          <div style="display:flex;flex-direction:column;gap:0.1rem;">
            <span class="panel-title">🏆 Redfur Weekly Leaderboard</span>
            <span class="text-muted" style="font-size:var(--fs-xs);">${escapeHtml(weekLabel)}</span>
          </div>
          <span class="stat-value" style="font-size:var(--fs-xs);">${data.totalSellers || data.sellers.length} sellers</span>
        </div>
        <div class="filter-bar">
          ${weekPicker('lb-week', weeksAgo)}
          <div class="filter-group">
            <label>Guild</label>
            <select class="form-input" id="lb-guild">${guildOptions()}</select>
          </div>
          ${sortToggle('lb-sort', sortBy)}
          <div class="filter-group" style="justify-content:flex-end;">
            <label>&nbsp;</label>
            <button class="btn btn-primary" id="lb-filter-btn">Filter</button>
          </div>
        </div>
        ${fallbackNotice}
        ${data.staffPlacement ? `
        <div class="staff-placement-notice">
          <span class="staff-placement-badge">🔒 Hidden from Public</span>
          Your placement: <strong>#${data.staffPlacement.rank}</strong> — ${gold(data.staffPlacement.totalGold)} gold, ${data.staffPlacement.totalSales.toLocaleString()} sales, ${data.staffPlacement.totalVolume.toLocaleString()} volume
        </div>` : ''}
        ${honorsHtml}
        ${top10Html || '<div class="empty-state">No sales data yet.</div>'}
        ${remainingTable}
      </div>`;
    $content.classList.remove('content-loading');
    // Sprint 10.7: flash changed cells on seamless background refresh.
    if (prevSnap) _flashLeaderboardChangedValues(prevSnap);

    // Set current guild
    if (guildFilter) document.getElementById('lb-guild').value = guildFilter;

    document.getElementById('lb-filter-btn').addEventListener('click', () => {
      const w = document.getElementById('lb-week').value;
      const g = document.getElementById('lb-guild').value;
      const s = document.getElementById('lb-sort').value;
      let hash = `#/leaderboard?w=${w}`;
      if (g) hash += `&guild=${encodeURIComponent(g)}`;
      if (s !== 'gold-desc') hash += `&sort=${s}`;
      navigate(hash);
      renderLeaderboard();
    });
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load leaderboard: ${escapeHtml(err.message)}</div>`;
    $content.classList.remove('content-loading');
  }
}

// ── Settings ────────────────────────────────────────────────────────────
async function renderSettings() {
  if (!currentUser) return renderLoginPrompt();
  $content.innerHTML = '<div class="loading-spinner">🎛️ Adjusting the dials...</div>';

  try {
    const settings = await api('/api/me/settings');
    if (!settings) return;

    // Client-side prefs from localStorage
    const relativeTs = localStorage.getItem('redfur-relative-ts') !== '0';
    const fxVal = localStorage.getItem('redfur-fx-intensity') || '70';
    const fxEnabled = localStorage.getItem('redfur-fx') !== '0';

    // Per-effect settings
    const fxScanlines = localStorage.getItem('redfur-fx-scanlines') !== '0';
    const fxFlicker = localStorage.getItem('redfur-fx-flicker') !== '0';
    const fxGlitch = localStorage.getItem('redfur-fx-glitch') !== '0';
    const fxBloom = localStorage.getItem('redfur-fx-bloom') !== '0';
    const fxScanlineSpeed = localStorage.getItem('redfur-fx-scanline-speed') || '50';
    const fxGlitchRate = localStorage.getItem('redfur-fx-glitch-rate') || '30';
    const fxBloomIntensity = localStorage.getItem('redfur-fx-bloom-intensity') || '60';

    function termSwitch(id, checked, label) {
      return `<label class="term-switch-row">
        <span class="term-switch ${checked ? 'active' : ''}">
          <input type="checkbox" id="${id}" ${checked ? 'checked' : ''}>
          <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
        </span>
        <span class="term-switch-label">${label}</span>
      </label>`;
    }

    $content.innerHTML = `
      <div class="panel" style="max-width: 600px;">
        <div class="panel-header"><span class="panel-title">Account Settings</span></div>
        <form id="settings-form">
          <div class="form-group">
            <label class="form-label">ESO @-Tag</label>
            <input type="text" class="form-input${settings.tagInDatabase ? ' qs-tag-input-verified' : ''}" id="input-tag" placeholder="@YourESOTag" value="${escapeHtml(settings.atTag || '')}">
            <span class="form-hint">${settings.atTag
              ? (settings.tagInDatabase
                  ? '✅ Tag linked &amp; verified in the database.'
                  : '⏳ Tag set — not yet seen in a sync. It will verify automatically after the next data upload.')
              : 'Your Elder Scrolls Online @-tag, not your Discord username.'
            }</span>
          </div>
          <div class="form-group">
            <label class="form-label">Embed Color</label>
            <input type="color" id="input-color" value="${settings.color || '#FF7433'}" style="vertical-align: middle;">
            <span style="font-family: var(--font-mono); font-size: 0.85rem; margin-left: 0.5rem;" id="color-preview">${settings.color || '#FF7433'}</span>
          </div>

          <div class="screen-divider"></div>
          <div class="panel-header" style="border-bottom:none;padding-bottom:0;margin-bottom:0.5rem;"><span class="panel-title" style="font-size:var(--fs-xs);">Notifications & Privacy</span></div>

          ${termSwitch('input-alert', settings.active, 'Alert Me — DM when my items sell')}
          ${termSwitch('input-allow-others', settings.allowOthers !== false, 'Allow others to look up my tag')}
          ${termSwitch('input-anonymous', !!settings.anonymous, 'Anonymize — Show as "Anon. Redfur Trader" on leaderboard')}

          <div class="screen-divider"></div>
          <div class="panel-header" style="border-bottom:none;padding-bottom:0;margin-bottom:0.5rem;"><span class="panel-title" style="font-size:var(--fs-xs);">Display Preferences</span></div>

          ${termSwitch('input-relative-ts', relativeTs, 'Use relative timestamps (e.g. "2h ago")')}
          ${termSwitch('input-group-unknowns', localStorage.getItem('redfur-group-unknowns') !== '0', 'Group Unknown guilds/entities into one row')}
          <label class="term-switch-row" title="Shows every item in the database — including items that may no longer exist in-game or have no recent sale data. Unchecked by default.">
            <span class="term-switch ${localStorage.getItem('redfur-show-all-items') === '1' ? 'active' : ''}">
              <input type="checkbox" id="input-show-all-items" ${localStorage.getItem('redfur-show-all-items') === '1' ? 'checked' : ''}>
              <span class="term-switch-track"><span class="term-switch-thumb"></span></span>
            </span>
            <span class="term-switch-label">Show All Items in price search <span style="color:var(--t-dim);font-size:var(--fs-xs);">(includes items with no sale data)</span></span>
          </label>

          <div class="screen-divider"></div>
          <div class="panel-header" style="border-bottom:none;padding-bottom:0;margin-bottom:0.5rem;"><span class="panel-title" style="font-size:var(--fs-xs);">FX Engine</span></div>

          <div class="fx-master-row">
            ${termSwitch('input-fx-on', fxEnabled, 'Enable Visual FX')}
            <div class="fx-master-slider">
              <label class="form-label">Master Intensity</label>
              <div class="fx-slider-row">
                <input type="range" min="0" max="100" value="${fxVal}" id="input-fx-val" class="fx-slider">
                <span class="stat-value fx-val-readout" id="fx-val-label">${fxVal}%</span>
              </div>
              <div class="fx-presets">
                <button type="button" class="btn btn-xs fx-preset-btn" data-preset="low">Low</button>
                <button type="button" class="btn btn-xs fx-preset-btn" data-preset="medium">Med</button>
                <button type="button" class="btn btn-xs fx-preset-btn" data-preset="high">High</button>
                <button type="button" class="btn btn-xs fx-preset-btn" data-preset="ultra">Ultra</button>
                <button type="button" class="btn btn-xs btn-danger" id="fx-reset-defaults" title="Reset FX to defaults">↺ Reset</button>
              </div>
            </div>
          </div>

          <div class="fx-granular-section">
            <div class="panel-header" style="border-bottom:none;padding-bottom:0;margin-bottom:0.3rem;"><span class="panel-title" style="font-size:var(--fs-xs);color:var(--t-dim);">Individual Effects</span></div>

            <div class="fx-effect-row">
              ${termSwitch('fx-scanlines', fxScanlines, 'Scanlines')}
              <div class="fx-effect-slider">
                <label class="pf-label">Speed</label>
                <input type="range" min="0" max="100" value="${fxScanlineSpeed}" id="fx-scanline-speed" class="fx-slider fx-slider-sm">
              </div>
            </div>
            <div class="fx-effect-row">
              ${termSwitch('fx-flicker', fxFlicker, 'Screen Flicker')}
            </div>
            <div class="fx-effect-row">
              ${termSwitch('fx-glitch', fxGlitch, 'Glitch / Artifacts')}
              <div class="fx-effect-slider">
                <label class="pf-label">Occurrence</label>
                <input type="range" min="0" max="100" value="${fxGlitchRate}" id="fx-glitch-rate" class="fx-slider fx-slider-sm">
              </div>
            </div>
            <div class="fx-effect-row">
              ${termSwitch('fx-bloom', fxBloom, 'Glow / Bloom')}
              <div class="fx-effect-slider">
                <label class="pf-label">Intensity</label>
                <input type="range" min="0" max="100" value="${fxBloomIntensity}" id="fx-bloom-intensity" class="fx-slider fx-slider-sm">
              </div>
            </div>
          </div>

          <div class="screen-divider"></div>
          <button type="submit" class="btn btn-primary">Save</button>
        </form>
      </div>

      <!-- Sync History -->
      <div class="panel sync-history-panel" style="max-width: 600px;">
        <div class="panel-header"><span class="panel-title">📡 Your Sync History</span></div>
        <div class="sync-history-search">
          <input type="text" class="form-input" id="sync-history-q" placeholder="Search sync history..." autocomplete="off">
          <button class="btn btn-primary" id="sync-history-search-btn">Search</button>
        </div>
        <div id="sync-history-content"><div class="loading-spinner">Loading...</div></div>
      </div>`;

    // Load sync history
    loadSyncHistory();
    async function loadSyncHistory(q) {
      const el = document.getElementById('sync-history-content');
      if (!el) return;
      try {
        const qs = q ? `?q=${encodeURIComponent(q)}` : '';
        const data = await api(`/api/me/sync-history${qs}`);
        if (!data || (!data.sessions?.length && !data.points?.length)) {
          el.innerHTML = '<div class="empty-state" style="padding:0.5rem;font-size:var(--fs-xs);">No sync history found.</div>';
          return;
        }
        let html = '';
        if (data.sessions?.length) {
          for (const s of data.sessions) {
            const syncType = (s.sync_type || 'mm').toUpperCase();
            const countSummary = [];
            if (s.sales_added > 0) countSummary.push(`${s.sales_added.toLocaleString()} sales`);
            if (s.items_added > 0) countSummary.push(`${s.items_added.toLocaleString()} items`);
            if (s.ttc_updated > 0) countSummary.push('TTC updated');
            html += `
              <div class="sync-history-session" data-session-id="${s.id}">
                <div class="sync-history-session-header" style="cursor:pointer;" onclick="this.parentElement.classList.toggle('expanded');if(this.parentElement.classList.contains('expanded'))loadSyncDetail(${s.id},this.parentElement);">
                  <span class="sync-history-session-time">${relativeTime(s.finished_at || s.started_at)}</span>
                  <span class="sync-history-session-type"><span class="icon-${(s.sync_type||'mm') === 'ttc' ? 'ttc' : 'mm'}"></span> ${syncType}</span>
                  <span class="sync-history-session-summary">${countSummary.join(' · ') || '—'}</span>
                  <span class="sync-history-session-expand">▸</span>
                </div>
                <div class="sync-history-detail" style="display:none;"></div>
              </div>`;
          }
        } else if (data.points?.length) {
          for (const p of data.points) {
            html += `
              <div class="sync-history-session">
                <div class="sync-history-session-header">
                  <span class="sync-history-session-time">${relativeTime(p.timestamp)}</span>
                  <span class="sync-history-session-points">${p.points ? '+' + p.points + ' pts' : ''} &middot; Total: ${(p.totalPoints || 0).toLocaleString()}</span>
                </div>
              </div>`;
          }
        }
        el.innerHTML = html || '<div class="empty-state">No sync data.</div>';
      } catch (err) {
        el.innerHTML = `<span class="text-dim">Could not load sync history.</span>`;
      }
    }

    // Expandable sync detail loader
    window.loadSyncDetail = async function(sessionId, el) {
      const detailEl = el.querySelector('.sync-history-detail');
      if (!detailEl) return;
      if (detailEl.dataset.loaded) {
        detailEl.style.display = el.classList.contains('expanded') ? '' : 'none';
        return;
      }
      detailEl.style.display = '';
      detailEl.innerHTML = '<div class="loading-spinner" style="padding:0.3rem;">Loading...</div>';
      try {
        const data = await api(`/api/me/sync-history/${sessionId}`);
        const s = data.session;
        const pb = data.pointsBreakdown;
        const guildsHtml = Array.isArray(s.guilds) && s.guilds.length
          ? s.guilds.map(g => typeof g === 'string' ? `<span class="sync-history-file-tag">${escapeHtml(g)}</span>` : `<span class="sync-history-file-tag">${escapeHtml(g.name || g.guildName || JSON.stringify(g))}</span>`).join('')
          : '<span class="text-dim">—</span>';
        detailEl.innerHTML = `
          <div class="sync-detail-grid">
            <div class="sync-detail-row"><span class="sync-detail-label">Timestamp</span><span class="sync-detail-value">${new Date(s.startedAt).toLocaleString()}</span></div>
            <div class="sync-detail-row"><span class="sync-detail-label">Duration</span><span class="sync-detail-value">${s.finishedAt ? ((s.finishedAt - s.startedAt) / 1000).toFixed(1) + 's' : '—'}</span></div>
            <div class="sync-detail-row"><span class="sync-detail-label">Type</span><span class="sync-detail-value">${escapeHtml(s.syncType || '').toUpperCase()}</span></div>
            <div class="sync-detail-row"><span class="sync-detail-label">Files</span><span class="sync-detail-value">${s.fileCount}</span></div>
            <div class="sync-detail-row"><span class="sync-detail-label">Sales Added</span><span class="sync-detail-value gold">${s.salesAdded.toLocaleString()}</span></div>
            <div class="sync-detail-row"><span class="sync-detail-label">Items Added</span><span class="sync-detail-value">${s.itemsAdded.toLocaleString()}</span></div>
            <div class="sync-detail-row"><span class="sync-detail-label">TTC Updated</span><span class="sync-detail-value">${s.ttcUpdated > 0 ? '✓ Yes' : '—'}</span></div>
            <div class="sync-detail-row"><span class="sync-detail-label">Guilds</span><span class="sync-detail-value">${guildsHtml}</span></div>
          </div>
          <div class="sync-detail-points">
            <div class="sync-detail-points-title">Points Earned: <span class="gold">+${pb.total}</span></div>
            <div class="sync-detail-points-breakdown">
              Sync bonus: +${pb.syncBonus}
              ${pb.filePoints > 0 ? ` · Files: +${pb.filePoints}` : ''}
              ${pb.itemPoints > 0 ? ` · Items: +${pb.itemPoints}` : ''}
              ${pb.salePoints > 0 ? ` · Sales: +${pb.salePoints}` : ''}
              ${pb.ttcPoints > 0 ? ` · TTC: +${pb.ttcPoints}` : ''}
            </div>
          </div>`;
        detailEl.dataset.loaded = '1';
      } catch (err) {
        detailEl.innerHTML = `<span class="text-dim">Failed to load details.</span>`;
      }
    };

    document.getElementById('sync-history-search-btn')?.addEventListener('click', () => {
      loadSyncHistory(document.getElementById('sync-history-q')?.value?.trim());
    });
    document.getElementById('sync-history-q')?.addEventListener('keydown', e => {
      if (e.key === 'Enter') loadSyncHistory(e.target.value.trim());
    });

    // Wire terminal switches
    document.querySelectorAll('.term-switch input[type="checkbox"]').forEach(cb => {
      cb.addEventListener('change', () => {
        cb.closest('.term-switch').classList.toggle('active', cb.checked);
      });
    });

    document.getElementById('input-color').addEventListener('input', e => {
      document.getElementById('color-preview').textContent = e.target.value;
    });

    document.getElementById('input-fx-val').addEventListener('input', e => {
      document.getElementById('fx-val-label').textContent = e.target.value + '%';
      setFxIntensity(e.target.value);
    });

    // FX preset buttons
    document.querySelectorAll('.fx-preset-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const presets = {
          low:    { master: 25, scanlines: true, scanlineSpeed: 20, flicker: false, glitch: false, glitchRate: 0, bloom: false, bloomIntensity: 20 },
          medium: { master: 50, scanlines: true, scanlineSpeed: 40, flicker: true, glitch: false, glitchRate: 15, bloom: true, bloomIntensity: 40 },
          high:   { master: 75, scanlines: true, scanlineSpeed: 60, flicker: true, glitch: true, glitchRate: 40, bloom: true, bloomIntensity: 70 },
          ultra:  { master: 100, scanlines: true, scanlineSpeed: 80, flicker: true, glitch: true, glitchRate: 80, bloom: true, bloomIntensity: 100 },
        };
        const p = presets[btn.dataset.preset];
        if (!p) return;
        document.getElementById('input-fx-val').value = p.master;
        document.getElementById('fx-val-label').textContent = p.master + '%';
        document.getElementById('input-fx-on').checked = true;
        document.getElementById('input-fx-on').closest('.term-switch').classList.add('active');
        setFx(true); setFxIntensity(p.master);

        const setSwitch = (id, val) => { const el = document.getElementById(id); if (el) { el.checked = val; el.closest('.term-switch').classList.toggle('active', val); } };
        setSwitch('fx-scanlines', p.scanlines);
        setSwitch('fx-flicker', p.flicker);
        setSwitch('fx-glitch', p.glitch);
        setSwitch('fx-bloom', p.bloom);

        const setSlider = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
        setSlider('fx-scanline-speed', p.scanlineSpeed);
        setSlider('fx-glitch-rate', p.glitchRate);
        setSlider('fx-bloom-intensity', p.bloomIntensity);

        applyFxGranular();
        toast(`FX preset: ${btn.dataset.preset.charAt(0).toUpperCase() + btn.dataset.preset.slice(1)}`, 'success');
      });
    });

    // FX Reset to defaults
    document.getElementById('fx-reset-defaults')?.addEventListener('click', () => {
      const defaults = { master: 70, scanlines: true, scanlineSpeed: 50, flicker: true, glitch: true, glitchRate: 30, bloom: true, bloomIntensity: 60 };
      document.getElementById('input-fx-val').value = defaults.master;
      document.getElementById('fx-val-label').textContent = defaults.master + '%';
      document.getElementById('input-fx-on').checked = true;
      document.getElementById('input-fx-on').closest('.term-switch').classList.add('active');
      setFx(true); setFxIntensity(defaults.master);

      const setSwitch = (id, val) => { const el = document.getElementById(id); if (el) { el.checked = val; el.closest('.term-switch').classList.toggle('active', val); } };
      setSwitch('fx-scanlines', defaults.scanlines);
      setSwitch('fx-flicker', defaults.flicker);
      setSwitch('fx-glitch', defaults.glitch);
      setSwitch('fx-bloom', defaults.bloom);

      const setSlider = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
      setSlider('fx-scanline-speed', defaults.scanlineSpeed);
      setSlider('fx-glitch-rate', defaults.glitchRate);
      setSlider('fx-bloom-intensity', defaults.bloomIntensity);

      // Clear localStorage FX keys so they return to defaults
      localStorage.setItem('redfur-fx', '1');
      localStorage.setItem('redfur-fx-intensity', '70');
      localStorage.setItem('redfur-fx-scanlines', '1');
      localStorage.setItem('redfur-fx-flicker', '1');
      localStorage.setItem('redfur-fx-glitch', '1');
      localStorage.setItem('redfur-fx-bloom', '1');
      localStorage.setItem('redfur-fx-scanline-speed', '50');
      localStorage.setItem('redfur-fx-glitch-rate', '30');
      localStorage.setItem('redfur-fx-bloom-intensity', '60');

      applyFxGranular();
      toast('FX settings reset to defaults', 'success');
    });

    // Live FX granular changes
    ['fx-scanlines', 'fx-flicker', 'fx-glitch', 'fx-bloom'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('change', applyFxGranular);
    });
    ['fx-scanline-speed', 'fx-glitch-rate', 'fx-bloom-intensity'].forEach(id => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('input', applyFxGranular);
    });

    function applyFxGranular() {
      const scanlines = document.getElementById('fx-scanlines')?.checked;
      const flicker = document.getElementById('fx-flicker')?.checked;
      const glitch = document.getElementById('fx-glitch')?.checked;
      const bloom = document.getElementById('fx-bloom')?.checked;
      const scanSpeed = document.getElementById('fx-scanline-speed')?.value || '50';
      const glitchRate = document.getElementById('fx-glitch-rate')?.value || '30';
      const bloomInt = document.getElementById('fx-bloom-intensity')?.value || '60';

      document.body.classList.toggle('fx-no-scanlines', !scanlines);
      document.body.classList.toggle('fx-no-flicker', !flicker);
      document.body.classList.toggle('fx-no-glitch', !glitch);
      document.body.classList.toggle('fx-no-bloom', !bloom);
      document.documentElement.style.setProperty('--fx-scanline-speed', (12 - (scanSpeed / 100) * 10).toFixed(1) + 's');
      document.documentElement.style.setProperty('--fx-glitch-rate', (20 - (glitchRate / 100) * 16).toFixed(1) + 's');
      document.documentElement.style.setProperty('--fx-bloom-intensity', (bloomInt / 100).toFixed(2));
    }

    document.getElementById('settings-form').addEventListener('submit', async e => {
      e.preventDefault();
      const esoTag = document.getElementById('input-tag').value.trim();
      const color = document.getElementById('input-color').value;
      const alertMe = document.getElementById('input-alert').checked;
      const allowOthers = document.getElementById('input-allow-others').checked;
      const anonymous = document.getElementById('input-anonymous').checked;
      const relTs = document.getElementById('input-relative-ts').checked;
      const fxOn = document.getElementById('input-fx-on').checked;
      const fxIntensity = document.getElementById('input-fx-val').value;

      // Save server-side settings
      try {
        await apiPost('/api/me/settings', { esoTag: esoTag || undefined, color, alertMe, allowOthers, anonymous });
      } catch (err) {
        toast(err.message, 'error');
        return;
      }

      // Save client-side display prefs
      localStorage.setItem('redfur-relative-ts', relTs ? '1' : '0');
      localStorage.setItem('redfur-group-unknowns', document.getElementById('input-group-unknowns')?.checked ? '1' : '0');
      localStorage.setItem('redfur-show-all-items', document.getElementById('input-show-all-items')?.checked ? '1' : '0');
      localStorage.setItem('redfur-fx', fxOn ? '1' : '0');
      localStorage.setItem('redfur-fx-intensity', fxIntensity);
      localStorage.setItem('redfur-fx-scanlines', document.getElementById('fx-scanlines')?.checked ? '1' : '0');
      localStorage.setItem('redfur-fx-flicker', document.getElementById('fx-flicker')?.checked ? '1' : '0');
      localStorage.setItem('redfur-fx-glitch', document.getElementById('fx-glitch')?.checked ? '1' : '0');
      localStorage.setItem('redfur-fx-bloom', document.getElementById('fx-bloom')?.checked ? '1' : '0');
      localStorage.setItem('redfur-fx-scanline-speed', document.getElementById('fx-scanline-speed')?.value || '50');
      localStorage.setItem('redfur-fx-glitch-rate', document.getElementById('fx-glitch-rate')?.value || '30');
      localStorage.setItem('redfur-fx-bloom-intensity', document.getElementById('fx-bloom-intensity')?.value || '60');
      setFx(fxOn);
      setFxIntensity(fxIntensity);
      applyFxGranular();

      toast('Settings saved.', 'success');
      // Refresh session user
      currentUser = await api('/auth/me');
      renderDualIdentity();
    });

    // Apply current granular FX on load
    applyFxGranular();
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load settings: ${escapeHtml(err.message)}</div>`;
  }
}

// ── Price Check ──────────────────────────────────────────────
let _priceSearchTimeout = null;
let _priceFilters = null;
let _priceGuildData = null;  // { redfur: [], others: [], isAdmin: bool }

// ── Reusable CRT Calendar Date Range Picker ──────────────────
// Returns HTML + wiring function. Used in price check, admin panels, user panels.
function crtDateRangePicker(idPrefix, opts = {}) {
  const startId = `${idPrefix}-start`;
  const endId = `${idPrefix}-end`;
  const startVal = opts.startVal || '';
  const endVal = opts.endVal || '';

  const html = `
    <div class="crt-date-picker">
      <div class="crt-date-field">
        <label class="pf-label">Start Date</label>
        <input type="date" class="form-input crt-date-input" id="${startId}" value="${startVal}">
      </div>
      <div class="crt-date-field">
        <label class="pf-label">End Date</label>
        <input type="date" class="form-input crt-date-input" id="${endId}" value="${endVal}">
      </div>
    </div>`;

  return { html, startId, endId };
}

// ── Reusable Trade Week Picker ───────────────────────────────
function tradeWeekPicker(id, selected = 0) {
  const noneOpt = `<option value="-1" ${selected === -1 ? 'selected' : ''}>None</option>`;
  const options = weekLabels.length > 0
    ? weekLabels.map((w, i) =>
        `<option value="${i}" ${selected === i ? 'selected' : ''}>${i === 0 ? 'Current Week' : w.label}</option>`
      ).join('')
    : `<option value="0">Current Week</option>` +
      Array.from({length: 11}, (_, i) => i + 1).map(w =>
        `<option value="${w}" ${selected === w ? 'selected' : ''}>-${w} week${w > 1 ? 's' : ''}</option>`
      ).join('');
  return `<select class="form-input crt-date-input" id="${id}" style="min-width:140px;">${noneOpt}${options}</select>`;
}

// ── Reusable Guild Filter Dropdown (multi-select with search) ─
function guildFilterDropdown(idPrefix, guildData, opts = {}) {
  if (!guildData) return { html: '', wireEvents: () => {} };

  const rfGuilds = guildData.redfur || [];
  const otherGuilds = guildData.others || [];
  const isAdmin = guildData.isAdmin;
  const userGuildIds = new Set(guildData.userGuildIds || []);
  const caravanId = 822467; // Redfur Trading Caravan guildID

  const id = `${idPrefix}-guild-dd`;
  const html = `
    <div class="guild-filter-dropdown" id="${id}">
      <div class="guild-dd-trigger" id="${id}-trigger">
        <span class="guild-dd-label" id="${id}-label">Any Guild</span>
        <span class="guild-dd-arrow">▾</span>
      </div>
      <div class="guild-dd-menu" id="${id}-menu" style="display:none;">
        <div class="guild-dd-search-wrap">
          <input type="text" class="form-input guild-dd-search" id="${id}-search" placeholder="Search guilds..." autocomplete="off">
        </div>
        <div class="guild-dd-options" id="${id}-options">
          <div class="guild-dd-option guild-dd-special" data-value="any">
            <span class="guild-dd-check" data-for="any">✓</span> Any Guild
          </div>
          <div class="guild-dd-option guild-dd-special guild-dd-redfur-all" data-value="all-redfur">
            <span class="guild-dd-check" data-for="all-redfur"></span> All Redfur Guilds
          </div>
          ${isAdmin && otherGuilds.length ? `
            <div class="guild-dd-option guild-dd-special guild-dd-other-all" data-value="all-other">
              <span class="guild-dd-check" data-for="all-other"></span> Non-Redfur
            </div>
          ` : ''}
          ${isAdmin ? `
            <div class="guild-dd-option guild-dd-special guild-dd-nodata-all" data-value="all-nodata">
              <span class="guild-dd-check" data-for="all-nodata"></span> No Data
            </div>
          ` : ''}
          ${isAdmin && userGuildIds.size > 0 ? `
            <div class="guild-dd-option guild-dd-special guild-dd-mine" data-value="my-guilds">
              <span class="guild-dd-check" data-for="my-guilds"></span> Guilds I'm In
            </div>
          ` : ''}
          <div class="guild-dd-group-label guild-dd-redfur-label">Redfur Guilds</div>
          ${rfGuilds.map(g => {
            const isMember = userGuildIds.has(g.guildID);
            const isCaravan = g.guildID === caravanId;
            const memberCls = isMember ? ' guild-dd-member' : '';
            const caravanCls = isCaravan && isMember ? ' guild-dd-caravan' : '';
            return `
            <div class="guild-dd-option guild-dd-redfur${memberCls}${caravanCls}" data-value="${g.guildID}" data-name="${escapeHtml(g.guildName)}">
              <span class="guild-dd-check" data-for="${g.guildID}"></span>
              ${isMember ? `<span class="guild-dd-member-light" data-guild-id="${g.guildID}" title="You're a member">●</span>` : ''}
              ${escapeHtml(g.guildName)}
              ${isCaravan && isMember ? '<span class="guild-dd-caravan-badge">★</span>' : ''}
            </div>`;
          }).join('')}
          ${isAdmin && otherGuilds.length ? (() => {
            const knownOther = otherGuilds.filter(g => !g.guildName?.toLowerCase().includes('unknown'));
            const unknownOther = otherGuilds.filter(g => g.guildName?.toLowerCase().includes('unknown'));

            // Split known guilds by sales data presence
            const knownWithData = knownOther.filter(g => (g.salesCount || 0) > 0);
            const knownNoData = knownOther.filter(g => !(g.salesCount > 0));

            // Split unknown guilds by sales data presence
            const unknownWithData = unknownOther.filter(g => (g.salesCount || 0) > 0);
            const unknownNoData = unknownOther.filter(g => !(g.salesCount > 0));

            const renderGuildOption = (g, extraCls = '') => {
              const isMember = userGuildIds.has(g.guildID);
              const memberCls = isMember ? (isAdmin ? ' guild-dd-admin-member' : ' guild-dd-member') : '';
              return `
              <div class="guild-dd-option guild-dd-other${memberCls}${extraCls}" data-value="${g.guildID}" data-name="${escapeHtml(g.guildName)}">
                <span class="guild-dd-check" data-for="${g.guildID}"></span>
                ${isMember ? `<span class="guild-dd-member-light" data-guild-id="${g.guildID}" title="You're a member">●</span>` : ''}
                ${escapeHtml(g.guildName)}
              </div>`;
            };

            let otherHtml = '';

            // Other Guilds (With Data)
            if (knownWithData.length) {
              otherHtml += `<div class="guild-dd-group-label guild-dd-other-label">Other Guilds (With Data)${isAdmin ? ' — Admin' : ''}</div>`;
              otherHtml += knownWithData.map(g => renderGuildOption(g)).join('');
            }

            // Other Guilds (No Data)
            if (knownNoData.length) {
              otherHtml += `<div class="guild-dd-group-label guild-dd-other-label guild-dd-nodata-label">Other Guilds (No Data)</div>`;
              otherHtml += knownNoData.map(g => renderGuildOption(g, ' guild-dd-nodata')).join('');
            }

            // Unknown Guilds (With Data)
            if (unknownWithData.length) {
              otherHtml += `<div class="guild-dd-group-label guild-dd-unknown-label">Unknown Guilds (With Data) (${unknownWithData.length})</div>`;
              otherHtml += unknownWithData.map(g => `
                <div class="guild-dd-option guild-dd-unknown" data-value="${g.guildID}" data-name="${escapeHtml(g.guildName)}">
                  <span class="guild-dd-check" data-for="${g.guildID}"></span>
                  ${escapeHtml(g.guildName)}
                </div>`).join('');
            }

            // Unknown Guilds (No Data)
            if (unknownNoData.length) {
              otherHtml += `<div class="guild-dd-group-label guild-dd-unknown-label guild-dd-nodata-label">Unknown Guilds (No Data) (${unknownNoData.length})</div>`;
              otherHtml += unknownNoData.map(g => `
                <div class="guild-dd-option guild-dd-unknown guild-dd-nodata" data-value="${g.guildID}" data-name="${escapeHtml(g.guildName)}">
                  <span class="guild-dd-check" data-for="${g.guildID}"></span>
                  ${escapeHtml(g.guildName)}
                </div>`).join('');
            }

            return otherHtml;
          })() : ''}
        </div>
      </div>
    </div>`;

  function wireEvents(onChange) {
    const trigger = document.getElementById(`${id}-trigger`);
    const menu = document.getElementById(`${id}-menu`);
    const label = document.getElementById(`${id}-label`);
    const searchInput = document.getElementById(`${id}-search`);
    const optionsContainer = document.getElementById(`${id}-options`);
    if (!trigger || !menu) return;

    let selected = new Set(); // guild IDs
    let mode = 'any'; // 'any', 'all-redfur', 'all-other', 'all-nodata', 'my-guilds', 'multi'
    let prevRedfurSelection = new Set(); // for toggle restore

    function updateLabel() {
      if (mode === 'any') {
        label.textContent = 'Any Guild';
      } else if (mode === 'all-redfur') {
        label.textContent = 'All Redfur Guilds';
      } else if (mode === 'all-other') {
        label.textContent = 'All Non-Redfur Guilds';
      } else if (mode === 'all-nodata') {
        label.textContent = 'All Guilds (No Data)';
      } else if (mode === 'my-guilds') {
        label.textContent = 'Guilds I\'m In';
      } else {
        const names = [];
        selected.forEach(sid => {
          const el = optionsContainer.querySelector(`[data-value="${sid}"]`);
          if (el) names.push(el.dataset.name || el.textContent.trim());
        });
        label.textContent = names.length > 2 ? `${names.length} guilds selected` : names.join(', ') || 'Any Guild';
      }
      // Update check marks — glowing checkmark style
      optionsContainer.querySelectorAll('.guild-dd-check').forEach(el => {
        const val = el.dataset.for;
        let isChecked = false;
        if (val === 'any') isChecked = mode === 'any';
        else if (val === 'all-redfur') isChecked = mode === 'all-redfur';
        else if (val === 'all-other') isChecked = mode === 'all-other';
        else if (val === 'all-nodata') isChecked = mode === 'all-nodata';
        else if (val === 'my-guilds') isChecked = mode === 'my-guilds';
        else isChecked = selected.has(Number(val));
        el.textContent = isChecked ? '✓' : '';
        el.classList.toggle('checked', isChecked);
      });
    }

    function getSelectedIds() {
      if (mode === 'any') return [];
      if (mode === 'all-redfur') return rfGuilds.map(g => g.guildID);
      if (mode === 'all-other') return otherGuilds.map(g => g.guildID);
      if (mode === 'all-nodata') return otherGuilds.filter(g => !(g.salesCount > 0)).map(g => g.guildID);
      if (mode === 'my-guilds') return [...userGuildIds];
      return [...selected];
    }

    trigger.addEventListener('click', () => {
      const wasHidden = menu.style.display === 'none';
      if (wasHidden) {
        menu.style.display = 'block';
        menu.classList.remove('glitch-close');
        menu.classList.add('glitch-open');
        searchInput.focus();
      } else {
        menu.classList.remove('glitch-open');
        menu.classList.add('glitch-close');
        setTimeout(() => { menu.style.display = 'none'; menu.classList.remove('glitch-close'); }, 200);
      }
    });

    document.addEventListener('click', (e) => {
      if (!e.target.closest(`#${id}`)) {
        if (menu.style.display !== 'none') {
          menu.classList.remove('glitch-open');
          menu.classList.add('glitch-close');
          setTimeout(() => { menu.style.display = 'none'; menu.classList.remove('glitch-close'); }, 200);
        }
      }
    });

    searchInput.addEventListener('input', () => {
      const q = searchInput.value.toLowerCase();
      optionsContainer.querySelectorAll('.guild-dd-option').forEach(el => {
        const name = (el.dataset.name || el.textContent || '').toLowerCase();
        el.style.display = !q || name.includes(q) || el.classList.contains('guild-dd-special') ? '' : 'none';
      });
    });

    optionsContainer.addEventListener('click', (e) => {
      const opt = e.target.closest('.guild-dd-option');
      if (!opt) return;
      const val = opt.dataset.value;

      if (val === 'any') {
        mode = 'any'; selected.clear();
      } else if (val === 'all-redfur') {
        if (mode === 'all-redfur') {
          // Toggle off → restore previous redfur selections
          mode = prevRedfurSelection.size > 0 ? 'multi' : 'any';
          selected = new Set(prevRedfurSelection);
          if (selected.size === 0) mode = 'any';
        } else {
          // Save current redfur selections before toggling
          prevRedfurSelection = new Set([...selected].filter(sid => rfGuilds.some(g => g.guildID === sid)));
          mode = 'all-redfur';
          selected.clear();
        }
      } else if (val === 'all-other') {
        mode = mode === 'all-other' ? 'any' : 'all-other';
        selected.clear();
      } else if (val === 'all-nodata') {
        mode = mode === 'all-nodata' ? 'any' : 'all-nodata';
        selected.clear();
      } else if (val === 'my-guilds') {
        mode = mode === 'my-guilds' ? 'any' : 'my-guilds';
        selected.clear();
      } else {
        mode = 'multi';
        const gid = Number(val);
        if (selected.has(gid)) selected.delete(gid);
        else selected.add(gid);
        if (selected.size === 0) mode = 'any';
      }
      updateLabel();
      if (onChange) onChange(getSelectedIds());
    });

    updateLabel();

    // ── Guild member tooltip on green indicator light ──
    let guildTooltip = null;
    optionsContainer.querySelectorAll('.guild-dd-member-light').forEach(light => {
      light.addEventListener('mouseenter', async (e) => {
        e.stopPropagation();
        const guildId = light.dataset.guildId;
        if (!guildId) return;

        // Remove any existing tooltip
        if (guildTooltip) { guildTooltip.remove(); guildTooltip = null; }

        // Fetch user's guild stats
        const guildName = light.closest('.guild-dd-option')?.dataset.name || '';
        let statsHtml = '<div class="guild-tooltip-loading">Loading...</div>';

        guildTooltip = document.createElement('div');
        guildTooltip.className = 'guild-member-tooltip';
        guildTooltip.innerHTML = statsHtml;

        // Position near the light
        const rect = light.getBoundingClientRect();
        guildTooltip.style.position = 'fixed';
        guildTooltip.style.top = (rect.top - 4) + 'px';
        guildTooltip.style.left = (rect.right + 8) + 'px';
        guildTooltip.style.zIndex = '1100';
        document.body.appendChild(guildTooltip);

        try {
          const tag = currentUser?.esoTag || localStorage.getItem('redfur-claimed-tag') || '';
          if (tag) {
            const data = await api(`/api/me/guild-stats?guildId=${guildId}`);
            if (data && guildTooltip) {
              guildTooltip.innerHTML = `
                <div class="guild-tooltip-header">${escapeHtml(guildName)}</div>
                <div class="guild-tooltip-stat"><span>Sales:</span> <span class="gold">${(data.totalSales || 0).toLocaleString()}</span></div>
                <div class="guild-tooltip-stat"><span>Revenue:</span> <span class="gold">${(data.totalGold || 0).toLocaleString()}g</span></div>
                <div class="guild-tooltip-stat"><span>Rank:</span> <span>${data.rank ? '#' + data.rank : '—'}</span></div>
              `;
            }
          } else if (guildTooltip) {
            guildTooltip.innerHTML = '<div class="guild-tooltip-header">Login to see stats</div>';
          }
        } catch {
          if (guildTooltip) guildTooltip.innerHTML = '<div class="guild-tooltip-header">Stats unavailable</div>';
        }
      });

      light.addEventListener('mouseleave', () => {
        if (guildTooltip) { guildTooltip.remove(); guildTooltip = null; }
      });
    });

    return { getSelectedIds };
  }

  return { html, wireEvents };
}

// ── Item Pricing & Analysis: tabbed wrapper merging Price Check + Item Analysis ──
// Single page, two horizontal categories. ?tab=analysis switches to the items report
// (formerly the Item Analysis admin page). Default tab is the price-check view.
async function renderItemPricingAndAnalysis() {
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const tab = params.get('tab') === 'analysis' ? 'analysis' : 'prices';

  // Run the underlying renderer first so it owns $content, then prepend the tab strip
  if (tab === 'analysis') {
    await renderAdminItems();
  } else {
    await renderPriceCheck();
  }

  const tabsHtml = `
    <div class="page-tabs item-page-tabs" role="tablist" aria-label="Item Pricing & Analysis">
      <button class="page-tab ${tab === 'prices' ? 'active' : ''}" data-tab="prices" role="tab" aria-selected="${tab === 'prices'}">🏷️ Price Check</button>
      <button class="page-tab ${tab === 'analysis' ? 'active' : ''}" data-tab="analysis" role="tab" aria-selected="${tab === 'analysis'}">📊 Analysis</button>
    </div>`;
  const wrapper = document.createElement('div');
  wrapper.innerHTML = tabsHtml;
  const tabBar = wrapper.firstElementChild;
  if (tabBar && $content.firstChild) {
    $content.insertBefore(tabBar, $content.firstChild);
  } else if (tabBar) {
    $content.appendChild(tabBar);
  }
  $content.querySelectorAll('.item-page-tabs .page-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      const t = btn.dataset.tab;
      const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
      // Switching tabs resets pagination/filter state from the OTHER tab to keep things clean
      const keep = new URLSearchParams();
      if (t === 'analysis') keep.set('tab', 'analysis');
      const qs = keep.toString();
      navigate('/prices' + (qs ? '?' + qs : ''));
    });
  });
}

async function renderPriceCheck() {
  // Load filter options and guild data in parallel
  const [filters, guildData] = await Promise.all([
    _priceFilters || api('/api/items/filters').catch(() => ({ qualities: [], traits: [], guilds: [] })),
    _priceGuildData || api('/api/items/guilds').catch(() => ({ redfur: [], others: [], isAdmin: false })),
  ]);
  _priceFilters = filters;
  _priceGuildData = guildData;

  const showAllItemsChecked = localStorage.getItem('redfur-show-all-items') === '1';

  const qualityOpts = (_priceFilters.qualities || []).map(q => {
    const cls = `quality-opt-${q.toLowerCase()}`;
    return `<option value="${q}" class="${cls}">${q.charAt(0).toUpperCase() + q.slice(1)}</option>`;
  }).join('');

  // Trait grouping by equipment type (with display config for category headers)
  const TRAIT_GROUPS = {
    '__sep_player': { separator: true, label: '── Player Gear ──' },
    'Weapon Traits':    { emoji: '⚔️', color: '#f87171', bg: 'rgba(248, 113, 113, 0.06)', traits: ['charged', 'decisive', 'infused', 'nirnhoned', 'powered', 'precise', 'sharpened', 'training'] },
    'Armor Traits':     { emoji: '🛡️', color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.06)', traits: ['defending', 'divines', 'impenetrable', 'invigorating', 'reinforced', 'sturdy', 'training', 'well-fitted', 'nirnhoned'] },
    'Jewelry Traits':   { emoji: '💎', color: '#c084fc', bg: 'rgba(192, 132, 252, 0.06)', traits: ['arcane', 'bloodthirsty', 'harmony', 'healthy', 'protective', 'robust', 'swift', 'triune', 'infused'] },
    '__sep_companion': { separator: true, label: '── Companion Gear ──' },
    'Companion Weapon': { emoji: '🐾⚔️', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.06)', traits: ['aggressive', 'bolstered', 'focused', 'sharpened', 'precise'] },
    'Companion Armor':  { emoji: '🐾🛡️', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.06)', traits: ['augmented', 'bolstered', 'prolific', 'quickened', 'vigorous'] },
    'Companion Jewelry':{ emoji: '🐾💎', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.06)', traits: ['focused', 'quickened', 'soothing', 'vigorous'] },
    'Companion Special': { emoji: '🐾✦', color: '#fb923c', bg: 'rgba(251, 146, 60, 0.06)', traits: ['aggressive', 'augmented', 'shattered', 'special'] },
    'Universal':        { emoji: '✦',  color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.06)', traits: ['intricate', 'ornate'] },
  };
  const allTraits = _priceFilters.traits || [];
  const groupedUsed = new Set();

  // Build trait panel categories HTML
  let traitPanelCatsHtml = '';
  for (const [groupLabel, groupCfg] of Object.entries(TRAIT_GROUPS)) {
    if (groupCfg.separator) {
      traitPanelCatsHtml += `<div class="trait-panel-separator">${groupCfg.label}</div>`;
      continue;
    }
    const available = groupCfg.traits.filter(t => allTraits.includes(t));
    if (available.length) {
      available.forEach(t => groupedUsed.add(t));
      const cssClass = `trait-group-${groupLabel.split(' ')[0].toLowerCase()}`;
      traitPanelCatsHtml += `<div class="trait-panel-category ${cssClass}" data-group="${groupLabel}">
        <div class="trait-panel-category-header" style="color:${groupCfg.color}">${groupCfg.emoji} ${groupLabel}</div>
        <div class="trait-panel-items">
          ${available.map(t => `<div class="trait-panel-item" data-trait="${t}" style="--trait-color:${groupCfg.color};--trait-bg:${groupCfg.bg}">${t.charAt(0).toUpperCase() + t.slice(1)}</div>`).join('')}
        </div>
      </div>`;
    }
  }
  // Any ungrouped traits
  const ungrouped = allTraits.filter(t => !groupedUsed.has(t));
  if (ungrouped.length) {
    traitPanelCatsHtml += `<div class="trait-panel-category trait-group-other" data-group="Other">
      <div class="trait-panel-category-header" style="color:#9ca3af">🔹 Other</div>
      <div class="trait-panel-items">
        ${ungrouped.map(t => `<div class="trait-panel-item" data-trait="${t}" style="--trait-color:#9ca3af;--trait-bg:rgba(156,163,175,0.06)">${t.charAt(0).toUpperCase() + t.slice(1)}</div>`).join('')}
      </div>
    </div>`;
  }

  const guildDd = guildFilterDropdown('pc', _priceGuildData);

  $content.innerHTML = `
    <div class="price-check-layout">
      <div class="price-filters panel">
        <div class="panel-header"><span class="panel-title">⚙ Filters</span></div>
        <div class="pf-group">
          <label class="pf-label">Search</label>
          <input type="text" id="pc-search" class="form-input" placeholder="Item name..." autocomplete="off">
        </div>
        <div class="pf-group">
          <label class="pf-label">Quality</label>
          <div class="pf-select-icon-wrap">
            <span id="pc-quality-icon" class="pf-select-icon"></span>
            <select id="pc-quality" class="form-input">
              <option value="">Any</option>
              ${qualityOpts}
            </select>
          </div>
        </div>
        <div class="pf-group" style="position:relative;">
          <label class="pf-label">Trait</label>
          <div class="pf-select-icon-wrap">
            <span id="pc-trait-icon" class="pf-select-icon"></span>
            <button type="button" id="pc-trait-trigger" class="form-input trait-panel-trigger" data-value="">
              <span class="trait-panel-trigger-text">Any</span>
              <span class="trait-panel-trigger-arrow">▾</span>
            </button>
            <input type="hidden" id="pc-trait" value="">
          </div>
          <div id="pc-trait-panel" class="trait-panel-popup" style="display:none;">
            <div class="trait-panel-header">
              <span>Select Trait</span>
              <button type="button" class="trait-panel-clear" id="pc-trait-clear">✕ Clear</button>
            </div>
            <div class="trait-panel-grid">
              ${traitPanelCatsHtml}
            </div>
          </div>
        </div>
        <div class="pf-group">
          <label class="pf-label">Guild</label>
          ${guildDd.html}
        </div>
        <div class="pf-group">
          <label class="pf-checkbox-label">
            <input type="checkbox" id="pc-include-ttc" checked>
            <span class="has-tooltip" data-tooltip="When enabled, search results include server-wide TTC pricing data alongside Redfur guild sales. Disable to see only Redfur guild data.">Always include TTC items</span>
          </label>
        </div>
        <div class="pf-group">
          <label class="pf-checkbox-label">
            <input type="checkbox" id="pc-show-all-items" ${showAllItemsChecked ? 'checked' : ''}>
            <span class="has-tooltip" data-tooltip="Shows every item in the database — including items that may no longer exist in-game or have no recent sale data. Unchecked by default.">Show All Items</span>
          </label>
        </div>
        <div class="pf-group">
          <label class="pf-label">Sort</label>
          <select id="pc-sort" class="form-input">
            <option value="relevance">Relevance</option>
            <option value="price-desc">Price ↓</option>
            <option value="price-asc">Price ↑</option>
            <option value="recent">Recent</option>
            <option value="name">Name A-Z</option>
          </select>
        </div>
        <div class="pf-group">
          <button class="btn btn-primary" id="pc-clear" style="width:100%;">Clear Filters</button>
        </div>
      </div>
      <div class="price-results">
        <div class="panel">
          <div class="panel-header">
            <span class="panel-title">📋 Results</span>
            <span class="stat-value" id="pc-count" style="font-size:var(--fs-xs);">—</span>
          </div>
          <div id="pc-results" class="pc-results-grid">
            <div class="empty-state">Type an item name to search the guild trader database.</div>
          </div>
          <div id="pc-pagination" class="pc-pagination"></div>
        </div>
      </div>
    </div>
  `;

  const $search = document.getElementById('pc-search');
  const $quality = document.getElementById('pc-quality');
  const $trait = document.getElementById('pc-trait');
  const $sort = document.getElementById('pc-sort');
  const $includeTtc = document.getElementById('pc-include-ttc');
  const $showAllItems = document.getElementById('pc-show-all-items');
  let currentPage = 1;
  let selectedGuildIds = [];

  // Sprint 10.3: honour a pending prefill from the dashboard widget.
  let _pendingPriceQ = '';
  try {
    _pendingPriceQ = sessionStorage.getItem('redfur_prefill_price_q') || '';
    if (_pendingPriceQ) sessionStorage.removeItem('redfur_prefill_price_q');
  } catch {}

  // Wire guild dropdown
  const guildCtrl = guildDd.wireEvents((ids) => {
    selectedGuildIds = ids;
    doSearch(1);
  });

  // Wire TTC checkbox
  $includeTtc.addEventListener('change', () => { _searchCache.clear(); doSearch(1); });

  // Wire Show All Items checkbox — persist to localStorage
  $showAllItems.addEventListener('change', () => {
    localStorage.setItem('redfur-show-all-items', $showAllItems.checked ? '1' : '0');
    _searchCache.clear();
    doSearch(1);
  });

  function doSearch(page = 1) {
    currentPage = page;
    const q = $search.value.trim();
    if (q.length < 2) {
      document.getElementById('pc-results').innerHTML = '<div class="empty-state">Type at least 2 characters to search.</div>';
      document.getElementById('pc-count').textContent = '—';
      document.getElementById('pc-pagination').innerHTML = '';
      return;
    }
    // Show subtle loading indicator without destroying existing results (prevents layout shift/flicker)
    const $results = document.getElementById('pc-results');
    $results.classList.add('pc-loading');
    if (!$results.children.length || $results.querySelector('.empty-state')) {
      $results.innerHTML = '<div class="loading-spinner">Searching...</div>';
    }
    clearTimeout(_priceSearchTimeout);
    _priceSearchTimeout = setTimeout(() => _execPriceSearch(q, page), 120);
  }

  // Simple search result cache (keyed by full query string)
  const _searchCache = new Map();

  async function _execPriceSearch(q, page) {
    const params = new URLSearchParams({ q, page, sort: $sort.value });
    if ($quality.value) params.set('quality', $quality.value);
    if ($trait.value) params.set('trait', $trait.value);
    if (selectedGuildIds.length) params.set('guild', selectedGuildIds.join(','));
    if ($includeTtc && $includeTtc.checked) params.set('includeTtc', '1');
    if ($showAllItems && $showAllItems.checked) params.set('showAllItems', '1');

    const cacheKey = params.toString();
    if (_searchCache.has(cacheKey)) {
      renderPriceResults(_searchCache.get(cacheKey));
      return;
    }

    try {
      const data = await api('/api/items/search?' + params);
      if (!data || !data.items) throw new Error('No data');
      // Cache up to 20 recent searches
      if (_searchCache.size > 20) {
        const firstKey = _searchCache.keys().next().value;
        _searchCache.delete(firstKey);
      }
      _searchCache.set(cacheKey, data);
      renderPriceResults(data);
    } catch (err) {
      const $r = document.getElementById('pc-results');
      if ($r) { $r.classList.remove('pc-loading'); $r.innerHTML = `<div class="error-msg">${escapeHtml(err.message)}</div>`; }
    }
  }

  function renderPriceResults(data) {
    const $results = document.getElementById('pc-results');
    const $count = document.getElementById('pc-count');
    const $pag = document.getElementById('pc-pagination');

    $results.classList.remove('pc-loading');
    $count.textContent = `${data.total} item${data.total !== 1 ? 's' : ''} found`;

    if (!data.items.length) {
      $results.innerHTML = '<div class="empty-state-fissal"><span class="fissal-emote">🌙</span><div class="fissal-action">*The moons are quiet tonight*</div><div class="fissal-line">No items match your search.</div></div>';
      $pag.innerHTML = '';
      return;
    }

    const QUALITY_COLORS = { worn: '#9a9a9a', white: '#e8e8e8', green: '#5bffa0', blue: '#7ab8ff', purple: '#d09cff', gold: '#ffcc40' };

    // Cache icons from search results for global itemLink/itemCell use
    for (const item of data.items) {
      const cacheIcon = item.icon || item.iconUrl;
      if (cacheIcon) cacheItemIcon(item.name, cacheIcon);
    }

    $results.innerHTML = data.items.map(item => {
      const qColor = QUALITY_COLORS[item.quality] || '#aaa';
      const qDot = item.quality ? `<span class="quality-dot" style="background:${qColor};" title="${item.quality}"></span>` : '';
      const traitBadge = item.trait ? `<span class="trait-badge">${traitIconImg(item.trait)} ${escapeHtml(item.trait)}</span>` : '';
      const iconSrc = item.iconUrl || esoIconUrl(item.icon);
      const iconHtml = `<img class="pc-item-icon" src="${iconSrc}" alt="" loading="lazy" onerror="if(this.src.endsWith('.png')){this.onerror=function(){this.onerror=null;this.src='${ESO_ICON_FALLBACK}'};this.src=this.src.replace('.png','.jpg')}else{this.onerror=null;this.src='${ESO_ICON_FALLBACK}'}">`;
      const guildList = (item.guilds || []).slice(0, 3).map(g => escapeHtml(g)).join(', ');

      // MM data
      const mmAvg = item.mmAvgPrice > 0 ? gold(item.mmAvgPrice) : '<span class="t-dim">—</span>';
      const salesDisplay = item.totalSales > 0 ? item.totalSales.toLocaleString() : '0';
      const volDisplay = item.totalVolume > 0 ? item.totalVolume.toLocaleString() : '0';
      const lastSeenDisplay = item.lastSeen > 0 ? relativeTime(item.lastSeen) : '—';
      const freqDisplay = item.sellFrequency > 0 ? `${item.sellFrequency}/day` : '—';
      const hasMM = item.mmAvgPrice > 0 || item.totalSales > 0;

      // TTC data
      const ttcAvg = item.ttcAvgPrice > 0 ? gold(item.ttcAvgPrice) : '<span class="t-dim">—</span>';
      const sugDisplay = item.suggestedPrice > 0 ? gold(item.suggestedPrice) : '<span class="t-dim">—</span>';
      const hasTTC = item.ttcAvgPrice > 0 || item.suggestedPrice > 0;

      // Ghost item / TTC-only labels
      const ghostClass = item.noSalesData ? ' pc-item-ghost' : (item.ttcOnly ? ' pc-item-ttconly' : '');
      const ghostLabel = item.noSalesData
        ? '<span class="pc-ghost-label">No sales recorded</span>'
        : (item.ttcOnly ? '<span class="pc-ghost-label pc-ttconly-label">TTC only — no guild sales</span>' : '');

      return `
        <div class="pc-item${ghostClass}" data-name="${escapeHtml(item.name)}" data-item="${escapeHtml(item.name)}" onclick="showItemDetail('${escapeHtml(item.name).replace(/'/g, "\\'")}')">
          <div class="pc-item-header">
            ${iconHtml}
            ${qDot}
            <span class="pc-item-name" style="color:${qColor};">${escapeHtml(item.name)}</span>
            ${traitBadge}
            ${ghostLabel}
          </div>
          <div class="pc-item-dual-prices">
            ${!hasMM && hasTTC ? `
            <div class="pc-price-source pc-price-ttc">
              <span class="pc-price-source-tag"><span class="icon-ttc"></span> TTC</span>
              <span class="pc-price-main">${item.suggestedPrice > 0 ? gold(item.suggestedPrice) : ttcAvg} <span style="font-size:0.55rem;color:var(--t-dim);font-weight:400;">${item.suggestedPrice > 0 ? 'suggested' : 'avg'}</span></span>
              <span class="pc-price-sub">${item.suggestedPrice > 0 && item.ttcAvgPrice > 0 ? 'Avg: ' + gold(item.ttcAvgPrice) : ''}</span>
            </div>
            <div class="pc-price-source pc-price-mm pc-price-nodata">
              <span class="pc-price-source-tag"><span class="icon-mm"></span> MM</span>
              <span class="pc-price-main"><span class="t-dim">—</span></span>
              <span class="pc-price-sub">No guild sales</span>
            </div>
            ` : `
            <div class="pc-price-source pc-price-mm${hasMM ? '' : ' pc-price-nodata'}">
              <span class="pc-price-source-tag"><span class="icon-mm"></span> MM</span>
              <span class="pc-price-main">${mmAvg}</span>
              <span class="pc-price-sub">${salesDisplay} sales · ${volDisplay} vol</span>
            </div>
            <div class="pc-price-source pc-price-ttc${hasTTC ? '' : ' pc-price-nodata'}">
              <span class="pc-price-source-tag"><span class="icon-ttc"></span> TTC</span>
              <span class="pc-price-main">${ttcAvg}</span>
              <span class="pc-price-sub">Sug: ${sugDisplay}</span>
            </div>
            `}
          </div>
          <div class="pc-item-stats pc-item-stats-bottom">
            <span class="pc-stat"><span class="pc-stat-label">Last Sale</span> ${lastSeenDisplay}</span>
            <span class="pc-stat"><span class="pc-stat-label">Freq</span> ${freqDisplay}</span>
          </div>
          ${guildList ? `<div class="pc-item-guilds">${guildList}</div>` : ''}
        </div>`;
    }).join('');

    // Pagination
    if (data.pages > 1) {
      let pag = '<div class="pc-pages">';
      if (data.page > 1) pag += `<button class="btn btn-sm" data-page="${data.page - 1}">‹</button>`;
      const startP = Math.max(1, data.page - 3);
      const endP = Math.min(data.pages, data.page + 3);
      for (let p = startP; p <= endP; p++) {
        pag += `<button class="btn btn-sm ${p === data.page ? 'btn-primary' : ''}" data-page="${p}">${p}</button>`;
      }
      if (data.page < data.pages) pag += `<button class="btn btn-sm" data-page="${data.page + 1}">›</button>`;
      pag += '</div>';
      $pag.innerHTML = pag;
      $pag.querySelectorAll('[data-page]').forEach(btn => {
        btn.addEventListener('click', () => doSearch(parseInt(btn.dataset.page)));
      });
    } else {
      $pag.innerHTML = '';
    }
  }

  // Icon indicators next to selects
  function updateQualityIcon() {
    const span = document.getElementById('pc-quality-icon');
    if (!span) return;
    const v = $quality.value;
    span.innerHTML = v ? qualityDot(v) : '';
  }
  function updateTraitIcon() {
    const span = document.getElementById('pc-trait-icon');
    if (!span) return;
    const v = $trait.value;
    span.innerHTML = v ? traitIconImg(v) : '';
  }

  // Events
  $search.addEventListener('input', () => doSearch(1));
  $quality.addEventListener('change', () => { updateQualityIcon(); doSearch(1); });
  $sort.addEventListener('change', () => doSearch(currentPage));

  // ── Trait Panel Popup wiring ──
  const $traitTrigger = document.getElementById('pc-trait-trigger');
  const $traitPanel = document.getElementById('pc-trait-panel');
  const $traitTriggerText = $traitTrigger.querySelector('.trait-panel-trigger-text');

  function openTraitPanel() {
    $traitPanel.style.display = $traitPanel.style.display === 'none' ? '' : 'none';
  }
  function closeTraitPanel() { $traitPanel.style.display = 'none'; }

  function selectTrait(val, label) {
    $trait.value = val;
    $traitTrigger.dataset.value = val;
    $traitTriggerText.textContent = label || 'Any';
    $traitTrigger.classList.toggle('has-value', !!val);
    // Highlight selected item
    $traitPanel.querySelectorAll('.trait-panel-item').forEach(el => {
      el.classList.toggle('selected', el.dataset.trait === val);
    });
    updateTraitIcon();
    doSearch(1);
    closeTraitPanel();
  }

  $traitTrigger.addEventListener('click', openTraitPanel);

  $traitPanel.querySelectorAll('.trait-panel-item').forEach(el => {
    el.addEventListener('click', () => {
      selectTrait(el.dataset.trait, el.textContent);
    });
  });

  document.getElementById('pc-trait-clear').addEventListener('click', () => {
    selectTrait('', 'Any');
  });

  // Close trait panel on outside click
  document.addEventListener('click', (e) => {
    if (!$traitTrigger.contains(e.target) && !$traitPanel.contains(e.target)) {
      closeTraitPanel();
    }
  });

  document.getElementById('pc-clear').addEventListener('click', () => {
    $search.value = '';
    $quality.value = '';
    selectTrait('', 'Any');
    $sort.value = 'relevance';
    selectedGuildIds = [];
    updateQualityIcon();
    document.getElementById('pc-results').innerHTML = '<div class="empty-state">Type an item name to search the guild trader database.</div>';
    document.getElementById('pc-count').textContent = '—';
    document.getElementById('pc-pagination').innerHTML = '';
  });

  $search.focus();

  // Sprint 10.3: if the Price Check dashboard widget stashed a query, run it now.
  if (_pendingPriceQ) {
    $search.value = _pendingPriceQ;
    doSearch(1);
  }
}

// ── Dedicated item page (shareable via #/item/NAME or ?item=NAME) ────────
// Full-page layout: hero stats → Fissal suggestion → filters → price sources
// → price trend chart → guild breakdown → recent sales.
async function renderItemPage(name) {
  document.title = `${name} — Redfur`;

  $content.innerHTML = `<div class="item-page">
    <div class="item-page-nav">
      <button class="btn btn-sm ip-back-btn">← Prices</button>
      <button class="btn btn-sm ip-share-btn" title="Copy shareable link">🔗 Share</button>
    </div>
    <div class="item-page-loading"><div class="loading-spinner">Loading item data…</div></div>
  </div>`;

  // Load guild data if not cached
  if (!_priceGuildData) {
    try { _priceGuildData = await cachedApi('/api/items/guilds'); } catch { _priceGuildData = { redfur: [], others: [], isAdmin: false }; }
  }

  const QUALITY_COLORS = { worn: '#9a9a9a', white: '#e8e8e8', green: '#5bffa0', blue: '#7ab8ff', purple: '#d09cff', gold: '#ffcc40' };

  let ipFilterMode = 'tradeweek';
  let ipGuildIds = [];
  let ipSavedStart = '';
  let ipSavedEnd = '';
  let ipSavedTW = '0';

  function itemPageUrl() {
    return `${window.location.origin}${window.location.pathname}?item=${encodeURIComponent(name)}`;
  }

  function buildParams() {
    const p = new URLSearchParams();
    if (ipGuildIds.length) p.set('guilds', ipGuildIds.join(','));
    if (ipFilterMode === 'calendar') {
      if (ipSavedStart) p.set('startTs', Math.floor(new Date(ipSavedStart).getTime() / 1000));
      if (ipSavedEnd) p.set('endTs', Math.floor(new Date(ipSavedEnd + 'T23:59:59').getTime() / 1000));
    } else if (ipFilterMode === 'tradeweek') {
      const twIdx = parseInt(ipSavedTW || '0', 10);
      if (weekLabels[twIdx]) {
        const wk = weekLabels[twIdx];
        if (wk.start) p.set('startTs', Math.floor(wk.start / 1000));
        if (wk.end) p.set('endTs', Math.floor(wk.end / 1000));
      }
    }
    return p;
  }

  // Compute price trend bars from raw sales array (weekly buckets, newest first)
  function buildTrendBars(sales) {
    if (!sales || sales.length === 0) return '<div class="item-page-no-trend">No sales data for trend chart.</div>';
    // Sort ascending by timestamp
    const sorted = [...sales].sort((a, b) => a.timestamp - b.timestamp);
    const buckets = {};
    for (const s of sorted) {
      const week = Math.floor(s.timestamp / (7 * 86400));
      if (!buckets[week]) buckets[week] = { total: 0, count: 0, ts: s.timestamp };
      buckets[week].total += s.price;
      buckets[week].count++;
    }
    const weeks = Object.values(buckets).sort((a, b) => a.ts - b.ts).slice(-12);
    if (weeks.length < 2) return '<div class="item-page-no-trend">Not enough data for trend.</div>';
    const maxAvg = Math.max(...weeks.map(w => w.total / w.count));
    return `<div class="item-trend-chart">${weeks.map(w => {
      const avg = Math.round(w.total / w.count);
      const pct = maxAvg > 0 ? Math.round((avg / maxAvg) * 100) : 0;
      const label = shortDate(w.ts);
      return `<div class="item-trend-bar-wrap" title="${label}: ${avg.toLocaleString()}g avg">
        <div class="item-trend-bar" style="height:${Math.max(pct, 4)}%"></div>
        <div class="item-trend-bar-value">${avg >= 1000 ? (avg / 1000).toFixed(1) + 'k' : avg}</div>
        <div class="item-trend-bar-label">${label}</div>
      </div>`;
    }).join('')}</div>`;
  }

  async function loadAndRenderPage() {
    try {
      const data = await api(`/api/items/${encodeURIComponent(name)}/detail?${buildParams()}`);
      renderPage(data);
    } catch (err) {
      const loading = $content.querySelector('.item-page-loading');
      if (loading) loading.innerHTML = `<div class="error-msg">${escapeHtml(err.message)}</div>`;
    }
  }

  function renderPage(data) {
    const rf = data.rf;
    const ttc = data.ttc;
    const fissal = data.fissal;
    const guildBreakdown = data.guildBreakdown || [];
    const hasMM = rf.saleCount > 0;

    const iconSrc = esoIconUrl(rf.icon);
    const iconHtml = `<img class="item-page-hero-icon" src="${iconSrc}" alt=""
      onerror="if(this.src.endsWith('.png')){this.onerror=function(){this.onerror=null;this.src='${ESO_ICON_FALLBACK}'};this.src=this.src.replace('.png','.jpg')}else{this.onerror=null;this.src='${ESO_ICON_FALLBACK}'}">`;

    const displayName = rf.actualItemName || ttc.itemName || name;
    const ttcPrimary = (ttc.items || []).find(t => t.suggested > 0 || t.average > 0);
    const ttcPrice = ttcPrimary ? (ttcPrimary.suggested || ttcPrimary.average) : 0;

    const overallAvg = guildBreakdown.length
      ? Math.round(guildBreakdown.reduce((s, g) => s + g.avgPrice, 0) / guildBreakdown.length)
      : rf.avgPrice;
    const overallMin = guildBreakdown.length
      ? Math.min(...guildBreakdown.map(g => g.minPrice).filter(p => p > 0))
      : rf.minPrice;
    const overallMax = guildBreakdown.length
      ? Math.max(...guildBreakdown.map(g => g.maxPrice))
      : rf.maxPrice;

    const recentRows = (rf.sales || []).slice(0, 30).map(s => {
      const qc = QUALITY_COLORS[s.quality] || '#aaa';
      return `<tr>
        <td>${gold(s.price)}</td>
        <td>×${s.quant}</td>
        <td style="color:${qc}">${qualityDot(s.quality || '')} ${s.quality || '—'}</td>
        <td class="clickable-guild" data-guild="${escapeHtml(s.guildName || '')}">${escapeHtml(s.guildName || '—')}</td>
        <td>${shortDate(s.timestamp)}</td>
        <td class="clickable-tag" data-user="${escapeHtml(s.seller)}">${escapeHtml(s.seller)}</td>
      </tr>`;
    }).join('');

    const _gbPageId = 'ip-gb-table';

    const ttcRows = (ttc.items || []).slice(0, 10).map(t => `<tr>
      <td style="color:${QUALITY_COLORS[t.quality] || '#aaa'}">${qualityDot(t.quality || '')} ${t.quality || '—'}</td>
      <td>${traitIconImg(t.trait || '')} ${t.trait || '—'}</td>
      <td>${t.level || '—'}</td>
      <td>${gold(Math.round(t.average || 0))}</td>
      <td>${gold(Math.round(t.suggested || 0))}</td>
      <td>${t.saleCount || 0}</td>
    </tr>`).join('');

    const datePicker = crtDateRangePicker('ip-date', { startVal: ipSavedStart, endVal: ipSavedEnd });
    const twPicker = tradeWeekPicker('ip-tw', parseInt(ipSavedTW) || 0);
    const guildDd = guildFilterDropdown('ip', _priceGuildData);

    $content.innerHTML = `<div class="item-page">
      <div class="item-page-nav">
        <button class="btn btn-sm ip-back-btn">← Prices</button>
        <button class="btn btn-sm ip-share-btn" title="Copy shareable link">🔗 Share</button>
      </div>

      <!-- Hero -->
      <div class="item-page-hero">
        ${iconHtml}
        <div class="item-page-hero-info">
          <h1 class="item-page-title">${escapeHtml(displayName)}</h1>
        </div>
      </div>

      <!-- Key stats strip -->
      <div class="item-page-stats-strip">
        <div class="item-page-stat">
          <span class="item-page-stat-label">MM Avg</span>
          <span class="item-page-stat-val">${rf.avgPrice > 0 ? gold(rf.avgPrice) : '<span class="t-dim">—</span>'}</span>
        </div>
        ${ttcPrice > 0 ? `<div class="item-page-stat">
          <span class="item-page-stat-label">TTC Suggested</span>
          <span class="item-page-stat-val">${gold(Math.round(ttcPrice))}</span>
        </div>` : ''}
        ${fissal?.fissalSuggested ? `<div class="item-page-stat item-page-stat-fissal">
          <span class="item-page-stat-label"><span class="icon-fissal"></span> Fissal</span>
          <span class="item-page-stat-val">${gold(fissal.fissalSuggested)}</span>
          <span class="item-page-stat-badge fissal-confidence-${escapeHtml(fissal.confidence || 'low')}">${escapeHtml(fissal.confidence || '?')}</span>
        </div>` : ''}
        <div class="item-page-stat">
          <span class="item-page-stat-label">Sales</span>
          <span class="item-page-stat-val">${rf.saleCount.toLocaleString()}</span>
        </div>
        <div class="item-page-stat">
          <span class="item-page-stat-label">Freq</span>
          <span class="item-page-stat-val">${freqText(rf.sellFrequency)}</span>
        </div>
        <div class="item-page-stat">
          <span class="item-page-stat-label">Last Sold</span>
          <span class="item-page-stat-val">${rf.lastSell ? relativeTime(rf.lastSell) : '—'}</span>
        </div>
        <div class="item-page-stat">
          <span class="item-page-stat-label">Min / Max</span>
          <span class="item-page-stat-val">${gold(isFinite(overallMin) ? overallMin : 0)} / ${gold(overallMax)}</span>
        </div>
      </div>

      <!-- Fissal insight bar (when available) -->
      ${fissal?.fissalSuggested ? `<div class="fissal-suggestion-bar">
        <span class="fissal-suggestion-label has-tooltip" data-tooltip="AI-calculated fair market value. Source: ${escapeHtml(fissal.source)} (${escapeHtml(fissal.confidence)} confidence)."><span class="icon-fissal"></span> Fissal Suggested Price</span>
        <span class="fissal-suggestion-price">${gold(fissal.fissalSuggested)}</span>
        <span class="fissal-suggestion-badge fissal-confidence-${escapeHtml(fissal.confidence || 'low')}">${escapeHtml(fissal.confidence || '?')}</span>
      </div>` : ''}

      <!-- Filter bar -->
      <div class="detail-filter-bar detail-filter-bar-top${hasMM ? '' : ' detail-filter-bar-mm-disabled'}" style="margin-bottom:1rem;">
        ${!hasMM ? `<div class="detail-filter-mm-notice" title="These filters apply to Master Merchant guild sale records. This item has no MM data — only aggregated TTC data is available, which cannot be filtered by guild or date.">⚠ Filters apply to Master Merchant data only — not applicable for TTC-only items.</div>` : ''}
        <div class="detail-filter-section" style="flex:1;min-width:0;">
          <label class="pf-label">Guild</label>
          ${guildDd.html}
        </div>
        <div class="detail-filter-section detail-time-filter-section">
          <div class="detail-time-mode">
            <button class="btn btn-sm ${ipFilterMode === 'calendar' ? 'btn-primary' : ''}" id="ip-mode-cal">Calendar</button>
            <button class="btn btn-sm ${ipFilterMode === 'tradeweek' ? 'btn-primary' : ''}" id="ip-mode-tw">Trade Week</button>
          </div>
          <div id="ip-time-container">
            ${ipFilterMode === 'tradeweek' ? `<div class="crt-date-picker"><div class="crt-date-field"><label class="pf-label">FILTER TRADE WEEK</label>${twPicker}</div></div>` : datePicker.html}
          </div>
          <button class="btn btn-primary" id="ip-apply-filter">Apply</button>
        </div>
      </div>

      <!-- Data region (refreshed on filter changes) -->
      <div id="ip-data-region">

        <!-- Price trend -->
        <div class="item-page-section">
          <h3 class="item-page-section-title">📈 Price Trend</h3>
          ${buildTrendBars(rf.sales)}
        </div>

        <!-- Side-by-side MM + TTC -->
        <div class="detail-sources-grid" style="margin-top:0;">
          <div class="detail-source-section detail-source-mm">
            <div class="detail-source-header detail-source-header-mm"><span class="icon-mm"></span> Master Merchant</div>
            <div class="detail-avg-price-box">
              <span class="detail-avg-label">Average Price</span>
              <span class="detail-avg-value">${gold(rf.avgPrice)}</span>
            </div>
            <div class="detail-metrics-grid">
              <div class="detail-metric"><span class="detail-metric-label">Sales</span><span class="detail-metric-value">${rf.saleCount.toLocaleString()}</span></div>
              <div class="detail-metric"><span class="detail-metric-label">Volume</span><span class="detail-metric-value">${rf.volumeCount.toLocaleString()}</span></div>
              <div class="detail-metric"><span class="detail-metric-label">Min</span><span class="detail-metric-value">${gold(isFinite(overallMin) ? overallMin : 0)}</span></div>
              <div class="detail-metric"><span class="detail-metric-label">Max</span><span class="detail-metric-value">${gold(overallMax)}</span></div>
              <div class="detail-metric"><span class="detail-metric-label">Frequency</span><span class="detail-metric-value">${freqText(rf.sellFrequency)}</span></div>
              <div class="detail-metric"><span class="detail-metric-label">Last Sell</span><span class="detail-metric-value">${rf.lastSell ? relativeTime(rf.lastSell) : '—'}</span></div>
            </div>
          </div>
          ${ttcRows ? `<div class="detail-source-section detail-source-ttc">
            <div class="detail-source-header detail-source-header-ttc"><span class="icon-ttc"></span> Tamriel Trade Centre</div>
            ${ttcPrimary ? `<div class="ttc-suggested-box">
              <span class="ttc-suggested-label">Suggested Sale Price</span>
              <span class="ttc-suggested-value">${gold(Math.round(ttcPrimary.suggested || ttcPrimary.average))}</span>
            </div>` : ''}
            <div class="table-wrap"><table class="data-table">
              <thead><tr><th>Quality</th><th>Trait</th><th>Lvl</th><th>Avg</th><th>Suggested</th><th>Sales</th></tr></thead>
              <tbody>${ttcRows}</tbody>
            </table></div>
          </div>` : '<div class="detail-source-section detail-source-ttc" style="display:flex;align-items:center;justify-content:center;"><div class="empty-state">No TTC data.</div></div>'}
        </div>

        <!-- Guild Breakdown -->
        ${guildBreakdown.length ? `<div class="item-page-section detail-guild-breakdown-section">
          <h3 class="item-page-section-title">🏛 Guild Breakdown</h3>
          ${buildGuildBreakdownTable(guildBreakdown, rf, _gbPageId)}
        </div>` : ''}

        <!-- Recent Sales -->
        <div class="item-page-section">
          <h3 class="item-page-section-title">🛒 Recent Sales</h3>
          ${recentRows ? `<div class="table-wrap"><table class="data-table">
            <thead><tr><th>Price</th><th>Qty</th><th>Quality</th><th>Guild</th><th>Date</th><th>Seller</th></tr></thead>
            <tbody>${recentRows}</tbody>
          </table></div>` : '<div class="empty-state">No sales found for this filter.</div>'}
        </div>
      </div>
    </div>`;

    // Wire guild breakdown sort (full page)
    wireGuildBreakdownSort(_gbPageId, guildBreakdown, rf);

    // Wire back button
    $content.querySelector('.ip-back-btn')?.addEventListener('click', () => navigate('#/prices'));

    // Wire share button
    $content.querySelector('.ip-share-btn')?.addEventListener('click', () => {
      const url = itemPageUrl();
      navigator.clipboard?.writeText(url).then(() => {
        const btn = $content.querySelector('.ip-share-btn');
        if (btn) { btn.textContent = '✓ Copied!'; setTimeout(() => { btn.textContent = '🔗 Share'; }, 2000); }
      }).catch(() => prompt('Copy this link:', url));
    });

    // Disable MM-only filter controls when item has no MM data
    if (!hasMM) {
      const guildTrigger = $content.querySelector('#ip-guild-dd-trigger');
      const calBtn = $content.querySelector('#ip-mode-cal');
      const twBtn  = $content.querySelector('#ip-mode-tw');
      const applyBtn = $content.querySelector('#ip-apply-filter');
      const timeContainer = $content.querySelector('#ip-time-container');
      [guildTrigger, calBtn, twBtn, applyBtn].forEach(el => {
        if (!el) return;
        el.disabled = true;
        el.style.opacity = '0.38';
        el.style.cursor = 'not-allowed';
        el.style.pointerEvents = 'none';
      });
      if (timeContainer) {
        timeContainer.querySelectorAll('input, select, button').forEach(el => {
          el.disabled = true;
          el.style.opacity = '0.38';
          el.style.cursor = 'not-allowed';
        });
      }
    }

    // Wire filter mode toggle
    $content.querySelector('#ip-mode-cal')?.addEventListener('click', () => {
      ipFilterMode = 'calendar';
      const tc = document.getElementById('ip-time-container');
      if (tc) tc.innerHTML = crtDateRangePicker('ip-date', { startVal: ipSavedStart, endVal: ipSavedEnd }).html;
      $content.querySelector('#ip-mode-cal')?.classList.add('btn-primary');
      $content.querySelector('#ip-mode-tw')?.classList.remove('btn-primary');
    });
    $content.querySelector('#ip-mode-tw')?.addEventListener('click', () => {
      ipFilterMode = 'tradeweek';
      const tc = document.getElementById('ip-time-container');
      if (tc) tc.innerHTML = `<div class="crt-date-picker"><div class="crt-date-field"><label class="pf-label">FILTER TRADE WEEK</label>${tradeWeekPicker('ip-tw', parseInt(ipSavedTW) || 0)}</div></div>`;
      $content.querySelector('#ip-mode-cal')?.classList.remove('btn-primary');
      $content.querySelector('#ip-mode-tw')?.classList.add('btn-primary');
    });

    // Wire guild dropdown
    if (guildDd.wireEvents) guildDd.wireEvents((ids) => { if (hasMM) ipGuildIds = ids; });

    // Wire apply button
    $content.querySelector('#ip-apply-filter')?.addEventListener('click', async () => {
      if (!hasMM) return;
      ipSavedStart = document.getElementById('ip-date-start')?.value || ipSavedStart;
      ipSavedEnd = document.getElementById('ip-date-end')?.value || ipSavedEnd;
      ipSavedTW = document.getElementById('ip-tw')?.value || ipSavedTW;
      const region = document.getElementById('ip-data-region');
      if (region) { region.style.opacity = '0.45'; region.style.pointerEvents = 'none'; }
      try {
        const fresh = await api(`/api/items/${encodeURIComponent(name)}/detail?${buildParams()}`);
        renderPage(fresh);
      } catch (err) {
        if (region) region.innerHTML = `<div class="error-msg">${escapeHtml(err.message)}</div>`;
      }
    });
  }

  await loadAndRenderPage();
}

// ── Guild Breakdown Table helpers ────────────────────────────────────────
// Shared by showItemDetail (modal) and renderItemPage (full page).
// Sortable columns, color-coded avg price, player-color guild names.

const _gbSortState = new Map(); // tableId → { key, dir }

function _gbLastSellClass(ts) {
  if (!ts) return 'gb-last-old';
  const age = Date.now() / 1000 - ts;
  if (age < 86400)     return 'gb-last-fresh';
  if (age < 7 * 86400) return 'gb-last-recent';
  return 'gb-last-old';
}

function _buildGuildBreakdownRows(guildBreakdown, overallAvg) {
  if (!guildBreakdown.length) return '<tr><td colspan="8" class="empty-state" style="text-align:center;">No guild data.</td></tr>';
  const maxAvg = Math.max(...guildBreakdown.map(g => g.avgPrice || 0), 1);
  return guildBreakdown.map(g => {
    const gColor = playerColor(g.guildName);
    const barPct = Math.round(((g.avgPrice || 0) / maxAvg) * 100);
    let priceClass = 'gb-price-mid';
    if (overallAvg > 0) {
      const ratio = g.avgPrice / overallAvg;
      if (ratio < 0.90) priceClass = 'gb-price-low';
      else if (ratio > 1.10) priceClass = 'gb-price-high';
    }
    const lsClass = _gbLastSellClass(g.lastSell);
    return `<tr class="gb-row">
      <td class="gb-guild-name clickable-guild" data-guild="${escapeHtml(g.guildName)}">
        <span class="gb-guild-dot" style="background:${gColor}"></span>
        <span style="color:${gColor}">${escapeHtml(g.guildName)}</span>
      </td>
      <td class="gb-price-cell ${priceClass}">
        <div class="gb-price-val">${gold(g.avgPrice)}</div>
        <div class="gb-price-bar-track"><div class="gb-price-bar-fill" style="width:${barPct}%"></div></div>
      </td>
      <td class="gb-num">${gold(g.minPrice)}</td>
      <td class="gb-num">${gold(g.maxPrice)}</td>
      <td class="gb-num">${g.saleCount.toLocaleString()}</td>
      <td class="gb-num">${g.volumeCount.toLocaleString()}</td>
      <td class="gb-num">${freqText(g.sellFrequency)}</td>
      <td class="gb-num ${lsClass}">${g.lastSell ? relativeTime(g.lastSell) : '—'}</td>
    </tr>`;
  }).join('');
}

function buildGuildBreakdownTable(guildBreakdown, rf, tableId) {
  if (!guildBreakdown.length) return '';
  const overallAvg = Math.round(guildBreakdown.reduce((s, g) => s + g.avgPrice, 0) / guildBreakdown.length);
  const overallMin = Math.min(...guildBreakdown.map(g => g.minPrice).filter(p => p > 0));
  const overallMax = Math.max(...guildBreakdown.map(g => g.maxPrice));
  const { key: sk = 'avgPrice', dir: sd = 'desc' } = _gbSortState.get(tableId) || {};
  const arrowFor = (col) => col === sk ? (sd === 'desc' ? ' ▾' : ' ▴') : '';
  const activeCls = (col) => col === sk ? ' gb-th-active' : '';

  const COLS = [
    { key: 'name',          label: 'Guild' },
    { key: 'avgPrice',      label: 'Avg'   },
    { key: 'minPrice',      label: 'Min'   },
    { key: 'maxPrice',      label: 'Max'   },
    { key: 'saleCount',     label: 'Sales' },
    { key: 'volumeCount',   label: 'Vol'   },
    { key: 'sellFrequency', label: 'Freq'  },
    { key: 'lastSell',      label: 'Last'  },
  ];

  const sorted = _gbApplySort([...guildBreakdown], sk, sd);
  return `<div class="gb-wrap">
    <table class="guild-breakdown-table" id="${tableId}">
      <thead><tr>${COLS.map(c =>
        `<th class="gb-th gb-th-sortable${activeCls(c.key)}" data-sort="${c.key}">${c.label}<span class="gb-sort-arrow">${arrowFor(c.key)}</span></th>`
      ).join('')}</tr></thead>
      <tbody id="${tableId}-tbody">${_buildGuildBreakdownRows(sorted, overallAvg)}</tbody>
      <tfoot><tr class="gb-overall-row">
        <td>Overall</td>
        <td class="gb-num">${gold(overallAvg)}</td>
        <td class="gb-num">${gold(isFinite(overallMin) ? overallMin : 0)}</td>
        <td class="gb-num">${gold(overallMax)}</td>
        <td class="gb-num">${rf.saleCount.toLocaleString()}</td>
        <td class="gb-num">${rf.volumeCount.toLocaleString()}</td>
        <td class="gb-num">${freqText(rf.sellFrequency)}</td>
        <td class="gb-num">${rf.lastSell ? relativeTime(rf.lastSell) : '—'}</td>
      </tr></tfoot>
    </table>
  </div>`;
}

function _gbApplySort(arr, key, dir) {
  return arr.sort((a, b) => {
    let av = key === 'name' ? (a.guildName || '').toLowerCase() : (a[key] || 0);
    let bv = key === 'name' ? (b.guildName || '').toLowerCase() : (b[key] || 0);
    if (av < bv) return dir === 'asc' ? -1 : 1;
    if (av > bv) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

function wireGuildBreakdownSort(tableId, guildBreakdown, rf) {
  const table = document.getElementById(tableId);
  if (!table) return;
  if (!_gbSortState.has(tableId)) _gbSortState.set(tableId, { key: 'avgPrice', dir: 'desc' });
  const overallAvg = guildBreakdown.length
    ? Math.round(guildBreakdown.reduce((s, g) => s + g.avgPrice, 0) / guildBreakdown.length) : 0;

  table.querySelectorAll('th[data-sort]').forEach(th => {
    th.addEventListener('click', () => {
      const state = _gbSortState.get(tableId);
      const col = th.dataset.sort;
      state.dir = state.key === col ? (state.dir === 'asc' ? 'desc' : 'asc') : 'desc';
      state.key = col;
      // Update header indicators
      table.querySelectorAll('th[data-sort]').forEach(h => {
        h.classList.toggle('gb-th-active', h.dataset.sort === col);
        const arrow = h.querySelector('.gb-sort-arrow');
        if (arrow) arrow.textContent = h.dataset.sort === col ? (state.dir === 'desc' ? ' ▾' : ' ▴') : '';
      });
      // Re-render rows
      const tbody = document.getElementById(tableId + '-tbody');
      if (tbody) tbody.innerHTML = _buildGuildBreakdownRows(_gbApplySort([...guildBreakdown], state.key, state.dir), overallAvg);
    });
  });
}

// ── Item detail popup (enhanced with guild breakdown + date filtering) ──
async function showItemDetail(name) {
  pushModalRenderer(() => showItemDetail(name));
  const $modal = document.getElementById('profile-modal');
  const $content = document.getElementById('profile-modal-content');
  $content.innerHTML = '<div class="loading-spinner">Loading item details...</div>';
  $modal.style.display = 'flex';

  // Load guild data if not cached
  if (!_priceGuildData) {
    try { _priceGuildData = await cachedApi('/api/items/guilds'); } catch { _priceGuildData = { redfur: [], others: [], isAdmin: false }; }
  }

  const QUALITY_COLORS = { worn: '#9a9a9a', white: '#e8e8e8', green: '#5bffa0', blue: '#7ab8ff', purple: '#d09cff', gold: '#ffcc40' };

  // State for the detail view
  let detailGuildIds = [];
  let filterMode = 'calendar'; // 'calendar' or 'tradeweek'
  let _savedStartDate = '';
  let _savedEndDate = '';
  let _savedTW = '0';
  let _savedFilterMode = filterMode;

  async function loadAndRender() {
    // Sprint 8.9: remember focused filter input + caret position so re-render
    // doesn't yank focus away mid-typing
    const activeId = document.activeElement?.id || null;
    const selStart = activeId && document.activeElement?.selectionStart;
    const selEnd = activeId && document.activeElement?.selectionEnd;
    const params = new URLSearchParams();
    if (detailGuildIds.length) params.set('guilds', detailGuildIds.join(','));

    if (filterMode === 'calendar') {
      const startVal = document.getElementById('detail-date-start')?.value || _savedStartDate || '';
      const endVal = document.getElementById('detail-date-end')?.value || _savedEndDate || '';
      if (startVal) params.set('startTs', Math.floor(new Date(startVal).getTime() / 1000));
      if (endVal) params.set('endTs', Math.floor(new Date(endVal + 'T23:59:59').getTime() / 1000));
    } else if (filterMode === 'tradeweek') {
      const twIdx = parseInt(document.getElementById('detail-tw')?.value || _savedTW || '0', 10);
      if (weekLabels[twIdx]) {
        const wk = weekLabels[twIdx];
        if (wk.start) params.set('startTs', Math.floor(wk.start / 1000));
        if (wk.end) params.set('endTs', Math.floor(wk.end / 1000));
      }
    }

    try {
      const data = await api(`/api/items/${encodeURIComponent(name)}/detail?${params}`);
      renderDetail(data);
      // Sprint 8.9: restore focus to filter input that was typed into
      if (activeId) {
        const restored = document.getElementById(activeId);
        if (restored) {
          restored.focus();
          if (typeof selStart === 'number' && typeof restored.setSelectionRange === 'function') {
            try { restored.setSelectionRange(selStart, selEnd); } catch { /* some input types reject */ }
          }
        }
      }
    } catch (err) {
      $content.innerHTML = `${modalNavBtnHtml()}
        <div class="error-msg">${escapeHtml(err.message)}</div>
        <button type="button" class="btn btn-primary" style="margin-top:0.5rem;" data-ui-action="retry-item-detail" data-item="${escapeHtml(name)}">Retry</button>`;
    }
  }

  function renderDetail(data) {
    // Sprint 11 Phase 4: bundler caught pre-existing bug — `rf` was declared
    // const but then reassigned (`rf = rf2`) further down on metric-trend
    // re-renders. Changed to `let` to match the clearly-intended behavior.
    let rf = data.rf;
    const ttc = data.ttc;
    const guildBreakdown = data.guildBreakdown || [];

    const datePicker = crtDateRangePicker('detail-date', { startVal: _savedStartDate, endVal: _savedEndDate });
    const twPicker = tradeWeekPicker('detail-tw', parseInt(_savedTW) || 0);
    filterMode = _savedFilterMode || filterMode;
    const guildDd = guildFilterDropdown('detail', _priceGuildData);
    const iconSrc = esoIconUrl(rf.icon);
    const iconHtml = `<img class="detail-item-icon" src="${iconSrc}" alt="" onerror="if(this.src.endsWith('.png')){this.onerror=function(){this.onerror=null;this.src='${ESO_ICON_FALLBACK}'};this.src=this.src.replace('.png','.jpg')}else{this.onerror=null;this.src='${ESO_ICON_FALLBACK}'}">`;

    // TTC price variants
    const ttcRows = (ttc.items || []).slice(0, 10).map(t => `
      <tr>
        <td style="color:${QUALITY_COLORS[t.quality] || '#aaa'};">${qualityDot(t.quality || '')} ${t.quality || '—'}</td>
        <td>${traitIconImg(t.trait || '')} ${t.trait || '—'}</td>
        <td>${t.level || '—'}</td>
        <td>${gold(Math.round(t.average || 0))}</td>
        <td>${gold(Math.round(t.suggested || 0))}</td>
        <td>${t.saleCount || 0}</td>
      </tr>
    `).join('');

    // Recent RF sales (last 25)
    const recentSales = (rf.sales || []).slice(0, 25).map(s => {
      const qc = QUALITY_COLORS[s.quality] || '#aaa';
      return `<tr>
        <td>${gold(s.price)}</td>
        <td>×${s.quant}</td>
        <td style="color:${qc};">${qualityDot(s.quality || '')} ${s.quality || '—'}</td>
        <td class="clickable-guild" data-guild="${escapeHtml(s.guildName || '')}">${escapeHtml(s.guildName || '—')}</td>
        <td>${shortDate(s.timestamp)}</td>
        <td class="clickable-tag" data-user="${escapeHtml(s.seller)}">${escapeHtml(s.seller)}</td>
      </tr>`;
    }).join('');

    // Guild breakdown — rendered via shared helper (sortable, color-coded)
    const _gbModalId = 'detail-gb-table';
    const overallAvg = guildBreakdown.length > 0
      ? Math.round(guildBreakdown.reduce((s, g) => s + g.avgPrice, 0) / guildBreakdown.length)
      : rf.avgPrice;
    const overallMin = guildBreakdown.length > 0
      ? Math.min(...guildBreakdown.map(g => g.minPrice).filter(p => p > 0))
      : rf.minPrice;
    const overallMax = guildBreakdown.length > 0
      ? Math.max(...guildBreakdown.map(g => g.maxPrice))
      : rf.maxPrice;

    $content.innerHTML = `
      ${modalNavBtnHtml()}
      <div class="detail-header-row">
        ${iconHtml}
        <h2 class="profile-name">${escapeHtml(rf.actualItemName || ttc.itemName || name)}</h2>
        <a class="btn btn-sm detail-open-page-btn" data-ui-action="open-item-page" data-route="/item/${encodeURIComponent(name)}" title="Open dedicated item page">↗ Full Page</a>
        <button class="btn btn-sm fissal-tip-btn" id="fissal-tip-btn" title="Ask Fissal for selling tips">⚙ Ask Fissal</button>
      </div>
      <div class="fissal-tip-box" id="fissal-tip-box" style="display:none;">
        <div class="fissal-tip-header"><span class="fissal-avatar-sm">⚙</span> Fissal's Tips</div>
        <div class="fissal-tip-content" id="fissal-tip-content"></div>
      </div>

      ${data.fissal?.fissalSuggested ? `
      <div class="fissal-suggestion-bar" id="detail-fissal-bar">
        <span class="fissal-suggestion-label has-tooltip" data-tooltip="AI-calculated fair market value based on recent Redfur guild sales and server-wide trends. Source: ${escapeHtml(data.fissal.source)} (${escapeHtml(data.fissal.confidence)} confidence)."><span class="icon-fissal"></span> Fissal Suggested Price</span>
        <span class="fissal-suggestion-price">${gold(data.fissal.fissalSuggested)}</span>
        <span class="fissal-suggestion-badge fissal-confidence-${escapeHtml(data.fissal.confidence)} has-tooltip" data-tooltip="${data.fissal.confidence === 'high' ? 'High confidence — based on many recent guild sales with strong price agreement.' : data.fissal.confidence === 'medium' ? 'Moderate confidence — fewer sales or wider price variation. Use as a guideline.' : data.fissal.confidence === 'low' ? 'Low confidence estimate — limited data available. Verify with in-game market checks.' : 'No reliable pricing data. Fissal cannot make a confident suggestion.'}">${escapeHtml(data.fissal.confidence)}</span>
      </div>` : '<div class="fissal-suggestion-bar" id="detail-fissal-bar" style="display:none;"></div>'}

      <!-- Guild Filter — Top Position -->
      <div class="detail-filter-bar detail-filter-bar-top">
        <div class="detail-filter-section" style="flex:1;min-width:0;">
          <label class="pf-label">Guild</label>
          ${guildDd.html}
        </div>
        <div class="detail-filter-section detail-time-filter-section">
          <div class="detail-time-mode">
            <button class="btn btn-sm ${filterMode === 'calendar' ? 'btn-primary' : ''}" id="detail-mode-cal">Calendar</button>
            <button class="btn btn-sm ${filterMode === 'tradeweek' ? 'btn-primary' : ''}" id="detail-mode-tw">Trade Week</button>
          </div>
          <div id="detail-time-container">
            ${filterMode === 'calendar' ? datePicker.html : `<div class="crt-date-picker"><div class="crt-date-field"><label class="pf-label">FILTER TRADE WEEK</label>${twPicker}</div></div>`}
          </div>
          <button class="btn btn-primary" id="detail-apply-filter">Apply</button>
        </div>
      </div>

      <!-- ═══ SIDE-BY-SIDE: MM + TTC — Primary Pricing ═══ -->
      <div class="detail-sources-grid">
        <!-- ═══ MASTER MERCHANT (Redfur Guilds) ═══ -->
        <div class="detail-source-section detail-source-mm">
          <div class="detail-source-header detail-source-header-mm"><span class="icon-mm"></span> Master Merchant</div>

          <!-- Average Price — prominent -->
          <div class="detail-avg-price-box">
            <span class="detail-avg-label">Average Price</span>
            <span class="detail-avg-value" id="detail-metric-avg">${gold(rf.avgPrice)}</span>
          </div>

          <!-- Secondary MM Metrics -->
          <div class="detail-metrics-grid">
            <div class="detail-metric clickable-metric" data-metric="sales"><span class="detail-metric-label">Sales</span><span class="detail-metric-value" id="detail-metric-sales">${rf.saleCount.toLocaleString()}</span></div>
            <div class="detail-metric clickable-metric" data-metric="volume"><span class="detail-metric-label">Volume</span><span class="detail-metric-value" id="detail-metric-volume">${rf.volumeCount.toLocaleString()}</span></div>
            <div class="detail-metric clickable-metric" data-metric="min"><span class="detail-metric-label">Min</span><span class="detail-metric-value" id="detail-metric-min">${gold(isFinite(overallMin) ? overallMin : 0)}</span></div>
            <div class="detail-metric clickable-metric" data-metric="max"><span class="detail-metric-label">Max</span><span class="detail-metric-value" id="detail-metric-max">${gold(overallMax)}</span></div>
            <div class="detail-metric"><span class="detail-metric-label">Frequency</span><span class="detail-metric-value" id="detail-metric-freq">${freqText(rf.sellFrequency)}</span></div>
            <div class="detail-metric"><span class="detail-metric-label">Last Sell</span><span class="detail-metric-value" id="detail-metric-lastsell">${rf.lastSell ? relativeTime(rf.lastSell) : '—'}</span></div>
          </div>
        </div>

        <!-- ═══ TAMRIEL TRADE CENTRE ═══ -->
        ${ttcRows ? `
          <div class="detail-source-section detail-source-ttc" id="detail-ttc-section">
            <div class="detail-source-header detail-source-header-ttc"><span class="icon-ttc"></span> Tamriel Trade Centre</div>

            <!-- TTC Suggested Sale Price — prominent -->
            ${(() => {
              const firstSug = (ttc.items || []).find(t => t.suggested > 0);
              const firstAvg = (ttc.items || []).find(t => t.average > 0);
              if (firstSug) {
                return `<div class="ttc-suggested-box">
                  <span class="ttc-suggested-label">Suggested Sale Price</span>
                  <span class="ttc-suggested-value">${gold(Math.round(firstSug.suggested))}</span>
                  ${firstAvg ? `<span class="ttc-avg-nested">Avg Sold: ${gold(Math.round(firstAvg.average))}</span>` : ''}
                </div>`;
              }
              return '';
            })()}

            <div class="detail-metrics-grid">
              <div class="detail-metric"><span class="detail-metric-label">TTC Entries</span><span class="detail-metric-value">${ttc.totalCount}</span></div>
            </div>
            <div class="table-wrap"><table class="data-table">
              <thead><tr><th>Quality</th><th>Trait</th><th>Lvl</th><th>Avg</th><th>Suggested</th><th>Sales</th></tr></thead>
              <tbody>${ttcRows}</tbody>
            </table></div>
          </div>
        ` : '<div class="detail-source-section detail-source-ttc" id="detail-ttc-section" style="display:flex;align-items:center;justify-content:center;"><div class="empty-state">No TTC data available.</div></div>'}

        <!-- ═══ COMBINED "BOTH" AGGREGATION ═══ -->
        ${(() => {
          const bd = data.fissal?.breakdown || {};
          if (bd.combinedAvg > 0) {
            const mmSrc = bd.mmAvgAllTime || (data.fissal?.fissalSuggested && data.fissal?.source !== 'ttc_suggested' ? data.fissal.fissalSuggested : 0);
            const ttcSrc = bd.ttcAverage || bd.ttcSuggested || 0;
            const diff = mmSrc && ttcSrc ? Math.abs(mmSrc - ttcSrc) : 0;
            const diffPct = mmSrc && ttcSrc ? Math.round((diff / Math.max(mmSrc, ttcSrc)) * 100) : 0;
            let fissalNote = '⚙ Fissal: Both sources agree — price looks stable.';
            if (diffPct > 25) fissalNote = '⚙ Fissal: Large gap between sources — market may be volatile. Price with caution.';
            else if (diffPct > 10) fissalNote = '⚙ Fissal: Moderate spread between sources — consider listing in the middle.';
            return `<div class="detail-source-section detail-source-both">
              <div class="detail-source-header detail-source-header-both">Combined Estimate</div>
              <div class="both-avg-box">
                <span class="both-avg-label">Aggregated Average</span>
                <span class="both-avg-value">${gold(bd.combinedAvg)}</span>
                <div class="both-source-breakdown">
                  <span><span class="source-tag source-tag-mm"><span class="icon-mm"></span> MM</span> ${gold(mmSrc)}</span>
                  <span><span class="source-tag source-tag-ttc"><span class="icon-ttc"></span> TTC</span> ${gold(ttcSrc)}</span>
                </div>
              </div>
              <div class="both-fissal-note">${fissalNote}</div>
            </div>`;
          }
          return '';
        })()}
      </div>

      <!-- Guild Breakdown (full width, below) -->
      ${guildBreakdown.length ? `
        <div class="detail-source-section detail-guild-breakdown-section" style="margin-top:1rem;">
          <h3 class="detail-section-title">🏛 Guild Breakdown</h3>
          ${buildGuildBreakdownTable(guildBreakdown, rf, _gbModalId)}
        </div>
      ` : ''}

      <!-- Recent Sales (full width, below) -->
      ${recentSales ? `
        <div class="detail-source-section detail-source-mm" style="margin-top:1rem;">
          <h3 class="detail-section-title">Recent Sales</h3>
          <div class="table-wrap"><table class="data-table">
            <thead><tr><th>Price</th><th>Qty</th><th>Quality</th><th>Guild</th><th>Date</th><th>Seller</th></tr></thead>
            <tbody id="detail-sales-tbody">${recentSales}</tbody>
          </table></div>
        </div>
      ` : '<div class="empty-state" style="margin-top:1rem;">No sales found for this filter.</div>'}
    `;

    // Wire guild breakdown sort (modal)
    wireGuildBreakdownSort(_gbModalId, guildBreakdown, rf);

    // Debounced auto-apply: instant filter reactivity
    let _detailFilterTimer = null;
    // Snapshot filter state before fetching (prevents date/mode values from being lost)
    function debouncedReload() {
      if (_detailFilterTimer) clearTimeout(_detailFilterTimer);
      // Capture current filter values from live DOM
      _savedStartDate = document.getElementById('detail-date-start')?.value || _savedStartDate;
      _savedEndDate = document.getElementById('detail-date-end')?.value || _savedEndDate;
      _savedTW = document.getElementById('detail-tw')?.value || _savedTW;
      _savedFilterMode = filterMode;
      _detailFilterTimer = setTimeout(async () => {
        // Show a subtle loading indicator WITHOUT destroying the filter bar
        const dataRegion = document.getElementById('detail-data-region');
        if (dataRegion) {
          dataRegion.style.opacity = '0.45';
          dataRegion.style.pointerEvents = 'none';
        }
        try {
          const params = new URLSearchParams();
          if (detailGuildIds.length) params.set('guilds', detailGuildIds.join(','));
          if (filterMode === 'calendar') {
            const startVal = document.getElementById('detail-date-start')?.value || _savedStartDate || '';
            const endVal = document.getElementById('detail-date-end')?.value || _savedEndDate || '';
            if (startVal) params.set('startTs', Math.floor(new Date(startVal).getTime() / 1000));
            if (endVal) params.set('endTs', Math.floor(new Date(endVal + 'T23:59:59').getTime() / 1000));
          } else if (filterMode === 'tradeweek') {
            const twIdx = parseInt(document.getElementById('detail-tw')?.value || _savedTW || '0', 10);
            if (weekLabels[twIdx]) {
              const wk = weekLabels[twIdx];
              if (wk.start) params.set('startTs', Math.floor(wk.start / 1000));
              if (wk.end) params.set('endTs', Math.floor(wk.end / 1000));
            }
          }
          const freshData = await api(`/api/items/${encodeURIComponent(name)}/detail?${params}`);
          updateDataRegion(freshData);
        } catch (err) {
          if (dataRegion) {
            dataRegion.innerHTML = `<div class="error-msg">${escapeHtml(err.message)}</div>`;
          }
        }
        if (dataRegion) {
          dataRegion.style.opacity = '';
          dataRegion.style.pointerEvents = '';
        }
      }, 300);
    }

    // Targeted DOM updater — only swaps the data region, preserving filters
    function updateDataRegion(data) {
      const rf2 = data.rf;
      const ttc2 = data.ttc;
      const gb2 = data.guildBreakdown || [];

      const overallAvg2 = gb2.length > 0 ? Math.round(gb2.reduce((s, g) => s + g.avgPrice, 0) / gb2.length) : rf2.avgPrice;
      const overallMin2 = gb2.length > 0 ? Math.min(...gb2.map(g => g.minPrice).filter(p => p > 0)) : rf2.minPrice;
      const overallMax2 = gb2.length > 0 ? Math.max(...gb2.map(g => g.maxPrice)) : rf2.maxPrice;

      // Update metric values in-place via textContent
      const metricMap = {
        'detail-metric-avg': gold(rf2.avgPrice),
        'detail-metric-sales': rf2.saleCount.toLocaleString(),
        'detail-metric-volume': rf2.volumeCount.toLocaleString(),
        'detail-metric-min': gold(isFinite(overallMin2) ? overallMin2 : 0),
        'detail-metric-max': gold(overallMax2),
        'detail-metric-freq': freqText(rf2.sellFrequency),
        'detail-metric-lastsell': rf2.lastSell ? relativeTime(rf2.lastSell) : '—',
      };
      for (const [id, html] of Object.entries(metricMap)) {
        const el = document.getElementById(id);
        if (el) el.innerHTML = html;
      }

      // Update TTC section
      const ttcContainer = document.getElementById('detail-ttc-section');
      if (ttcContainer) {
        const ttcRows2 = (ttc2.items || []).slice(0, 10).map(t => `
          <tr>
            <td style="color:${QUALITY_COLORS[t.quality] || '#aaa'};">${qualityDot(t.quality || '')} ${t.quality || '—'}</td>
            <td>${traitIconImg(t.trait || '')} ${t.trait || '—'}</td>
            <td>${t.level || '—'}</td>
            <td>${gold(Math.round(t.average || 0))}</td>
            <td>${gold(Math.round(t.suggested || 0))}</td>
            <td>${t.saleCount || 0}</td>
          </tr>
        `).join('');
        if (ttcRows2) {
          const firstSug2 = (ttc2.items || []).find(t => t.suggested > 0);
          const firstAvg2 = (ttc2.items || []).find(t => t.average > 0);
          ttcContainer.innerHTML = `
            <div class="detail-source-header detail-source-header-ttc"><span class="icon-ttc"></span> Tamriel Trade Centre</div>
            ${firstSug2 ? `<div class="ttc-suggested-box">
              <span class="ttc-suggested-label">Suggested Sale Price</span>
              <span class="ttc-suggested-value">${gold(Math.round(firstSug2.suggested))}</span>
              ${firstAvg2 ? `<span class="ttc-avg-nested">Avg Sold: ${gold(Math.round(firstAvg2.average))}</span>` : ''}
            </div>` : ''}
            <div class="detail-metrics-grid">
              <div class="detail-metric"><span class="detail-metric-label">TTC Entries</span><span class="detail-metric-value">${ttc2.totalCount}</span></div>
            </div>
            <div class="table-wrap"><table class="data-table">
              <thead><tr><th>Quality</th><th>Trait</th><th>Lvl</th><th>Avg</th><th>Suggested</th><th>Sales</th></tr></thead>
              <tbody>${ttcRows2}</tbody>
            </table></div>
          `;
        } else {
          ttcContainer.innerHTML = '<div class="empty-state">No TTC data available.</div>';
        }
      }

      // Update guild breakdown table — re-render with new data, preserve sort state
      const gbSection = document.querySelector('.detail-guild-breakdown-section');
      if (gbSection && gb2.length) {
        const overallAvg2gb = Math.round(gb2.reduce((s, g) => s + g.avgPrice, 0) / gb2.length);
        const state2 = _gbSortState.get(_gbModalId) || { key: 'avgPrice', dir: 'desc' };
        const sorted2 = _gbApplySort([...gb2], state2.key, state2.dir);
        const tbody2 = document.getElementById(_gbModalId + '-tbody');
        if (tbody2) tbody2.innerHTML = _buildGuildBreakdownRows(sorted2, overallAvg2gb);
        const tfoot2 = document.querySelector('#' + _gbModalId + ' tfoot .gb-overall-row');
        if (tfoot2) {
          const min2 = Math.min(...gb2.map(g => g.minPrice).filter(p => p > 0));
          const max2 = Math.max(...gb2.map(g => g.maxPrice));
          tfoot2.children[1].innerHTML = gold(overallAvg2gb);
          tfoot2.children[2].innerHTML = gold(isFinite(min2) ? min2 : 0);
          tfoot2.children[3].innerHTML = gold(max2);
          tfoot2.children[4].textContent = rf2.saleCount.toLocaleString();
          tfoot2.children[5].textContent = rf2.volumeCount.toLocaleString();
          tfoot2.children[6].innerHTML = freqText(rf2.sellFrequency);
          tfoot2.children[7].innerHTML = rf2.lastSell ? relativeTime(rf2.lastSell) : '—';
        }
        // Re-wire sort with updated data
        wireGuildBreakdownSort(_gbModalId, gb2, rf2);
      } else if (gbSection && !gb2.length) {
        gbSection.style.display = 'none';
      }

      // Update recent sales table body
      const salesTbody = document.getElementById('detail-sales-tbody');
      if (salesTbody) {
        salesTbody.innerHTML = (rf2.sales || []).slice(0, 25).map(s => {
          const qc = QUALITY_COLORS[s.quality] || '#aaa';
          return `<tr>
            <td>${gold(s.price)}</td>
            <td>×${s.quant}</td>
            <td style="color:${qc};">${qualityDot(s.quality || '')} ${s.quality || '—'}</td>
            <td class="clickable-guild" data-guild="${escapeHtml(s.guildName || '')}">${escapeHtml(s.guildName || '—')}</td>
            <td>${shortDate(s.timestamp)}</td>
            <td class="clickable-tag" data-user="${escapeHtml(s.seller)}">${escapeHtml(s.seller)}</td>
          </tr>`;
        }).join('');
      }

      // Update Fissal suggestion bar if present
      const fissalBar = document.getElementById('detail-fissal-bar');
      if (fissalBar && data.fissal?.fissalSuggested) {
        fissalBar.innerHTML = `
          <span class="fissal-suggestion-label has-tooltip" data-tooltip="AI-calculated fair market value. Source: ${escapeHtml(data.fissal.source)} (${escapeHtml(data.fissal.confidence)} confidence)."><span class="icon-fissal"></span> Fissal Suggested Price</span>
          <span class="fissal-suggestion-price">${gold(data.fissal.fissalSuggested)}</span>
          <span class="fissal-suggestion-badge fissal-confidence-${escapeHtml(data.fissal.confidence)} has-tooltip" data-tooltip="${data.fissal.confidence === 'high' ? 'High confidence — based on many recent guild sales with strong price agreement.' : data.fissal.confidence === 'medium' ? 'Moderate confidence — fewer sales or wider price variation. Use as a guideline.' : data.fissal.confidence === 'low' ? 'Low confidence estimate — limited data available. Verify with in-game market checks.' : 'No reliable pricing data. Fissal cannot make a confident suggestion.'}">${escapeHtml(data.fissal.confidence)}</span>
        `;
        fissalBar.style.display = '';
      } else if (fissalBar) {
        fissalBar.style.display = 'none';
      }

      // Re-cache rf data for metric trend clicks
      rf = rf2;
    }

    function wireTimeInputListeners() {
      document.getElementById('detail-date-start')?.addEventListener('change', debouncedReload);
      document.getElementById('detail-date-end')?.addEventListener('change', debouncedReload);
      document.getElementById('detail-tw')?.addEventListener('change', debouncedReload);
    }
    wireTimeInputListeners();

    // Wire guild dropdown inside modal — auto-reload on guild change
    const guildCtrl = guildDd.wireEvents((ids) => {
      detailGuildIds = ids;
      debouncedReload();
    });

    // Wire Fissal tip button
    document.getElementById('fissal-tip-btn')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      const tipBox = document.getElementById('fissal-tip-box');
      const tipContent = document.getElementById('fissal-tip-content');
      if (!tipBox || !tipContent) return;
      if (!currentUser) {
        fissalToast('Log in with Discord to ask Fissal.');
        return;
      }
      tipBox.style.display = '';
      tipContent.innerHTML = '<div class="fissal-typing"><span class="fissal-typing-dot"></span><span class="fissal-typing-dot"></span><span class="fissal-typing-dot"></span></div>';

      // Build context from item data
      const itemName = rf.actualItemName || ttc.itemName || name;
      const ctx = [
        `Item: ${itemName}`,
        rf.avgPrice > 0 ? `MM Avg Price: ${rf.avgPrice.toLocaleString()}g` : null,
        rf.saleCount > 0 ? `MM Sales: ${rf.saleCount}, Volume: ${rf.volumeCount}` : null,
        rf.sellFrequency > 0 ? `Sell Frequency: ${rf.sellFrequency}/day` : null,
        ttc.items?.length ? `TTC Suggested: ${Math.round(ttc.items[0].suggested || 0).toLocaleString()}g, TTC Avg: ${Math.round(ttc.items[0].average || 0).toLocaleString()}g` : null,
        guildBreakdown.length > 0 ? `Guilds: ${guildBreakdown.map(g => g.guildName).join(', ')}` : null,
      ].filter(Boolean).join('\n');

      try {
        const result = await apiPost('/api/fissal/analyze', {
          prompt: `Give me quick selling tips for this item. Keep it brief (2-3 sentences max). Context:\n${ctx}`,
          weeksAgo: 0,
        });
        const text = result.analysis || result.response || 'Fissal has no tips right now.';
        tipContent.innerHTML = formatFissalResponse(text);
      } catch (err) {
        tipContent.innerHTML = `<span class="fissal-error">#~# ${escapeHtml(err.message)}</span>`;
      }
    });

    // Wire time mode toggle (switch UI + re-attach listeners)
    document.getElementById('detail-mode-cal')?.addEventListener('click', () => {
      filterMode = 'calendar';
      const container = document.getElementById('detail-time-container');
      if (container) {
        const dp = crtDateRangePicker('detail-date');
        container.innerHTML = dp.html;
        wireTimeInputListeners();
      }
      document.getElementById('detail-mode-cal')?.classList.add('btn-primary');
      document.getElementById('detail-mode-tw')?.classList.remove('btn-primary');
    });
    document.getElementById('detail-mode-tw')?.addEventListener('click', () => {
      filterMode = 'tradeweek';
      const container = document.getElementById('detail-time-container');
      if (container) {
        const twp = tradeWeekPicker('detail-tw', 0);
        container.innerHTML = `<div class="crt-date-picker"><div class="crt-date-field"><label class="pf-label">FILTER TRADE WEEK</label>${twp}</div></div>`;
        wireTimeInputListeners();
      }
      document.getElementById('detail-mode-tw')?.classList.add('btn-primary');
      document.getElementById('detail-mode-cal')?.classList.remove('btn-primary');
    });

    // Wire apply button — uses targeted update
    document.getElementById('detail-apply-filter')?.addEventListener('click', () => {
      debouncedReload();
    });

    // Wire clickable metrics — show trend visualization
    document.querySelectorAll('.clickable-metric').forEach(el => {
      el.addEventListener('click', () => {
        // Toggle open/close
        const existing = el.querySelector('.metric-trend-panel');
        if (existing) { existing.remove(); return; }

        // Close any other open trend panels
        document.querySelectorAll('.metric-trend-panel').forEach(p => p.remove());

        const metric = el.dataset.metric;
        const sales = rf.sales || [];
        if (!sales.length) return;

        // Build weekly trend bars from recent sales
        const weekBuckets = {};
        for (const s of sales) {
          const d = new Date(s.timestamp * 1000);
          const key = `${d.getFullYear()}-W${String(Math.ceil((d.getDate() + 6 - d.getDay()) / 7)).padStart(2, '0')}`;
          if (!weekBuckets[key]) weekBuckets[key] = { count: 0, volume: 0, total: 0, min: Infinity, max: 0 };
          weekBuckets[key].count++;
          weekBuckets[key].volume += (s.quant || 1);
          weekBuckets[key].total += (s.price || 0);
          weekBuckets[key].min = Math.min(weekBuckets[key].min, s.price || 0);
          weekBuckets[key].max = Math.max(weekBuckets[key].max, s.price || 0);
        }

        const weeks = Object.entries(weekBuckets).sort((a, b) => a[0].localeCompare(b[0])).slice(-8);
        const values = weeks.map(([, w]) => {
          if (metric === 'sales') return w.count;
          if (metric === 'volume') return w.volume;
          if (metric === 'min') return isFinite(w.min) ? w.min : 0;
          if (metric === 'max') return w.max;
          return w.count;
        });
        const maxVal = Math.max(...values, 1);

        const barsHtml = weeks.map(([label, w], i) => {
          const v = values[i];
          const pct = Math.max(5, (v / maxVal) * 100);
          return `<div class="metric-trend-bar-wrap" title="${label}: ${v.toLocaleString()}">
            <div class="metric-trend-bar" style="height:${pct}%"></div>
            <span class="metric-trend-bar-label">${label.split('-W')[1] || ''}</span>
          </div>`;
        }).join('');

        const panel = document.createElement('div');
        panel.className = 'metric-trend-panel';
        panel.innerHTML = `<div class="metric-trend-title">${metric} trend (last ${weeks.length} weeks)</div><div class="metric-trend-bars">${barsHtml}</div>`;
        el.appendChild(panel);
      });
    });
  }

  // Initial load
  await loadAndRender();
}

// ── API Documentation ────────────────────────────────────────
async function renderApiDocs() {
  $content.innerHTML = '<div class="loading-spinner">Loading API documentation...</div>';

  try {
    const docs = await api('/api/docs');
    if (!docs) return;

    let html = `<div class="panel">
      <h2 class="section-header">📄 ${escapeHtml(docs.name)} <span style="color:var(--t-dim);font-size:var(--fs-xs);">v${escapeHtml(docs.version)}</span></h2>
      <p style="color:var(--t-dim);font-size:var(--fs-sm);margin-bottom:1rem;">Base URL: <code>${escapeHtml(docs.baseUrl)}</code> &mdash; Rate limit: ${escapeHtml(docs.rateLimit)}</p>
      <p style="color:var(--t-dim);font-size:var(--fs-xs);margin-bottom:1.5rem;">${escapeHtml(docs.authentication)}</p>`;

    for (const ep of docs.endpoints) {
      const method = ep.method.toUpperCase();
      const methodClass = method === 'GET' ? 'color:var(--t-green)' : method === 'POST' ? 'color:var(--t-cyan)' : method === 'DELETE' ? 'color:var(--t-red)' : '';
      html += `<div class="api-endpoint" style="margin-bottom:1rem;padding:0.75rem;border:1px solid var(--border);border-radius:var(--radius);background:var(--surface);">`;
      html += `<div style="display:flex;align-items:center;gap:0.5rem;margin-bottom:0.3rem;">
        <span style="font-family:var(--font-mono);font-weight:bold;${methodClass}">${escapeHtml(method)}</span>
        <code style="color:var(--t-white);font-size:var(--fs-sm);">${escapeHtml(ep.path)}</code>
        <span style="color:var(--t-dim);font-size:var(--fs-xs);margin-left:auto;">${escapeHtml(ep.auth)}</span>
      </div>`;
      html += `<p style="color:var(--t-secondary);font-size:var(--fs-xs);margin-bottom:0.4rem;">${escapeHtml(ep.description)}</p>`;
      if (ep.params) {
        html += `<div style="font-size:var(--fs-xs);color:var(--t-dim);">`;
        for (const [k, v] of Object.entries(ep.params)) {
          html += `<div style="margin-left:1rem;"><code style="color:var(--neon);">${escapeHtml(k)}</code> &mdash; ${escapeHtml(v)}</div>`;
        }
        html += `</div>`;
      }
      if (ep.body) {
        html += `<div style="font-size:var(--fs-xs);color:var(--t-dim);margin-top:0.3rem;">Body:`;
        for (const [k, v] of Object.entries(ep.body)) {
          html += ` <code style="color:var(--neon);">${escapeHtml(k)}</code>: ${escapeHtml(v)}`;
        }
        html += `</div>`;
      }
      html += `</div>`;
    }

    // API Key management section (logged-in users only)
    if (currentUser) {
      html += `<div class="menu-divider" style="margin:1.5rem 0;"></div>`;
      html += `<h3 class="section-header">🔑 Your API Keys</h3>`;
      html += `<div id="api-keys-list"><div class="loading-spinner">Loading keys...</div></div>`;
      html += `<div style="margin-top:0.75rem;display:flex;gap:0.5rem;align-items:center;">
        <input type="text" class="form-input" id="api-key-label" placeholder="Key label (optional)" style="max-width:200px;">
        <button class="btn btn-primary" id="api-key-generate">Generate Key</button>
      </div>`;
      html += `<div id="api-key-result" style="margin-top:0.5rem;"></div>`;
    }

    html += `</div>`;
    $content.innerHTML = html;

    // Load and render API keys
    if (currentUser) {
      await _loadApiKeys();
      document.getElementById('api-key-generate')?.addEventListener('click', _generateApiKey);
    }
  } catch (err) {
    $content.innerHTML = `<div class="panel error-panel"><p>Failed to load API documentation.</p></div>`;
  }
}

async function _loadApiKeys() {
  const container = document.getElementById('api-keys-list');
  if (!container) return;
  try {
    const data = await api('/api/me/api-keys');
    if (!data?.keys?.length) {
      container.innerHTML = '<p style="color:var(--t-dim);font-size:var(--fs-xs);">No API keys yet.</p>';
      return;
    }
    let html = '<table class="data-table" style="font-size:var(--fs-xs);"><thead><tr><th>Prefix</th><th>Label</th><th>Created</th><th>Last Used</th><th>Requests</th><th></th></tr></thead><tbody>';
    for (const k of data.keys) {
      const created = new Date(k.created_at).toLocaleDateString();
      const lastUsed = k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : 'Never';
      const status = k.active ? '' : '<span style="color:var(--t-red);">[Revoked]</span> ';
      html += `<tr><td><code>${escapeHtml(k.key_prefix)}...</code></td><td>${status}${escapeHtml(k.label || '—')}</td><td>${created}</td><td>${lastUsed}</td><td>${k.requests}</td>`;
      html += k.active ? `<td><button class="btn btn-danger btn-sm" onclick="_revokeApiKey(${k.id})">Revoke</button></td>` : '<td></td>';
      html += `</tr>`;
    }
    html += '</tbody></table>';
    container.innerHTML = html;
  } catch { container.innerHTML = '<p style="color:var(--t-red);font-size:var(--fs-xs);">Failed to load keys.</p>'; }
}

async function _generateApiKey() {
  const label = document.getElementById('api-key-label')?.value || '';
  const resultEl = document.getElementById('api-key-result');
  try {
    const data = await apiPost('/api/me/api-keys', { label });
    resultEl.innerHTML = `<div style="background:var(--surface);border:1px solid var(--neon);padding:0.75rem;border-radius:var(--radius);font-size:var(--fs-xs);">
      <strong style="color:var(--t-green);">Key generated!</strong> Copy it now — it won't be shown again:<br>
      <code style="color:var(--neon);word-break:break-all;user-select:all;">${escapeHtml(data.key)}</code>
    </div>`;
    await _loadApiKeys();
  } catch (err) {
    resultEl.innerHTML = `<p style="color:var(--t-red);font-size:var(--fs-xs);">${escapeHtml(err.message)}</p>`;
  }
}

async function _revokeApiKey(id) {
  if (!confirm('Revoke this API key?')) return;
  try {
    await api(`/api/me/api-keys/${id}`, { method: 'DELETE' });
    toast('Key revoked.', 'success');
    await _loadApiKeys();
  } catch (err) { toast(err.message, 'error'); }
}

// ── Sync Attribution ─────────────────────────────────────────
async function renderSyncAttribution(opts = {}) {
  const { seamless = false } = opts;
  const alreadyRendered = !!$content.querySelector('.panel, .data-table');
  const prevSnap = (alreadyRendered && seamless) ? _snapshotLeaderboardValues() : null;
  if (!alreadyRendered) $content.innerHTML = '<div class="loading-spinner">Loading sync stats...</div>';
  else if (!seamless) $content.classList.add('content-loading');

  const hp = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const mode = hp.get('mode') === 'all' ? 'all' : 'week';
  const sessionSearch = hp.get('q') || '';
  const sessionGuild = hp.get('guild') || '';
  const pageSize = 25;
  const page = Math.max(0, parseInt(hp.get('p'), 10) || 0);
  const offset = page * pageSize;

  try {
    const qs = new URLSearchParams({ limit: String(pageSize), offset: String(offset), mode });
    if (sessionSearch) qs.set('q', sessionSearch);
    if (sessionGuild) qs.set('guild', sessionGuild);

    const data = await cachedApi(`/api/sync-attribution?${qs.toString()}`);
    if (!data) return;

    const lbRows = (data.leaderboard || []).map((r, i) => {
      const name = r.displayName || 'Unknown';
      return `<tr>
        <td class="rank">#${i + 1}</td>
        ${nameCell(name)}
        <td class="gold">${(r.totalPoints || 0).toLocaleString()}</td>
        <td>${r.totalSyncs || 0}</td>
        <td>${r.totalFiles || 0}</td>
        <td>${r.itemsContributed || 0}</td>
        <td>${r.salesContributed || 0}</td>
        <td>${r.ttcUpdates || 0}</td>
        <td>${relativeTime(r.lastSyncAt)}</td>
      </tr>`;
    }).join('');

    const sessionRows = (data.sessions || []).map(s => `
      <tr>
        <td class="clickable-tag" data-user="${escapeHtml(s.syncer_name)}">${escapeHtml(s.syncer_name)}</td>
        <td><span class="tag-badge ${s.sync_type === 'ttc' ? 'tag-cyan' : 'tag-green'}"><span class="icon-${s.sync_type === 'ttc' ? 'ttc' : 'mm'}"></span> ${s.sync_type.toUpperCase()}</span></td>
        <td>${s.items_added || 0}</td>
        <td>${s.sales_added || 0}</td>
        <td>${s.file_count || 0} files</td>
        <td>${relativeTime(s.started_at)}</td>
        <td>${s.guilds_synced || '—'}</td>
      </tr>
    `).join('');

    const totalSessions = data.total || 0;
    const lastPage = Math.max(0, Math.ceil(totalSessions / pageSize) - 1);
    const rangeStart = data.sessions?.length ? (offset + 1) : 0;
    const rangeEnd = offset + (data.sessions?.length || 0);

    const cards = data.guildCards || {};
    const guildCardsHtml = ['post', 'caravan', 'dealers'].map(key => {
      const info = cards[key];
      if (!info) return '';
      const syncerDisplay = info.syncedBy
        ? `<span class="value clickable-tag" data-user="${escapeHtml(info.syncedBy)}" style="font-size:var(--fs-sm);">${escapeHtml(info.syncedBy)}</span>`
        : `<span class="value" style="font-size:var(--fs-sm);color:var(--t-muted);">No user sync yet</span>`;
      return `<div class="stat-big sync-guild-card">
        <span class="label">${escapeHtml(info.guildName || key)}</span>
        ${syncerDisplay}
        <span class="label sync-guild-line" style="font-size:var(--fs-xs);color:var(--t-dim);">🔄 Last sync: ${info.finishedAt ? relativeTime(info.finishedAt) : 'never'}</span>
        <span class="label sync-guild-line" style="font-size:var(--fs-xs);color:var(--t-dim);">💰 Last noted sale: ${info.lastSaleTs ? relativeTime(info.lastSaleTs) : 'none this week'}</span>
      </div>`;
    }).join('');

    const mostSales = data.mostSalesItems || [];
    const mostVolume = data.mostVolumeItems || [];
    for (const it of [...mostSales, ...mostVolume]) {
      if (it.itemIcon) cacheItemIcon(it.itemName, it.itemIcon);
    }
    const renderTopList = (rows, metricLabel, metricKey) => rows.length
      ? `<div class="sync-top-items">${rows.map((it, idx) => {
          const safe = escapeHtml(it.itemName);
          const vol = it.totalVolume || 0;
          const avgUnit = vol > 0 ? Math.round(it.totalGold / vol) : (it.saleCount > 0 ? Math.round(it.totalGold / it.saleCount) : 0);
          return `<div class="sync-top-item clickable-item" data-item="${safe}" onclick="showItemDetail('${safe.replace(/'/g, "\\'")}')">
            <span class="sync-top-rank">#${idx + 1}</span>
            ${getItemIconHtml(it.itemName)}
            <div class="sync-top-meta">
              <div class="sync-top-name">${safe}</div>
              <div class="sync-top-gold">💰 ${gold(it.totalGold)}</div>
              <div class="sync-top-data">
                <span class="std-cell">${escapeHtml(metricLabel)} ${Number(it[metricKey] || 0).toLocaleString()}</span>
                <span class="std-cell">📦 ${vol.toLocaleString()} units</span>
                <span class="std-cell">🔁 ${it.saleCount.toLocaleString()} trades</span>
                <span class="std-cell">⚖️ ${gold(avgUnit)}/ea</span>
              </div>
            </div>
          </div>`;
        }).join('')}</div>`
      : '<div class="empty-state" style="padding:0.5rem;">No trade-week data yet.</div>';

    $content.innerHTML = `
      <div class="panel">
        <div class="panel-header"><span class="panel-title">🏠 Guild Sync Overview</span></div>
        <div class="panel-grid">${guildCardsHtml || '<div class="empty-state">No guild sync data yet.</div>'}</div>
      </div>

      <div class="panel" style="margin-top:1rem;">
        <div class="panel-header"><span class="panel-title">📊 Top Items · Sales & Volume</span></div>
        <div class="panel-grid sync-top-grid">
          <div class="sync-top-pane sync-top-sales">
            <div class="sync-top-pane-title">🔁 Most Sales</div>
            ${renderTopList(mostSales, '🔁', 'saleCount')}
          </div>
          <div class="sync-top-pane sync-top-volume">
            <div class="sync-top-pane-title">📦 Most Volume</div>
            ${renderTopList(mostVolume, '📦', 'totalVolume')}
          </div>
        </div>
      </div>

      <div class="panel" style="margin-top:1rem;">
        <div class="panel-header">
          <span class="panel-title">📡 Sync Leaderboard</span>
          <div style="display:flex;gap:0.35rem;align-items:center;">
            <button class="btn btn-sm ${mode === 'week' ? 'btn-primary' : ''}" id="sync-mode-week">📅 This Trade-Week</button>
            <button class="btn btn-sm ${mode === 'all' ? 'btn-primary' : ''}" id="sync-mode-all">🕰️ All Time</button>
            <button class="btn btn-sm" id="sync-points-info" title="How sync points are calculated">ℹ️ Points Info</button>
          </div>
        </div>
        <div class="table-wrap"><table class="data-table">
          <thead><tr><th></th><th>Syncer</th><th>Points</th><th>Syncs</th><th>Files</th><th>Items</th><th>Sales</th><th>TTC</th><th>Last</th></tr></thead>
          <tbody>${lbRows || '<tr><td colspan="9" class="empty-state">No sync data yet.</td></tr>'}</tbody>
        </table></div>
      </div>

      <div class="panel" style="margin-top:1rem;">
        <div class="panel-header">
          <span class="panel-title">📋 Sync Sessions</span>
          <span class="stat-value" style="font-size:var(--fs-xs);">${totalSessions.toLocaleString()} total</span>
        </div>
        <div class="filter-bar" style="display:flex;flex-wrap:wrap;gap:0.5rem;padding:0.5rem 0.75rem;border-bottom:1px solid var(--screen-border);">
          <div class="filter-group">
            <label>Search Player</label>
            <input type="text" class="form-input" id="sync-session-search" value="${escapeHtml(sessionSearch)}" placeholder="Player name..." style="min-width:170px;">
          </div>
          <div class="filter-group">
            <label>Guild</label>
            <select class="form-input" id="sync-session-guild" style="min-width:150px;">
              <option value="" ${!sessionGuild ? 'selected' : ''}>All guilds</option>
              <option value="post" ${sessionGuild === 'post' ? 'selected' : ''}>Redfur Trading Post</option>
              <option value="caravan" ${sessionGuild === 'caravan' ? 'selected' : ''}>Redfur Trading Caravan</option>
              <option value="dealers" ${sessionGuild === 'dealers' ? 'selected' : ''}>Redfur Dealers</option>
            </select>
          </div>
        </div>
        <div class="table-wrap"><table class="data-table">
          <thead><tr><th>Syncer</th><th>Type</th><th>Items</th><th>Sales</th><th>Files</th><th>When</th><th>Guilds</th></tr></thead>
          <tbody>${sessionRows || '<tr><td colspan="7" class="empty-state">No sessions for this filter.</td></tr>'}</tbody>
        </table></div>
        <div class="log-pagination" style="display:flex;align-items:center;justify-content:space-between;gap:0.75rem;padding:0.6rem 0.9rem;border-top:1px solid var(--screen-border);font-size:var(--fs-sm);">
          <span class="text-muted">${data.sessions?.length ? `Showing ${rangeStart}–${rangeEnd} of ${totalSessions.toLocaleString()}` : 'No sessions on this page.'}</span>
          <div style="display:flex;gap:0.4rem;align-items:center;">
            <button class="btn btn-sm" id="sync-prev" ${page <= 0 ? 'disabled' : ''}>‹ Prev</button>
            <span class="text-mono">Page ${page + 1} / ${lastPage + 1}</span>
            <button class="btn btn-sm" id="sync-next" ${page >= lastPage ? 'disabled' : ''}>Next ›</button>
          </div>
        </div>
      </div>
    `;

    const gotoPage = (n) => {
      const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
      if (n <= 0) p.delete('p'); else p.set('p', String(n));
      const qs2 = p.toString();
      history.replaceState(null, '', `#/sync-stats${qs2 ? '?' + qs2 : ''}`);
      renderSyncAttribution();
    };

    document.getElementById('sync-prev')?.addEventListener('click', () => gotoPage(page - 1));
    document.getElementById('sync-next')?.addEventListener('click', () => gotoPage(page + 1));
    document.getElementById('sync-mode-week')?.addEventListener('click', () => {
      const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
      p.set('mode', 'week');
      p.delete('p');
      history.replaceState(null, '', `#/sync-stats?${p.toString()}`);
      renderSyncAttribution();
    });
    document.getElementById('sync-mode-all')?.addEventListener('click', () => {
      const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
      p.set('mode', 'all');
      p.delete('p');
      history.replaceState(null, '', `#/sync-stats?${p.toString()}`);
      renderSyncAttribution();
    });
    let _sessionDebounce;
    document.getElementById('sync-session-search')?.addEventListener('input', (e) => {
      clearTimeout(_sessionDebounce);
      _sessionDebounce = setTimeout(() => {
        const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const val = (e.target.value || '').trim();
        if (val) p.set('q', val); else p.delete('q');
        p.delete('p');
        history.replaceState(null, '', `#/sync-stats?${p.toString()}`);
        renderSyncAttribution();
      }, 350);
    });
    document.getElementById('sync-session-guild')?.addEventListener('change', (e) => {
      const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const val = e.target.value || '';
      if (val) p.set('guild', val); else p.delete('guild');
      p.delete('p');
      history.replaceState(null, '', `#/sync-stats?${p.toString()}`);
      renderSyncAttribution();
    });
    document.getElementById('sync-points-info')?.addEventListener('click', showSyncPointsExplainer);

    $content.classList.remove('content-loading');
    if (prevSnap) _flashLeaderboardChangedValues(prevSnap);
  } catch (err) {
    $content.classList.remove('content-loading');
    $content.innerHTML = `<div class="error-msg">Failed to load sync stats: ${escapeHtml(err.message)}</div>`;
  }
}

// ── Sync points explainer modal (Sprint 8.6) ──────────────────────────
function showSyncPointsExplainer() {
  const modal = document.createElement('div');
  modal.className = 'sync-modal-overlay';
  modal.innerHTML = `
    <div class="sync-modal">
      <div class="sync-modal-header">
        <span>ⓘ How Sync Points Work</span>
        <button class="sync-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="sync-modal-body">
        <p>Sync points reward guildmates who keep our data fresh. Every time you upload <strong>Master Merchant</strong> or <strong>TTC</strong> data, you earn points based on what you contribute.</p>
        <div class="table-wrap" style="margin-top:0.5rem;">
          <table class="data-table">
            <thead><tr><th>Action</th><th class="text-right">Points</th></tr></thead>
            <tbody>
              <tr><td>Base sync (any upload)</td><td class="text-right text-mono">+1</td></tr>
              <tr><td>Per new sale inserted</td><td class="text-right text-mono">+0.01</td></tr>
              <tr><td>Per new item inserted</td><td class="text-right text-mono">+0.05</td></tr>
              <tr><td>TTC version update</td><td class="text-right text-mono">+5</td></tr>
              <tr><td>Each guild fully refreshed</td><td class="text-right text-mono">+2</td></tr>
            </tbody>
          </table>
        </div>
        <p style="margin-top:0.6rem;font-size:var(--fs-xs);color:var(--t-dim);">
          A sync that finds no new data still counts as a heartbeat but awards 0 bonus points. Points accumulate across all Trade Weeks and never reset.
        </p>
      </div>
      <div class="sync-modal-footer">
        <button class="btn btn-primary" data-action="close">Got it</button>
      </div>
    </div>
  `;
  const close = () => modal.remove();
  modal.addEventListener('click', (e) => {
    if (e.target === modal) return close();
    const btn = e.target.closest('button');
    if (!btn) return;
    if (btn.classList.contains('sync-modal-close') || btn.dataset.action === 'close') close();
  });
  document.body.appendChild(modal);
  requestAnimationFrame(() => modal.classList.add('visible'));
}

// ── Fissal AI Sales Analysis ─────────────────────────────────
async function renderFissal() {
  if (!currentUser) return renderLoginPrompt();

  // Hide the global chat bubble while on Fissal page
  document.body.classList.add('on-fissal-page');

  $content.innerHTML = '<div class="loading-spinner">Loading Fissal...</div>';

  try {

  // Load memories
  let memories = [];
  try {
    const memData = await api('/api/me/memories');
    memories = memData?.facts || [];
  } catch { /* ok */ }

  // Analyze context options — admin vs user
  const isAdmin = currentUser.isAdmin;
  const analyzeTargets = [
    { value: 'my-sales', label: 'My Sales', admin: false },
    { value: 'items', label: 'Item Trends', admin: false },
  ];
  if (isAdmin) {
    analyzeTargets.push(
      { value: 'guild-sales', label: 'Guild Sales', admin: true },
      { value: 'all-players', label: 'All Players', admin: true },
      { value: 'kiosk', label: 'Kiosk Report', admin: true },
    );
  }

  const targetOpts = analyzeTargets.map(t =>
    `<option value="${t.value}" class="${t.admin ? 'fissal-admin-option' : 'fissal-user-option'}">${t.label}</option>`
  ).join('');

  // Memory list HTML
  const memHtml = memories.length > 0
    ? memories.map(f => {
        const factText = f.fact || f.text || (typeof f === 'string' ? f : JSON.stringify(f));
        const confidence = f.confidence ? `<span class="fissal-memory-confidence" title="Confidence: ${Math.round(f.confidence * 100)}%">${Math.round(f.confidence * 100)}%</span>` : '';
        const timestamp = f.lastUpdated || f.timestamp;
        const timeStr = timestamp ? `<span class="fissal-memory-time">${relativeTime(timestamp)}</span>` : '';
        const reason = f.reason ? `<span class="fissal-memory-reason" title="Reason">${escapeHtml(f.reason)}</span>` : '';
        const priority = f.priority ? `<span class="fissal-memory-priority priority-${escapeHtml(String(f.priority))}">${escapeHtml(String(f.priority))}</span>` : '';
        const truncated = factText.length > 60 ? factText.slice(0, 60) + '…' : factText;
        return `
        <div class="fissal-memory-fact" data-id="${escapeHtml(f.id || '')}">
          <div class="fissal-memory-fact-text fissal-memory-truncated" title="${escapeHtml(factText)}">${escapeHtml(truncated)}</div>
          <div class="fissal-memory-expanded" style="display:none;">
            <div class="fissal-memory-fact-text-full">${escapeHtml(factText)}</div>
            <div class="fissal-memory-detail-row">
              ${timeStr}${confidence}${priority}${reason}
            </div>
            <div class="fissal-memory-actions">
              <button class="fissal-memory-edit" title="Edit memory" data-id="${escapeHtml(f.id || '')}">✎ Edit</button>
              <button class="fissal-memory-delete" title="Delete memory" data-id="${escapeHtml(f.id || '')}">✕ Delete</button>
            </div>
          </div>
        </div>`;
      }).join('')
    : '<div class="empty-state" style="padding:0.5rem;font-size:var(--fs-xs);">No memories stored yet.</div>';

  $content.innerHTML = `
    <div class="fissal-page-layout">
      <!-- Sidebar: History + Memory -->
      <div class="fissal-sidebar">
        <div class="panel" style="flex:1;">
          <div class="panel-header" style="display:flex;align-items:center;justify-content:space-between;gap:0.4rem;">
            <span class="panel-title" style="font-size:var(--fs-xs);">Chat History</span>
            <button class="btn btn-secondary fissal-session-new" id="fissal-session-new" title="Start a new chat session">+ New</button>
          </div>
          <div class="fissal-history-panel" id="fissal-history-list">
            <div class="empty-state" style="padding:0.5rem;font-size:var(--fs-xs);">Chat history builds as you talk.</div>
          </div>
        </div>
        <div class="panel">
          <div class="panel-header"><span class="panel-title" style="font-size:var(--fs-xs);">🧠 Fissal's Memories</span></div>
          <div class="fissal-memory-panel" id="fissal-memory-list">
            ${memHtml}
          </div>
        </div>
      </div>

      <!-- Main: Chatbox + Input w/ Inline Controls -->
      <div class="fissal-main">
        <div class="panel fissal-main-panel">
          <div class="panel-header fissal-main-header">
            <span class="panel-title fissal-title">Fissal — Sales Analysis Sub-Routine</span>
            <span class="stat-value" style="font-size:var(--fs-xs);color:var(--t-cyan);">^,,^</span>
          </div>

          <!-- Chat area (full height) -->
          <div class="fissal-chatbox" id="fissal-chatbox">
            <div class="fissal-msg fissal-msg-bot">
              <span class="fissal-avatar fissal-avatar-svg">${fissalAvatarSvg(22)}</span>
              <div class="fissal-bubble">
                <div class="fissal-bubble-content">
                  <em>-# Fissal's eyes flicker green, gears whirring softly.</em><br>
                  ^,,^ This one's Sales Analysis Sub-Routine is online. Select a context and Fissal will crunch the numbers!
                </div>
              </div>
            </div>
          </div>

          <!-- Input area with inline context pills -->
          <div class="fissal-input-area">
            <div class="fissal-input-toolbar">
              <div class="fissal-pill">
                <label>📊</label>
                <select class="fissal-pill-select" id="fissal-target">${targetOpts}</select>
              </div>
              <div class="fissal-pill">
                <label>📅</label>
                ${weekPicker('fissal-week', 0)}
              </div>
              <div class="fissal-pill">
                <label>🏛️</label>
                <select class="fissal-pill-select" id="fissal-guild">${guildOptions()}</select>
              </div>
            </div>
            <div class="fissal-input-row">
              <input type="text" class="form-input fissal-input" id="fissal-freetext" placeholder="Ask Fissal anything..." autocomplete="off">
              <button class="btn btn-primary fissal-send-btn" id="fissal-send-btn">▸ Send</button>
            </div>
          </div>
        </div>
      </div>
    </div>`;

  const chatbox = document.getElementById('fissal-chatbox');
  const historyList = document.getElementById('fissal-history-list');
  let sessions = []; // {id,title,updated_at,message_count,legacy?}

  // Sprint 12 Phase 4: chat history is now grouped by Sessions, persisted
  // server-side. Sessions are loaded once on page open; the active session's
  // messages are rendered into the chatbox. Switching sessions reloads the
  // chatbox transcript. The "+ New" button starts a fresh session.

  async function loadSessions(opts = {}) {
    try {
      const data = await api('/api/fissal/sessions');
      sessions = Array.isArray(data?.sessions) ? data.sessions : [];
    } catch { sessions = []; }
    renderHistory();
    if (opts.autoSelect) {
      // Pick the most-recent non-legacy session, fall back to legacy if any.
      const first = sessions.find(s => !s.legacy) || sessions[0];
      if (first) await openSession(first.id);
      else clearChatbox();
    }
  }

  function clearChatbox() {
    if (!chatbox) return;
    // Keep the original greeting (firstElementChild) when emptying.
    const greeting = chatbox.firstElementChild;
    while (chatbox.lastElementChild && chatbox.lastElementChild !== greeting) {
      chatbox.removeChild(chatbox.lastElementChild);
    }
    _fissalMsgs = [];
    _fissalActiveSessionId = null;
    renderHistory();
  }

  async function openSession(sessionId) {
    if (!sessionId) return;
    try {
      const url = sessionId === 0
        ? '/api/fissal/chat-history?limit=200'   // legacy bucket
        : `/api/fissal/chat-history?session_id=${encodeURIComponent(sessionId)}&limit=500`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('history fetch failed');
      const { messages } = await res.json();
      const list = Array.isArray(messages) ? messages : [];
      // For the legacy bucket, only show NULL session rows (the API returns
      // most-recent-N when session_id is omitted, so filter client-side).
      const filtered = sessionId === 0 ? list.filter(m => !m.session_id) : list;
      _fissalMsgs = filtered.map(m => ({
        role: m.role === 'assistant' ? 'bot' : m.role,
        content: m.content,
        html: (m.role === 'bot' || m.role === 'assistant') ? formatFissalResponse(m.content) : null,
        ts: m.ts || Date.now(),
      }));
      _fissalActiveSessionId = sessionId === 0 ? null : sessionId;
      _fissalSyncAllViews();
      // Force-redraw even when no messages.
      if (chatbox && _fissalMsgs.length === 0) clearChatbox();
    } catch (err) {
      toast('Failed to load session: ' + err.message, 'error');
    }
    renderHistory();
  }

  async function createSession() {
    try {
      const data = await apiPost('/api/fissal/sessions', { title: 'New chat' });
      _fissalActiveSessionId = data?.id || null;
      _fissalMsgs = [];
      clearChatbox();
      await loadSessions();
    } catch (err) {
      toast('Failed to create session: ' + err.message, 'error');
    }
  }

  async function deleteSession(sessionId) {
    if (!sessionId || sessionId === 0) return; // legacy bucket cannot be deleted via this API
    if (!confirm('Delete this chat session and all its messages?')) return;
    try {
      await apiDelete(`/api/fissal/sessions/${sessionId}`);
      if (_fissalActiveSessionId === sessionId) clearChatbox();
      await loadSessions();
    } catch (err) {
      toast('Failed to delete session: ' + err.message, 'error');
    }
  }

  async function renameSession(sessionId, newTitle) {
    if (!sessionId || sessionId === 0) return;
    try {
      await apiPatch(`/api/fissal/sessions/${sessionId}`, { title: newTitle });
      await loadSessions();
    } catch (err) {
      toast('Failed to rename: ' + err.message, 'error');
    }
  }

  function renderHistory() {
    if (!historyList) return;
    if (sessions.length === 0) {
      historyList.innerHTML = '<div class="empty-state" style="padding:0.5rem;font-size:var(--fs-xs);">No chat sessions yet — your first message will start one.</div>';
      return;
    }
    historyList.innerHTML = sessions.map(s => {
      const isActive = s.id === _fissalActiveSessionId;
      const isLegacy = !!s.legacy;
      const tsLabel = relativeTime(s.updated_at);
      const titleHtml = `<span class="fissal-history-label" data-id="${s.id}" title="${escapeHtml(s.title)}">${escapeHtml(s.title)}</span>`;
      const renameBtn = isLegacy
        ? ''
        : `<span class="fissal-history-rename" data-id="${s.id}" title="Rename">✎</span>`;
      const delBtn = isLegacy
        ? ''
        : `<span class="fissal-history-delete" data-id="${s.id}" title="Delete session">✕</span>`;
      return `<div class="fissal-history-item${isActive ? ' active' : ''}${isLegacy ? ' legacy' : ''}" data-session-id="${s.id}">
        ${titleHtml}
        <span class="fissal-history-time">${escapeHtml(tsLabel)}${s.message_count ? ` · ${s.message_count}` : ''}</span>
        ${renameBtn}${delBtn}
      </div>`;
    }).join('');

    historyList.querySelectorAll('.fissal-history-item').forEach(item => {
      item.addEventListener('click', (e) => {
        if (e.target.closest('.fissal-history-delete') || e.target.closest('.fissal-history-rename')) return;
        const id = parseInt(item.dataset.sessionId, 10);
        if (Number.isFinite(id)) openSession(id);
      });
    });
    historyList.querySelectorAll('.fissal-history-delete').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = parseInt(btn.dataset.id, 10);
        deleteSession(id);
      });
    });
    historyList.querySelectorAll('.fissal-history-rename').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = parseInt(btn.dataset.id, 10);
        const sess = sessions.find(s => s.id === id);
        const next = prompt('Rename chat session:', sess?.title || '');
        if (next && next.trim() && next.trim() !== sess?.title) renameSession(id, next.trim());
      });
    });
  }

  // Initial load: pull sessions, auto-open the most recent one. Falls back
  // to a clean empty chatbox if there are none yet.
  loadSessions({ autoSelect: true });

  document.getElementById('fissal-session-new')?.addEventListener('click', createSession);

  function _chatTimestamp() {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  function appendUserMsg(text) {
    _fissalAddMsg({ role: 'user', content: text, ts: Date.now() });
  }

  function appendBotMsg(formattedHtml, rawText) {
    _fissalAddMsg({ role: 'bot', content: rawText || '', html: formattedHtml, ts: Date.now() });
  }

  function showLoading() {
    chatbox.innerHTML += `
      <div class="fissal-msg fissal-msg-bot" id="fissal-loading">
        <span class="fissal-avatar fissal-avatar-svg">${fissalAvatarSvg(22)}</span>
        <div class="fissal-bubble"><div class="fissal-typing"><span class="fissal-typing-dot"></span><span class="fissal-typing-dot"></span><span class="fissal-typing-dot"></span></div></div>
      </div>`;
    chatbox.scrollTop = chatbox.scrollHeight;
  }

  function hideLoading() {
    const el = document.getElementById('fissal-loading');
    if (el) el.remove();
  }

  // Free-form send (auto-includes context from dropdowns)
  async function sendFreeText() {
    const input = document.getElementById('fissal-freetext');
    const msg = input.value.trim();
    if (!msg) return;
    input.value = '';

    // Auto-collect context from filter dropdowns
    const target = document.getElementById('fissal-target')?.value || 'my-sales';
    const w = parseInt(document.getElementById('fissal-week')?.value) || 0;
    const guild = document.getElementById('fissal-guild')?.value || '';

    appendUserMsg(msg);
    showLoading();

    try {
      const result = await apiPost('/api/fissal/analyze', {
        prompt: msg, weeksAgo: w, target, guild,
        session_id: _fissalActiveSessionId,
      });
      hideLoading();
      const rawText = result.analysis || result.response || 'No response.';
      // Sprint 12 Phase 4: server returns the (possibly newly-created) session id.
      if (result.session_id) {
        _fissalActiveSessionId = result.session_id;
      }
      appendBotMsg(formatFissalResponse(rawText), rawText);
      // Refresh session list so titles/timestamps stay current.
      loadSessions();
    } catch (err) {
      hideLoading();
      appendBotMsg(`<span class="fissal-error">#~# ${escapeHtml(err.message)}</span>`, `#~# ${err.message}`);
    }
  }

  document.getElementById('fissal-send-btn').addEventListener('click', sendFreeText);
  // Sprint 12 Phase 4: free-text input on the AI page also supports
  // Enter-to-send / Shift+Enter-for-newline for parity with the bubble.
  document.getElementById('fissal-freetext').addEventListener('keydown', e => {
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      sendFreeText();
    }
  });

  // Wire memory expand/collapse on click
  document.querySelectorAll('.fissal-memory-fact').forEach(fact => {
    fact.addEventListener('click', (e) => {
      // Don't toggle when clicking buttons
      if (e.target.closest('.fissal-memory-actions') || e.target.closest('button') || e.target.closest('input') || e.target.closest('textarea')) return;
      const expanded = fact.querySelector('.fissal-memory-expanded');
      const truncated = fact.querySelector('.fissal-memory-truncated');
      if (expanded && truncated) {
        const isOpen = expanded.style.display !== 'none';
        expanded.style.display = isOpen ? 'none' : '';
        truncated.style.display = isOpen ? '' : 'none';
        fact.classList.toggle('fissal-memory-fact-expanded', !isOpen);
      }
    });
  });

  // Wire memory deletion
  document.querySelectorAll('.fissal-memory-delete').forEach(btn => {
    btn.addEventListener('click', async () => {
      const factId = btn.dataset.id;
      if (!factId) return;
      try {
        await apiDelete(`/api/me/memories/${encodeURIComponent(factId)}`);
        btn.closest('.fissal-memory-fact')?.remove();
        const memList = document.getElementById('fissal-memory-list');
        if (memList && !memList.querySelector('.fissal-memory-fact')) {
          memList.innerHTML = '<div class="empty-state" style="padding:0.5rem;font-size:var(--fs-xs);">No memories stored yet.</div>';
        }
      } catch (err) {
        toast('Failed to delete memory: ' + err.message, 'error');
      }
    });
  });

  // Wire memory editing
  document.querySelectorAll('.fissal-memory-edit').forEach(btn => {
    btn.addEventListener('click', async () => {
      const factId = btn.dataset.id;
      const factEl = btn.closest('.fissal-memory-fact');
      const textEl = factEl?.querySelector('.fissal-memory-fact-text');
      if (!factId || !textEl) return;

      const currentText = textEl.textContent;
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'form-input';
      input.value = currentText;
      input.style.cssText = 'width:100%;font-size:var(--fs-xs);';
      textEl.replaceWith(input);
      input.focus();

      async function saveEdit() {
        const newText = input.value.trim();
        if (!newText || newText === currentText) {
          const span = document.createElement('div');
          span.className = 'fissal-memory-fact-text';
          span.textContent = currentText;
          input.replaceWith(span);
          return;
        }
        try {
          await apiPatch(`/api/me/memories/${encodeURIComponent(factId)}`, { text: newText });
          const span = document.createElement('div');
          span.className = 'fissal-memory-fact-text';
          span.textContent = newText;
          input.replaceWith(span);
        } catch (err) {
          toast('Failed to edit memory: ' + err.message, 'error');
          const span = document.createElement('div');
          span.className = 'fissal-memory-fact-text';
          span.textContent = currentText;
          input.replaceWith(span);
        }
      }

      input.addEventListener('blur', saveEdit);
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter') saveEdit();
        if (e.key === 'Escape') {
          const span = document.createElement('div');
          span.className = 'fissal-memory-fact-text';
          span.textContent = currentText;
          input.replaceWith(span);
        }
      });
    });
  });

  } catch (err) {
    // Ensure spinner never gets stuck — show error with retry option
    $content.innerHTML = `<div class="error-msg">Failed to load Fissal: ${escapeHtml(err.message)}<br><button class="btn btn-primary" onclick="renderFissal()" style="margin-top:0.5rem;">↻ Retry</button></div>`;
  }
}

/** Fissal avatar HTML — bot's live Discord avatar, falling back to local asset then emoji */
function fissalAvatarSvg(size = 26) {
  const src = _fissalAvatarUrl || '/assets/fissal-icon.png';
  return `<span class="fissal-avatar-image-wrap" style="width:${size}px;height:${size}px;">
    <img class="fissal-avatar-image" src="${escapeHtml(src)}" width="${size}" height="${size}" alt="Fissal"
      onerror="if(this.src.includes('/assets/')){this.onerror=null;this.style.display='none';this.nextElementSibling.style.display='inline-flex';}else{this.src='/assets/fissal-icon.png';}">
    <span class="fissal-avatar-fallback" style="display:none;width:${size}px;height:${size}px;line-height:${size}px;">🐱</span>
  </span>`;
}

/** Fetch Fissal's live bot avatar URL once per session after the user logs in. */
async function _fetchFissalAvatar() {
  if (_fissalAvatarUrl) return; // already loaded
  try {
    const data = await api('/api/fissal/info');
    if (data?.avatarUrl) {
      _fissalAvatarUrl = data.avatarUrl;
      // Update already-rendered Fissal avatars (bubble button + any open chat + header)
      document.querySelectorAll('.fissal-avatar-image, .fissal-face-image, #fissal-header-avatar').forEach(img => {
        img.src = _fissalAvatarUrl;
      });
    }
  } catch { /* non-fatal — falls back to local asset */ }
}

// ── Fissal shared chat session helpers ──────────────────────────────────────
// Both the floating bubble and the full /fissal page draw from _fissalMsgs so
// conversations carry across surfaces without a page reload.

function _fissalUserAvatarHtml(size = 26) {
  if (currentUser?.avatar) {
    return `<img class="fissal-user-avatar" src="${escapeHtml(currentUser.avatar)}" width="${size}" height="${size}" alt="" loading="lazy" onerror="this.onerror=null;this.style.opacity='0'">`;
  }
  const initial = (currentUser?.displayName || currentUser?.username || '?')[0].toUpperCase();
  return `<span class="fissal-user-avatar fissal-user-avatar-initial" style="width:${size}px;height:${size}px;line-height:${size}px;">${escapeHtml(initial)}</span>`;
}

// surface: 'page' → .fissal-bubble  |  'bubble' → .fissal-bubble-msg
function _fissalRenderMsg(msg, surface) {
  const bCls = surface === 'bubble' ? 'fissal-bubble-msg' : 'fissal-bubble';
  const ts = new Date(msg.ts || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (msg.role === 'user') {
    return `<div class="fissal-msg fissal-msg-user">
      <div class="${bCls}">
        <div class="fissal-bubble-content">${escapeHtml(msg.content)}</div>
        <span class="fissal-bubble-time">${ts}</span>
      </div>
      ${_fissalUserAvatarHtml(26)}
    </div>`;
  }
  const html = msg.html || formatFissalResponse(msg.content);
  return `<div class="fissal-msg fissal-msg-bot">
    <span class="fissal-avatar fissal-avatar-svg">${fissalAvatarSvg(22)}</span>
    <div class="${bCls}">
      <div class="fissal-bubble-content">${html}</div>
      <span class="fissal-bubble-time">${ts}</span>
    </div>
  </div>`;
}

function _fissalAddMsg(msg) {
  _fissalMsgs.push(msg);
  _fissalSyncAllViews();
  // Mark unread when a bot reply arrives and the float panel is closed
  if (msg.role === 'bot') {
    const floatEl = document.getElementById('fissal-chat-float');
    if (!floatEl || floatEl.style.display === 'none') {
      _fissalSetUnread(true);
    }
  }
}

/** Toggle the neon unread ring on Fissal's messenger portrait. */
function _fissalSetUnread(unread) {
  const bubble = document.getElementById('fissal-bubble');
  if (!bubble) return;
  bubble.classList.toggle('has-unread', !!unread);
}

function _fissalSyncAllViews() {
  // Full-page chatbox — keep the static initial greeting, replace the rest
  const chatbox = document.getElementById('fissal-chatbox');
  if (chatbox && _fissalMsgs.length > 0) {
    const greeting = chatbox.firstElementChild;
    while (chatbox.lastElementChild && chatbox.lastElementChild !== greeting) {
      chatbox.removeChild(chatbox.lastElementChild);
    }
    chatbox.insertAdjacentHTML('beforeend', _fissalMsgs.map(m => _fissalRenderMsg(m, 'page')).join(''));
    chatbox.scrollTop = chatbox.scrollHeight;
  }
  // Floating bubble — only update if currently visible
  const floatEl = document.getElementById('fissal-chat-float');
  const msgsEl  = document.getElementById('fissal-chat-msgs');
  if (msgsEl && floatEl && floatEl.style.display !== 'none') {
    msgsEl.innerHTML = _fissalMsgs.map(m => _fissalRenderMsg(m, 'bubble')).join('');
    msgsEl.scrollTop = msgsEl.scrollHeight;
  }
}

// Sprint 10.0: mood-driven eye color. Classes on .fissal-face pick up a CSS
// var from styles.css which tints every `.eye-gem` + `.soul-gem` polygon.
// Moods: idle, happy, confused, angry, thinking, affectionate.
function setFissalMood(mood) {
  const valid = ['idle', 'happy', 'confused', 'angry', 'thinking', 'affectionate'];
  const m = valid.includes(mood) ? mood : 'idle';
  document.querySelectorAll('.fissal-face').forEach(face => {
    valid.forEach(v => face.classList.remove('mood-' + v));
    face.classList.add('mood-' + m);
  });
}

/** Commentary bubble helpers — left (bot) and right (user) */
let _fissalVoteId = 0;
function _fissalBotBubble(contentHtml) {
  const vid = ++_fissalVoteId;
  return `<div class="fissal-msg fissal-msg-bot">
    <span class="fissal-avatar fissal-avatar-svg">${fissalAvatarSvg(22)}</span>
    <div class="fissal-bubble">
      <div class="fissal-bubble-content">${contentHtml}</div>
      <div class="fissal-vote-row">
        <button class="fissal-vote-btn fissal-vote-up" data-vid="${vid}" title="Helpful">▲</button>
        <button class="fissal-vote-btn fissal-vote-down" data-vid="${vid}" title="Regenerate">▼</button>
      </div>
    </div>
  </div>`;
}
function _fissalUserBubble(text) {
  return `<div class="fissal-msg fissal-msg-user">
    <div class="fissal-bubble">
      <div class="fissal-bubble-content">${escapeHtml(text)}</div>
    </div>
  </div>`;
}

/** Format Fissal response: markdown, emotes, RP actions
 *  Sprint 12 Phase 4: Fissal must NEVER emit two consecutive newlines.
 *  We collapse any run of newlines (with optional whitespace between them)
 *  down to a single \n. This runs before markdown parsing so block elements
 *  (headings/lists) still render but never with empty paragraph gaps. */
function sanitizeFissalText(raw) {
  if (!raw) return '';
  let text = String(raw);
  // Normalise CRLF → LF first.
  text = text.replace(/\r\n?/g, '\n');
  // Strip trailing whitespace on each line (keeps line content intact).
  text = text.replace(/[ \t]+$/gm, '');
  // Collapse any run of 2+ newlines (with possible blank/whitespace lines
  // in between) into exactly one newline.
  text = text.replace(/(?:\s*\n){2,}/g, '\n');
  // Trim leading/trailing whitespace from whole response.
  return text.trim();
}

function formatFissalResponse(text) {
  if (!text) return '<em>No data returned.</em>';
  const clean = sanitizeFissalText(text);
  let html = escapeHtml(clean);

  // Code blocks (``` ... ```) — extract before inline transforms to protect contents
  const codeBlocks = [];
  html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    const placeholder = `\x00CB${codeBlocks.length}\x00`;
    codeBlocks.push(`<pre class="md-codeblock" data-lang="${lang}">${code.trim()}</pre>`);
    return placeholder;
  });

  // Convert -# RP lines to italic
  html = html.replace(/^-#\s*(.+)$/gm, '<em class="fissal-rp">$1</em>');

  // Headings (# ## ###)
  html = html.replace(/^###\s+(.+)$/gm, '<span class="md-heading md-h3">$1</span>');
  html = html.replace(/^##\s+(.+)$/gm, '<span class="md-heading md-h2">$1</span>');
  html = html.replace(/^#\s+(.+)$/gm, '<span class="md-heading md-h1">$1</span>');

  // List items (- or *)
  html = html.replace(/^[\-\*]\s+(.+)$/gm, '<span class="md-list-item">$1</span>');

  // Inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code class="md-code">$1</code>');

  // Bold (**text**)
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="md-bold">$1</strong>');

  // Italic (*text*)
  html = html.replace(/\*(.+?)\*/g, '<em class="md-italic">$1</em>');

  // Style emotes
  const emotes = {
    '0//0': 'Blushing', '#~#': 'Processing', '!,,!': 'Excited',
    '~,,~': 'Annoyed', '!,,~': 'Cheeky', '0~0': 'Curious', '^,,^': 'Happy'
  };
  for (const [emote, label] of Object.entries(emotes)) {
    const escaped = emote.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&');
    html = html.replace(new RegExp(escaped, 'g'), `<span class="fissal-emote" title="${label}">${emote}</span>`);
  }

  // Strip newlines after block-level elements (they already create line breaks via display:block)
  html = html.replace(/(<\/span>)\n(?=<span class="md-)/g, '$1');
  html = html.replace(/(<\/span>)\n/g, (match, tag, offset) => {
    // Check if the preceding span was a block element (heading or list item)
    const before = html.substring(Math.max(0, offset - 60), offset + tag.length);
    if (/md-heading|md-h[123]|md-list-item/.test(before)) return tag;
    return match;
  });

  // Convert remaining newlines to <br>
  html = html.replace(/\n/g, '<br>');

  // Restore code blocks (which have their own whitespace handling via <pre>)
  codeBlocks.forEach((block, i) => {
    html = html.replace(`\x00CB${i}\x00`, block);
  });

  // Clean up double <br> around block elements
  html = html.replace(/<br>\s*(<span class="md-heading|<span class="md-list-item|<pre class="md-codeblock)/g, '$1');
  html = html.replace(/(<\/pre>)\s*<br>/g, '$1');

  return injectFissalPageLinks(html);
}

// ── Fissal page-link injection ────────────────────────────────────────────
// After Fissal renders text, recognisable page keywords become clickable
// in-chat navigation links so users can go straight to the relevant page.
const FISSAL_PAGE_LINKS = [
  // Ordered longest-match first to avoid partial replacements
  { pattern: /\b(leaderboard|guild standings|top sellers|rankings)\b/gi,      route: '/leaderboard' },
  { pattern: /\b(your sales|my sales|sales history|sales page)\b/gi,          route: '/sales'       },
  { pattern: /\b(price check|item price|item lookup|check (?:an? )?price)\b/gi, route: '/prices'    },
  { pattern: /\b(overview|home|dashboard|main page|home page)\b/gi,            route: '/'            },
  { pattern: /\b(settings|preferences|set color|set tag)\b/gi,                route: '/settings'    },
  { pattern: /\b(sync|sync (?:your )?data|upload|synchronize)\b/gi,           route: '/sync'        },
];

function injectFissalPageLinks(html) {
  // Split on HTML tags; only mutate text nodes so we never break inner HTML
  return html.replace(/(>)([^<]+)(<)/g, (match, open, textNode, close) => {
    let node = textNode;
    for (const { pattern, route } of FISSAL_PAGE_LINKS) {
      node = node.replace(pattern, (found) =>
        `<span class="fissal-page-link" data-route="${route}">${found} <span class="fissal-page-link-arrow">\u2192</span></span>`
      );
      pattern.lastIndex = 0;
    }
    return open + node + close;
  });
}

// Delegated click handler — wired once on DOMContentLoaded.
function initFissalPageLinks() {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.fissal-page-link');
    if (!link) return;
    const route = link.dataset.route;
    if (!route) return;
    e.preventDefault();
    e.stopPropagation();
    navigate(route);
  });
}

// ══════════════════════════════════════════════════════════════════════════
// ── ADMIN PAGES ──────────────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════

function adminGuard() {
  if (!currentUser?.isAdmin) {
    $content.innerHTML = '<div class="empty-state">Admin access required.</div>';
    return false;
  }
  return true;
}

// ── Sprint 10.0: Guild Audit sub-tab renderers ────────────────────────────
// Wired to existing /api/admin/* endpoints. Each renders into the shared
// #admin-report-content host element so the Sales filter bar is replaced
// cleanly when switching tabs.
async function renderGuildAuditSubreport(type, host) {
  host.innerHTML = '<div class="loading-spinner">Loading ' + escapeHtml(type.toLowerCase()) + ' report...</div>';

  if (type === 'Guilds') {
    const data = await api('/api/admin/sales?weeksAgo=0');
    if (!data) return;
    // Aggregate sellers → per-guild totals
    const map = {};
    for (const s of data.sellers || []) {
      const g = s.guildName || 'Unknown';
      if (!map[g]) map[g] = { guildName: g, profit: 0, sales: 0, items: 0, sellers: new Set() };
      map[g].profit += s.profit || 0;
      map[g].sales  += s.saleCount || 0;
      map[g].items  += s.totalItems || 0;
      if (s.sellerName) map[g].sellers.add(s.sellerName);
    }
    const rows = Object.values(map)
      .sort((a, b) => b.profit - a.profit)
      .map(g => `<tr>
        ${guildCell(g.guildName)}
        <td class="text-right text-mono text-gold">${gold(g.profit)}</td>
        <td class="text-right text-mono">${g.sales}</td>
        <td class="text-right text-mono">${g.items}</td>
        <td class="text-right text-mono">${g.sellers.size}</td>
      </tr>`).join('');
    host.innerHTML = `
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Guild</th><th class="text-right">Revenue</th><th class="text-right">Sales</th><th class="text-right">Items</th><th class="text-right">Sellers</th></tr></thead>
          <tbody>${rows || '<tr><td colspan="5" class="empty-state">No guild data.</td></tr>'}</tbody>
        </table>
      </div>`;
    return;
  }

  if (type === 'Players') {
      const pParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const pGuild  = pParams.get('guild')  || '';
      const pMember = pParams.get('member') || '';
      const pWeeks  = parseInt(pParams.get('w')) || 0;
      const pUrl = `/api/admin/members?weeksAgo=${pWeeks}`
        + (pGuild  ? `&guild=${encodeURIComponent(pGuild)}`  : '')
        + (pMember ? `&member=${encodeURIComponent(pMember)}` : '');
      const data = await api(pUrl);
      if (!data) return;

      const pGuildOpts = [['', 'All Guilds'], ['post', 'Trading Post'], ['caravan', 'Caravan'], ['dealers', 'Dealers']]
        .map(([v, l]) => `<option value="${v}" ${pGuild === v ? 'selected' : ''}>${l}</option>`).join('');

      host.innerHTML = `
        <div class="filter-bar" style="margin-bottom:0.6rem;">
          ${weekPicker('ap-week', pWeeks)}
          <div class="filter-group">
            <label>Guild</label>
            <select class="form-input" id="ap-guild">${pGuildOpts}</select>
          </div>
          <div class="filter-group">
            <label>Player</label>
            <input type="text" class="form-input" id="ap-member" placeholder="@tag..." value="${escapeHtml(pMember)}" style="min-width:140px;">
          </div>
          <div class="filter-group" style="justify-content:flex-end;">
            <label>&nbsp;</label>
            <button class="btn btn-primary" id="ap-filter-btn">Filter</button>
          </div>
        </div>
        <div id="ap-table-host"></div>`;

      const GUILD_NAME_MAP = { post: 'Redfur Trading Post', caravan: 'Redfur Trading Caravan', dealers: 'Redfur Dealers' };
      let filtered = data.members || [];
      if (pGuild && GUILD_NAME_MAP[pGuild]) filtered = filtered.filter(m => m.guilds && m.guilds[GUILD_NAME_MAP[pGuild]]);
      if (pMember) filtered = filtered.filter(m => m.sellerName.toLowerCase().includes(pMember.toLowerCase()));

      const rows = filtered.map(m => {
        const guildList = Object.entries(m.guilds || {}).map(([g, d]) => `${g}: ${gold(d.profit)}`).join(' · ');
        return `<tr>
          ${nameCell(m.sellerName)}
          <td class="text-right text-mono text-gold">${gold(m.totalProfit)}</td>
          <td class="text-right text-mono">${m.totalSales}</td>
          <td class="text-right text-mono">${m.totalItems}</td>
          <td style="font-size:var(--fs-xs);color:var(--t-dim);">${escapeHtml(guildList)}</td>
        </tr>`;
      }).join('');

      document.getElementById('ap-table-host').innerHTML = `
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Player</th><th class="text-right">Revenue</th><th class="text-right">Sales</th><th class="text-right">Items</th><th>By Guild</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="5" class="empty-state">No player data.</td></tr>'}</tbody>
          </table>
        </div>`;

      const allSellerNames2 = (data.members || []).map(m => m.sellerName);
      attachAutocomplete(document.getElementById('ap-member'), q => allSellerNames2.filter(n => n.toLowerCase().includes(q)));

      document.getElementById('ap-filter-btn').addEventListener('click', () => {
        const w = document.getElementById('ap-week').value;
        const g = document.getElementById('ap-guild').value;
        const m = document.getElementById('ap-member').value.trim();
        let hash = `#/admin/sales?tab=players&w=${w}`;
        if (g) hash += `&guild=${encodeURIComponent(g)}`;
        if (m) hash += `&member=${encodeURIComponent(m)}`;
        navigate(hash);
        renderGuildAuditSubreport('Players', host);
      });
      return;
    }

  if (type === 'Items') {
    const data = await api('/api/admin/items?limit=100&weeksAgo=0');
    if (!data) return;
    const rows = (data.items || []).map(i => `<tr>
      <td>${escapeHtml(i.itemName || '—')}</td>
      <td class="text-right text-mono text-gold">${gold(i.totalGold)}</td>
      <td class="text-right text-mono">${i.saleCount}</td>
      <td class="text-right text-mono">${i.totalItems}</td>
      <td class="text-right text-mono">${(i.avgUnitPrice || 0).toLocaleString()}</td>
      <td class="text-right text-mono">${i.uniqueSellers}</td>
    </tr>`).join('');
    host.innerHTML = `
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Item</th><th class="text-right">Revenue</th><th class="text-right">Sales</th><th class="text-right">Units</th><th class="text-right">Avg Unit</th><th class="text-right">Sellers</th></tr></thead>
          <tbody>${rows || '<tr><td colspan="6" class="empty-state">No item data.</td></tr>'}</tbody>
        </table>
      </div>`;
    return;
  }

  if (type === 'Syncs') {
    const data = await api('/api/admin/leaderboard');
    if (!data) return;
    const rows = (data.leaderboard || []).slice(0, 50).map(r => `<tr>
      <td class="text-mono">${r.rank}</td>
      <td>${escapeHtml(r.displayName || '—')}</td>
      <td class="text-right text-mono">${(r.totalPoints || 0).toLocaleString()}</td>
      <td class="text-right text-mono">${r.syncCount || '—'}</td>
      <td class="text-mono" style="color:var(--t-dim);font-size:var(--fs-xs);">${r.lastSync ? new Date(r.lastSync * 1000).toLocaleString() : '—'}</td>
    </tr>`).join('');
    const uncat = (data.uncategorized || []).length;
    host.innerHTML = `
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Rank</th><th>Syncer</th><th class="text-right">Points</th><th class="text-right">Syncs</th><th>Last Sync</th></tr></thead>
          <tbody>${rows || '<tr><td colspan="5" class="empty-state">No sync data.</td></tr>'}</tbody>
        </table>
      </div>
      ${uncat > 0 ? `<div class="text-dim" style="font-size:var(--fs-xs);margin-top:0.5rem;">${uncat} uncategorized/system syncer${uncat === 1 ? '' : 's'} hidden.</div>` : ''}`;
    return;
  }

  host.innerHTML = `<div class="empty-state">Unknown report type: ${escapeHtml(type)}</div>`;
}

// ── Guild Audit Landing Page ─────────────────────────────────
const GUILD_AUDIT_ACCENT = {
  post:    { hue: '28',  icon: '🦊' },
  caravan: { hue: '195', icon: '🛕' },
  dealers: { hue: '280', icon: '📦' },
};

function _auditSparkline(trend) {
  if (!trend || !trend.length) return '';
  const max = Math.max(...trend.map(d => d.gold)) || 1;
  const bars = trend.map(d => {
    const h = Math.max(2, Math.round((d.gold / max) * 40));
    return `<div class="ga-spark-bar" style="height:${h}px" title="${d.date}: ${d.gold.toLocaleString()}g, ${d.sales} sales"></div>`;
  }).join('');
  return `<div class="ga-sparkline">${bars}</div>`;
}

async function renderGuildAudit() {
  if (!adminGuard()) return;
  $content.innerHTML = '<div class="loading-spinner">Loading guild audit...</div>';
  try {
    const data = await api('/api/admin/guild-audit');
    if (!data) return;

    const guildOrder = ['post', 'caravan', 'dealers'];
    const panelsHtml = guildOrder.map(key => {
      const g = data[key];
      if (!g) return '';
      const acc = GUILD_AUDIT_ACCENT[key] || { hue: '0', icon: '🏛️' };
      const rule = g.duesRule || {};
      const duesDesc = key === 'post'
        ? `≥1 sale or raffle entry every ${rule.windowDays || 10} days`
        : key === 'caravan'
        ? `≥1 sale every ${rule.windowDays || 15} days`
        : `≥25,000g (sales + raffle) every ${rule.windowDays || 10} days`;

      const summary = `
        <div class="ga-summary">
          <div class="ga-stat"><span class="ga-stat-val text-gold">${gold(g.totalGold)}</span><span class="ga-stat-lbl">13-day gold</span></div>
          <div class="ga-stat"><span class="ga-stat-val">${(g.totalSales||0).toLocaleString()}</span><span class="ga-stat-lbl">sales</span></div>
          <div class="ga-stat"><span class="ga-stat-val">${g.activeSellers||0}</span><span class="ga-stat-lbl">active sellers</span></div>
          <div class="ga-stat"><span class="ga-stat-val ${(g.underperformers||[]).length > 0 ? 'text-warn' : 'text-ok'}">${(g.underperformers||[]).length}</span><span class="ga-stat-lbl">underperformers</span></div>
        </div>`;

      let tableHtml = '';
      if (!g.underperformers || g.underperformers.length === 0) {
        tableHtml = '<div class="empty-state" style="padding:0.6rem 0;">All members meeting dues ✓</div>';
      } else {
        const isDealers = key === 'dealers';
        const rows = g.underperformers.map(u => {
          const duesDot = u.duesMet ? '' : '<span class="ga-dues-dot" title="Missing dues">●</span>';
          const lastAct = u.daysSinceActivity != null ? `${u.daysSinceActivity}d ago` : '—';
          const combined = u.goldTotal + u.raffleGold;
          return `<tr>
            <td>${nameCell(u.sellerName)} ${duesDot}</td>
            <td class="text-right text-mono">${u.saleCount}</td>
            <td class="text-right text-mono text-gold">${gold(u.goldTotal)}</td>
            ${isDealers ? `<td class="text-right text-mono">${u.raffleGold > 0 ? gold(u.raffleGold) : '—'}</td><td class="text-right text-mono ${combined >= 25000 ? 'text-ok' : 'text-warn'}">${gold(combined)}</td>` : ''}
            <td class="text-mono" style="font-size:var(--fs-xs);color:var(--t-dim);">${lastAct}</td>
          </tr>`;
        }).join('');
        const extraTh = isDealers ? '<th class="text-right">Raffle</th><th class="text-right">Combined</th>' : '';
        tableHtml = `
          <div class="table-wrap" style="max-height:260px;overflow-y:auto;">
            <table class="data-table ga-under-table">
              <thead><tr><th>Player</th><th class="text-right">Sales</th><th class="text-right">Gold</th>${extraTh}<th>Last Activity</th></tr></thead>
              <tbody>${rows}</tbody>
            </table>
          </div>`;
      }

      return `
        <div class="ga-panel" style="--ga-hue:${acc.hue};">
          <div class="ga-panel-header">
            <span class="ga-icon">${acc.icon}</span>
            <span class="ga-guild-name">${escapeHtml(g.guildName)}</span>
            <span class="ga-dues-desc">${escapeHtml(duesDesc)}</span>
          </div>
          ${_auditSparkline(g.trend)}
          ${summary}
          <div class="ga-under-label">Underperformers <span class="ga-under-note">(past 13 days, worst → best)</span></div>
          ${tableHtml}
        </div>`;
    }).join('');

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="panel-header">
          <span class="panel-title">🔍 Guild Audit — Quick View</span>
          <span style="font-size:var(--fs-xs);color:var(--t-dim);">Past 13 days · Performance &amp; Dues</span>
        </div>
        <div class="ga-panels-row">${panelsHtml}</div>
        <div style="margin-top:1rem;display:flex;gap:0.6rem;">
          <button class="btn btn-secondary" onclick="navigate('#/admin/sales')">View Sales Report</button>
          <button class="btn btn-secondary" onclick="navigate('#/admin/sales?tab=players')">View All Players</button>
        </div>
      </div>`;
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load guild audit: ${escapeHtml(err.message)}</div>`;
  }
}

// ── Admin Sales Report ───────────────────────────────────────
async function renderAdminSales(opts = {}) {
  if (!adminGuard()) return;
  const { seamless = false } = opts;
  // Sprint 10.7: seamless SSE refresh keeps existing data visible.
  const alreadyRendered = !!$content.querySelector('.panel');
  const prevSnap = (alreadyRendered && seamless) ? _snapshotLeaderboardValues() : null;
  if (!alreadyRendered) $content.innerHTML = '<div class="loading-spinner">Loading sales report...</div>';
  else if (!seamless) $content.classList.add('content-loading');

  const params = new URLSearchParams(window.location.hash.split('?')[1]);
  const weeksAgo = parseInt(params.get('w'));
  const guildFilter = params.get('guild') || '';
  const memberFilter = params.get('member') || '';
  const sortBy = params.get('sort') || 'gold-desc';
  const startDate = params.get('startDate') || '';
  const endDate = params.get('endDate') || '';
  const tabParam = (params.get('tab') || '').toLowerCase();

  try {
    let url = `/api/admin/sales?weeksAgo=${isNaN(weeksAgo) ? 0 : weeksAgo}`;
    if (guildFilter) url += `&guild=${encodeURIComponent(guildFilter)}`;
    if (memberFilter) url += `&member=${encodeURIComponent(memberFilter)}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;
    const data = await api(url);
    if (!data) return;

    // Build latest-sale map first so sorting can use it
    const sellerLatestSale = {};
    for (const seller of data.sellers) {
      const latest = seller.sales && seller.sales.length > 0
        ? seller.sales.reduce((max, s) => Math.max(max, s.timestamp || 0), 0)
        : 0;
      sellerLatestSale[seller.sellerName + '|' + seller.guildName] = latest;
    }

    // Client-side sort (supports multi-key via comma-separated)
    let sellers = sortData(data.sellers, sortBy, sellerLatestSale);

    const totalProfit = data.sellers.reduce((s, x) => s + x.profit, 0);

    const sortArrow = (col) => {
      const isActive = sortBy.startsWith(col);
      const dir = sortBy.endsWith('-desc') ? '▼' : '▲';
      return `<span class="sort-arrow">${isActive ? dir : '⇅'}</span>`;
    };

    let sellersHtml = '';
    for (const seller of sellers) {
      const ts = sellerLatestSale[seller.sellerName + '|' + seller.guildName] || 0;
      const timeStr = ts > 0 ? relativeTime(ts * 1000) : '—';
      sellersHtml += `<tr>
        ${nameCell(seller.sellerName)}
        ${guildCell(seller.guildName)}
        <td class="text-right text-mono text-gold">${gold(seller.profit)}</td>
        <td class="text-right text-mono">${seller.saleCount}</td>
        <td class="text-right text-mono">${seller.totalItems}</td>
        <td class="text-right text-mono" style="font-size:var(--fs-xs);color:var(--t-dim);" title="${ts > 0 ? new Date(ts*1000).toISOString() : ''}">${timeStr}</td>
      </tr>`;
    }

    // Report type tabs
    const reportTypes = ['Sales', 'Guilds', 'Players', 'Items', 'Syncs'];
    const tabToReport = { sales: 'Sales', guilds: 'Guilds', players: 'Players', items: 'Items', syncs: 'Syncs' };
    const activeReport = tabToReport[tabParam] || 'Sales';
    const reportTabsHtml = `<div class="report-type-bar">${reportTypes.map(t =>
      `<button class="report-type-btn${t === activeReport ? ' active' : ''}" data-report="${t}">${t}</button>`
    ).join('')}</div>`;

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="panel-header">
          <span class="panel-title">Sales Report</span>
          <span class="stat-value gold">${gold(totalProfit)} total</span>
        </div>
        ${reportTabsHtml}
        <div id="admin-report-content">
        <div class="filter-bar">
          ${weekPicker('as-week', isNaN(weeksAgo) ? 0 : weeksAgo)}
          <div class="filter-group">
            <label>Or Date Range</label>
            <div style="display:flex;gap:0.3rem;align-items:center;">
              <input type="date" class="form-input" id="as-start-date" value="${escapeHtml(startDate)}" style="min-width:120px;">
              <span style="color:var(--t-dim);">→</span>
              <input type="date" class="form-input" id="as-end-date" value="${escapeHtml(endDate)}" style="min-width:120px;">
            </div>
          </div>
          <div class="filter-group">
            <label>Guild</label>
            <select class="form-input" id="as-guild">${guildOptions()}</select>
          </div>
          <div class="filter-group">
            <label>Member</label>
            <input type="text" class="form-input" id="as-member" placeholder="@tag..." value="${escapeHtml(memberFilter)}" style="min-width:140px;">
          </div>
          ${sortToggle('as-sort', sortBy)}
          <div class="filter-group" style="justify-content:flex-end;">
            <label>&nbsp;</label>
            <button class="btn btn-primary" id="as-filter-btn">Filter</button>
          </div>
        </div>
        <div class="table-wrap">
          <table class="data-table" id="as-table">
            <thead><tr>
              <th class="sortable" data-sort="name"${sortBy.startsWith('name') ? ` class="sortable sort-active"` : ''}>Seller ${sortArrow('name')}</th>
              <th class="sortable" data-sort="guild"${sortBy.startsWith('guild') ? ` class="sortable sort-active"` : ''}>Guild ${sortArrow('guild')}</th>
              <th class="sortable text-right" data-sort="gold"${sortBy.startsWith('gold') ? ` class="sortable sort-active text-right"` : ''}>Revenue ${sortArrow('gold')}</th>
              <th class="sortable text-right" data-sort="sales"${sortBy.startsWith('sales') ? ` class="sortable sort-active text-right"` : ''}>Sales ${sortArrow('sales')}</th>
              <th class="sortable text-right" data-sort="items"${sortBy.startsWith('items') ? ` class="sortable sort-active text-right"` : ''}>Items ${sortArrow('items')}</th>
              <th class="sortable text-right" data-sort="lastsale"${sortBy.startsWith('lastsale') ? ` class="sortable sort-active text-right"` : ''}>Last Sale ${sortArrow('lastsale')}</th>
            </tr></thead>
            <tbody>${sellersHtml || '<tr><td colspan="6" class="empty-state">No sales data.</td></tr>'}</tbody>
          </table>
        </div>
        </div>
      </div>`;
    $content.classList.remove('content-loading');
    // Sprint 10.7: flash changed rows on seamless background refresh.
    if (prevSnap) _flashLeaderboardChangedValues(prevSnap);

    if (guildFilter) document.getElementById('as-guild').value = guildFilter;

    // Wire autocomplete on member filter using known seller names
    const allSellerNames = [...new Set(data.sellers.map(s => s.sellerName).filter(Boolean))].sort();
    attachAutocomplete(document.getElementById('as-member'), (q) =>
      allSellerNames.filter(n => n.toLowerCase().includes(q))
    );

    // If date range is set, clear the week picker (and vice versa)
    const $weekPicker = document.getElementById('as-week');
    const $startDate = document.getElementById('as-start-date');
    const $endDate = document.getElementById('as-end-date');
    if ($startDate && $endDate) {
      $startDate.addEventListener('change', () => { if ($startDate.value) $weekPicker.value = '-1'; });
      $endDate.addEventListener('change', () => { if ($endDate.value) $weekPicker.value = '-1'; });
      $weekPicker.addEventListener('change', () => { $startDate.value = ''; $endDate.value = ''; });
    }

    document.getElementById('as-filter-btn').addEventListener('click', () => {
      const w = document.getElementById('as-week').value;
      const g = document.getElementById('as-guild').value;
      const m = document.getElementById('as-member').value.trim();
      const s = document.getElementById('as-sort').value;
      const sd = document.getElementById('as-start-date')?.value || '';
      const ed = document.getElementById('as-end-date')?.value || '';
      let hash = `#/admin/sales?w=${w}`;
      if (g) hash += `&guild=${encodeURIComponent(g)}`;
      if (m) hash += `&member=${encodeURIComponent(m)}`;
      if (s !== 'gold-desc') hash += `&sort=${s}`;
      if (sd) hash += `&startDate=${sd}`;
      if (ed) hash += `&endDate=${ed}`;
      navigate(hash);
      renderAdminSales();
    });

    // ── Click-to-sort on table headers ──
    document.querySelectorAll('#as-table th.sortable').forEach(th => {
      th.addEventListener('click', () => {
        const col = th.dataset.sort;
        const $sortEl = document.getElementById('as-sort');
        const currentSort = $sortEl?.value || 'gold-desc';
        const newSort = currentSort === `${col}-desc` ? `${col}-asc` : `${col}-desc`;
        if ($sortEl) $sortEl.value = newSort;
        document.getElementById('as-filter-btn').click();
      });
    });

    // ── Report type toggle (UI scaffold) ──
    document.querySelectorAll('.report-type-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.report-type-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const reportContent = document.getElementById('admin-report-content');
        const reportType = btn.dataset.report;
        if (reportType === 'Sales') {
          renderAdminSales();
        } else if (reportContent) {
          // Sprint 10.0: Guild Audit sub-tabs — wired to existing admin APIs.
          renderGuildAuditSubreport(reportType, reportContent).catch(err => {
            reportContent.innerHTML = `<div class="error-msg">Failed to load ${escapeHtml(reportType)}: ${escapeHtml(err.message || String(err))}</div>`;
          });
        }
      });
    });

    // Honor ?tab=... on first load of /admin/sales so deep-linking works.
    if (activeReport !== 'Sales') {
      const reportContent = document.getElementById('admin-report-content');
      if (reportContent) {
        renderGuildAuditSubreport(activeReport, reportContent).catch(err => {
          reportContent.innerHTML = `<div class="error-msg">Failed to load ${escapeHtml(activeReport)}: ${escapeHtml(err.message || String(err))}</div>`;
        });
      }
    }

  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load sales: ${escapeHtml(err.message)}</div>`;
    $content.classList.remove('content-loading');
  }
}

// ── Admin Members ────────────────────────────────────────────
async function renderAdminMembers() {
  // Members merged into Guild Audit → Sales → Players tab
  await renderAdminSales();
  await new Promise(r => setTimeout(r, 80));
  const btn = document.querySelector('.report-type-btn[data-report="Players"]');
  if (btn) btn.click();
}

// ── Admin Items Report (Sprint 9.0) ──────────────────────────
async function renderAdminItems() {
  if (!currentUser) { $content.innerHTML = '<div class="empty-state">Login required.</div>'; return; }
  $content.innerHTML = '<div class="loading-spinner">Loading items report...</div>';

  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const weeksAgo = params.has('w') ? parseInt(params.get('w')) : 0;
  const guild = params.get('guild') || '';
  const search = params.get('q') || '';
  const limit = parseInt(params.get('limit')) || 100;

  try {
    const qs = new URLSearchParams();
    qs.set('weeksAgo', String(weeksAgo));
    if (guild) qs.set('guild', guild);
    if (search) qs.set('search', search);
    qs.set('limit', String(limit));
    const endpoint = currentUser.isAdmin ? `/api/admin/items?${qs.toString()}` : `/api/items?${qs.toString()}`;
    const data = await api(endpoint);
    if (!data) return;

    let rows = '';
    for (const it of data.items) {
      const icon = it.itemIcon
        ? `<img src="${esoIconUrl(it.itemIcon)}" alt="" class="item-icon" style="width:20px;height:20px;vertical-align:middle;">`
        : '';
      rows += `<tr>
        <td style="white-space:nowrap;">${icon} ${escapeHtml(it.itemName)}</td>
        <td class="text-right text-mono text-gold">${gold(it.totalGold)}</td>
        <td class="text-right text-mono">${Number(it.totalItems).toLocaleString()}</td>
        <td class="text-right text-mono">${Number(it.saleCount).toLocaleString()}</td>
        <td class="text-right text-mono">${gold(it.avgUnitPrice)}</td>
        <td class="text-right text-mono">${gold(it.biggestSale)}</td>
        <td class="text-right text-mono">${it.uniqueSellers}</td>
        <td class="text-mono" style="font-size:var(--fs-xs);color:var(--t-dim);">${relativeTime(it.lastSaleTs)}</td>
      </tr>`;
    }

    const guildOpts = ['', 'post', 'caravan', 'dealers']
      .map(g => `<option value="${g}" ${guild === g ? 'selected' : ''}>${g ? g.charAt(0).toUpperCase() + g.slice(1) : 'All guilds'}</option>`)
      .join('');

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="panel-header">
          <span class="panel-title">📦 Items Report — ${data.count} items</span>
          <span style="font-size:var(--fs-xs);color:var(--t-dim);">
            ${data.weekOffset === -1 ? 'All time' : (data.weekOffset === 0 ? 'This week' : `${data.weekOffset} week${data.weekOffset === 1 ? '' : 's'} ago`)}
            ${guild ? ` · ${guild}` : ''}
            ${search ? ` · "${escapeHtml(search)}"` : ''}
          </span>
        </div>
        <div class="filter-bar">
          ${weekPicker('ai-week', weeksAgo)}
          <div class="filter-group">
            <label>Guild</label>
            <select class="form-input" id="ai-guild">${guildOpts}</select>
          </div>
          <div class="filter-group">
            <label>Search</label>
            <input type="text" class="form-input" id="ai-search" placeholder="item name..." value="${escapeHtml(search)}">
          </div>
          <div class="filter-group">
            <label>Limit</label>
            <select class="form-input" id="ai-limit">
              ${[50, 100, 200, 500].map(n => `<option value="${n}" ${limit === n ? 'selected' : ''}>${n}</option>`).join('')}
            </select>
          </div>
          <div class="filter-group" style="justify-content:flex-end;">
            <label>&nbsp;</label>
            <button class="btn btn-primary" id="ai-filter-btn">Filter</button>
          </div>
        </div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr>
              <th>Item</th>
              <th class="text-right">Revenue</th>
              <th class="text-right">Qty</th>
              <th class="text-right">Sales</th>
              <th class="text-right">Avg Unit</th>
              <th class="text-right">Biggest</th>
              <th class="text-right">Sellers</th>
              <th>Last Sale</th>
            </tr></thead>
            <tbody>${rows || '<tr><td colspan="8" class="empty-state">No items for this filter.</td></tr>'}</tbody>
          </table>
        </div>
      </div>`;

    document.getElementById('ai-filter-btn').addEventListener('click', () => {
      const w = document.getElementById('ai-week').value;
      const g = document.getElementById('ai-guild').value;
      const q = document.getElementById('ai-search').value.trim();
      const l = document.getElementById('ai-limit').value;
      const qp = new URLSearchParams();
      qp.set('w', w);
      if (g) qp.set('guild', g);
      if (q) qp.set('q', q);
      if (l && l !== '100') qp.set('limit', l);
      navigate(`/admin/items?${qp.toString()}`);
    });
    document.getElementById('ai-search').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') document.getElementById('ai-filter-btn').click();
    });
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load items: ${escapeHtml(err.message)}</div>`;
  }
}

// ── Admin Leaderboard ────────────────────────────────────────
async function renderAdminLeaderboard() {
  // Sprint 8.5: when invoked via public /sync-stats alias, skip admin guard + hit public endpoint
  const route = (window.location.hash.slice(1) || '/').split('?')[0];
  const isPublic = route === '/sync-stats';
  if (!isPublic && !adminGuard()) return;
  $content.innerHTML = '<div class="loading-spinner">Loading sync stats...</div>';

  try {
    const data = await api(isPublic ? '/api/sync-stats' : '/api/admin/leaderboard');
    if (!data) return;

    const entries = data.leaderboard || data;
    const uncategorized = data.uncategorized || [];

    let rows = '';
    for (const entry of entries) {
      const rankCls = entry.rank <= 3 ? `top-${entry.rank}` : '';
      rows += `<tr>
        <td class="leaderboard-rank ${rankCls}">#${entry.rank}</td>
        <td>${escapeHtml(entry.displayName)}</td>
        <td class="text-right text-mono text-gold">${entry.totalPoints.toLocaleString()}</td>
        <td class="text-right text-mono">${entry.totalSyncs}</td>
        <td class="text-right text-mono">${entry.totalFiles}</td>
        <td class="text-right text-mono">${entry.itemsContributed.toLocaleString()}</td>
        <td class="text-right text-mono">${entry.salesContributed.toLocaleString()}</td>
        <td class="text-right text-mono">${entry.ttcUpdates}</td>
        <td class="text-mono">${relativeTime(entry.lastSyncAt)}</td>
      </tr>`;
    }

    // Uncategorized section (system entries)
    let uncatHtml = '';
    if (uncategorized.length > 0) {
      let uncatRows = '';
      for (const entry of uncategorized) {
        uncatRows += `<tr>
          <td style="color:var(--t-dim);">—</td>
          <td>${escapeHtml(entry.displayName || '(empty)')}</td>
          <td class="text-right text-mono">${entry.totalPoints?.toLocaleString() || 0}</td>
          <td class="text-right text-mono">${entry.totalSyncs || 0}</td>
          <td class="text-right text-mono">${entry.totalFiles || 0}</td>
          <td colspan="3" style="font-size:var(--fs-xs);color:var(--t-orange);">${escapeHtml(entry.reason || 'Unknown reason')}</td>
          <td class="text-mono">${relativeTime(entry.lastSyncAt)}</td>
        </tr>`;
      }

      uncatHtml = `
        <div class="screen-divider"></div>
        <div class="panel-header">
          <span class="panel-title" style="color:var(--t-orange);">⚠ Uncategorized Entries (${uncategorized.length})</span>
        </div>
        <p style="font-size:var(--fs-xs);color:var(--t-dim);margin-bottom:0.5rem;">
          These entries are from automated/system processes and are not attributed to a real player.
          They may indicate data imports without proper syncer names, startup sweeps, or other system operations.
        </p>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr>
              <th></th><th>Name</th><th class="text-right">Points</th><th class="text-right">Syncs</th>
              <th class="text-right">Files</th><th colspan="3">Reason</th><th>Last Active</th>
            </tr></thead>
            <tbody>${uncatRows}</tbody>
          </table>
        </div>`;
    }

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">Data Sync Leaderboard — Full Detail</span></div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr>
              <th></th><th>Syncer</th><th class="text-right">Points</th><th class="text-right">Syncs</th>
              <th class="text-right">Files</th><th class="text-right">Items</th><th class="text-right">Sales</th>
              <th class="text-right"><span class="icon-ttc"></span> TTC</th><th>Last Active</th>
            </tr></thead>
            <tbody>${rows || '<tr><td colspan="9" class="empty-state">No data.</td></tr>'}</tbody>
          </table>
        </div>
        ${uncatHtml}
      </div>`;
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load leaderboard: ${escapeHtml(err.message)}</div>`;
  }
}

// ── Admin Live Log ───────────────────────────────────────────
function _guildBadgeCell(guildName) {
  if (!guildName) return '<td>—</td>';
  const safe = escapeHtml(guildName);
  const info = GUILD_COLOR_MAP[(guildName || '').toLowerCase()];
  const colorCls = info ? info.cls : '';
  const dot = info ? `<span class="guild-dot" style="background:${info.color};"></span>` : '';
  return `<td class="clickable-guild log-guild-cell ${colorCls}" data-guild="${safe}">${dot}${safe}</td>`;
}

function _buildLogRows(data) {
  // Cache icons from API response so itemCell() can render them
  for (const entry of data) {
    if (entry.itemIcon) cacheItemIcon(entry.itemName, entry.itemIcon);
  }
  let rows = '';
  for (const entry of data) {
    const info = GUILD_COLOR_MAP[(entry.guildName || '').toLowerCase()];
    const rowCls = info ? `log-row-${info.key}` : '';
    rows += `<tr class="${rowCls}">
      <td class="text-mono">${shortDate(entry.timestamp)}</td>
      ${itemCell(entry.itemName)}
      <td class="text-right text-mono text-gold">${gold(entry.price)}</td>
      <td class="text-right text-mono">${entry.quant}</td>
      ${nameCell(entry.sellerName)}
      ${nameCell(entry.buyerName)}
      ${_guildBadgeCell(entry.guildName)}
    </tr>`;
  }
  return rows;
}

async function _fetchAdminLog(params) {
  const qs = new URLSearchParams({ limit: String(params.limit || 50) });
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.guild) qs.set('guild', params.guild);
  if (params.weeksAgo !== undefined && params.weeksAgo !== '-1') qs.set('weeksAgo', params.weeksAgo);
  if (params.item) qs.set('item', params.item);
  return api(`/api/log?${qs.toString()}`);
}

async function renderAdminLog() {
  $content.innerHTML = '<div class="loading-spinner">Loading live log...</div>';

  const hp = new URLSearchParams(window.location.hash.split('?')[1]);
  const guildFilter = hp.get('guild') || '';
  const weeksAgo = hp.get('w') || '-1';
  const itemFilter = hp.get('item') || '';
  const sortBy = hp.get('sort') || 'date-desc';
  const pageSize = clampIntClient(parseInt(hp.get('ps'), 10), 1, 200, 25);
  const page = Math.max(0, parseInt(hp.get('p'), 10) || 0);
  const offset = page * pageSize;
  const routeBase = (window.location.hash || '').startsWith('#/admin/log') ? '#/admin/log' : '#/log';

  try {
    const resp = await _fetchAdminLog({ guild: guildFilter, weeksAgo, item: itemFilter, limit: pageSize, offset });
    if (!resp) return;
    const data = Array.isArray(resp) ? resp : (resp.entries || []);
    const hasMore = Array.isArray(resp) ? false : !!resp.hasMore;

    const sorted = _sortLogData(data, sortBy);
    const rows = _buildLogRows(sorted);

    // Smart empty state — mention active filters
    const activeFilters = [];
    if (guildFilter) activeFilters.push(`guild=${escapeHtml(guildFilter)}`);
    if (weeksAgo !== '-1') activeFilters.push(`week=${escapeHtml(weeksAgo)}`);
    if (itemFilter) activeFilters.push(`item=${escapeHtml(itemFilter)}`);
    const emptyMsg = activeFilters.length
      ? `No sales match the active filters (${activeFilters.join(', ')}). <a href="#" id="log-empty-clear">Clear filters</a>.`
      : (page > 0 ? 'No more sales on this page.' : 'No recent sales.');

    const rangeStart = data.length ? (offset + 1) : 0;
    const rangeEnd = offset + data.length;
    const rangeLabel = data.length
      ? `Showing ${rangeStart}–${rangeEnd}${hasMore ? '+' : ''}`
      : `Page ${page + 1} · empty`;

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">📋 Live Sales Log</span></div>
        <div class="filter-bar" style="display:flex;flex-wrap:wrap;gap:0.5rem;padding:0.5rem 0.75rem;border-bottom:1px solid var(--screen-border);">
          <div class="filter-group">
            <label>Guild</label>
            <select class="form-input" id="log-guild" style="min-width:130px;">
              ${guildOptions().replace(`value="${guildFilter}"`, `value="${guildFilter}" selected`)}
            </select>
          </div>
          ${weekPicker('log-week', parseInt(weeksAgo))}
          <div class="filter-group">
            <label>Item</label>
            <input type="text" class="form-input" id="log-item" placeholder="Filter by item..." value="${escapeHtml(itemFilter)}" style="min-width:120px;">
          </div>
          <div class="filter-group">
            <label>Sort By</label>
            <select class="form-input" id="log-sort" style="min-width:110px;">
              <option value="date-desc" ${sortBy === 'date-desc' ? 'selected' : ''}>Date ↓</option>
              <option value="date-asc" ${sortBy === 'date-asc' ? 'selected' : ''}>Date ↑</option>
              <option value="gold-desc" ${sortBy === 'gold-desc' ? 'selected' : ''}>Gold ↓</option>
              <option value="gold-asc" ${sortBy === 'gold-asc' ? 'selected' : ''}>Gold ↑</option>
              <option value="name-asc" ${sortBy === 'name-asc' ? 'selected' : ''}>Item A-Z</option>
              <option value="name-desc" ${sortBy === 'name-desc' ? 'selected' : ''}>Item Z-A</option>
              <option value="guild-asc" ${sortBy === 'guild-asc' ? 'selected' : ''}>Guild A-Z</option>
              <option value="guild-desc" ${sortBy === 'guild-desc' ? 'selected' : ''}>Guild Z-A</option>
            </select>
          </div>
          <div class="filter-group">
            <label>Page Size</label>
            <select class="form-input" id="log-pagesize" style="min-width:90px;">
              <option value="25" ${pageSize === 25 ? 'selected' : ''}>25</option>
              <option value="50" ${pageSize === 50 ? 'selected' : ''}>50</option>
              <option value="100" ${pageSize === 100 ? 'selected' : ''}>100</option>
              <option value="200" ${pageSize === 200 ? 'selected' : ''}>200</option>
            </select>
          </div>
          <div class="filter-group" style="display:flex;align-items:flex-end;">
            <button class="btn btn-sm" id="log-clear">Clear</button>
          </div>
        </div>
        <div class="table-wrap">
          <table class="data-table log-table">
            <thead><tr>
              <th>Date</th><th>Item</th><th class="text-right">Price</th><th class="text-right">Qty</th>
              <th>Seller</th><th>Buyer</th><th>Guild</th>
            </tr></thead>
            <tbody id="log-tbody">${rows || `<tr><td colspan="7" class="empty-state">${emptyMsg}</td></tr>`}</tbody>
          </table>
        </div>
        <div class="log-pagination" style="display:flex;align-items:center;justify-content:space-between;gap:0.75rem;padding:0.6rem 0.9rem;border-top:1px solid var(--screen-border);font-size:var(--fs-sm);">
          <span class="text-muted">${rangeLabel}</span>
          <div style="display:flex;gap:0.4rem;align-items:center;">
            <button class="btn btn-sm" id="log-prev" ${page <= 0 ? 'disabled' : ''}>‹ Prev</button>
            <span class="text-mono">Page ${page + 1}</span>
            <input id="log-page-jump" class="form-input" type="number" min="1" value="${page + 1}" style="width:4.3rem;min-width:4.3rem;" title="Jump to page">
            <button class="btn btn-sm" id="log-next" ${!hasMore ? 'disabled' : ''}>Next ›</button>
          </div>
        </div>
      </div>`;

    // Wire filter controls — filter change resets to page 0
    const applyFilters = (resetPage) => {
      const p = new URLSearchParams();
      const g = document.getElementById('log-guild')?.value || '';
      const w = document.getElementById('log-week')?.value ?? '-1';
      const it = document.getElementById('log-item')?.value?.trim() || '';
      const s = document.getElementById('log-sort')?.value || 'date-desc';
      const ps = document.getElementById('log-pagesize')?.value || '25';
      if (g) p.set('guild', g);
      if (w !== '-1') p.set('w', w);
      if (it) p.set('item', it);
      if (s !== 'date-desc') p.set('sort', s);
      if (ps !== '25') p.set('ps', ps);
      if (!resetPage && page > 0) p.set('p', String(page));
      const qs = p.toString();
      history.replaceState(null, '', `${routeBase}${qs ? '?' + qs : ''}`);
      renderAdminLog();
    };
    const gotoPage = (n) => {
      const p = new URLSearchParams(window.location.hash.split('?')[1] || '');
      if (n <= 0) p.delete('p'); else p.set('p', String(n));
      const qs = p.toString();
      history.replaceState(null, '', `${routeBase}${qs ? '?' + qs : ''}`);
      renderAdminLog();
    };
    document.getElementById('log-guild')?.addEventListener('change', () => applyFilters(true));
    document.getElementById('log-week')?.addEventListener('change', () => applyFilters(true));
    document.getElementById('log-sort')?.addEventListener('change', () => applyFilters(false));
    document.getElementById('log-pagesize')?.addEventListener('change', () => applyFilters(true));
    let _logItemDebounce;
    document.getElementById('log-item')?.addEventListener('input', () => {
      clearTimeout(_logItemDebounce);
      _logItemDebounce = setTimeout(() => applyFilters(true), 400);
    });
    document.getElementById('log-clear')?.addEventListener('click', () => {
      history.replaceState(null, '', routeBase);
      renderAdminLog();
    });
    document.getElementById('log-empty-clear')?.addEventListener('click', (e) => {
      e.preventDefault();
      history.replaceState(null, '', routeBase);
      renderAdminLog();
    });
    document.getElementById('log-prev')?.addEventListener('click', () => gotoPage(page - 1));
    document.getElementById('log-next')?.addEventListener('click', () => gotoPage(page + 1));
    document.getElementById('log-page-jump')?.addEventListener('change', (e) => {
      const desired = clampIntClient(parseInt(e.target.value, 10), 1, Number.MAX_SAFE_INTEGER, page + 1);
      gotoPage(desired - 1);
    });
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load log: ${escapeHtml(err.message)}</div>`;
  }
}

// ── Log-specific sort (date/gold/name/guild on per-sale rows) ─────────
function _sortLogData(rows, sortKey) {
  const [field, dir] = (sortKey || 'date-desc').split('-');
  const mul = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (field === 'date')      cmp = (a.timestamp || 0) - (b.timestamp || 0);
    else if (field === 'gold') cmp = (a.price || 0) - (b.price || 0);
    else if (field === 'name') cmp = (a.itemName || '').localeCompare(b.itemName || '');
    else if (field === 'guild')cmp = (a.guildName || '').localeCompare(b.guildName || '');
    return mul * cmp;
  });
}

// Small client-side int clamp used by log + others
function clampIntClient(val, min, max, dflt) {
  if (isNaN(val) || val === null || val === undefined) return dflt;
  return Math.min(max, Math.max(min, Math.floor(val)));
}

// ── Expose navigate globally for inline onclick handlers ─────
window.navigate = navigate;
window.renderMySales = renderMySales;
window.viewUser = viewUser;
window.toggleSidebar = toggleSidebar;
// Sprint 11 Phase 4: with the IIFE bundle, top-level functions no longer
// leak to the global scope automatically. The following are referenced
// from inline `onclick="..."` attributes in dynamically-rendered HTML and
// must remain reachable on `window`.
window.showItemDetail = showItemDetail;
window.showGuildInfo = showGuildInfo;
window.showUserProfile = showUserProfile;
// showTtcVersionHistory is already defined as `window.showTtcVersionHistory = ...`
// inside its own IIFE init block; no aliasing needed.
window.renderFissal = renderFissal;
window._revokeApiKey = _revokeApiKey;

// ══════════════════════════════════════════════════════════════════════════
// ── USER DETAIL (admin click-through) ────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════
async function renderUserDetail(tag) {
  if (!adminGuard()) return;
  $content.innerHTML = '<div class="loading-spinner">Loading user profile...</div>';

  const params = new URLSearchParams(window.location.hash.split('?')[1]);
  const weeksAgo = parseInt(params.get('w')) || 0;

  try {
    const data = await api(`/api/admin/user/${encodeURIComponent(tag)}?weeksAgo=${weeksAgo}`);
    if (!data) return;

    // Trend bars
    const maxGold = Math.max(...data.trend.map(t => t.gold), 1);
    let trendHtml = '<div class="trend-bars">';
    for (let i = data.trend.length - 1; i >= 0; i--) {
      const t = data.trend[i];
      const pct = Math.max(2, (t.gold / maxGold) * 100);
      const cls = t.weeksAgo === weeksAgo ? 'current' : '';
      trendHtml += `<div class="trend-bar ${cls}" style="height:${pct}%" title="Week -${t.weeksAgo}: ${goldText(t.gold)} (${t.sales} sales)"></div>`;
    }
    trendHtml += '</div>';

    // Guild breakdown with bar chart
    let guildHtml = '';
    const guildGolds = {};
    for (const [gname, g] of Object.entries(data.guilds)) {
      guildGolds[gname] = g.gold;
      guildHtml += `
        <div class="stat-row"><span class="stat-label">${guildLink(gname)}</span><span class="stat-value gold">${gold(g.gold)}</span></div>
        <div class="stat-row"><span class="stat-label" style="padding-left:1rem;">Sales / Volume</span><span class="stat-value">${g.sales} / ${g.volume}</span></div>`;
      if (g.biggestSale) {
        guildHtml += `<div class="stat-row"><span class="stat-label" style="padding-left:1rem;">Best Item</span><span class="stat-value cyan">${itemLink(g.biggestSale.itemName)} (${gold(g.biggestSale.price)})</span></div>`;
      }
    }

    // Guild bar chart
    const maxGG = Math.max(...Object.values(guildGolds), 1);
    let guildChartHtml = '<div class="bar-chart">';
    for (const [gn, total] of Object.entries(guildGolds)) {
      const pct = Math.max(2, (total / maxGG) * 100);
      guildChartHtml += `
        <div class="bar-row">
          <span class="bar-label">${escapeHtml(gn)}</span>
          <div class="bar-track"><div class="bar-fill bar-fill-admin" style="width:${pct}%" title="${goldText(total)}"></div></div>
          <span class="bar-value">${gold(total)}</span>
        </div>`;
    }
    guildChartHtml += '</div>';

    // Compute new metrics from recent sales
    const itemCounts = {};
    const itemTimestamps = {};
    for (const sale of data.recentSales) {
      const name = sale.itemName || 'Unknown';
      itemCounts[name] = (itemCounts[name] || 0) + sale.quant;
      if (!itemTimestamps[name]) itemTimestamps[name] = [];
      itemTimestamps[name].push(sale.timestamp);
    }

    let mostSoldItem = null, mostSoldQty = 0;
    for (const [name, qty] of Object.entries(itemCounts)) {
      if (qty > mostSoldQty) { mostSoldQty = qty; mostSoldItem = name; }
    }

    let fastestItem = null, fastestRate = 0;
    for (const [name, times] of Object.entries(itemTimestamps)) {
      if (times.length >= 2) {
        const sorted = times.sort((a, b) => a - b);
        const span = sorted[sorted.length - 1] - sorted[0];
        if (span > 0) {
          const rate = times.length / (span / 86400);
          if (rate > fastestRate) { fastestRate = rate; fastestItem = name; }
        }
      }
    }

    // Recent sales table
    let salesHtml = '';
    for (const sale of data.recentSales) {
      salesHtml += `<tr>
        ${itemCell(sale.itemName)}
        <td class="text-right text-mono text-gold">${gold(sale.price)}</td>
        <td class="text-right text-mono">${sale.quant}</td>
        ${guildCell(sale.guildName)}
        <td class="text-mono">${shortDate(sale.timestamp)}</td>
      </tr>`;
    }

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="user-detail-header">
          <a class="back-btn" onclick="history.back()">← Back</a>
          <span class="detail-name">${escapeHtml(data.sellerName)}</span>
        </div>

        <div class="filter-bar">
          ${weekPicker('ud-week', weeksAgo)}
          <div class="filter-group" style="justify-content:flex-end;">
            <label>&nbsp;</label>
            <button class="btn btn-primary" id="ud-filter-btn">Apply</button>
          </div>
        </div>

        <div class="panel-grid">
          <div>
            <div class="stat-big"><span class="value">${gold(data.totalGold)}</span><span class="label">Total Gold</span></div>
          </div>
          <div>
            <div class="stat-big"><span class="value">${data.totalSales.toLocaleString()}</span><span class="label">Sales</span></div>
          </div>
          <div>
            <div class="stat-big"><span class="value">${data.totalVolume.toLocaleString()}</span><span class="label">Volume</span></div>
          </div>
          ${mostSoldItem ? `<div><div class="stat-big"><span class="value" style="font-size:var(--fs-md);color:var(--t-cyan);">${itemLink(mostSoldItem)}</span><span class="label">Most Sold (${mostSoldQty})</span></div></div>` : ''}
          ${fastestItem ? `<div><div class="stat-big"><span class="value" style="font-size:var(--fs-md);color:var(--t-green);">${itemLink(fastestItem)}</span><span class="label">Fastest Selling</span></div></div>` : ''}
        </div>

        ${data.biggestSale ? `<div class="stat-row" style="margin-top:0.5rem;"><span class="stat-label">Biggest Sale</span><span class="stat-value cyan">${itemLink(data.biggestSale.itemName)} — ${gold(data.biggestSale.price)} (${guildLink(data.biggestSale.guildName)})</span></div>` : ''}

        <div class="screen-divider"></div>

        <div class="panel-header"><span class="panel-title">8-Week Trend</span></div>
        ${trendHtml}

        <div class="screen-divider"></div>

        <div class="panel-header"><span class="panel-title">Guild Breakdown</span></div>
        ${guildChartHtml}
        ${guildHtml || '<div class="empty-state">No guild data.</div>'}

        <div class="screen-divider"></div>

        <div class="panel-header"><span class="panel-title">Recent Sales</span></div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Item</th><th class="text-right">Price</th><th class="text-right">Qty</th><th>Guild</th><th>Date</th></tr></thead>
            <tbody>${salesHtml || '<tr><td colspan="5" class="empty-state">No sales.</td></tr>'}</tbody>
          </table>
        </div>
      </div>`;

    document.getElementById('ud-filter-btn').addEventListener('click', () => {
      const w = document.getElementById('ud-week').value;
      navigate(`#/admin/user/${encodeURIComponent(tag)}?w=${w}`);
      renderUserDetail(tag);
    });
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load user: ${escapeHtml(err.message)}</div>`;
  }
}

// ── FX Toggle + Intensity (now controlled via hamburger quick settings) ──
function setFx(on) {
  document.body.classList.toggle('fx-on', on);
  try { localStorage.setItem('redfur-fx', on ? '1' : '0'); } catch {}
}

function setFxIntensity(val) {
  const v = Math.max(0, Math.min(100, parseInt(val) || 0));
  document.documentElement.style.setProperty('--fx-intensity', (v / 100).toFixed(2));
  try { localStorage.setItem('redfur-fx-intensity', String(v)); } catch {}
}

function restoreGranularFx() {
  try {
    document.body.classList.toggle('fx-no-scanlines', localStorage.getItem('redfur-fx-scanlines') === '0');
    document.body.classList.toggle('fx-no-flicker', localStorage.getItem('redfur-fx-flicker') === '0');
    document.body.classList.toggle('fx-no-glitch', localStorage.getItem('redfur-fx-glitch') === '0');
    document.body.classList.toggle('fx-no-bloom', localStorage.getItem('redfur-fx-bloom') === '0');
    const ss = localStorage.getItem('redfur-fx-scanline-speed') || '50';
    const gr = localStorage.getItem('redfur-fx-glitch-rate') || '30';
    const bi = localStorage.getItem('redfur-fx-bloom-intensity') || '60';
    document.documentElement.style.setProperty('--fx-scanline-speed', (12 - (ss / 100) * 10).toFixed(1) + 's');
    document.documentElement.style.setProperty('--fx-glitch-rate', (20 - (gr / 100) * 16).toFixed(1) + 's');
    document.documentElement.style.setProperty('--fx-bloom-intensity', (bi / 100).toFixed(2));
  } catch {}
}

// Restore from localStorage
try {
  const saved = localStorage.getItem('redfur-fx');
  if (saved === '0') {
    setFx(false);
  }
  const savedIntensity = localStorage.getItem('redfur-fx-intensity');
  if (savedIntensity != null) {
    setFxIntensity(savedIntensity);
  }
  restoreGranularFx();
} catch {}

// ── Nav overflow: removed (replaced by scroll indicators + hamburger nav) ──

// ══════════════════════════════════════════════════════════════════════════
// ── ADMIN: RAFFLE MANAGEMENT ─────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════
// Format a raffle week_start Unix timestamp into "MMM D – MMM D" label
function formatRaffleWeekLabel(weekStartTs) {
  const start = new Date(weekStartTs * 1000);
  const end = new Date((weekStartTs + 7 * 86400) * 1000);
  const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(start)} \u2013 ${fmt(end)}`;
}

async function renderAdminRaffle() {
  if (!currentUser) return renderLoginPrompt();
  const isAdmin = !!currentUser.isAdmin;
  $content.innerHTML = '<div class="loading-spinner">Loading raffle data...</div>';

  try {
    // Admins hit the admin endpoint (includes voided-tickets management data);
    // regular users use the public endpoint (strips admin-only fields).
    const data = await api(isAdmin ? '/api/admin/raffle' : '/api/raffle');
    if (!data) return;

    function raffleGuildBlock(guild, label) {
      if (!guild) return `<div class="panel-section"><em>No ${label} data available.</em></div>`;
      const entrantCount = guild.entrants || 0;
      const ticketCount = guild.tickets || 0;
      const pot = guild.totalPot || 0;
      const topList = guild.topEntrants || [];
      const winnings = guild.winnings || {};

      let topHtml = '';
      if (topList.length) {
        topHtml = `<div style="margin-top:0.5rem;">
          <div class="stat-label" style="margin-bottom:0.25rem;">Top Entrants</div>
          <div class="raffle-top-list">
            ${topList.slice(0, 10).map((e, i) => `<div class="raffle-top-entry">
              <span class="raffle-rank">#${i + 1}</span>
              <span class="raffle-tag">${escapeHtml(e.tag || e.name || '?')}</span>
              <span class="raffle-tickets gold">${(e.tickets || 0).toLocaleString()} 🎟</span>
              <span class="raffle-gold t-dim">${gold(e.gold || 0)}</span>
            </div>`).join('')}
          </div>
        </div>`;
      }

      let winHtml = '';
      if (winnings.first || winnings.second || winnings.third) {
        winHtml = `<div style="margin-top:0.5rem;">
          <div class="stat-label" style="margin-bottom:0.25rem;">Prize Breakdown</div>
          <div class="raffle-prizes">
            ${winnings.first ? `<div class="raffle-prize"><span class="raffle-prize-place">🥇 1st</span> <span class="gold">${gold(winnings.first)}</span></div>` : ''}
            ${winnings.second ? `<div class="raffle-prize"><span class="raffle-prize-place">🥈 2nd</span> <span class="gold">${gold(winnings.second)}</span></div>` : ''}
            ${winnings.third ? `<div class="raffle-prize"><span class="raffle-prize-place">🥉 3rd</span> <span class="gold">${gold(winnings.third)}</span></div>` : ''}
          </div>
        </div>`;
      }

      return `
        <div class="panel-section">
          <div class="guild-info-metrics" style="margin-bottom:0.5rem;">
            <div class="guild-info-metric guild-info-metric-active">
              <span class="guild-metric-emoji">👤</span>
              <span class="guild-metric-value">${entrantCount.toLocaleString()}</span>
              <span class="guild-metric-label">Entrants</span>
            </div>
            <div class="guild-info-metric guild-info-metric-sales">
              <span class="guild-metric-emoji">🎟</span>
              <span class="guild-metric-value">${ticketCount.toLocaleString()}</span>
              <span class="guild-metric-label">Tickets</span>
            </div>
            <div class="guild-info-metric guild-info-metric-gold">
              <span class="guild-metric-emoji">💰</span>
              <span class="guild-metric-value gold">${gold(pot)}</span>
              <span class="guild-metric-label">Total Pot</span>
            </div>
          </div>
          ${winHtml}
          ${topHtml}
        </div>`;
    }

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">🎟 Raffle ${isAdmin ? 'Management' : 'Status'}</span></div>

        <div class="panel-header" style="margin-top:0.5rem;"><span class="panel-title" style="font-size:var(--fs-sm);">🏪 Redfur Trading Post</span></div>
        ${raffleGuildBlock(data.rtp, 'RTP')}

        <div class="screen-divider"></div>

        <div class="panel-header"><span class="panel-title" style="font-size:var(--fs-sm);">🏬 Redfur Dealers</span></div>
        ${raffleGuildBlock(data.rd, 'RD')}

        ${isAdmin ? `
        <div class="screen-divider"></div>

        <div class="panel-header"><span class="panel-title" style="font-size:var(--fs-xs);">Voided Tickets</span></div>
        <div class="panel-grid">
          <div>
            <div class="stat-row"><span class="stat-label">Voided Count</span><span class="stat-value red">${data.voidedCount || 0}</span></div>
            <div class="stat-row"><span class="stat-label">Voided Users</span><span class="stat-value">${(data.voidedUsers || []).length}</span></div>
          </div>
        </div>
        ${(data.voidedUsers || []).length > 0
          ? `<div style="margin:0.5rem 0;">${(data.voidedUsers || []).map(u => `<span style="display:inline-block;padding:0.15rem 0.4rem;margin:0.1rem;background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.2);border-radius:var(--radius);font-family:var(--font-mono);font-size:var(--fs-xs);color:var(--t-red);">${escapeHtml(u)}</span>`).join('')}</div>`
          : ''
        }

        <div class="screen-divider"></div>
        <div class="panel-header"><span class="panel-title" style="font-size:var(--fs-xs);">Edit Voided Tickets</span></div>
        <div class="form-group">
          <label class="form-label">Voided Count</label>
          <input type="number" class="form-input" id="raffle-void-count" value="${data.voidedCount || 0}" min="0" style="max-width:120px;">
        </div>
        <div class="form-group">
          <label class="form-label">Voided Users (comma-separated @tags)</label>
          <input type="text" class="form-input" id="raffle-void-users" value="${escapeHtml((data.voidedUsers || []).join(', '))}" placeholder="@User1, @User2">
        </div>
        <button class="btn btn-primary" id="raffle-void-save">Save Voided Tickets</button>

        <div class="screen-divider"></div>
        <div class="panel-header"><span class="panel-title" style="font-size:var(--fs-xs);">Allowed Officers (Raffle Exemption)</span></div>
        <p class="t-dim" style="font-size:var(--fs-xs);margin-bottom:0.4rem;">Officers listed here are exempt from rank-based raffle exclusion and are permitted to participate in drawings.</p>
        <div class="form-group">
          <label class="form-label">Allowed Officers (comma-separated @tags)</label>
          <input type="text" class="form-input" id="raffle-allowed-officers" value="${escapeHtml((data.allowedOfficers || []).join(', '))}" placeholder="@Officer1, @Officer2">
        </div>
        <button class="btn btn-primary" id="raffle-allowed-save">Save Allowed Officers</button>
        ` : ''}

        <div class="screen-divider"></div>
        <div class="panel-header"><span class="panel-title" style="font-size:var(--fs-sm);">📜 Previous Draw Results</span></div>
        <div id="raffle-prev-winners" class="panel-section"><div class="loading-spinner" style="height:2rem;"></div></div>
      </div>`;

    if (isAdmin) {
      document.getElementById('raffle-void-save')?.addEventListener('click', async () => {
        const count = parseInt(document.getElementById('raffle-void-count').value, 10) || 0;
        const usersRaw = document.getElementById('raffle-void-users').value;
        const usersList = usersRaw.split(',').map(u => u.trim()).filter(Boolean);
        try {
          await apiPost('/api/admin/raffle/void', { voidedCount: count, voidedUsers: usersList });
          toast('Raffle voided tickets updated.', 'success');
          renderAdminRaffle();
        } catch (err) { toast(err.message, 'error'); }
      });

      document.getElementById('raffle-allowed-save')?.addEventListener('click', async () => {
        const raw = document.getElementById('raffle-allowed-officers').value;
        const list = raw.split(',').map(u => u.trim()).filter(Boolean);
        try {
          await apiPost('/api/admin/raffle/allowed-officers', { allowedOfficers: list });
          toast('Allowed officers list updated.', 'success');
          renderAdminRaffle();
        } catch (err) { toast(err.message, 'error'); }
      });
    }

    // Previous winners section — loaded separately so current-week data renders fast
    const winnersWrap = document.getElementById('raffle-prev-winners');
    if (winnersWrap) {
      try {
        const wdata = await api('/api/raffle/winners');
        const weeks = wdata?.weeks || [];
        if (!weeks.length) {
          winnersWrap.innerHTML = '<p class="t-dim" style="margin:0.5rem 0;">No previous draw results yet.</p>';
        } else {
          winnersWrap.innerHTML = weeks.map(w => {
            const label = formatRaffleWeekLabel(w.week_start);
            const place = ['🥇', '🥈', '🥉'];
            function winnerRows(list) {
              if (!list.length) return '<span class="t-dim">—</span>';
              return list.map((r, i) => `<span class="raffle-winner-row">${place[i] || (i+1+'.')} <span class="raffle-tag">${escapeHtml(r.user_tag)}</span>${r.prize ? ` <span class="t-dim raffle-prize-amt">${gold(Number(r.prize))}</span>` : ''}</span>`).join('');
            }
            return `<div class="raffle-prev-week">
              <div class="raffle-prev-week-label t-dim">${escapeHtml(label)}</div>
              <div class="raffle-prev-guilds">
                <div><span class="stat-label">🏪 Post</span><div class="raffle-winner-list">${winnerRows(w.post)}</div></div>
                <div><span class="stat-label">🏬 Dealers</span><div class="raffle-winner-list">${winnerRows(w.dealers)}</div></div>
              </div>
            </div>`;
          }).join('<div class="screen-divider" style="margin:0.4rem 0;"></div>');
        }
      } catch { winnersWrap.innerHTML = '<p class="t-dim">Could not load previous winners.</p>'; }
    }
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load raffle: ${escapeHtml(err.message)}</div>`;
  }
}

// ══════════════════════════════════════════════════════════════════════════
// ── ADMIN: TRADER TRACKER (Sprint 10.5) ──────────────────────────────────
// Kiosk location history + bid estimator for ESO guild traders.
// Reuses: .panel/.admin-panel, .panel-header, .filter-bar, .filter-group,
//         .form-input, .btn, .data-table-like tr/td, .empty-state.
// ══════════════════════════════════════════════════════════════════════════
let _traderState = {
  tab: 'guilds',   // 'guilds' | 'zones'
  query: '',
  zone: '',
  isDLC: '',       // '', '0', '1'
  tiers: new Set(),
  hasBank: false,
  week: '',        // '' = latest; otherwise weekStartTs as string
  weeks: null,     // lazy-loaded [{weekStartTs, guildCount}]
  zones: null,     // lazy-loaded list
  rows: null,
  lastFetchedAt: null,
  weekStartTs: null,
  openedGuild: null,
};

function _traderTierClass(tier) {
  return `trader-tier trader-tier-${tier || 'mid'}`;
}

function _fmtGold(n) {
  if (n == null || isNaN(n)) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(0) + 'k';
  return String(Math.round(n));
}

async function renderAdminTraders() {
  if (!adminGuard()) return;
  $content.innerHTML = '<div class="loading-spinner">Loading trader tracker…</div>';

  // Lazy-load zones + weeks + history tabs once
  const needZones = !_traderState.zones;
  const needWeeks = !_traderState.weeks;
  const needHist  = !_traderState.historyTabs;
  if (needZones || needWeeks || needHist) {
    try {
      const [z, w, h] = await Promise.all([
        needZones ? api('/api/admin/traders/zones') : Promise.resolve(null),
        needWeeks ? api('/api/admin/traders/weeks') : Promise.resolve(null),
        needHist  ? api('/api/admin/traders/history-tabs') : Promise.resolve(null),
      ]);
      if (z) _traderState.zones = z?.zones || [];
      if (w) _traderState.weeks = w?.weeks || [];
      if (h) _traderState.historyTabs = h?.tabs || [];
    } catch {
      _traderState.zones ||= [];
      _traderState.weeks ||= [];
      _traderState.historyTabs ||= [];
    }
  }

  if (_traderState.tab === 'zones') {
    _traderRenderZonesView();
  } else {
    await _traderFetchAndRender();
  }
}

async function _traderFetchAndRender() {
  const qs = new URLSearchParams();
  if (_traderState.query) qs.set('q', _traderState.query);
  if (_traderState.zone) qs.set('zone', _traderState.zone);
  if (_traderState.isDLC !== '') qs.set('isDLC', _traderState.isDLC);
  if (_traderState.hasBank) qs.set('hasBank', '1');
  if (_traderState.week) qs.set('weekStartTs', _traderState.week);
  // tier is server-side single-value; for multi-select we fetch all and filter client-side
  let data;
  try { data = await api('/api/admin/traders/current' + (qs.toString() ? '?' + qs : '')); }
  catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load trader data: ${escapeHtml(err.message)}</div>`;
    return;
  }
  if (!data?.ok) {
    $content.innerHTML = `<div class="error-msg">Trader API error</div>`;
    return;
  }

  _traderState.lastFetchedAt = data.lastFetchedAt;
  _traderState.weekStartTs = data.weekStartTs;
  _traderState.rows = data.rows || [];
  _traderRender();
}

function _traderRender() {
  const rows = _traderState.rows || [];
  const filtered = _traderState.tiers.size
    ? rows.filter(r => _traderState.tiers.has(r.tier))
    : rows;

  const weekLabel = _traderState.weekStartTs
    ? new Date(_traderState.weekStartTs).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York',
      })
    : '—';
  const fetchLabel = _traderState.lastFetchedAt
    ? relativeTime(_traderState.lastFetchedAt)
    : 'never';

  const zoneOptions = ['<option value="">All zones</option>'].concat(
    (_traderState.zones || []).map(z =>
      `<option value="${escapeHtml(z.zone)}" ${_traderState.zone === z.zone ? 'selected' : ''}>${escapeHtml(z.zone)}</option>`
    )
  ).join('');

  const weekOpts = (_traderState.weeks || []).map(w => {
    const label = new Date(w.weekStartTs).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York',
    });
    const val = String(w.weekStartTs);
    const sel = _traderState.week === val ? 'selected' : '';
    return `<option value="${val}" ${sel}>${escapeHtml(label)} · ${w.guildCount} guilds</option>`;
  }).join('');
  const weekOptions = `<option value="" ${_traderState.week === '' ? 'selected' : ''}>Latest week</option>` + weekOpts;

  const tierChip = (t, label) => {
    const active = _traderState.tiers.has(t);
    return `<button class="filter-chip trader-tier-chip ${active ? 'active' : ''}" data-tier="${t}">${label}</button>`;
  };

  let bodyHtml;
  if (filtered.length === 0) {
    bodyHtml = `<div class="empty-state">No guilds match your filters for the week of ${escapeHtml(weekLabel)}.</div>`;
  } else {
    bodyHtml = `
      <div class="trader-table-wrap">
        <table class="trader-table">
          <thead>
            <tr>
              <th>Guild</th>
              <th>Zone</th>
              <th>City</th>
              <th>Trader</th>
              <th class="text-right">Weeks Held</th>
              <th>Tier</th>
              <th class="text-right">Est. Bid</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            ${filtered.map(r => {
              const est = r.bidEstimate || {};
              const estStr = est.estimate != null
                ? `<span class="text-gold">${_fmtGold(est.estimate)}</span>
                   <span class="trader-bid-band">±${_fmtGold((est.high - est.low) / 2)}</span>`
                : '<span class="t-dim">—</span>';
              const confPct = est.confidence != null ? Math.round(est.confidence * 100) + '%' : '—';
              const badges = [];
              if (r.isDLC) badges.push('<span class="trader-badge dlc">DLC</span>');
              if (r.hasBank) badges.push('<span class="trader-badge bank">🏦</span>');
              const tierCls = `trader-row-${r.tier || 'mid'}`;
              return `<tr class="trader-row ${tierCls}" data-guild="${escapeHtml(r.guildName || '')}" tabindex="0" role="button" title="Click for guild history & bid intel">
                <td><strong>${escapeHtml(r.guildName || '—')}</strong></td>
                <td>${escapeHtml(r.zone || '—')} ${badges.join(' ')}</td>
                <td>${escapeHtml(r.city || '—')}</td>
                <td>${escapeHtml(r.trader || '—')}</td>
                <td class="text-right text-mono">${r.weeksHeld ?? '—'}</td>
                <td><span class="${_traderTierClass(r.tier)}">${escapeHtml(r.tier || '—')}</span></td>
                <td class="text-right text-mono">${estStr}</td>
                <td class="text-mono t-dim">${confPct} <span class="trader-bid-src">${escapeHtml(est.source || '')}</span></td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>`;
  }

  $content.innerHTML = `
    <div class="panel admin-panel">
      <div class="panel-header">
        <span class="panel-title">📍 Trader Tracker</span>
        <span class="t-dim" style="font-size:var(--fs-xs);">
          last fetch ${escapeHtml(fetchLabel)}
        </span>
      </div>

      ${_traderSubTabsHtml()}

      <div class="trader-summary-bar">
        <span>Week of <strong>${escapeHtml(weekLabel)}</strong></span>
        <span class="t-dim">· showing ${filtered.length}/${rows.length} guilds</span>
      </div>

      <div class="filter-bar">
        <div class="filter-group">
          <label>Week</label>
          <select class="form-input" id="tt-week">${weekOptions}</select>
        </div>
        <div class="filter-group">
          <label>Guild</label>
          <input type="text" class="form-input" id="tt-q" placeholder="guild name…" value="${escapeHtml(_traderState.query)}" style="min-width:160px;">
        </div>
        <div class="filter-group">
          <label>Zone</label>
          <select class="form-input" id="tt-zone">${zoneOptions}</select>
        </div>
        <div class="filter-group">
          <label>Base / DLC</label>
          <select class="form-input" id="tt-dlc">
            <option value="" ${_traderState.isDLC === '' ? 'selected' : ''}>All</option>
            <option value="0" ${_traderState.isDLC === '0' ? 'selected' : ''}>Base Game</option>
            <option value="1" ${_traderState.isDLC === '1' ? 'selected' : ''}>DLC</option>
          </select>
        </div>
        <div class="filter-group">
          <label>Tier</label>
          <div class="filter-chip-row">
            ${tierChip('premium', 'Premium')}
            ${tierChip('high',    'High')}
            ${tierChip('mid',     'Mid')}
            ${tierChip('low',     'Low')}
          </div>
        </div>
        <div class="filter-group">
          <label>&nbsp;</label>
          <label class="trader-bank-toggle">
            <input type="checkbox" id="tt-bank" ${_traderState.hasBank ? 'checked' : ''}>
            Bank only
          </label>
        </div>
        <div class="filter-group" style="justify-content:flex-end;">
          <label>&nbsp;</label>
          <button class="btn btn-secondary" id="tt-refresh">Refresh</button>
        </div>
      </div>

      ${_traderImportBarHtml()}

      ${bodyHtml}
    </div>
  `;

  _traderBindEvents();
}

function _traderSubTabsHtml() {
  const t = _traderState.tab;
  return `
    <div class="trader-subtabs">
      <button class="trader-subtab ${t === 'guilds' ? 'active' : ''}" data-tab="guilds">
        ⚖️ Guilds
      </button>
      <button class="trader-subtab ${t === 'zones' ? 'active' : ''}" data-tab="zones">
        🗺️ Zones
      </button>
    </div>
  `;
}

function _traderImportBarHtml() {
  const tabs = _traderState.historyTabs || [];
  const sel = _traderState.importYear || 'current';
  const yearOptions = tabs.map(t => {
    const count = t.rowCount ? ` (${t.rowCount.toLocaleString()} rows)` : '';
    return `<option value="${t.year}" ${String(sel) === String(t.year) ? 'selected' : ''}>${t.year}${count}</option>`;
  }).join('');
  const loadedHint = tabs.length
    ? `<span class="t-dim" style="font-size:var(--fs-xs);">${tabs.filter(t => t.rowCount > 0).length}/${tabs.length} year tabs imported</span>`
    : '';
  return `
    <div class="trader-import-bar">
      <span class="trader-import-label">📥 Import historical data:</span>
      <select class="form-input" id="tt-import-year" style="max-width:220px;">
        <option value="all" ${sel === 'all' ? 'selected' : ''}>All years</option>
        <option value="current" ${sel === 'current' ? 'selected' : ''}>Current year only</option>
        <optgroup label="Specific year">${yearOptions}</optgroup>
      </select>
      <button class="btn btn-primary" id="tt-import-btn">Import</button>
      <span id="tt-import-status" class="t-dim" style="font-size:var(--fs-xs);"></span>
      ${loadedHint}
    </div>
  `;
}

function _traderRenderZonesView() {
  const zones = _traderState.zones || [];
  const fetchLabel = _traderState.lastFetchedAt
    ? relativeTime(_traderState.lastFetchedAt)
    : 'never';

  // Tier filter chips reuse tiers state
  const tierChip = (t, label) => {
    const active = _traderState.tiers.has(t);
    return `<button class="filter-chip trader-tier-chip ${active ? 'active' : ''}" data-tier="${t}">${label}</button>`;
  };

  const filtered = _traderState.tiers.size
    ? zones.filter(z => _traderState.tiers.has(z.tier))
    : zones;

  const rowsHtml = filtered.length === 0
    ? `<tr><td colspan="7" class="empty-state">No zones match your filters.</td></tr>`
    : filtered.map(z => {
        const bankBadge = z.hasBank ? '<span class="trader-badge bank" title="Banker in city">🏦</span>' : '';
        const wsBadge   = z.hasWayshrine ? `<span class="trader-badge" title="Wayshrine ${z.wayshrineDistance || ''}">🕯</span>` : '';
        const dlcBadge  = z.isDLC ? '<span class="trader-badge dlc">DLC</span>' : '';
        const tierCls = `trader-row-${z.tier || 'mid'}`;
        const band = (z.estBidMin != null && z.estBidMax != null)
          ? `<span class="text-gold">${_fmtGold(z.estBidMin)}–${_fmtGold(z.estBidMax)}</span>`
          : '<span class="t-dim">—</span>';
        return `<tr class="trader-row ${tierCls}">
          <td><strong>${escapeHtml(z.zone)}</strong> ${dlcBadge}</td>
          <td>${escapeHtml(z.city || '—')} ${bankBadge} ${wsBadge}</td>
          <td><span class="${_traderTierClass(z.tier)}">${escapeHtml(z.tier || '—')}</span></td>
          <td class="text-right text-mono">${z.prestigeScore ?? '—'}</td>
          <td class="text-right text-mono">${band}</td>
          <td class="text-right text-mono">${z.currentOccupancy ?? 0}</td>
          <td class="text-mono t-dim">${escapeHtml(z.wayshrineDistance || '—')}</td>
        </tr>`;
      }).join('');

  $content.innerHTML = `
    <div class="panel admin-panel">
      <div class="panel-header">
        <span class="panel-title">📍 Trader Tracker</span>
        <span class="t-dim" style="font-size:var(--fs-xs);">
          ${zones.length} zones · last fetch ${escapeHtml(fetchLabel)}
        </span>
      </div>

      ${_traderSubTabsHtml()}

      <div class="filter-bar">
        <div class="filter-group">
          <label>Tier</label>
          <div class="filter-chip-row">
            ${tierChip('premium', 'Premium')}
            ${tierChip('high',    'High')}
            ${tierChip('mid',     'Mid')}
            ${tierChip('low',     'Low')}
          </div>
        </div>
      </div>

      <div class="trader-table-wrap">
        <table class="trader-table">
          <thead>
            <tr>
              <th>Zone</th>
              <th>Flagship City</th>
              <th>Tier</th>
              <th class="text-right">Prestige</th>
              <th class="text-right">Community Bid Range</th>
              <th class="text-right">Current Occupancy</th>
              <th>Wayshrine</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
    </div>
  `;

  // Bind sub-tab + tier-chip events only
  document.querySelectorAll('.trader-subtab').forEach(btn => {
    btn.addEventListener('click', () => {
      _traderState.tab = btn.dataset.tab;
      renderAdminTraders();
    });
  });
  document.querySelectorAll('.trader-tier-chip').forEach(btn => {
    btn.addEventListener('click', () => {
      const t = btn.dataset.tier;
      if (_traderState.tiers.has(t)) _traderState.tiers.delete(t);
      else _traderState.tiers.add(t);
      _traderRenderZonesView();
    });
  });
}

function _traderBindEvents() {
  const $ = id => document.getElementById(id);

  document.querySelectorAll('.trader-subtab').forEach(btn => {
    btn.addEventListener('click', () => {
      _traderState.tab = btn.dataset.tab;
      renderAdminTraders();
    });
  });

  const qInput = $('tt-q');
  let qTimer = null;
  qInput?.addEventListener('input', () => {
    clearTimeout(qTimer);
    qTimer = setTimeout(() => {
      _traderState.query = qInput.value.trim();
      _traderFetchAndRender();
    }, 300);
  });

  $('tt-week')?.addEventListener('change', e => {
    _traderState.week = e.target.value;
    _traderFetchAndRender();
  });
  $('tt-zone')?.addEventListener('change', e => {
    _traderState.zone = e.target.value;
    _traderFetchAndRender();
  });
  $('tt-dlc')?.addEventListener('change', e => {
    _traderState.isDLC = e.target.value;
    _traderFetchAndRender();
  });
  $('tt-bank')?.addEventListener('change', e => {
    _traderState.hasBank = e.target.checked;
    _traderFetchAndRender();
  });

  document.querySelectorAll('.trader-tier-chip').forEach(btn => {
    btn.addEventListener('click', () => {
      const t = btn.dataset.tier;
      if (_traderState.tiers.has(t)) _traderState.tiers.delete(t);
      else _traderState.tiers.add(t);
      _traderRender();
    });
  });

  $('tt-refresh')?.addEventListener('click', async () => {
    const btn = $('tt-refresh');
    btn.disabled = true; btn.textContent = 'Refreshing…';
    try {
      await fetch('/api/admin/traders/refresh', { method: 'POST', credentials: 'include' });
      // Invalidate weeks cache so new snapshot shows up in dropdown
      _traderState.weeks = null;
      await renderAdminTraders();
    } catch (err) {
      alert('Refresh failed: ' + err.message);
    } finally {
      const b = $('tt-refresh'); if (b) { b.disabled = false; b.textContent = 'Refresh'; }
    }
  });

  $('tt-import-year')?.addEventListener('change', e => {
    _traderState.importYear = e.target.value;
  });

  $('tt-import-btn')?.addEventListener('click', async () => {
    const btn = $('tt-import-btn');
    const status = $('tt-import-status');
    const sel = _traderState.importYear || 'current';
    let payload;
    if (sel === 'all' || sel === 'current') payload = { years: sel };
    else payload = { years: [parseInt(sel, 10)] };

    const label = sel === 'all' ? 'all years'
                : sel === 'current' ? 'current year'
                : `year ${sel}`;
    if (!confirm(`Import historical data for ${label}? This may take ~10s per year.`)) return;

    btn.disabled = true; btn.textContent = 'Importing…';
    if (status) status.textContent = `Fetching ${label}…`;
    try {
      const res = await fetch('/api/admin/traders/import-history', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'import failed');
      const s = data.summary || {};
      if (status) {
        status.textContent = `✓ Inserted ${s.inserted || 0} / updated ${s.updated || 0} rows`
          + (s.errors?.length ? ` · ${s.errors.length} error(s)` : '');
      }
      // Invalidate caches so new weeks + row counts show up.
      _traderState.weeks = null;
      _traderState.historyTabs = null;
      setTimeout(() => renderAdminTraders(), 1200);
    } catch (err) {
      if (status) status.textContent = '✗ ' + err.message;
      alert('Import failed: ' + err.message);
    } finally {
      const b = $('tt-import-btn'); if (b) { b.disabled = false; b.textContent = 'Import'; }
    }
  });

  document.querySelectorAll('.trader-row').forEach(row => {
    const open = () => {
      const g = row.dataset.guild;
      if (!g) return;
      _traderOpenGuildModal(g);
    };
    row.addEventListener('click', open);
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });
  });
}

async function _traderOpenGuildModal(guildName) {
  // Close any pre-existing trader modal
  document.querySelectorAll('.trader-modal-overlay').forEach(n => n.remove());

  const overlay = document.createElement('div');
  overlay.className = 'sync-modal-overlay trader-modal-overlay';
  overlay.innerHTML = `
    <div class="sync-modal trader-modal">
      <div class="sync-modal-header">
        <span>📍 ${escapeHtml(guildName)}</span>
        <button class="sync-modal-close" data-ui-action="close-trader-modal">✕</button>
      </div>
      <div class="sync-modal-body trader-modal-body">
        <div class="loading-spinner">Loading guild history…</div>
      </div>
    </div>
  `;
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('.sync-modal-close').addEventListener('click', close);
  const escHandler = (e) => { if (e.key === 'Escape') { close(); document.removeEventListener('keydown', escHandler); } };
  document.addEventListener('keydown', escHandler);

  document.body.appendChild(overlay);
  requestAnimationFrame(() => overlay.classList.add('visible'));

  const body = overlay.querySelector('.trader-modal-body');

  let data;
  try { data = await api('/api/admin/traders/guild/' + encodeURIComponent(guildName)); }
  catch (err) {
    body.innerHTML = `<div class="error-msg">Failed to load guild: ${escapeHtml(err.message)}</div>`;
    return;
  }
  if (!data?.ok) { body.innerHTML = '<div class="error-msg">API error</div>'; return; }

  const hist = data.history || [];
  const agg = data.aggregates || {};
  const obs = data.observedBids || [];

  const zoneFreq = Object.entries(agg.zoneCounts || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);
  const maxFreq = zoneFreq[0]?.[1] || 1;

  const historyRowsHtml = hist.map(h => {
    const est = h.bidEstimate || {};
    const wk = h.weekStartTs
      ? new Date(h.weekStartTs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' })
      : '—';
    const tierCls = `trader-row-${h.tier || 'mid'}`;
    return `<tr class="${tierCls}">
      <td class="text-mono">${escapeHtml(wk)}</td>
      <td>${escapeHtml(h.zone || '—')}</td>
      <td>${escapeHtml(h.city || '—')}</td>
      <td class="text-right text-mono">${h.weeksHeld ?? '—'}</td>
      <td><span class="${_traderTierClass(h.tier)}">${escapeHtml(h.tier || '—')}</span></td>
      <td class="text-right text-mono text-gold">${est.estimate != null ? _fmtGold(est.estimate) : '—'}</td>
    </tr>`;
  }).join('');

  const obsHtml = obs.length === 0
    ? '<div class="t-dim" style="padding:0.5rem;">No Discord bid observations yet for these zones.</div>'
    : obs.map(o => {
        const when = o.observedAt ? relativeTime(o.observedAt) : '—';
        const amt = o.bidAmount != null ? _fmtGold(o.bidAmount) : '—';
        const outcome = o.wonBid === 1 ? '<span class="trader-bid-won">won</span>'
                      : o.wonBid === 0 ? '<span class="trader-bid-lost">lost</span>'
                      : '';
        return `<div class="trader-obs-row">
          <div class="trader-obs-head">
            <strong>${escapeHtml(o.authorName || 'unknown')}</strong>
            <span class="t-dim">· ${escapeHtml(o.zone || '—')} · ${when}</span>
            <span class="text-gold">${amt}</span> ${outcome}
          </div>
          <div class="trader-obs-text">${escapeHtml((o.rawText || '').slice(0, 280))}</div>
        </div>`;
      }).join('');

  body.innerHTML = `
    <div class="trader-detail-stats">
      <div><span class="t-dim">Weeks tracked:</span> <strong>${agg.totalWeeks || 0}</strong></div>
      <div><span class="t-dim">Most frequent zone:</span> <strong>${escapeHtml(agg.mostFrequentZone || '—')}</strong></div>
      <div><span class="t-dim">Current streak:</span> <strong>${agg.currentStreak || 0}w</strong></div>
    </div>

    <h4 class="trader-detail-subtitle">Zone Frequency</h4>
    <div class="trader-zone-bars">
      ${zoneFreq.map(([z, n]) => `
        <div class="trader-zone-bar-row">
          <span class="trader-zone-bar-label">${escapeHtml(z)}</span>
          <div class="trader-zone-bar-track">
            <div class="trader-zone-bar-fill" style="width:${(n / maxFreq * 100).toFixed(0)}%"></div>
          </div>
          <span class="trader-zone-bar-count">${n}</span>
        </div>
      `).join('') || '<div class="t-dim">No history.</div>'}
    </div>

    <h4 class="trader-detail-subtitle">Location History</h4>
    <div class="trader-table-wrap">
      <table class="trader-table">
        <thead>
          <tr><th>Week</th><th>Zone</th><th>City</th><th class="text-right">Held</th><th>Tier</th><th class="text-right">Est. Bid</th></tr>
        </thead>
        <tbody>${historyRowsHtml || '<tr><td colspan="6" class="t-dim">No history.</td></tr>'}</tbody>
      </table>
    </div>

    <h4 class="trader-detail-subtitle">Observed Bid Notes (Discord)</h4>
    <div class="trader-obs-list">${obsHtml}</div>
  `;
}

// ══════════════════════════════════════════════════════════════════════════
// ── ADMIN: TOOLS — OWNER-ONLY SSH-LEVEL CONTROLS (Sprint 8.5) ────────────
// ══════════════════════════════════════════════════════════════════════════
async function renderAdminTools() {
  if (!currentUser || !currentUser.isOwner) {
    $content.innerHTML = `<div class="panel admin-panel"><div class="panel-header"><span class="panel-title">🛠️ Tools</span></div><div class="empty-state">Owner access required.</div></div>`;
    return;
  }
  $content.innerHTML = '<div class="loading-spinner">Loading tools...</div>';
  try {
    const status = await api('/api/admin/tools/status');
    const pm2Rows = Array.isArray(status?.pm2)
      ? status.pm2.map(p => `<tr>
          <td class="text-mono">${escapeHtml(String(p.name ?? ''))}</td>
          <td class="text-mono">${p.id ?? ''}</td>
          <td>${escapeHtml(String(p.status ?? ''))}</td>
          <td class="text-right">${p.restarts ?? 0}</td>
          <td class="text-right">${p.memoryMb != null ? p.memoryMb + ' MB' : '—'}</td>
        </tr>`).join('')
      : `<tr><td colspan="5" class="empty-state">${escapeHtml(status?.pm2?.error || 'PM2 unavailable')}</td></tr>`;

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">🛠️ Tools — Owner Controls</span></div>
        <div style="padding:1rem;display:flex;flex-direction:column;gap:1rem;">
          <div class="info-box" style="border-left:3px solid var(--accent-orange);padding:0.6rem 0.9rem;">
            <strong>⚠️ Restricted:</strong> These controls directly affect the running process. Use with care.
          </div>

          <div>
            <h3 style="margin:0 0 0.5rem;font-size:var(--fs-md);">Process Status</h3>
            <div class="table-wrap">
              <table class="data-table">
                <thead><tr><th>Name</th><th>PM2 ID</th><th>Status</th><th class="text-right">Restarts</th><th class="text-right">Memory</th></tr></thead>
                <tbody>${pm2Rows}</tbody>
              </table>
            </div>
            <div class="text-xs text-muted" style="margin-top:0.4rem;">
              Node ${escapeHtml(String(status?.node ?? ''))} · Uptime ${status?.uptimeSec ?? 0}s · RSS ${status?.memoryMb ?? 0} MB
            </div>
          </div>

          <div>
            <h3 style="margin:0 0 0.5rem;font-size:var(--fs-md);">Restart Controls</h3>
            <div style="display:flex;gap:0.5rem;flex-wrap:wrap;">
              <button class="btn btn-danger" id="tools-restart-bot">🔴 Restart Bot (full process)</button>
              <button class="btn" id="tools-restart-web">🔄 Restart Web (soft reload)</button>
              <button class="btn" id="tools-refresh-status">↻ Refresh Status</button>
            </div>
            <div class="text-xs text-muted" style="margin-top:0.4rem;">
              Restart Bot exits the process — PM2 will respawn it automatically. Web reload re-mounts routes without exiting.
            </div>
          </div>
        </div>
      </div>`;

    document.getElementById('tools-restart-bot')?.addEventListener('click', async () => {
      if (!confirm('Restart the entire bot process? PM2 will respawn it.')) return;
      try {
        const r = await apiPost('/api/admin/tools/restart-bot', { confirm: 'RESTART BOT' });
        toast(r?.message || 'Bot restart scheduled.', 'warning');
      } catch (err) { toast(err.message, 'error'); }
    });
    document.getElementById('tools-restart-web')?.addEventListener('click', async () => {
      if (!confirm('Reload web routes without exiting the process?')) return;
      try {
        const r = await apiPost('/api/admin/tools/restart-web', { confirm: 'RESTART WEB' });
        toast(r?.message || 'Web reloaded.', 'success');
      } catch (err) { toast(err.message, 'error'); }
    });
    document.getElementById('tools-refresh-status')?.addEventListener('click', renderAdminTools);
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load tools: ${escapeHtml(err.message)}</div>`;
  }
}

// ══════════════════════════════════════════════════════════════════════════
// ── ADMIN: AI MEMORY MANAGEMENT ──────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════
async function renderAdminMemories() {
  if (!adminGuard()) return;
  $content.innerHTML = '<div class="loading-spinner">Loading AI user memories...</div>';

  try {
    const data = await api('/api/admin/memories');
    if (!data) return;

    let rows = '';
    for (const user of data) {
      const factCount = user.factCount ?? 0;
      // Backend returns snake_case (display_name, discord_user_id, last_seen);
      // accept both shapes for forward-compat with any alias layer.
      const displayName = user.displayName ?? user.display_name ?? '(unknown)';
      const discordId = user.discordUserId ?? user.discord_user_id ?? '';
      const lastSeen = (user.lastSeen ?? user.last_seen) ? relativeTime(user.lastSeen ?? user.last_seen) : '—';
      rows += `<tr>
        <td>${escapeHtml(displayName)}</td>
        <td class="text-mono" style="font-size:var(--fs-xs);">${escapeHtml(discordId)}</td>
        <td class="text-right text-mono">${factCount}</td>
        <td class="text-mono">${lastSeen}</td>
        <td>
          <button class="btn" style="font-size:var(--fs-xs);padding:0.15rem 0.4rem;" onclick="viewAdminUserMemory('${escapeHtml(discordId)}')">View</button>
          <button class="btn btn-danger" style="font-size:var(--fs-xs);padding:0.15rem 0.4rem;" onclick="wipeAdminUserMemory('${escapeHtml(discordId)}')">Wipe</button>
        </td>
      </tr>`;
    }

    $content.innerHTML = `
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">🧠 AI User Memories</span></div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>User</th><th>Discord ID</th><th class="text-right">Facts</th><th>Last Seen</th><th>Actions</th></tr></thead>
            <tbody>${rows || '<tr><td colspan="5" class="empty-state">No AI user memories stored.</td></tr>'}</tbody>
          </table>
        </div>
      </div>
      <div id="admin-memory-detail"></div>`;
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load memories: ${escapeHtml(err.message)}</div>`;
  }
}

window.viewAdminUserMemory = async function(userId) {
  const detail = document.getElementById('admin-memory-detail');
  if (!detail) return;
  detail.innerHTML = '<div class="loading-spinner">Loading user memories...</div>';

  try {
    const data = await api(`/api/admin/memories/${userId}`);
    let factsHtml = '';
    for (const fact of (data.facts || [])) {
      factsHtml += `<tr>
        <td style="max-width:300px;word-break:break-word;">${escapeHtml(fact.fact)}</td>
        <td class="text-right text-mono">${Math.round((fact.confidence || 0) * 100)}%</td>
        <td class="text-mono" style="font-size:var(--fs-xs);">${fact.reasoning ? escapeHtml(fact.reasoning) : '—'}</td>
        <td class="text-mono">${relativeTime(fact.timestamp)}</td>
        <td>
          <button class="btn btn-danger" style="font-size:var(--fs-xs);padding:0.1rem 0.3rem;" onclick="deleteAdminFact('${userId}', ${fact.id})">✕</button>
        </td>
      </tr>`;
    }

    detail.innerHTML = `
      <div class="panel admin-panel" style="margin-top:1rem;">
        <div class="panel-header"><span class="panel-title">Memories for ${escapeHtml(data.displayName || userId)}</span></div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Fact</th><th class="text-right">Confidence</th><th>Reasoning</th><th>Created</th><th></th></tr></thead>
            <tbody>${factsHtml || '<tr><td colspan="5" class="empty-state">No facts stored.</td></tr>'}</tbody>
          </table>
        </div>
      </div>`;
  } catch (err) {
    detail.innerHTML = `<div class="error-msg">Failed to load user memories: ${escapeHtml(err.message)}</div>`;
  }
};

window.deleteAdminFact = async function(userId, factId) {
  try {
    await apiDelete(`/api/admin/memories/${userId}/${factId}`);
    toast('Fact deleted.', 'success');
    viewAdminUserMemory(userId);
  } catch (err) { toast(err.message, 'error'); }
};

window.wipeAdminUserMemory = async function(userId) {
  if (!confirm('Wipe ALL memories for this user? This cannot be undone.')) return;
  try {
    await apiDelete(`/api/admin/memories/${userId}`);
    toast('User memories wiped.', 'success');
    renderAdminMemories();
  } catch (err) { toast(err.message, 'error'); }
};

// ══════════════════════════════════════════════════════════════════════════
// ── ADMIN: DIAGNOSTICS PANEL ─────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════
function fmtBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
  return (bytes / 1073741824).toFixed(2) + ' GB';
}

function fmtUptime(seconds) {
  if (!seconds) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
}

// ── Admin: Performance hub (Sprint 13) ──────────────────────────
// Tabbed wrapper merging Performance + Diagnostics + Storage into one route.
// Default tab is performance; ?tab=diagnostics / ?tab=storage switches.
// Each child renderer still owns $content; we prepend the tab strip after.
async function renderAdminPerformance() {
  if (!adminGuard()) return;
  const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const rawTab = (params.get('tab') || '').toLowerCase();
  const tab = ['diagnostics', 'storage'].includes(rawTab) ? rawTab : 'performance';

  // Run the underlying renderer first so it owns $content, then prepend tabs.
  if (tab === 'diagnostics') {
    await renderAdminDiagnostics();
  } else if (tab === 'storage') {
    await renderAdminStorage();
  } else {
    await renderAdminPerf();
  }

  const tabsHtml = `
    <div class="page-tabs admin-perf-tabs" role="tablist" aria-label="Performance hub">
      <button class="page-tab ${tab === 'performance' ? 'active' : ''}" data-tab="performance" role="tab" aria-selected="${tab === 'performance'}">📊 Performance</button>
      <button class="page-tab ${tab === 'diagnostics' ? 'active' : ''}" data-tab="diagnostics" role="tab" aria-selected="${tab === 'diagnostics'}">⚙️ Diagnostics</button>
      <button class="page-tab ${tab === 'storage' ? 'active' : ''}" data-tab="storage" role="tab" aria-selected="${tab === 'storage'}">💾 Storage</button>
    </div>`;
  const wrapper = document.createElement('div');
  wrapper.innerHTML = tabsHtml;
  const tabBar = wrapper.firstElementChild;
  if (tabBar && $content.firstChild) {
    $content.insertBefore(tabBar, $content.firstChild);
  } else if (tabBar) {
    $content.appendChild(tabBar);
  }
  $content.querySelectorAll('.admin-perf-tabs .page-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      const t = btn.dataset.tab;
      const qs = t === 'performance' ? '' : ('?tab=' + t);
      navigate('/admin/perf' + qs);
    });
  });
}

// ── Admin: Performance dashboard (Sprint 9.7) ──────────────────────────
let _perfAutoRefreshTimer = null;

async function renderAdminPerf() {
  if (!adminGuard()) return;
  $content.innerHTML = '<div class="loading-spinner">Loading performance metrics...</div>';

  async function load() {
    try {
      const snap = await api('/api/admin/perf');
      $content.innerHTML = renderPerfSnapshot(snap);
      wirePerfControls();
    } catch (err) {
      $content.innerHTML = `<div class="error-panel">Failed to load /api/admin/perf: ${escapeHtml(err.message)}</div>`;
    }
  }

  function renderPerfSnapshot(s) {
    const probe = s.caches?.sse || {};
    const resp = s.caches?.response || {};
    const priv = s.caches?.privacy || {};
    const p = s.probe || {};
    const probeOk = p.lastStatus === 200 && p.consecutiveFailures === 0;
    const probeClass = p.lastCheckAt
      ? (probeOk ? 'status-healthy' : 'status-degraded')
      : 'status-unknown';

    const top = (s.topRoutes || []).map(r => `
      <tr>
        <td><code>${escapeHtml(r.route)}</code></td>
        <td style="text-align:right">${r.count.toLocaleString()}</td>
        <td style="text-align:right">${r.avgMs.toFixed(1)}</td>
        <td style="text-align:right">${r.maxMs.toFixed(1)}</td>
        <td>
          <span class="pill pill-ok">${r.statuses['2xx']}</span>
          ${r.statuses['3xx'] ? `<span class="pill">${r.statuses['3xx']}</span>` : ''}
          ${r.statuses['4xx'] ? `<span class="pill pill-warn">${r.statuses['4xx']}</span>` : ''}
          ${r.statuses['5xx'] ? `<span class="pill pill-err">${r.statuses['5xx']}</span>` : ''}
        </td>
      </tr>`).join('') || '<tr><td colspan="5" style="text-align:center;opacity:0.6">No requests sampled yet</td></tr>';

    const errs = (s.recentErrors || []).map(e => `
      <tr>
        <td>${new Date(e.at).toLocaleTimeString()}</td>
        <td><span class="pill pill-err">${e.status || '—'}</span></td>
        <td><code>${escapeHtml(e.method)} ${escapeHtml(e.url)}</code></td>
        <td>${escapeHtml(e.message || '')}</td>
      </tr>`).join('') || '<tr><td colspan="4" style="text-align:center;opacity:0.6">No errors recorded 🎉</td></tr>';

    return `
      <div class="admin-perf-page">
        <div class="page-header">
          <h1>📊 Performance</h1>
          <div class="page-actions">
            <label style="font-size:0.9em"><input type="checkbox" id="perf-autorefresh" checked> Auto-refresh 5s</label>
            <button id="perf-refresh" class="btn btn-secondary">↻ Refresh</button>
            <button id="perf-reset" class="btn btn-danger">Reset counters</button>
          </div>
        </div>

        <div class="perf-stat-grid">
          <div class="stat-card" data-tooltip="Time elapsed since bot process started.">
            <div class="stat-label">Uptime</div>
            <div class="stat-value">${fmtUptime(s.uptimeSec)}</div>
          </div>
          <div class="stat-card" data-tooltip="Resident Set Size: actual memory used. Heap shows current/total heap allocation.">
            <div class="stat-label">Memory (RSS)</div>
            <div class="stat-value">${s.process.memMB.toFixed(1)} MB</div>
            <div class="stat-sub">heap ${s.process.heapUsedMB.toFixed(1)} / ${s.process.heapTotalMB.toFixed(1)} MB</div>
          </div>
          <div class="stat-card" data-tooltip="Request latency percentiles (p50=median, p95, p99). Shows distribution of request times across requests sampled in current period.">
            <div class="stat-label">Latency (p50 / p95 / p99)</div>
            <div class="stat-value">${s.requests.p50Ms} / ${s.requests.p95Ms} / ${s.requests.p99Ms} ms</div>
            <div class="stat-sub">${s.requests.totalSampled} samples</div>
          </div>
          <div class="stat-card ${probeClass}" data-tooltip="External health check (GET /api/healthz). Healthy = 200 response and non-stale data. Runs every 5 minutes.">
            <div class="stat-label">Uptime Probe</div>
            <div class="stat-value">${p.lastCheckAt ? (probeOk ? '✓ Healthy' : '✗ Failing') : '— Pending'}</div>
            <div class="stat-sub">
              ${p.lastCheckAt ? `${new Date(p.lastCheckAt).toLocaleTimeString()} · ${p.lastDurMs}ms · ${p.totalFailures}/${p.totalChecks} failed` : 'first probe runs 30s after start'}
              ${p.consecutiveFailures > 0 ? `<br><b>${p.consecutiveFailures} consecutive failures</b>: ${escapeHtml(p.lastError || '')}` : ''}
            </div>
          </div>
        </div>

        <h2>Caches</h2>
        <div class="perf-cache-grid">
          <div class="stat-card">
            <div class="stat-label">Response cache</div>
            <div class="stat-value">${(resp.hitRate * 100).toFixed(1)}% hit</div>
            <div class="stat-sub">${resp.hits} hits / ${resp.misses} misses · ${resp.size}/${resp.max} entries · ${resp.evictions || 0} evicted</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Privacy cache</div>
            <div class="stat-value">${(priv.hitRate * 100).toFixed(1)}% hit</div>
            <div class="stat-sub">${priv.hits} hits / ${priv.misses} misses</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">SSE clients</div>
            <div class="stat-value">${probe.clients ?? 0} / ${probe.cap ?? '?'}</div>
            <div class="stat-sub">${probe.cap ? ((probe.clients / probe.cap) * 100).toFixed(0) + '% of cap' : ''}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">Sessions</div>
            <div class="stat-value">${s.sessions?.active ?? '—'} active</div>
            <div class="stat-sub">${s.sessions?.total ?? 0} total in store${s.sessions?.lastCleanup?.at ? ` · last cleanup ${new Date(s.sessions.lastCleanup.at).toLocaleTimeString()} (−${s.sessions.lastCleanup.expired})` : ''}</div>
          </div>
        </div>

        <h2>Top routes (by request count)</h2>
        <table class="admin-table perf-routes-table">
          <thead><tr><th>Route</th><th style="text-align:right">Requests</th><th style="text-align:right">Avg ms</th><th style="text-align:right">Max ms</th><th>Status mix</th></tr></thead>
          <tbody>${top}</tbody>
        </table>

        <h2>Recent errors (last ${Math.min(25, s.errorCountTotal)} of ${s.errorCountTotal})</h2>
        <table class="admin-table perf-errors-table">
          <thead><tr><th>Time</th><th>Status</th><th>Request</th><th>Message</th></tr></thead>
          <tbody>${errs}</tbody>
        </table>
      </div>
    `;
  }

  function wirePerfControls() {
    const $refresh = document.getElementById('perf-refresh');
    const $reset = document.getElementById('perf-reset');
    const $auto = document.getElementById('perf-autorefresh');
    if ($refresh) $refresh.onclick = load;
    if ($reset) $reset.onclick = async () => {
      if (!confirm('Reset performance counters? (does not affect caches)')) return;
      try {
        await apiPost('/api/admin/perf/reset', {});
        load();
      } catch (e) { alert('Reset failed: ' + e.message); }
    };
    // Auto-refresh loop, canceled on navigation (checked via currentRoute).
    // Sprint 10.6 — 5s → 30s and gated on visibilityState so a backgrounded
    // tab doesn't keep hammering the perf endpoint.
    if (_perfAutoRefreshTimer) clearInterval(_perfAutoRefreshTimer);
    const tick = () => {
      if (!currentRoute.startsWith('/admin/perf')) {
        clearInterval(_perfAutoRefreshTimer);
        _perfAutoRefreshTimer = null;
        return;
      }
      if (document.visibilityState !== 'visible') return;
      if ($auto && $auto.checked) load();
    };
    _perfAutoRefreshTimer = setInterval(tick, 30000);
  }

  load();
}

async function renderAdminDiagnostics() {
  if (!adminGuard()) return;
  $content.innerHTML = '<div class="loading-spinner">Loading diagnostics...</div>';

  try {
    const [diag, discordStatus, dbStats, commands] = await Promise.all([
      api('/api/admin/diagnostics'),
      api('/api/admin/discord-status'),
      api('/api/admin/db-stats'),
      api('/api/admin/commands'),
    ]);
    if (!diag) return;

    const sys = diag.system;
    const disc = diag.discord;
    const dbs = diag.databases;
    const sync = diag.syncService;

    // System stats panel
    const cpuBarPct = Math.max(2, sys.cpuUsage);
    const memPct = Math.round((sys.usedMemory / sys.totalMemory) * 100);
    const memBarPct = Math.max(2, memPct);

    // Discord status indicator
    const discColor = disc.connected ? 'var(--t-green)' : 'var(--t-red)';
    const discLabel = disc.connected ? 'CONNECTED' : 'DISCONNECTED';

    // DB integrity indicator
    const salesDbColor = dbs.sales.integrity ? 'var(--t-green)' : 'var(--t-red)';
    const aiDbColor = dbs.ai.integrity ? 'var(--t-green)' : 'var(--t-red)';

    // Table stats
    let tableRows = '';
    if (dbStats?.tables) {
      for (const [name, info] of Object.entries(dbStats.tables)) {
        tableRows += `<tr>
          <td class="text-mono" style="font-size:var(--fs-xs);">${escapeHtml(name)}</td>
          <td class="text-right text-mono">${info.error ? `<span style="color:var(--t-red);">${escapeHtml(info.error)}</span>` : info.count.toLocaleString()}</td>
        </tr>`;
      }
    }

    // Channel status
    let channelRows = '';
    if (discordStatus?.channels) {
      for (const [name, ch] of Object.entries(discordStatus.channels)) {
        const statusColor = ch.found ? 'var(--t-green)' : 'var(--t-red)';
        channelRows += `<tr>
          <td class="text-mono" style="font-size:var(--fs-xs);">${escapeHtml(name)}</td>
          <td style="color:${statusColor};">${ch.found ? '● Found' : '✕ Missing'}</td>
          <td style="font-size:var(--fs-xs);">${escapeHtml(ch.name || '—')}</td>
        </tr>`;
      }
    }

    // Command list for dropdown — store usage/examples/description as data attributes.
    // Option label kept short (just name + admin flag) to prevent dropdown overflow;
    // full description renders in a help panel below when a command is selected.
    const cmdList = commands || [];
    const cmdOptions = cmdList.map(c =>
      `<option value="${escapeHtml(c.name)}" data-usage="${escapeHtml(c.usage || '')}" data-desc="${escapeHtml(c.description || '')}" data-admin="${c.adminOnly ? '1' : ''}" data-examples="${escapeHtml((c.examples || []).join('|'))}">.${escapeHtml(c.name)}${c.adminOnly ? ' 🔒' : ''}</option>`
    ).join('');

    $content.innerHTML = `
      <div class="panel-grid">
        <!-- System Stats -->
        <div class="panel admin-panel">
          <div class="panel-header"><span class="panel-title">🖥 System</span></div>
          <div class="stat-row"><span class="stat-label">Hostname</span><span class="stat-value">${escapeHtml(sys.hostname)}</span></div>
          <div class="stat-row"><span class="stat-label">Platform</span><span class="stat-value">${sys.platform} ${sys.arch}</span></div>
          <div class="stat-row"><span class="stat-label">Node.js</span><span class="stat-value cyan">${sys.nodeVersion}</span></div>
          <div class="stat-row"><span class="stat-label">System Uptime</span><span class="stat-value">${fmtUptime(sys.uptime)}</span></div>
          <div class="stat-row"><span class="stat-label">Bot Uptime</span><span class="stat-value green">${fmtUptime(sys.processUptime)}</span></div>
          ${sys.cpuTemp != null ? `<div class="stat-row"><span class="stat-label">CPU Temp</span><span class="stat-value ${sys.cpuTemp > 80 ? 'red' : sys.cpuTemp > 60 ? 'orange' : 'green'}">${sys.cpuTemp.toFixed(1)}°C</span></div>` : ''}
          <div class="stat-row"><span class="stat-label">CPU</span><span class="stat-value">${escapeHtml(sys.cpuModel)} (${sys.cpuCores} cores)</span></div>
          <div class="stat-row"><span class="stat-label">CPU Usage</span><span class="stat-value">${sys.cpuUsage}%</span></div>
          <div class="bar-track" style="height:8px;margin-bottom:0.5rem;"><div class="bar-fill${cpuBarPct > 80 ? ' bar-fill-admin' : ''}" style="width:${cpuBarPct}%"></div></div>
          <div class="stat-row"><span class="stat-label">Memory</span><span class="stat-value">${fmtBytes(sys.usedMemory)} / ${fmtBytes(sys.totalMemory)} (${memPct}%)</span></div>
          <div class="bar-track" style="height:8px;margin-bottom:0.5rem;"><div class="bar-fill${memBarPct > 80 ? ' bar-fill-admin' : ''}" style="width:${memBarPct}%"></div></div>
          <div class="stat-row"><span class="stat-label">Process RSS</span><span class="stat-value">${fmtBytes(sys.processMemory.rss)}</span></div>
          <div class="stat-row"><span class="stat-label">Heap Used</span><span class="stat-value">${fmtBytes(sys.processMemory.heapUsed)} / ${fmtBytes(sys.processMemory.heapTotal)}</span></div>
        </div>

        <!-- Discord Status -->
        <div class="panel admin-panel">
          <div class="panel-header"><span class="panel-title">🤖 Discord</span></div>
          <div class="stat-row"><span class="stat-label">Status</span><span class="stat-value" style="color:${discColor};">● ${discLabel}</span></div>
          <div class="stat-row"><span class="stat-label">Gateway</span><span class="stat-value">${disc.gatewayStatus || '—'}</span></div>
          <div class="stat-row"><span class="stat-label">Ping</span><span class="stat-value ${(disc.ping || 0) > 200 ? 'red' : 'green'}">${disc.ping != null ? disc.ping + 'ms' : '—'}</span></div>
          <div class="stat-row"><span class="stat-label">Bot Uptime</span><span class="stat-value">${disc.uptime ? fmtUptime(disc.uptime / 1000) : '—'}</span></div>
          <div class="stat-row"><span class="stat-label">Guilds Cached</span><span class="stat-value">${disc.guilds}</span></div>
          <div class="stat-row"><span class="stat-label">Users Cached</span><span class="stat-value">${disc.users}</span></div>
          ${discordStatus?.guild ? `
            <div class="screen-divider"></div>
            <div class="stat-row"><span class="stat-label">Guild</span><span class="stat-value cyan">${escapeHtml(discordStatus.guild.name)}</span></div>
            <div class="stat-row"><span class="stat-label">Members</span><span class="stat-value">${discordStatus.guild.memberCount}</span></div>
            <div class="stat-row"><span class="stat-label">Available</span><span class="stat-value ${discordStatus.guild.available ? 'green' : 'red'}">${discordStatus.guild.available ? 'Yes' : 'No'}</span></div>
          ` : ''}
          ${discordStatus?.botUser ? `
            <div class="screen-divider"></div>
            <div class="stat-row"><span class="stat-label">Bot Tag</span><span class="stat-value">${escapeHtml(discordStatus.botUser.tag)}</span></div>
          ` : ''}
        </div>

        <!-- Database Status -->
        <div class="panel admin-panel">
          <div class="panel-header"><span class="panel-title">🗄 Databases</span></div>
          <div class="stat-row"><span class="stat-label">Sales DB</span><span class="stat-value" style="color:${salesDbColor};">● ${dbs.sales.integrity ? 'OK' : 'CORRUPT'}</span></div>
          <div class="stat-row"><span class="stat-label">Size</span><span class="stat-value">${fmtBytes(dbs.sales.size)}</span></div>
          <div class="stat-row"><span class="stat-label">Tables</span><span class="stat-value">${dbs.sales.tables}</span></div>
          <div class="screen-divider"></div>
          <div class="stat-row"><span class="stat-label">AI DB</span><span class="stat-value" style="color:${aiDbColor};">● ${dbs.ai.integrity ? 'OK' : 'CORRUPT'}</span></div>
          <div class="stat-row"><span class="stat-label">Size</span><span class="stat-value">${fmtBytes(dbs.ai.size)}</span></div>
          <div class="stat-row"><span class="stat-label">Tables</span><span class="stat-value">${dbs.ai.tables}</span></div>
          <div class="screen-divider"></div>
          <div class="stat-row"><span class="stat-label">Sync Queue</span><span class="stat-value ${sync.queueLength > 0 ? 'orange' : ''}">${sync.queueLength} pending</span></div>
          <div class="stat-row"><span class="stat-label">Sync Active</span><span class="stat-value">${sync.isProcessing ? 'Yes' : 'No'}</span></div>
          <div class="stat-row"><span class="stat-label">Data Stale</span><span class="stat-value ${sync.isStale ? 'red' : 'green'}">${sync.isStale ? 'Yes' : 'No'}</span></div>
          <div class="stat-row"><span class="stat-label">Last Sync</span><span class="stat-value">${relativeTime(sync.lastSync)}</span></div>
        </div>
      </div>

      <!-- Channel Status -->
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">📡 Discord Channels</span></div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Channel</th><th>Status</th><th>Name</th></tr></thead>
            <tbody>${channelRows || '<tr><td colspan="3" class="empty-state">No channel data.</td></tr>'}</tbody>
          </table>
        </div>
      </div>

      <!-- DB Table Details -->
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">📋 Table Row Counts</span></div>
        <div class="table-wrap">
          <table class="data-table">
            <thead><tr><th>Table</th><th class="text-right">Rows</th></tr></thead>
            <tbody>${tableRows || '<tr><td colspan="2" class="empty-state">No table data.</td></tr>'}</tbody>
          </table>
        </div>
      </div>

      <!-- Command Test Console -->
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">🔧 Command Test Console</span></div>
        <div class="filter-bar" style="margin-bottom:0.5rem;">
          <div class="filter-group">
            <label>Command</label>
            <select class="form-input" id="diag-cmd-select" style="min-width:180px;max-width:220px;text-overflow:ellipsis;overflow:hidden;">
              <option value="">Select a command...</option>
              ${cmdOptions}
            </select>
          </div>
          <div class="filter-group" id="diag-cmd-args-wrap">
            <label>Arguments</label>
            <input type="text" class="form-input" id="diag-cmd-args" placeholder="e.g. @PlayerTag" style="min-width:200px;">
          </div>
          <div class="filter-group">
            <label class="pf-checkbox-label"><input type="checkbox" id="diag-cmd-debug"> Debug</label>
          </div>
          <div class="filter-group" style="justify-content:flex-end;">
            <label>&nbsp;</label>
            <button class="btn btn-primary" id="diag-cmd-run">▶ Run</button>
          </div>
        </div>
        <div id="diag-cmd-help" class="cmd-help-box" style="display:none;margin-bottom:0.5rem;padding:0.5rem 0.75rem;background:rgba(0,0,0,0.25);border-left:3px solid var(--t-cyan);border-radius:var(--radius);font-size:var(--fs-xs);line-height:1.5;"></div>
        <div id="diag-cmd-dynamic-args" class="cmd-arg-dynamic" style="display:none;margin-bottom:0.5rem;"></div>
        <div id="diag-cmd-api-url" class="cmd-api-url" style="display:none;"></div>
        <div class="cmd-output-wrap">
          <div id="diag-cmd-output" class="cmd-output-box">Waiting for command...</div>
          <button class="cmd-copy-btn" id="diag-cmd-copy" title="Copy output" style="display:none;">📋</button>
        </div>
      </div>

      <!-- AI Code Assistant -->
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">🤖 AI Code Assistant</span></div>
        <p style="font-size:var(--fs-xs);color:var(--t-dim);margin-bottom:0.5rem;">Ask Gemini about bugs, errors, or code questions. It has full access to the bot's source code.</p>
        <div class="form-group">
          <textarea class="form-input" id="diag-ai-prompt" rows="3" placeholder="Describe the error or ask a question..." style="max-width:100%;width:100%;resize:vertical;"></textarea>
        </div>
        <div class="form-group">
          <textarea class="form-input" id="diag-ai-context" rows="2" placeholder="(Optional) Paste error logs or additional context..." style="max-width:100%;width:100%;resize:vertical;"></textarea>
        </div>
        <button class="btn btn-primary" id="diag-ai-ask">🔍 Ask AI</button>
        <div id="diag-ai-output" style="margin-top:0.75rem;background:rgba(0,0,0,0.3);border:1px solid var(--screen-border);border-radius:var(--radius);padding:0.75rem;min-height:80px;max-height:600px;overflow:auto;font-family:var(--font-mono);font-size:var(--fs-xs);color:var(--t-white);white-space:pre-wrap;display:none;">
        </div>
      </div>

      <!-- Hot Reload Controls -->
      <div class="panel admin-panel">
        <div class="panel-header"><span class="panel-title">↻ Hot Reload</span></div>
        <p style="font-size:var(--fs-xs);color:var(--t-dim);margin-bottom:0.75rem;">Reload modules without restarting the bot. Route files auto-reload on save. Use these controls for manual reloads or data layer refreshes.</p>
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:0.5rem;">
          <button class="btn btn-primary" id="reload-routes" style="width:100%;">↻ Reload API Routes</button>
          <button class="btn btn-primary" id="reload-commands" style="width:100%;">↻ Reload Commands</button>
          <button class="btn btn-primary" id="reload-caches" style="width:100%;">↻ Refresh Caches</button>
          <button class="btn btn-primary" id="reload-data-items" style="width:100%;">↻ Reload Items Data</button>
          <button class="btn btn-primary" id="reload-data-sales" style="width:100%;">↻ Reload Sales Data</button>
          <button class="btn btn-primary" id="reload-data-users" style="width:100%;">↻ Reload Users Data</button>
        </div>
        <div id="reload-status" style="margin-top:0.5rem;font-family:var(--font-mono);font-size:var(--fs-xs);color:var(--t-dim);min-height:1.5rem;"></div>
      </div>

      <!-- Refresh button -->
      <div style="text-align:center;margin:1rem 0;">
        <button class="btn btn-primary" id="diag-refresh">↻ Refresh Diagnostics</button>
      </div>
    `;

    // Event: Update arg placeholder & dynamic args when command changes
    document.getElementById('diag-cmd-select').addEventListener('change', (e) => {
      const opt = e.target.selectedOptions[0];
      const argsInput = document.getElementById('diag-cmd-args');
      const dynWrap = document.getElementById('diag-cmd-dynamic-args');
      const helpBox = document.getElementById('diag-cmd-help');
      if (!opt || !opt.value) {
        argsInput.placeholder = 'e.g. @PlayerTag';
        dynWrap.style.display = 'none';
        if (helpBox) { helpBox.style.display = 'none'; helpBox.innerHTML = ''; }
        return;
      }
      const usage = opt.dataset.usage || '';
      const desc = opt.dataset.desc || '';
      const admin = opt.dataset.admin === '1';
      const examples = (opt.dataset.examples || '').split('|').filter(Boolean);
      // Per-command help panel
      if (helpBox) {
        const exHtml = examples.length
          ? `<div style="margin-top:0.3rem;"><strong>Examples:</strong> ${examples.map(ex => `<code style="background:rgba(0,0,0,0.35);padding:1px 5px;border-radius:3px;margin-right:0.3rem;">${escapeHtml(ex)}</code>`).join('')}</div>`
          : '';
        helpBox.innerHTML = `
          <div><strong>.${escapeHtml(opt.value)}</strong>${admin ? ' <span style="color:var(--t-orange);">🔒 admin</span>' : ''}${desc ? ' — ' + escapeHtml(desc) : ''}</div>
          ${usage ? `<div style="margin-top:0.2rem;color:var(--t-dim);"><strong>Usage:</strong> <code style="color:var(--t-cyan);">${escapeHtml(usage)}</code></div>` : ''}
          ${exHtml}`;
        helpBox.style.display = 'block';
      }
      if (examples.length) {
        argsInput.placeholder = examples[0].replace(/^\.\w+\s*/, '');
      } else if (usage) {
        argsInput.placeholder = usage.replace(/^\.\w+\s*/, '');
      } else {
        argsInput.placeholder = '(no arguments)';
      }
      // Parse dynamic arg fields from usage pattern (e.g. "<item> [count]")
      const argMatches = usage.match(/[<\[]([^>\]]+)[>\]]/g);
      if (argMatches && argMatches.length > 1) {
        dynWrap.style.display = 'flex';
        dynWrap.innerHTML = argMatches.map(a => {
          const name = a.replace(/[<>\[\]]/g, '');
          const required = a.startsWith('<');
          return `<div class="cmd-arg-field"><label>${escapeHtml(name)}${required ? ' *' : ''}</label><input type="text" class="form-input cmd-dyn-arg" data-name="${escapeHtml(name)}" placeholder="${escapeHtml(name)}" style="min-width:120px;"></div>`;
        }).join('');
      } else {
        dynWrap.style.display = 'none';
      }
    });

    // Event: Enter key to submit command
    document.getElementById('diag-cmd-args').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); document.getElementById('diag-cmd-run').click(); }
    });

    // Event: Copy output
    document.getElementById('diag-cmd-copy').addEventListener('click', () => {
      const text = document.getElementById('diag-cmd-output').textContent;
      navigator.clipboard.writeText(text).then(() => toast('Copied to clipboard', 'success'));
    });

    // Event: Run command
    document.getElementById('diag-cmd-run').addEventListener('click', async () => {
      const cmd = document.getElementById('diag-cmd-select').value;
      const argsEl = document.getElementById('diag-cmd-args');
      const debugOn = document.getElementById('diag-cmd-debug')?.checked || false;
      const output = document.getElementById('diag-cmd-output');
      const copyBtn = document.getElementById('diag-cmd-copy');
      const apiUrlEl = document.getElementById('diag-cmd-api-url');

      // Collect dynamic args if visible, else use raw args input
      let args = argsEl.value.trim();
      const dynArgs = document.querySelectorAll('.cmd-dyn-arg');
      if (dynArgs.length > 0) {
        const parts = [];
        dynArgs.forEach(a => { if (a.value.trim()) parts.push(a.value.trim()); });
        if (parts.length > 0) args = parts.join(' ');
      }

      if (!cmd) { output.textContent = 'Error: Select a command first.'; return; }

      // Show API URL being called
      const apiPath = `/api/admin/test-command`;
      apiUrlEl.style.display = 'block';
      apiUrlEl.textContent = `POST ${apiPath}  { commandName: "${cmd}", args: "${args}"${debugOn ? ', debug: true' : ''} }`;

      output.innerHTML = '<span class="loading-spinner" style="padding:0.3rem 0;">Running...</span>';
      copyBtn.style.display = 'none';

      try {
        const payload = { commandName: cmd, args };
        if (debugOn) payload.debug = true;
        const result = await apiPost('/api/admin/test-command', payload);
        copyBtn.style.display = 'block';
        if (result.success) {
          output.innerHTML = `<span style="color:var(--t-green);">✓ ${escapeHtml(result.commandName)} completed in ${result.elapsed}ms</span>\n\n`;
          if (result.result.content) {
            output.innerHTML += `<span style="color:var(--t-cyan);">Content:</span>\n${escapeHtml(result.result.content)}\n`;
          }
          if (result.result.embeds) {
            output.innerHTML += `\n<span style="color:var(--t-cyan);">Embeds:</span>\n${escapeHtml(JSON.stringify(result.result.embeds, null, 2))}`;
          }
          if (result.result.panelId) {
            output.innerHTML += `\n<span style="color:var(--t-cyan);">Panel:</span> ${escapeHtml(result.result.panelId)}`;
            if (result.result.title) output.innerHTML += ` — ${escapeHtml(result.result.title)}`;
            output.innerHTML += '\n';
          }
          if (result.result.data) {
            output.innerHTML += `\n<span style="color:var(--t-cyan);">Data:</span>\n${escapeHtml(JSON.stringify(result.result.data, null, 2))}`;
          }
          if (debugOn && result.debug) {
            output.innerHTML += `\n\n<span style="color:var(--t-orange);">─── Debug Info ───</span>\n${escapeHtml(JSON.stringify(result.debug, null, 2))}`;
          }
        } else {
          output.innerHTML = `<span style="color:var(--t-red);">✕ Error in ${escapeHtml(result.commandName)}:</span>\n${escapeHtml(result.error)}\n\n<span style="color:var(--t-dim);">Stack:</span>\n${escapeHtml(result.stack || 'No stack trace')}`;
        }
      } catch (err) {
        copyBtn.style.display = 'block';
        output.innerHTML = `<span style="color:var(--t-red);">✕ Request failed:</span> ${escapeHtml(err.message)}`;
      }
    });

    // Event: AI Assistant
    document.getElementById('diag-ai-ask').addEventListener('click', async () => {
      const prompt = document.getElementById('diag-ai-prompt').value.trim();
      const context = document.getElementById('diag-ai-context').value.trim();
      const output = document.getElementById('diag-ai-output');

      if (!prompt) { toast('Enter a question or error description.', 'error'); return; }

      output.style.display = 'block';
      output.innerHTML = '<span class="loading-spinner" style="padding:0.3rem 0;">Analyzing with Gemini...</span>';

      try {
        const result = await apiPost('/api/admin/ai-assist', { prompt, context: context || undefined });
        output.innerHTML = escapeHtml(result.response || 'No response.');
      } catch (err) {
        output.innerHTML = `<span style="color:var(--t-red);">AI Error:</span> ${escapeHtml(err.message)}`;
      }
    });

    // Event: Refresh
    document.getElementById('diag-refresh').addEventListener('click', renderAdminDiagnostics);

    // Event: Hot reload buttons
    async function doReload(url, body, label) {
      const status = document.getElementById('reload-status');
      status.innerHTML = `<span style="color:var(--t-cyan);">↻ Reloading ${escapeHtml(label)}...</span>`;
      try {
        const result = await apiPost(url, body);
        if (result.ok) {
          status.innerHTML = `<span style="color:var(--t-green);">✓ ${escapeHtml(label)} reloaded successfully.</span>`;
        } else {
          status.innerHTML = `<span style="color:var(--t-red);">✕ ${escapeHtml(label)} failed: ${escapeHtml(result.error || 'Unknown error')}</span>`;
        }
      } catch (err) {
        status.innerHTML = `<span style="color:var(--t-red);">✕ ${escapeHtml(err.message)}</span>`;
      }
    }

    document.getElementById('reload-routes')?.addEventListener('click', () => doReload('/api/admin/reload/routes', {}, 'API Routes'));
    document.getElementById('reload-commands')?.addEventListener('click', () => doReload('/api/admin/reload/commands', {}, 'Discord Commands'));
    document.getElementById('reload-caches')?.addEventListener('click', () => doReload('/api/admin/reload/caches', {}, 'Item/Guild Caches'));
    document.getElementById('reload-data-items')?.addEventListener('click', () => doReload('/api/admin/reload/data', { key: 'items' }, 'Items Data'));
    document.getElementById('reload-data-sales')?.addEventListener('click', () => doReload('/api/admin/reload/data', { key: 'sales' }, 'Sales Data'));
    document.getElementById('reload-data-users')?.addEventListener('click', () => doReload('/api/admin/reload/data', { key: 'users' }, 'Users Data'));

  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load diagnostics: ${escapeHtml(err.message)}</div>`;
  }
}

// ══════════════════════════════════════════════════════════════════════════
// ── ADMIN FISSAL — AI Cost Tracking & Invocation Dashboard ──────────────
// ══════════════════════════════════════════════════════════════════════════

async function renderAdminFissal() {
  if (!currentUser?.isAdmin) return renderLoginPrompt();
  $content.innerHTML = '<div class="loading-spinner">Loading Fissal AI control...</div>';

  try {
    const [data, modelConfig] = await Promise.all([
      api('/api/admin/fissal/invocations'),
      api('/api/admin/fissal/model-config').catch(() => ({ config: [], available_models: [] })),
    ]);
    const inv = data || {};
    const totalCost = (inv.totals?.cost || 0).toFixed(6);
    const totalTokensIn = (inv.totals?.tokensIn || 0).toLocaleString();
    const totalTokensOut = (inv.totals?.tokensOut || 0).toLocaleString();
    const totalInvocations = (inv.totals?.count || 0).toLocaleString();

    // Source breakdown chips
    const sourceChips = (inv.bySource || []).map(s => `
      <div class="admin-fissal-source-chip">
        <span class="chip-label">${escapeHtml(s.source)}</span>
        <span class="chip-value">${s.count.toLocaleString()} calls</span>
        <span class="chip-cost">$${(s.cost || 0).toFixed(6)}</span>
      </div>
    `).join('');

    // Model breakdown cards
    const modelCards = (inv.byModel || []).map(m => `
      <div class="admin-fissal-model-card">
        <div class="model-card-name">${escapeHtml(m.model || 'unknown')}</div>
        <div class="model-card-stats">
          <span>${m.count.toLocaleString()} calls</span>
          <span style="color:var(--admin);">$${(m.cost || 0).toFixed(6)}</span>
        </div>
        <div class="model-card-tokens" style="font-size:var(--fs-xxs);color:var(--t-dim);">
          ${(m.tokensIn || 0).toLocaleString()} in / ${(m.tokensOut || 0).toLocaleString()} out
        </div>
      </div>
    `).join('');

    // Label/task breakdown chips
    const labelChips = (inv.byLabel || []).map(l => `
      <div class="admin-fissal-task-chip">
        <span class="chip-label">${escapeHtml(l.label || 'unlabeled')}</span>
        <span class="chip-value">${l.count.toLocaleString()}</span>
        <span class="chip-cost">$${(l.cost || 0).toFixed(6)}</span>
      </div>
    `).join('');

    // Daily cost chart
    const daily = inv.dailyCosts || [];
    const maxDailyCost = Math.max(...daily.map(d => d.cost), 0.000001);
    const dailyBars = daily.map(d => {
      const pct = Math.max((d.cost / maxDailyCost) * 100, 2);
      const label = new Date(d.day * 86400000).toISOString().slice(5, 10); // MM-DD
      return `<div class="admin-fissal-daily-bar" style="height:${pct}%" data-tooltip="${label}: $${d.cost.toFixed(6)} (${d.count})"></div>`;
    }).join('');

    // Model config table
    const availModels = modelConfig.available_models || [];
    const defaultModel = modelConfig.default_model || '';
    const defaultLabel = defaultModel ? `(default — ${defaultModel})` : '(default)';
    const configMap = {};
    (modelConfig.config || []).forEach(c => { configMap[c.task_key] = c.model_name; });
    const tasks = ['general', 'memory', 'pricing', 'intent', 'code-assist'];
    const configRows = tasks.map(task => {
      const currentModel = configMap[task] || defaultLabel;
      // Always render the default option FIRST and use empty value so the POST
      // handler knows to remove the row (fall back to hardcoded default).
      const defaultOpt = `<option value="" ${currentModel === defaultLabel ? 'selected' : ''}>${escapeHtml(defaultLabel)}</option>`;
      const options = defaultOpt + availModels.map(m =>
        `<option value="${escapeHtml(m)}" ${m === currentModel ? 'selected' : ''}>${escapeHtml(m)}</option>`
      ).join('');
      return `
        <tr>
          <td class="text-mono">${escapeHtml(task)}</td>
          <td><select class="model-config-select" data-task="${escapeHtml(task)}">${options}</select></td>
        </tr>
      `;
    }).join('');

    // Recent invocations table
    const rows = (inv.invocations || []).slice(0, 50).map(r => `
      <tr>
        <td>${new Date(r.timestamp).toLocaleString([], { month:'short', day:'2-digit', hour:'2-digit', minute:'2-digit' })}</td>
        <td>${escapeHtml(r.source || '—')}</td>
        <td>${escapeHtml(r.model || '—')}</td>
        <td>${escapeHtml(r.user_name || '—')}</td>
        <td class="text-mono">${(r.tokens_in || 0).toLocaleString()}</td>
        <td class="text-mono">${(r.tokens_out || 0).toLocaleString()}</td>
        <td class="text-mono" style="color:var(--admin);">$${(r.cost || 0).toFixed(6)}</td>
        <td>${r.duration_ms ? r.duration_ms + 'ms' : '—'}</td>
      </tr>
    `).join('');

    $content.innerHTML = `
      <div class="panel">
        <div class="panel-header"><span class="panel-title" style="color:var(--admin);">🤖 Fissal AI Control</span></div>

        <!-- Totals -->
        <div class="admin-fissal-layout">
          <div class="admin-fissal-stat-card"><span class="label">Total Cost</span><span class="value" style="color:var(--admin);">$${totalCost}</span></div>
          <div class="admin-fissal-stat-card"><span class="label">Invocations</span><span class="value">${totalInvocations}</span></div>
          <div class="admin-fissal-stat-card"><span class="label">Tokens In</span><span class="value">${totalTokensIn}</span></div>
          <div class="admin-fissal-stat-card"><span class="label">Tokens Out</span><span class="value">${totalTokensOut}</span></div>
        </div>

        <!-- Model breakdown -->
        <div class="panel-header" style="border-bottom:none;padding-top:0.5rem;"><span class="panel-title" style="font-size:var(--fs-xs);">By Model</span></div>
        <div class="admin-fissal-model-grid">${modelCards || '<span class="text-dim">No model data</span>'}</div>

        <!-- Task/Label breakdown -->
        <div class="panel-header" style="border-bottom:none;padding-top:0.5rem;"><span class="panel-title" style="font-size:var(--fs-xs);">By Task Label</span></div>
        <div class="admin-fissal-task-grid">${labelChips || '<span class="text-dim">No task data</span>'}</div>

        <!-- Source breakdown -->
        <div class="panel-header" style="border-bottom:none;padding-top:0.5rem;"><span class="panel-title" style="font-size:var(--fs-xs);">By Source</span></div>
        <div class="admin-fissal-source-breakdown">${sourceChips || '<span class="text-dim">No data</span>'}</div>

        <!-- Daily cost chart -->
        <div class="panel-header" style="border-bottom:none;"><span class="panel-title" style="font-size:var(--fs-xs);">Daily Cost (30 days)</span></div>
        <div class="admin-fissal-daily-chart">${dailyBars || '<span class="text-dim">No data</span>'}</div>

        <div class="screen-divider"></div>

        <!-- Model config -->
        <div class="panel-header" style="border-bottom:none;"><span class="panel-title" style="font-size:var(--fs-xs);">⚙ Model Assignment</span></div>
        <div class="model-config-table">
          <table class="data-table">
            <thead><tr><th>Task</th><th>Model</th></tr></thead>
            <tbody>${configRows}</tbody>
          </table>
          <button class="btn btn-primary" id="admin-model-config-save" style="margin-top:0.5rem;">Save Model Config</button>
        </div>

        <div class="screen-divider"></div>

        <!-- Invocations table -->
        <div class="panel-header" style="border-bottom:none;"><span class="panel-title" style="font-size:var(--fs-xs);">Recent Invocations</span></div>
        <div class="admin-fissal-invocations-table">
          <div class="table-wrap">
            <table class="data-table">
              <thead><tr>
                <th>Time</th><th>Source</th><th>Model</th><th>User</th><th>Tok In</th><th>Tok Out</th><th>Cost</th><th>Duration</th>
              </tr></thead>
              <tbody>${rows || '<tr><td colspan="8" class="text-dim">No invocations logged yet.</td></tr>'}</tbody>
            </table>
          </div>
        </div>

        <div class="screen-divider"></div>
        <button class="btn btn-danger admin-fissal-clear-btn" id="admin-fissal-clear">🗑 Clear Invocation Logs</button>
        <button class="btn btn-primary" id="admin-fissal-refresh" style="margin-top:0.5rem;margin-left:0.5rem;">↻ Refresh</button>
      </div>
    `;

    document.getElementById('admin-fissal-refresh')?.addEventListener('click', renderAdminFissal);
    document.getElementById('admin-fissal-clear')?.addEventListener('click', async () => {
      if (!confirm('Clear all Fissal invocation logs?')) return;
      try {
        await apiDelete('/api/admin/fissal/invocations');
        toast('Invocation logs cleared.', 'success');
        renderAdminFissal();
      } catch (err) {
        toast(err.message, 'error');
      }
    });
    document.getElementById('admin-model-config-save')?.addEventListener('click', async () => {
      const selects = document.querySelectorAll('.model-config-select');
      const config = {};
      selects.forEach(sel => {
        const task = sel.dataset.task;
        const val = sel.value;
        if (val && val !== '(default)') config[task] = val;
        else config[task] = null;
      });
      try {
        await apiPost('/api/admin/fissal/model-config', config);
        toast('Model config saved.', 'success');
      } catch (err) {
        toast(err.message, 'error');
      }
    });
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load Fissal AI control: ${escapeHtml(err.message)}</div>`;
  }
}

// ══════════════════════════════════════════════════════════════════════════
// ── ADMIN STORAGE MANAGEMENT ────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════
async function renderAdminStorage() {
  if (!currentUser?.isAdmin) return renderLoginPrompt();
  $content.innerHTML = '<div class="loading-spinner">Loading storage data...</div>';

  try {
    const data = await api('/api/admin/storage');

    const usageColor = data.usagePercent > 80 ? 'var(--t-red)' : data.usagePercent > 50 ? 'var(--gold-bright)' : 'var(--t-green)';

    const fileRows = (data.files || []).map(f => `
      <tr>
        <td class="text-mono">${escapeHtml(f.name)}</td>
        <td>${f.sizeMB.toFixed(3)} MB</td>
        <td>${relativeTime(f.createdAt)}</td>
      </tr>
    `).join('');

    $content.innerHTML = `
      <div class="panel" style="max-width: 700px;">
        <div class="panel-header">
          <span class="panel-title">💾 Chart Storage Management</span>
          <div style="display:flex;gap:0.5rem;">
            <button class="btn btn-sm" id="storage-refresh">↻ Refresh</button>
            <button class="btn btn-sm btn-danger" id="storage-cleanup">🧹 Cleanup</button>
          </div>
        </div>

        <div class="admin-storage-stats">
          <div class="stat-row">
            <span class="stat-label">Usage</span>
            <span class="stat-value" style="color:${usageColor}">${data.totalMB} MB / ${data.maxMB} MB (${data.usagePercent}%)</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">Files</span>
            <span class="stat-value">${data.fileCount}</span>
          </div>
          <div class="admin-storage-bar">
            <div class="admin-storage-bar-fill" style="width:${Math.min(data.usagePercent, 100)}%;background:${usageColor};"></div>
          </div>
        </div>

        ${data.files?.length ? `
        <div class="table-wrap" style="max-height:400px;overflow-y:auto;margin-top:0.5rem;">
          <table class="data-table">
            <thead><tr><th>File</th><th>Size</th><th>Created</th></tr></thead>
            <tbody>${fileRows}</tbody>
          </table>
        </div>` : '<div class="empty-state" style="padding:1rem;">No chart files found.</div>'}
      </div>`;

    document.getElementById('storage-refresh')?.addEventListener('click', renderAdminStorage);
    document.getElementById('storage-cleanup')?.addEventListener('click', async () => {
      if (!confirm('Force cleanup chart storage?')) return;
      try {
        const result = await apiPost('/api/admin/storage/cleanup', {});
        toast(`Cleaned up ${result.deleted} files. Remaining: ${result.remainingMB} MB`, 'success');
        renderAdminStorage();
      } catch (err) {
        toast(err.message, 'error');
      }
    });
  } catch (err) {
    $content.innerHTML = `<div class="error-msg">Failed to load storage: ${escapeHtml(err.message)}</div>`;
  }
}

// ══════════════════════════════════════════════════════════════════════════
// ── FISSAL CHAT BUBBLE ──────────────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════

function initFissalBubble() {
  const bubble = document.getElementById('fissal-bubble');
  const chatFloat = document.getElementById('fissal-chat-float');
  const closeBtn = document.getElementById('fissal-btn-close');
  const brainBtn = document.getElementById('fissal-btn-brain');
  const sendBtn = document.getElementById('fissal-chat-send');
  const inputEl = document.getElementById('fissal-chat-input');
  const chatHeader = chatFloat?.querySelector('.fissal-chat-header');

  if (!bubble || !chatFloat) return;

  // ── Drag support for the bubble ──
  let bubbleDragging = false;
  let bubbleDragged = false;
  let dragOffsetX = 0, dragOffsetY = 0;

  bubble.addEventListener('mousedown', (e) => {
    bubbleDragging = true;
    bubbleDragged = false;
    dragOffsetX = e.clientX - bubble.getBoundingClientRect().left;
    dragOffsetY = e.clientY - bubble.getBoundingClientRect().top;
    bubble.classList.add('dragging');
  });

  document.addEventListener('mousemove', (e) => {
    if (!bubbleDragging) return;
    bubbleDragged = true;
    const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--app-header-h')) || 48;
    const x = Math.max(0, Math.min(window.innerWidth - 56, e.clientX - dragOffsetX));
    const y = Math.max(headerH, Math.min(window.innerHeight - 56, e.clientY - dragOffsetY));
    bubble.style.right = 'auto';
    bubble.style.bottom = 'auto';
    bubble.style.left = x + 'px';
    bubble.style.top = y + 'px';
  });

  document.addEventListener('mouseup', () => {
    if (bubbleDragging) {
      bubbleDragging = false;
      bubble.classList.remove('dragging');
    }
  });

  // ── Drag support for the chat float window ──
  let chatDragging = false;
  let chatDragOffX = 0, chatDragOffY = 0;

  if (chatHeader) {
    chatHeader.addEventListener('mousedown', (e) => {
      if (e.target.closest('button')) return; // Don't drag on button clicks
      chatDragging = true;
      chatDragOffX = e.clientX - chatFloat.getBoundingClientRect().left;
      chatDragOffY = e.clientY - chatFloat.getBoundingClientRect().top;
      chatFloat.classList.add('dragging');
      chatHeader.classList.add('dragging');
    });
  }

  document.addEventListener('mousemove', (e) => {
    if (!chatDragging) return;
    const headerH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--app-header-h')) || 48;
    const x = Math.max(0, Math.min(window.innerWidth - 340, e.clientX - chatDragOffX));
    const y = Math.max(headerH, Math.min(window.innerHeight - 50, e.clientY - chatDragOffY));
    chatFloat.style.right = 'auto';
    chatFloat.style.bottom = 'auto';
    chatFloat.style.left = x + 'px';
    chatFloat.style.top = y + 'px';
  });

  document.addEventListener('mouseup', () => {
    if (chatDragging) {
      chatDragging = false;
      chatFloat.classList.remove('dragging');
      chatHeader?.classList.remove('dragging');
    }
  });

  // Click to toggle chat (only if not dragged)
  bubble.addEventListener('click', (e) => {
    if (bubbleDragged) return;
    if (!currentUser) {
      fissalToast('Log in with Discord to chat with Fissal.');
      return;
    }
    const isVisible = chatFloat.style.display !== 'none';
    chatFloat.style.display = isVisible ? 'none' : 'flex';
    chatFloat.classList.remove('collapsed');
    const badge = document.getElementById('fissal-bubble-badge');
    if (badge) badge.style.display = 'none';
    if (!isVisible) _fissalSetUnread(false); // clear amber ring on open
    if (!isVisible && inputEl) inputEl.focus();
    // Sync session into bubble on open, or show greeting on first ever open
    if (!isVisible) {
      const msgsEl = document.getElementById('fissal-chat-msgs');
      if (msgsEl) {
        if (_fissalMsgs.length > 0) {
          msgsEl.innerHTML = _fissalMsgs.map(m => _fissalRenderMsg(m, 'bubble')).join('');
          msgsEl.scrollTop = msgsEl.scrollHeight;
        } else if (msgsEl.children.length === 0) {
          const ts = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
          const name = currentUser?.esoTag || currentUser?.displayName || 'traveller';
          const greeting = formatFissalResponse(
            `^,,^ Ah, ${escapeHtml(name)}! This one has been watching the ledgers. Ask about your sales, item prices, the leaderboard — or anything on your mind.`
          );
          msgsEl.innerHTML = `
            <div class="fissal-msg fissal-msg-bot">
              <span class="fissal-avatar fissal-avatar-svg">${fissalAvatarSvg(22)}</span>
              <div class="fissal-bubble-msg">
                <div class="fissal-bubble-content">${greeting}</div>
                <span class="fissal-bubble-time">${ts}</span>
              </div>
            </div>`;
        }
      }
    }
  });

  // Close/minimize button — collapse to small header-only window
  if (closeBtn) closeBtn.addEventListener('click', () => {
    const isCollapsed = chatFloat.classList.contains('collapsed');
    if (isCollapsed) {
      chatFloat.style.display = 'none';
      chatFloat.classList.remove('collapsed');
    } else {
      chatFloat.classList.add('collapsed');
    }
  });

  // Brain button → memories page
  if (brainBtn) brainBtn.addEventListener('click', () => {
    if (currentUser) {
      navigate('#/fissal');
      chatFloat.style.display = 'none';
    }
  });

  // ── Scroll fade detection for chat messages ──
  const chatMsgs = document.getElementById('fissal-chat-msgs');
  if (chatMsgs) {
    chatMsgs.addEventListener('scroll', () => {
      const atBottom = chatMsgs.scrollHeight - chatMsgs.scrollTop - chatMsgs.clientHeight < 20;
      chatMsgs.classList.toggle('at-bottom', atBottom);
    }, { passive: true });
  }

  // Send message
  async function sendFissalMessage() {
    if (!inputEl) return;
    const msg = inputEl.value.trim();
    if (!msg) return;
    inputEl.value = '';

    const msgsEl = document.getElementById('fissal-chat-msgs');

    // Add user message to the shared session (syncs page chatbox too if open)
    _fissalAddMsg({ role: 'user', content: msg, ts: Date.now() });

    // Typing indicator (transient — not stored in session)
    if (msgsEl) {
      msgsEl.insertAdjacentHTML('beforeend', `
        <div class="fissal-msg fissal-msg-bot" id="fissal-typing">
          <span class="fissal-avatar fissal-avatar-svg">${fissalAvatarSvg(22)}</span>
          <div class="fissal-bubble-msg"><div class="fissal-typing"><span class="fissal-typing-dot"></span><span class="fissal-typing-dot"></span><span class="fissal-typing-dot"></span></div></div>
        </div>`);
      msgsEl.scrollTop = msgsEl.scrollHeight;
    }

    try {
      const result = await apiPost('/api/fissal/analyze', {
        prompt: msg, weeksAgo: 0,
        session_id: _fissalActiveSessionId,
      });
      document.getElementById('fissal-typing')?.remove();
      const rawText = result.analysis || result.response || 'No response.';
      if (result.session_id) _fissalActiveSessionId = result.session_id;
      _fissalAddMsg({ role: 'bot', content: rawText, html: formatFissalResponse(rawText), ts: Date.now() });
    } catch (err) {
      document.getElementById('fissal-typing')?.remove();
      const errHtml = `<span class="fissal-error">#~# ${escapeHtml(err.message)}</span>`;
      _fissalAddMsg({ role: 'bot', content: `#~# ${err.message}`, html: errHtml, ts: Date.now() });
    }
  }

  if (sendBtn) sendBtn.addEventListener('click', sendFissalMessage);
  if (inputEl) {
    // Sprint 12 Phase 4: textarea — Enter sends, Shift+Enter inserts a newline,
    // and the textarea auto-resizes up to its CSS max-height.
    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        sendFissalMessage();
      }
    });
    const autosize = () => {
      inputEl.style.height = 'auto';
      inputEl.style.height = Math.min(inputEl.scrollHeight, 144) + 'px';
    };
    inputEl.addEventListener('input', autosize);
    // Reset height after send (the value is cleared inside sendFissalMessage).
    const origSend = sendFissalMessage;
    // Hook the existing send to also reset autosize after dispatch.
    sendBtn?.addEventListener('click', () => requestAnimationFrame(autosize));
    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) requestAnimationFrame(autosize);
    });
  }

  // Periodic goggle expression animation
  const expressions = ['', 'expr-happy', 'expr-think', 'expr-excited'];
  setInterval(() => {
    const expr = expressions[Math.floor(Math.random() * expressions.length)];
    bubble.className = 'fissal-bubble' + (expr ? ' ' + expr : '');
  }, 8000);
}

// ── Profile modal close on backdrop click ────────────────────
document.addEventListener('click', (e) => {
  const modal = document.getElementById('profile-modal');
  if (modal && e.target === modal) closeProfileModal();
});
// ── Sprint 12 Phase 3: ESC pops one modal level ──────────────
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (_isProfileModalOpen()) {
    e.preventDefault();
    popModal();
  }
});

// ── Make seller name cells clickable for profiles ─────────────
function nameCell(sellerName, extraClasses = '') {
  const safe = escapeHtml(sellerName || '—');
  if (sellerName) {
    const safeAttr = safe.replace(/'/g, "\\'");
    // Sprint 10.0: Spy Mode — extra admin-only inspection controls. Rendered
    // inline but hidden unless body.spy-mode is active (CSS handles visibility).
    const spyControls = (currentUser?.isAdmin)
      ? `<span class="spy-controls">
           <button class="spy-btn" title="View sales" data-ui-action="spy-sales" data-tag="${encodeURIComponent(sellerName)}">📊</button>
           <button class="spy-btn" title="Filter raffle" data-ui-action="spy-raffle" data-tag="${encodeURIComponent(sellerName)}">🎟</button>
         </span>`
      : '';
    return `<td class="clickable-tag ${extraClasses}" data-user="${safe}">${safe}${spyControls}</td>`;
  }
  return `<td class="${extraClasses}">${safe}</td>`;
}

// ── Boot Sequence ────────────────────────────────────────────────────────
const BOOT_LINES = [
  { text: '> Initializing Dwemer relay network...', cls: 'boot-ok', delay: 0 },
  { text: '> Calibrating lunar frequencies... Jode ✓ Jone ✓', cls: 'boot-ok', delay: 200 },
  { text: '> Fissal neural link: ESTABLISHED', cls: 'boot-ok', delay: 400 },
  { text: '> Loading guild manifests...', cls: 'boot-ok', delay: 550 },
  { text: '> Trade data synchronized ✓', cls: 'boot-ok', delay: 700 },
  { text: '  Welcome to Redfur Trading', cls: 'boot-final', delay: 900 },
];

function runBootSequence() {
  if (localStorage.getItem('redfur-boot-seen')) return Promise.resolve();
  localStorage.setItem('redfur-boot-seen', '1');

  return new Promise(resolve => {
    const overlay = document.createElement('div');
    overlay.className = 'boot-overlay';
    overlay.addEventListener('click', () => {
      overlay.classList.add('boot-fade-out');
      setTimeout(() => { overlay.remove(); resolve(); }, 500);
    });
    document.body.appendChild(overlay);

    BOOT_LINES.forEach(({ text, cls, delay }) => {
      setTimeout(() => {
        const line = document.createElement('div');
        line.className = `boot-line ${cls}`;
        line.textContent = text;
        overlay.appendChild(line);
      }, delay);
    });

    setTimeout(() => {
      overlay.classList.add('boot-fade-out');
      setTimeout(() => { overlay.remove(); resolve(); }, 500);
    }, 2200);
  });
}

// ── Footer Rotating Tips ────────────────────────────────────────────────
const FOOTER_TIPS = [
  '🐱 Tip: Use quality filters to narrow your search',
  '🔮 Tip: Click any seller name to view their profile',
  '📜 Tip: Check the Sync Log to see who uploaded data',
  '👑 Tip: Leaderboard updates every trade week reset',
  '⚡ Tip: Toggle CRT effects in Settings for the full experience',
  '🏡 Tip: Overview shows your guild activity at a glance',
  '🌙 The moons above track Tamriel time — hover to see phases',
  '🐱 Fissal says: "Every coin tells a story, friend."',
  '🔮 Fissal says: "The crystals reveal much to patient eyes."',
  '📜 Fissal says: "A good trader reads the trends, not just the prices."',
];

let _footerTipIdx = 0;
let _footerTipInterval = null;

function initFooterTips() {
  const footer = document.getElementById('app-footer');
  if (!footer) return;

  // Create tip element if it doesn't exist
  let tip = footer.querySelector('.footer-tip');
  if (!tip) {
    tip = document.createElement('span');
    tip.className = 'footer-tip';
    footer.appendChild(tip);
  }

  function showNextTip() {
    tip.classList.add('fading');
    setTimeout(() => {
      tip.textContent = FOOTER_TIPS[_footerTipIdx % FOOTER_TIPS.length];
      tip.classList.remove('fading');
      _footerTipIdx++;
    }, 400);
  }

  showNextTip();
  if (_footerTipInterval) clearInterval(_footerTipInterval);
  _footerTipInterval = setInterval(showNextTip, 30000);
}

// ── Drag-to-scroll on horizontal overflow containers ────────────────────
// Applies to .table-wrap and .gb-wrap. Delegated from document so it
// automatically covers containers added after page load.
(function initDragScroll() {
  const SELECTOR = '.table-wrap, .gb-wrap, #app-nav, .navbar-scroll-container, .live-feed-filters, .item-trend-chart';
  let _ds = null; // active drag state

  document.addEventListener('mousedown', (e) => {
    const container = e.target.closest(SELECTOR);
    if (!container) return;
    // Only horizontal-scrollable containers are interesting
    if (container.scrollWidth <= container.clientWidth) return;
    _ds = {
      el: container,
      startX: e.clientX,
      startScroll: container.scrollLeft,
      moved: false,
    };
    container.classList.add('drag-scroll-active');
    e.preventDefault(); // prevent text selection while dragging
  }, { passive: false });

  document.addEventListener('mousemove', (e) => {
    if (!_ds) return;
    const dx = e.clientX - _ds.startX;
    if (Math.abs(dx) > 3) _ds.moved = true;
    _ds.el.scrollLeft = _ds.startScroll - dx;
  });

  function stopDrag() {
    if (!_ds) return;
    _ds.el.classList.remove('drag-scroll-active');
    _ds = null;
  }
  document.addEventListener('mouseup', stopDrag);
  document.addEventListener('mouseleave', stopDrag);

  // Prevent click events from firing after a drag (avoids accidental row clicks)
  document.addEventListener('click', (e) => {
    if (_ds?.moved) e.stopPropagation();
  }, true);
})();

// ── Boot ─────────────────────────────────────────────────────────────────
runBootSequence().then(() => {
  init();
  initFissalBubble();
  initFooterTips();
});
